import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import TourGuide from './TourGuide';
import ModuleHelp, { ManualButton, MANUAL_STEP } from './ModuleHelp';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import i18n from './i18n';
import ListPicker from './ListPicker';
import TicketFormFields, { missingRequired, renderBlock, renderBlockHtml } from './TicketFormFields';
import SkuMaterialList from './SkuMaterialList';
import { useSkuMaterialDrafts } from './skuMaterialDrafts';
import { Pencil, Copy, Trash2, Plus, Download, Filter, X, ArrowUpAZ, ArrowDownZA, ChevronDown, ChevronRight, ArrowUp, ArrowDown, CalendarDays, Ticket as TicketIcon, Eye, History as HistoryIcon } from 'lucide-react';
import { AiStatusPill } from './AiTag';
import RichText, { richToMailHtml, richToText } from './RichText';
import { usePageItem } from './pageContext';
import TicketFiles from './TicketFiles';
import TicketMessages from './TicketMessages';
// Jay, 2026-09-17: "use term OPEN not Pending even from Ticket Station". One label table,
// shared with the report, so a value is never printed under two names.
import { taskStatusLabel } from './ticketStatus';
import Header from './Header';
import TabIcon from './TabIcon';
import DevDashboard from './21_DevDashboard';
import useAuth from './useAuth';
import TicketCodes from './25_TicketCodes';
import { BACKEND_URL } from './backendConfig';
import useEscapeClose from './useEscapeClose';
import useRowWindow from './useRowWindow';
import useFreezeFit from './useFreezeFit';
import useDiscardGuard, { DiscardPrompt, fingerprint } from './useDiscardGuard';
import ProjectPhotos, { uploadPendingPhotos } from './ProjectPhotos';
import { MoveSkusButton, MoveSkusPicker, MoveInList, moveSkusInto } from './MoveSkusIn';
import './ProjectPhotos.css';
import './SampleTracker.css';
import { todayVN, fmtDateVN, fmtDateTimeVN, fmtDateTimeSecVN } from './timeZone';
import { padProjectId } from './projectId';
import { DelayBadge, ProjectDatesModal, fmtDay } from './ProjectDates';
import { pickL10n, langOf } from './l10nField';
import { valueLabel } from './valueLabel';
import { RecordHistory } from './CatchyCard';
import { launchCheck, launchErrors, launchFlags } from './projectLaunchCheck';
// The module's own instance under a second name, for the few loaders that run once and must not depend on the
// component's `i18n` (which shadows the import inside SampleTracker).
const portalI18n = i18n;

const API_BASE = `${BACKEND_URL}/api/development/sample-tracker`;
const PROJECT_API_BASE = `${BACKEND_URL}/api/development/project-tracker`;
/* The JSON round trip MoveSkusIn.jsx is handed — that module talks to this API and nothing else. */
const trackerApi = async (path, options = {}) => {
  const res = await fetch(`${PROJECT_API_BASE}${path}`, {
    credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...options,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || i18n.t('stx.requestFailed', { status: res.status }));
  return data;
};

/* THE GUIDES FOR THE PROJECT TRACKER AND THE TICKET STATION (Max 01/11: every module has one, ending on the manual —
   both screens are in the Phase 1 handbook). The Sample Tracker keeps its own tour below (TOUR_STEPS), which now ends
   on the Sample Tracker handbook too. */
const PROJECT_TOUR = [
  { selector: '.pt-kpi', title: 'tourTracker.ptCardsT', body: 'tourTracker.ptCardsB' },
  { selector: '.pg-bar', title: 'tourTracker.ptFiltersT', body: 'tourTracker.ptFiltersB' },
  { selector: '[data-tour="pt-new"]', title: 'tourTracker.ptNewT', body: 'tourTracker.ptNewB' },
  { selector: '.pt-scroll-grid', title: 'tourTracker.ptGridT', body: 'tourTracker.ptGridB' },
  { selector: '[data-tour="pt-report"]', title: 'tourTracker.ptReportT', body: 'tourTracker.ptReportB' },
];
const TICKET_TOUR = [
  { selector: '.task-kpis', title: 'tourTracker.tkCardsT', body: 'tourTracker.tkCardsB' },
  { selector: '.pg-bar', title: 'tourTracker.tkFiltersT', body: 'tourTracker.tkFiltersB' },
  { selector: '[data-tour="tk-new"]', title: 'tourTracker.tkNewT', body: 'tourTracker.tkNewB' },
  { selector: '.task-table', title: 'tourTracker.tkTableT', body: 'tourTracker.tkTableB' },
];

const sampleTabs = [
  { id: 'all', label: 'st.tabAll' },
  { id: 'product', label: 'st.tabSummary' },
];

/* Project and trial samples were two tabs for half a day (Max, 2026-09-09). Jay read the label the
   way anyone would — "All Samples" that shows one series at a time is a label promising more than it
   does — and asked how he was meant to see the full R&D workload. So they are one list again, with a
   filter: everything is shown by default, and either series can be looked at alone on demand.

   The values are what the filter STORES as well as what it shows, so they are plain strings like every
   other option in this toolbar. */
const SAMPLE_TYPE_PROJECT = 'Project (SMP)';
const SAMPLE_TYPE_TRIAL = 'Trial (TSMP)';
const SAMPLE_TYPES = [SAMPLE_TYPE_PROJECT, SAMPLE_TYPE_TRIAL];

/* Jay, 2026-09-09, after seeing the card version: he wants the two pills back, plus "1 cái nút có
   thể Both hoặc ctrl click 2 cái tab thì sẽ thấy được cả 2 loại". So they are pills again — but
   pills over ONE multi-select filter rather than two separate lists of rows, which is what let the
   tab count and the table under it disagree the first time.

   Both is stored as no filter at all, so the Both pill and a ctrl+click that ends up selecting both
   land in exactly the same state instead of two states that only look alike. */
const SAMPLE_PILLS = [
  { id: SAMPLE_TYPE_PROJECT, label: 'st.pillProject', count: 'project' },
  { id: SAMPLE_TYPE_TRIAL, label: 'st.pillTrial', count: 'trial' },
  { id: null, label: 'st.pillBoth', count: 'all' },
];

// Ticket Station has the same shape: the tickets themselves, and the master list behind their codes.
const ticketTabs = [
  { id: 'tickets', label: 'st.tkTabTickets' },
  { id: 'codes', label: 'st.tkManageCodes' },
];

// Module-level, so the labels are i18n KEYS rather than text — every render site passes them through t()
// (or colLabel, which forwards anything starting with "st." to t).
export const stageColumns = [
  ['ldSoff', 'st.stgLdSoff'],
  ['proto', 'st.stgProto'],
  ['counter', 'st.stgCounter'],
  ['costing', 'st.stgCosting'],
  ['handOffMeeting', 'st.stgHandOffMeeting'],
  // PO Status sits right after Hand-off Meeting — Max, 2026-10-15: "chuyển cột PO status lên sau cột
  // Hand-off meeting". The table, the Columns menu, the export and the SKU form all read this order.
  ['poStatus', 'st.stgPoStatus'],
  ['ppsPpm', 'st.stgPpsPpm'],
  ['production', 'st.stgProduction'],
  ['finalInspection', 'st.stgFinalInspection'],
  ['exfShipment', 'st.stgExfShipment'],
  ['payment', 'st.stgPayment'],
];

/* PROJECT STATUS — Jay, 2026-10-20: "please add 2 columns — Sales Rep, Status"; Max, the same day: "trạng
   thái của project là complete, dropped hay active thui, còn con của nó thì thể hiện project SKU status".
   The project row reads the three cards' own rule (groupStateKeysOf), so the column, the cards and their
   filters cannot disagree about one project; a SKU row prints the SKU's own status. A first version read
   the furthest stage each SKU had reached ("Proto · Sewing P1"); Max chose the plainer answer. */
const PROJECT_STATE_RANK = { active: 0, complete: 1, dropped: 2 };
const projectStateLabel = (state, t) => (state === 'complete' ? t('st.ptWhereComplete')
  : (state === 'dropped' ? t('st.ptWhereDropped') : t('st.ptWhereActive')));
const projectStatePill = (state) => (state === 'complete' ? projectStatusClass('Complete')
  : (state === 'dropped' ? projectStatusClass('Dropped') : 'pt-state-active'));

const STAGE_WIDTHS = {
  ldSoff: 130, proto: 140, counter: 140, costing: 130, handOffMeeting: 150,
  ppsPpm: 120, production: 130, finalInspection: 140, exfShipment: 140, payment: 120, poStatus: 120,
};

// Proto & Counter are auto-derived from the linked sample's rounds (ProjectTracker.gs
// _ptAutoStageValueFromRounds_) — the backend recomputes them on every load.

/* The home cards that list a stage still Pending on an open SKU (2026-10-15): the flag each arrives
   with, the stage it reads, and its chip. One table, so the URL, the filter and the chip cannot name
   three different things. The server counts with the same rule (homeQueueRows, stagePendingRows). */
const STAGE_PENDING_FLAGS = {
  // `label` is an i18n key: the chip passes it through t().
  costing_pending: { key: 'costing', label: 'stx.flag.costingPending' },
  production_pending: { key: 'production', label: 'stx.flag.productionPending' },
  shipment_pending: { key: 'exfShipment', label: 'stx.flag.shipmentPending' },
};

// The statuses that make up "Active" (i.e. not Complete/Dropped) — clicking the Active card filters to these.
// Priority joined on 2026-10-15 (Max added it to the status list that day): the ACTIVE card already
// counted it, as everything not Complete/Dropped, so leaving it out here made the card's filter show
// fewer rows than the card said.
const PROJECT_ACTIVE_STATUSES = ['Priority', 'On Track', 'Concern', 'Delayed', 'On Hold', 'Ship Finished'];

// Special Project "Case" options + Entity — ports Portal.html (value A/B, Entity shown only for Case B).
export const PROJECT_CASE_OPTIONS = [
  { value: 'A', label: 'Case A - HPAC Direct Collaboration' },
  { value: 'B', label: 'Case B - Subsidiary-led Collaboration' },
];
// What each case PRINTS; the export above keeps its English labels for any other reader.
const CASE_LABEL_KEYS = { A: 'stx.caseA', B: 'stx.caseB' };
export const PROJECT_ENTITY_OPTIONS = ['HINC', 'HUSA', 'HEMA', 'HJPN'];

/* A ticket being written has no id yet, and a file has to hang off something — Max, 2026-09-08:
   "có cảnh nào đính kèm lúc tạo luôn hong". So the browser makes up a key, files are uploaded under
   it straight away, and the save hands them to the real task id the moment it is minted. Generated
   once per ticket and carried on the ticket itself, or a re-render would orphan what was uploaded a
   second ago. */
const newFileDraftKey = () => `draft-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-6)}`;

// Build an editable ticket object from a task type (prefills name/description/team/email), like the web app.
const makeTicketFromType = (code, taskTypes) => {
  const tt = taskTypes.find((t) => t.code === code) || {};
  return {
    taskType: code,
    name: tt.name || '',
    description: [tt.phase, tt.scope].filter(Boolean).join(' — '),
    email: tt.groupEmail || '',
    department: tt.receivingTeam || '',
    priority: 'Medium',
    dueDate: '',
    status: 'Pending',
    attachmentUrl: '',
    note: '',
    fileDraftKey: newFileDraftKey(),
    emailCc: '',
    sendEmail: false,
    sendManual: false, // set when the user clicks "Send email" with auto-send off
    /* The answers to this code's request form. Cleared when the code changes: the questions are
       different, and a leftover answer under a key the new form does not ask for would be invisible
       on screen and still land in the mail. */
    fields: {},
  };
};

// "PRJ-144_26FW Zero line facelift" -> { id: 'PRJ-144', name: '26FW Zero line facelift' }. The two
// halves are shown in the opposite order in a subject line, so they have to be pulled apart again.
/* The seventeen request forms, fetched once and shared the way the dropdown lists are. A ticket
   card asks for its code's form; the email builder asks for the same object, so the block on screen
   and the block in the mail cannot drift. */
const TicketFormsContext = React.createContext(null);
/* On the test server only, until Max says otherwise (2026-08-30). With the flag off the ticket card
   and the mail keep the shape they have always had — the fallback path in ticketEmailBody. */
export const TICKET_FORMS_ON = String(process.env.REACT_APP_TICKET_FORMS || '') === '1';
/* The SKU material list is on the test server only while Max is still trying it out (2026-09-04).
   Its own flag rather than TICKET_FORMS_ON: the two are unrelated, and sharing one would mean
   neither could ship without the other. Nothing has been typed into it yet — the table is empty —
   so taking it off production takes nothing with it. */
export const SKU_MATERIALS_ON = String(process.env.REACT_APP_SKU_MATERIALS || '') === '1';
/* The same lookup without the hook, for the save path: the mail a ticket is stored with has to be
   the one its card previewed, and that one is built from the form. */
export const ticketFormOf = (cfg, code) => {
  if (!TICKET_FORMS_ON || !cfg || !code) return null;
  const key = cfg.byCode[code];
  const form = key && cfg.forms.find((f) => f.formKey === key);
  return form ? { ...form, header: cfg.header, footer: cfg.footer,
    headerKo: cfg.headerKo, footerKo: cfg.footerKo } : null;
};
export const useTicketForm = (code) => ticketFormOf(useContext(TicketFormsContext), code);

/* The Korean name and receiving team for one code. Same shape as ticketFormOf and read from the
   same config, so the mail builder is handed words rather than having to know where they live. */
export const ticketKoOf = (cfg, code) => (cfg && code && cfg.koByCode ? cfg.koByCode[code] : null) || null;
export const useTicketKo = (code) => ticketKoOf(useContext(TicketFormsContext), code);

export const splitProjectLabel = (label) => {
  const v = String(label || '').trim();
  const m = v.match(/^(PRJ-\d+)_?(.*)$/);
  return m ? { id: m[1], name: m[2].trim() } : { id: '', name: v };
};

// The subject every ticket mail uses, so the first mail and its reminders read as the same thread.
export const ticketSubject = ({ projectLabel, code, name, reminder = false }) => {
  const { id, name: projName } = splitProjectLabel(projectLabel);
  const tail = [projName, id ? `(${id})` : ''].filter(Boolean).join(' ');
  return `[${reminder ? 'Ticket Reminder' : 'Ticket'}] — ${name} (${code})${tail ? ` - ${tail}` : ''}`
    .replace(/\s+/g, ' ').trim();
};

/* The ticket email — ONE template. It used to exist twice, word for word: once as the live preview in a
   ticket card and once as the body actually sent, and both said "Requesting team: Special Project" as a
   hard-coded string. Max caught it on 2026-08-20: the requesting team is the project's Sales OP team,
   Team 1 or Team 2. "Special Project" was the team name back in the Apps Script era and had been frozen
   into the text ever since.

   Two copies of one email is the same trap as two copies of a form: fixing one leaves the other lying. */
const ticketEmailBody = ({
  projectLabel, code, name, requestingTeam, projectOwner, receivingTeam, requester,
  priority, dueDate, attachmentUrl, note, updateUrl, form, fieldValues, skuNames,
  /* { name, team } in Korean for THIS ticket code, from task_name_ko / receiving_team_ko. All 41
     codes have carried theirs since they were seeded; the mail simply never read them. */
  koInfo,
}) => {
  const { id: projectId, name: projectNameOnly } = splitProjectLabel(projectLabel);
  /* Jay's tokens, in his words. Everything here is filled by the portal; everything in the request
     block comes from what the requester typed. */
  const auto = {
    ticket_code: code || '-',
    ticket_name: name || '-',
    project_id: projectId || '-',
    project_name: projectNameOnly || projectLabel || '-',
    requester: requester || '-',
    requesting_team: requestingTeam || '-',
    project_owner: projectOwner || '-',
    priority: priority || 'Medium',
    due_date: dueDate || '-',
    attachment_links: attachmentUrl || '-',
    /* Since 2026-09-07 the note holds html, not a string — the requester can bold a line or colour
       it. The text half of the mail gets it flattened; the html half keeps the markup, which is why
       {{note}} is the one token filled differently in the two passes below. */
    note: richToText(note) || '-',
    update_link: updateUrl || '[update link will be generated when sent]',
  };
  const fillWith = (text, noteOut) => String(text || '').replace(/\{\{(\w+)\}\}/g,
    (whole, key) => {
      if (key === 'note') return noteOut;
      return Object.prototype.hasOwnProperty.call(auto, key) ? auto[key] : whole;
    });
  const fill = (text) => fillWith(text, auto.note);

  /* Until a code has a form — an older code, or one added since the seed — the mail keeps the shape
     it always had, so nothing stops working while the forms are being filled in. */
  const middle = form ? renderBlock(form, fieldValues || {}, skuNames || {}) : [
    `- Receiving team: ${receivingTeam || '-'}`,
  ].join('\n');

  const header = form ? fill(form.header) : [
    'This email was generated automatically by the HEVN Project Ticket System.',
    '',
    `- Project: ${projectLabel}`,
    `- Ticket code: ${code}`,
    `- Ticket name: ${name}`,
    `- Requesting team: ${requestingTeam || '-'}`,
    `- Project Owner: ${projectOwner || '-'}`,
    `- Requester: ${requester || '-'}`,
    `- Priority: ${priority || 'Medium'}`,
    `- Due date: ${dueDate || '-'}`,
  ].join('\n');

  /* Built twice from one template: once with the note flattened to text, once with a marker that
     survives html-escaping and is swapped for the note's own markup afterwards. Escaping the note
     along with everything else would print "<b>urgent</b>" in the inbox. */
  const footerOf = (noteOut) => (form ? fillWith(form.footer, noteOut) : [
    `- Attachment Link: ${attachmentUrl || ''}`,
    `- Note: ${noteOut}`,
    '',
    'Please acknowledge or update the ticket status here:',
    updateUrl || '[update link will be generated when sent]',
    '',
    'Do not reply to this email unless follow-up discussion is needed.',
  ].join('\n'));
  const emptyNote = form ? '-' : '';
  const NOTE_MARK = '\u0000note\u0000';
  const footer = footerOf(richToText(note) || emptyNote);

  /* The mail goes out as text AND html (taskTrackerSendEmail has always sent both). The html half
     used to be the text with <br> between the lines, which turned a seven-column SKU list into a
     wall of "a / b / c" — so the html is built from the same values with the list as a real table.
     One function, two outputs; the text half stays exactly what the preview shows. */
  const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const asHtml = (text) => esc(text).split('\n').join('<br>');
  const middleHtml = form ? renderBlockHtml(form, fieldValues || {}, skuNames || {}) : asHtml(middle);
  const footerHtml = asHtml(footerOf(NOTE_MARK)).split(NOTE_MARK).join(richToMailHtml(note) || emptyNote);

  /* THE KOREAN HALF — Jay, 2026-10-01 point 2a: "Put Korean in the email as well as English. Same
     content, Korean below the English block. The recipients are HINC Korea." Max, 2026-10-04: on
     every ticket code, not only B-4 (which is 6 of the 57 tickets raised; T1-01, 30 of them, goes
     to the same receiving team under almost the same name).

     THE FRAME IS TRANSLATED AND THE VALUES ARE NOT. Ticket name and receiving team come from
     task_name_ko / receiving_team_ko, which all 41 codes have carried since they were seeded and
     this mail simply never read; the request block's labels come from label_ko. Everything else —
     the project, the SKUs, the dates, and above all the REQUESTER'S OWN NOTE — is printed exactly
     as it is. Machine-translating a technical instruction is how the receiving team comes to do
     something nobody asked for, with nobody able to see where it went wrong.

     ONE HEADING SAYS SO, in Korean: the note block is labelled as the requester's own words. */
  const ko = koInfo || {};
  /* THE KOREAN HALF IS THE SAME TEMPLATE, FILLED TWICE. The English header and footer are settings
     rows an admin edits on the Ticket Forms screen; the Korean pair sits beside them, line for line,
     with the same {{tokens}} — see sql/20261004b. Writing this block into the code instead was the
     first attempt and it had already drifted from the English before anybody edited anything: the
     ATTACHMENTS section was missing from the Korean half altogether.

     TWO TOKENS RESOLVE DIFFERENTLY HERE: the ticket's name and its receiving team take the Korean
     the ticket codes have carried since they were seeded. Everything else is the value as it
     stands — project names, SKUs, dates, people, and above all {{note}}, the requester's own words,
     which this half labels as unchanged rather than translating. */
  const koAuto = { ...auto, ticket_name: ko.name || auto.ticket_name };
  const fillKo = (text, noteOut) => String(text || '').replace(/\{\{(\w+)\}\}/g,
    (whole, key) => {
      if (key === 'note') return noteOut;
      return Object.prototype.hasOwnProperty.call(koAuto, key) ? koAuto[key] : whole;
    });
  const koMiddle = form ? renderBlock(form, fieldValues || {}, skuNames || {}, 'ko') : '';
  const koHeader = form && form.headerKo ? fillKo(form.headerKo, koAuto.note) : '';
  const koFooterOf = (noteOut) => (form && form.footerKo ? fillKo(form.footerKo, noteOut) : '');
  /* Drawn only when both halves of the frame exist, so a portal whose Korean rows were never filled
     in sends the English mail it always sent rather than a half-Korean one. */
  const koBlock = koHeader
    ? [koHeader, '', koMiddle, '', koFooterOf(richToText(note) || emptyNote)].join('\n')
    : '';
  const koMiddleHtml = form ? renderBlockHtml(form, fieldValues || {}, skuNames || {}, 'ko') : '';
  const koFooterHtml = asHtml(koFooterOf(NOTE_MARK)).split(NOTE_MARK)
    .join(richToMailHtml(note) || emptyNote);
  const koHtml = koBlock
    ? `${asHtml(koHeader)}<br><br>${koMiddleHtml}<br>${koFooterHtml}` : '';

  return {
    subject: ticketSubject({ projectLabel, code, name }),
    body: [header, '', fill(middle), '', footer, ...(koBlock ? ['', '', koBlock] : [])].join('\n'),
    html: `<div style="font-family:Arial,sans-serif;font-size:14px;line-height:1.45;color:#202124;">`
      + `${asHtml(header)}<br><br>${middleHtml}<br>${footerHtml}`
      + (koHtml ? `<hr style="border:none;border-top:1px solid #d7dde5;margin:22px 0">${koHtml}` : '')
      + `</div>`,
  };
};

// Live preview inside a ticket card. Same template as the sent mail, different source of values.
const ticketEmailDraft = (ticket, requester, projectName, requestingTeam, projectOwner, form, skuNames, koInfo) => ticketEmailBody({
  koInfo,
  projectLabel: String(projectName || '').trim() || '[[project name]]',
  code: ticket.taskType || '-',
  name: String(ticket.name || '').trim() || 'New ticket',
  requestingTeam,
  projectOwner,
  receivingTeam: ticket.department,
  requester,
  priority: ticket.priority,
  dueDate: ticket.dueDate,
  attachmentUrl: ticket.attachmentUrl,
  note: ticket.note,
  form,
  fieldValues: ticket.fields,
  skuNames,
});

// Stage status suggestions (datalist) — pick one or type free text (incl. a dd/mm/yyyy date), like the web app.
// Datalist suggestions for the stage boxes. The list is a superset of 00_PORTAL_stage_status_dim:
// every status that really exists in the data is pickable (P4/P5, Sewing Counter/P4/P9,
// Draft PO Issued and No were missing and had to be typed by hand), and a few convenience entries
// stay even though the dim has no row for them. Anything not in the dim keeps its text and leaves
// the status foreign key on Pending — the same treatment a typed date gets.
// Proto and Counter are derived from the linked sample's rounds by the backend
// (projectTrackerAutoStageFromRounds) and rewritten on every load, so they are never hand-edited.
export const AUTO_STAGE_KEYS = ['proto', 'counter'];
export const isAutoStage = (key) => AUTO_STAGE_KEYS.includes(key);

const STAGE_STATUS_SUGGESTIONS = [
  'Pending', 'In Progress', 'Waiting Feedback', 'Complete', 'Delayed', 'On Hold', 'No',
];

const PROJECT_COLUMNS = [
  { key: 'no', label: 'st.ptNo', width: 52, locked: true },
  { key: 'id', label: 'st.ptId', width: 90, locked: true, sortKey: 'id' },
  { key: 'name', label: 'st.ptProjectItem', width: 250, locked: true, sortKey: 'projectItem' },
  // Team + Project Owner sit before Category: they are the first thing Sales OP filters by.
  { key: 'team', label: 'st.ptTeam', width: 130, sortKey: 'teamName' },
  { key: 'owner', label: 'st.ptOwner', width: 130, sortKey: 'ownerName' },
  // Jay, 2026-10-20: "between these columns, please add 2 columns — Sales Rep, Status".
  { key: 'salesRep', label: 'st.ptSalesRep', width: 120, sortKey: 'salesRepName' },
  /* Project Status: Completed / Dropped / Active on a project row, the SKU's own status on a SKU row — so
     the separate SKU Status column further right came off (Max, 2026-10-20: "bỏ cột này nhé"). */
  { key: 'projectStage', label: 'st.ptWhere', width: 220, sortKey: 'groupStage' },
  { key: 'category', label: 'st.ptCategory', width: 130, sortKey: 'category' },
  { key: 'projectCase', label: 'st.ptCase', width: 110, sortKey: 'groupCase' },
  { key: 'entity', label: 'st.ptEntity', width: 110, sortKey: 'groupEntity' },
  { key: 'itemCategory', label: 'st.ptItemCategory', width: 140, sortKey: 'productType' },
  { key: 'frameColor', label: 'st.ptFrameColor', width: 190, sortKey: 'frameColor' },
  { key: 'qty', label: 'st.ptQty', width: 80, sortKey: 'estimateQty' },
  // Required on a new SKU since 2026-10-16 (Max) — see SKU_REQUIRED_FIELDS.
  { key: 'targetPrice', label: 'st.ptTargetPrice', width: 110, sortKey: 'targetPrice' },
  { key: 'assigned', label: 'st.ptAssigned', width: 110, sortKey: 'assignedTo' },
  { key: 'designer', label: 'st.ptDesigner', width: 110, sortKey: 'designerName' },
  { key: 'allocation', label: 'st.ptAllocation', width: 120, sortKey: 'allocation' },
  { key: 'startDate', label: 'st.ptStart', width: 110, sortKey: 'startDate' },
  { key: 'exfDate', label: 'st.ptExfDate', width: 110, sortKey: 'exfShipDate' },
  { key: 'launchDate', label: 'st.ptLaunchDate', width: 120, sortKey: 'launchDate' },
  { key: 'weeksLeft', label: 'st.ptWeeksLeft', width: 120, sortKey: 'leadTime' },
  { key: 'pct', label: 'st.ptProgress', width: 110, sortKey: 'completePct' },
  ...stageColumns.map(([key, label]) => ({ key: `stage:${key}`, label, width: STAGE_WIDTHS[key] || 120, sortKey: key })),
  { key: 'remark', label: 'st.ptRemark', width: 320, sortKey: 'remark' },
  { key: 'sampleId', label: 'st.ptSampleId', width: 100, sortKey: 'sampleId' },
  // 195 → 260 with View (2026-10-15): a SKU row is "+ Sample", Complete and three icons.
  { key: 'action', label: 'st.ptAction', width: 260, locked: true },
];

// Column widths for the All Samples grid (must match SAMPLE_COL_WIDTHS count = 24 columns).
const SAMPLE_COL_WIDTHS = [
  // Base ID 110 → 180 and Style 230 → 320 (Max, 2026-10-15): sticky, so the offsets in SampleTracker.css move too.
  32, 50, 180, 320, 90, 90, 100, 110, 110, // toggle · # · Base ID · Style · Round · Season · Location · Developer · Designer  (sticky block)
  // Days Waiting was dropped from the grid on 2026-08-13 (Max) — its width went with it. The
  // column still exists in the All Samples Excel export, which is why daysWaitingFor stays.
  // Status is 145, not 115: "Pending Outcome" did not fit and the pill wrapped to two lines, which
  // made those 30 rows 51px against everyone else's 47 — and a table can only render just the rows
  // in view when every row is the same height. Widened rather than truncated, because a status cut
  // to "Pending Outc…" is worse than a wider column.
  110, 120, 130, 115, 115, 130, 115, 115, 80, 85, 145, 90, 440, 130, // Project ID … Remarks · Actions
];
/* How much of the All Samples grid stays frozen, from most to least (Jay, 2026-10-30: on half a
   monitor the 1,212 frozen px left the scrolling columns no room at all, so the scrollbar moved and
   nothing on screen did). The full block is kept whenever the box can afford it — a 1920 screen
   looks exactly as before — then it gives up to Style / Item, then to Base ID, then lets Actions
   scroll too, and last of all freezes nothing. Each level is a class on the table; the CSS that
   undoes the freeze sits under the sticky rules in SampleTracker.css. */
const sampleFrozenWidth = (n) => SAMPLE_COL_WIDTHS.slice(0, n).reduce((a, b) => a + b, 0);
const SAMPLE_ACTIONS_W = SAMPLE_COL_WIDTHS[SAMPLE_COL_WIDTHS.length - 1];
const SAMPLE_FREEZE_LEVELS = [
  { key: 'all', width: sampleFrozenWidth(9) + SAMPLE_ACTIONS_W },
  { key: 'style', width: sampleFrozenWidth(4) + SAMPLE_ACTIONS_W },
  { key: 'id', width: sampleFrozenWidth(3) + SAMPLE_ACTIONS_W },
  { key: 'idonly', width: sampleFrozenWidth(3) },
  { key: 'none', width: 0 },
];
// What the scrolling columns are always left: 1920 today leaves 388, and nobody has asked for more.
const SAMPLE_SCROLL_ROOM = 360;

// Ticket Station column widths (12 cols) — the first three and Actions are frozen, so the widths
// must be fixed for the sticky offsets in SampleTracker.css to line up.
/* Column order: # · Project · Project Name · Ticket ID · Ticket · Manager status · Assignee reply ·
   Team · Owner · Receiving team · Requester · Type · Priority · Due · D-Day · Note · Actions.
   The key list that used to sit here existed only to keep the group header aligned with the body;
   the group header is gone (flat rows, 2026-08-25), so the widths are the single source of order.
   Project Name is its own column since 2026-08-25 (Jay): the station is one flat row per ticket now,
   so every row has to carry the project it belongs to instead of borrowing it from a group header.
   The other columns each gave up a little width to pay for it. */
/* One width per column of the ticket table, IN ORDER, and the list must be exactly as long as the
   header row. It was seventeen long when the four audit columns took it to twenty-one (Max,
   2026-09-08: "bị lỗi nè"): a colgroup that runs out does not stretch the rest, it leaves the tail
   columns with almost nothing, so "2026-09-08 06:08" came out one character per line and the row
   grew to fifteen lines tall.

     #    project  name  ticket-id  ticket  mgr-status  reply  team  owner  receiving  requester
     type  priority  due  d-day  note  created-by  created-at  modified-by  modified-at  actions

   Project Owner is 150 rather than 120 — "Thuan Nguyen" and "Christine" were wrapping (Max, same
   message). The two timestamps are 140: "2026-09-08 06:08" needs about 110 at this size. */
const TASK_COL_WIDTHS = [46, 110, 200, 100, 240, 150, 180, 140, 150, 130, 100, 90, 100, 110, 90, 200,
  120, 140, 120, 140, 190];

/* These five lists live in the database now and are managed on /admin/master-data (Max,
   2026-08-26). The arrays stay as the FALLBACK: an older server, or a list emptied by accident,
   must not leave a dropdown with nothing in it. `usePortalList` is what every dropdown reads. */
const PortalListsContext = React.createContext(null);
const usePortalList = (key, fallback) => {
  const lists = useContext(PortalListsContext);
  const value = lists && lists[key];
  return Array.isArray(value) && value.length ? value : fallback;
};

const TASK_PRIORITY_OPTIONS = ['High', 'Medium', 'Low'];
const TASK_STATUS_OPTIONS = ['Pending', 'Done', 'Cancelled'];

const taskNormalizeStatus = (status) => {
  const s = String(status || 'Pending').trim();
  return s === 'In Progress' ? 'Pending' : (s || 'Pending');
};
const taskTodayStr = () => todayVN();
const taskIsOpen = (t) => {
  const s = taskNormalizeStatus(t.status);
  return s !== 'Done' && s !== 'Cancelled';
};
const taskIsOverdue = (t) => taskIsOpen(t) && t.dueDate && t.dueDate < taskTodayStr();
const taskStatusClass = (status) => {
  const s = taskNormalizeStatus(status);
  if (s === 'Done') return 'done';
  if (s === 'Cancelled') return 'cancel';
  return 'pending';
};
/* "Tina Lee" -> "Tina"; "Chloe Park, Christine Kim" -> "Chloe, Christine" (Jay, 2026-08-25: "please
   show the name without family name"). Names are stored given-name-first here, so the first word is
   the given name, and a comma-separated list is handled member by member. */
export const firstNameOf = (value) => String(value || '').split(',')
  .map((part) => part.trim().split(/\s+/)[0])
  .filter(Boolean)
  .join(', ') || '—';

const titleCaseWords = (value) => String(value || '').trim().replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
const emptyTaskForm = () => ({
  taskId: '', projectId: '', name: '', description: '', assignee: '', email: '', department: '',
  taskType: '', priority: 'Medium', dueDate: '', status: 'Pending', attachmentUrl: '', note: '',
  fileDraftKey: newFileDraftKey(),
  emailSubject: '', emailBody: '', emailCc: '', sendEmail: false, sentAt: '',
});

const PT_PROJECT_COL_STORE = 'hevn_pt_project_columns_v1';

const loadProjectColumnVisible = () => {
  let map = {};
  try { map = JSON.parse(localStorage.getItem(PT_PROJECT_COL_STORE) || '{}') || {}; } catch (e) { map = {}; }
  PROJECT_COLUMNS.forEach((c) => { if (c.locked) map[c.key] = true; });
  return map;
};

// Round labels in development order. 'Trial / Mock-up' and 'Final proto' were missing even though
// they exist in the data (19 and 2 rounds), so they could not be picked for a new round — and worse,
// editing such a round showed a mismatched select that overwrote the label on save.
export const ROUND_OPTIONS = [
  'Counter', 'SMS', 'Photo', 'Trial / Mock-up',
  ...Array.from({ length: 15 }, (_, i) => `P${i + 1}`),
  // 'Final proto' is not part of the order Max specified, but two existing rounds use it, so it stays
  // at the end — otherwise that label could never be picked again for a new round.
  'Final proto',
  // A sample built for a show rather than for the P-sequence (Max, 2026-08-26). Last on purpose: it
  // is not a development stage, so it must not sit between the P numbers.
  'Exhibition Sample',
];
// "Advance" means the round passed and development moves on, so the follow-up round is the next P-number.
// A non-P round (Counter / SMS / Photo / Trial / Mock-up) advances into P1 unless a P round already exists
// for the sample, in which case it continues that numbering.
export const nextRoundLabel = (currentRound, sameSampleRounds = []) => {
  const pNumber = (label) => {
    const match = String(label || '').trim().match(/^P\s*(\d+)$/i);
    return match ? Number(match[1]) : 0;
  };
  const highest = Math.max(pNumber(currentRound), ...sameSampleRounds.map((round) => pNumber(round.round)), 0);
  return `P${Math.min(highest + 1, 15)}`;
};

export const CATEGORY_FALLBACK = ['Inline', 'Special Project', 'ETC'];
const LOCATION_FALLBACK = ['HEVN', 'TS', 'MS'];
export const SEASON_FALLBACK = ['SS26', 'FW26', 'SS27', 'FW27', 'SS28', 'FW28'];
export const OUTCOME_OPTIONS = ['Advance', 'Rework', 'Confirmed', 'On Hold', 'Dropped'];
export const REWORK_REASON_OPTIONS = [
  'Sample differs from instruction',
  'Quality below standard',
  'Instruction was unclear',
  'Direction / external change',
  'Vendor executed correct spec incorrectly',
];

export const REASON_OWNER_MAP = {
  'Sample differs from instruction': 'HEVN',
  'Quality below standard': 'HEVN',
  'Instruction was unclear': 'HINC',
  'Direction / external change': 'External',
  'Vendor executed correct spec incorrectly': 'Vendor',
};

/* Keyed by OWNER, not by the reason's exact wording. The old map was keyed by the sentence, so a
   reason added on the Master data screen — or one merely reworded — lost its colour (2026-08-26). */
const REASON_OWNER_CLASS = {
  HEVN: 'owner-hevn',
  HINC: 'owner-hinc',
  External: 'owner-neutral',
  Vendor: 'owner-neutral',
};
export const reasonOwnerClass = (owner) => REASON_OWNER_CLASS[owner] || 'owner-neutral';

export const fmt = (value) => value || '—';
export const num = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;
export const pct = (value, total) => total ? `${Math.round((value / total) * 100)}%` : '0%';
export const avg = (values, digits = 1) => {
  const nums = values.map(Number).filter((value) => Number.isFinite(value));
  if (!nums.length) return digits ? '0.0' : '0';
  return (nums.reduce((sum, value) => sum + value, 0) / nums.length).toFixed(digits);
};

const dateValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const daysBetween = (start, end) => {
  const first = dateValue(start);
  const second = dateValue(end);
  if (!first || !second) return '';
  return Math.round((second - first) / 86400000);
};

export const fmtDays = (value) => (value === '' || value === null || value === undefined || Number.isNaN(value)) ? '—' : `${value}d`;

// D-day: red if overdue, orange if due within a week, green otherwise (matches legacy Portal.html exactly)
// D-DAY = today − Development Due (Max, 2026-08-12): positive is overdue, negative is days still left.
// A finished round (Confirmed / Dropped / Advance) is frozen at its Comment Date and printed in
// black — the colours warn about work still running, and there is nothing left to warn about once
// it is done (Jay via Max, 2026-08-13).
export const dDayClass = (dday, done) => {
  if (!Number.isFinite(dday)) return '';
  if (done) return 'dday-done';
  if (dday > 0) return 'red';        // past the due date
  if (dday >= -7) return 'orange';   // a week or less to go
  return 'green';
};
export const dDayLabel = (dday) => (!Number.isFinite(dday) ? '—' : `${dday < 0 ? '' : '+'}${dday}d`);

const unique = (rows, field) => Array.from(new Set(rows.map((row) => row[field]).filter(Boolean))).sort();

export const roundStatus = (round) => {
  if (round.trialInternal) return 'Internal Trial';
  if (round.outcome) return round.outcome;
  if (round.sampleFinishDate) return 'Pending Outcome';
  if (round.rmReadyDate) return 'In Production';
  return 'RM Waiting';
};

export const outcomePillClass = (status) => {
  if (status === 'Confirmed') return 'p-conf';
  if (status === 'Rework') return 'p-rew';
  if (status === 'Advance') return 'p-adv';
  if (status === 'Dropped') return 'p-drop';
  if (status === 'Internal Trial') return 'p-internal';
  if (status === 'On Hold') return 'p-hold';
  // computed workflow statuses shown on round rows — same wording as the KPI cards
  if (status === 'In Production') return 'p-prog';
  if (status === 'RM Waiting') return 'p-hold';
  return 'p-pending';
};

export const SHORT_OUTCOME = { Advance: 'Adv', Rework: 'Rew', Confirmed: 'Conf', 'On Hold': 'Hold', Dropped: 'Drop' };
export const productStatusPillClass = (status) => (status === 'Confirmed' ? 'p-conf' : status === 'Dropped' ? 'p-drop' : 'p-prog');


/* ---- Stage values: text status OR a target date (Jay item 4) --------------------------
   A stage cell holds either a workflow status ("Pending", "Sewing P2") or a target DATE, stored the
   way the source sheet writes it: dd/mm/yyyy. These parse/format between that and the yyyy-mm-dd a
   native <input type="date"> needs, and decide when a stage is visibly late. */
const STAGE_DATE_RE = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/;

const stageDateToISO = (value) => {
  const m = String(value || '').trim().match(STAGE_DATE_RE);
  if (!m) return '';
  const [, d, mo, y] = m;
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
};

const stageDateFromISO = (iso) => {
  const m = String(iso || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  const [, y, mo, d] = m;
  return `${d}/${mo}/${y}`;
};

// A stage is late when it carries a target date that is in the past and it is not finished.
// "Complete" / "No" / "N/A" close a stage; anything else with a past date is still open.
const STAGE_CLOSED = ['complete', 'no', 'n/a', 'done'];
// The hint on a late stage is t('stx.stageLateHint'), drawn where the stage is.
const stageIsOverdue = (value) => {
  const iso = stageDateToISO(value);
  if (!iso) return false;
  if (STAGE_CLOSED.includes(String(value || '').trim().toLowerCase())) return false;
  return iso < todayVN();
};

const stageClass = (value) => {
  if (stageIsOverdue(value)) return 's-overdue';
  if (value === 'Complete') return 's-complete';
  if (value === 'Delayed') return 's-delayed';
  if (String(value || '').match(/In Progress|Sewing|RM Waiting|Waiting Feedback|Waiting (Manager|CEO) Approval/)) return 's-inprog';
  return '';
};

const projectStatusClass = (status) => {
  if (status === 'Priority') return 'p-priority';
  if (status === 'On Track' || status === 'Complete') return 'p-conf';
  if (status === 'Concern') return 'p-pending';
  if (status === 'Delayed') return 'p-drop-red';
  if (status === 'Ship Finished') return 'p-adv';
  if (status === 'On Hold' || status === 'Dropped') return 'p-drop';
  return '';
};

const isProjectOverdue = (project) => {
  if (!project.exfShipDate || project.status === 'Complete' || project.status === 'Dropped') return false;
  const exf = new Date(project.exfShipDate);
  if (Number.isNaN(exf.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return exf < today;
};

// RM Waiting = not yet sent, no outcome yet, and RM Ready date is missing or still a future "expected" date.
// In Production = not yet sent, no outcome yet, and RM Ready date has actually been reached.
//
// "No outcome yet" used to be "not Confirmed and not Dropped", which let a round whose outcome was
// already On Hold / Rework / Advance keep matching RM Waiting. roundStatus() shows such a round as
// On Hold, so clicking In Progress Rounds listed rows that plainly read "On Hold" (Max, 2026-08-13).
// isPendingOutcomeRound already required !outcome; these two now agree with it and with the KPI
// card, which counts rounds by !outcome.
/* HEVN internal-trial rounds are out of ALL THREE watches, not only Pending Outcome (Jay, 29/10 in HEVN DEV / R&D: "Please work
   with Max to remove Internal Trial samples from 'Pending Outcome' … each card (Pending Outcome / RM Waiting / In Production) are
   not working well"). They never get a designer outcome and they are HEVN's own review, so a card that tracks work for the
   designer side has nothing to say about them. */
export const isRmWaitingRound = (round) => {
  if (round.trialInternal || round.sampleFinishDate || round.outcome) return false;
  if (!round.rmReadyDate) return true;
  const rmDate = dateValue(round.rmReadyDate);
  return Boolean(round.materialReadyExpected) && rmDate && rmDate.getTime() > Date.now();
};
export const isInProductionRound = (round) => {
  if (round.trialInternal || round.sampleFinishDate || round.outcome || !round.rmReadyDate) return false;
  return !isRmWaitingRound(round);
};
export const isPendingOutcomeRound = (round) => Boolean(round.sampleFinishDate) && !round.outcome && !round.trialInternal;
/* A SAMPLE on one of the three cards — counted and listed by the same rule. Any round of it may match (two P1 options run side by
   side: SMP-0320's Charcoal waits for an outcome while its Black is Confirmed, so "the latest round only" would lose a real one),
   EXCEPT when the sample's newest round is an Internal Trial: the sample is in HEVN's own review now, and listing it under a card
   for an older round is what Jay saw as irrelevant (SMP-0130 / SMP-0131 on 29/10). */
export const sampleInWatch = (sample, predicate) => {
  const rounds = sample?.rounds || [];
  if (!rounds.length || rounds[rounds.length - 1].trialInternal) return false;
  return rounds.some(predicate);
};

// SMP came out of a project SKU, TSMP was raised straight from the tracker for a one-off. The Base ID
// is the only thing on a round that says which, so it is what the two tabs split on.
const isTrialBase = (v) => /^TSMP-/i.test(String(v || '').trim());

/* The screen opens on project samples (Max, 2026-09-09: "cho default là SMP thay vì Both nhé").
   Trial samples are the exception route, so they are one click away rather than mixed into the list
   everyone lives in. Because this is a real filter and not a special case, the KPI cards read as
   filtered from the first paint — which is what keeps them agreeing with the table under them. */
/* The URL's ?tab= as this screen's own tab name. Kept beside the state that uses it so the initial
   value and the effect that follows later changes cannot drift apart. */
const sampleTabFromSearch = (search) => {
  const tab = new URLSearchParams(String(search || '')).get('tab');
  if (tab === 'sample') return 'sample';
  if (tab === 'project') return 'projects';
  if (tab === 'tickets') return 'tickets';
  return 'dash';
};

// Every row of the All Samples grid, measured after the cells were made single-line.
const SAMPLE_ROW_H = 47;

/* `lateDays` is a NUMBER, not a list: it arrives from the Sales OP home card carrying the threshold
   that card counted with, so the list on screen is the number that was clicked. Empty means no
   late filter at all — the normal state. */
const SAMPLE_FILTERS_DEFAULT = { sampleType: [SAMPLE_TYPE_PROJECT], sampleStatus: [], roundStatus: [], developer: [], designer: [], location: [], search: '', lateDays: '', review: '', reviewDays: 0 };

/* THE DESIGNER DONUT'S THREE STATES, as a predicate over one round — Jay Jung, 2026-10-01, and the
   drill-through Max asked for on 2026-10-04 ("khi click vào pie hay legend thì có thể nhảy đến
   sample tracker để biết danh sách của cái đó").
 *
 * THIS IS THE SAME RULE THE SERVER COUNTS WITH, AND IT IS HERE TWICE ON PURPOSE — one half is SQL
 * and one is a browser filtering rows it already holds, so they cannot share an implementation.
 * What stops them drifting is a CHECK that clicks the slice and compares the list's row count
 * against the figure the donut printed: check_designer_donut.py fails if they disagree.
 *
 * Jay's words: target rounds have a designer and a Sample Finish Date (the screen's name for
 * sent_date), excluding Round 'Trial / Mock-up' and any sample id starting TSMP-. Done carries BOTH
 * a Comment Date and an Outcome; the other two split on 14 days. */
export const REVIEW_STATES = ['done', 'in_review', 'waiting'];
/* The words the donut's legend prints, so the chip on the tracker and the slice that was clicked
   read as the same thing rather than as two names for one list. */
export const REVIEW_LABEL = { done: 'Done', in_review: 'In review', waiting: 'Waiting 2 weeks+' };
// The same three as i18n keys — what the tracker's chip prints; REVIEW_LABEL stays for any other reader.
const REVIEW_LABEL_KEYS = { done: 'stx.review.done', in_review: 'stx.review.inReview', waiting: 'stx.review.waiting' };
/* The Workload-by-owner row of projects with nobody named. A key the code groups by, so it stays one fixed
   string; it is translated only where it is drawn. */
const UNASSIGNED_OWNER = '— unassigned —';
const reviewAgeDays = (finish) => {
  if (!finish) return null;
  const d = new Date(`${String(finish).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  return Math.floor((new Date(today.getFullYear(), today.getMonth(), today.getDate()) - d) / 86400000);
};
export const roundReviewState = (round, days) => {
  /* `hincDesigner`, NOT `designer`. A round's designer column is called hincDesigner everywhere in
     this file (the sample-level `designer` is a roll-up of the LATEST round's); reading the wrong
     one returns null for every round, so the filter would quietly produce an empty list rather than
     an error. Exactly the mistake check_dept_manager_view.py made on its first run. */
  if (!round || !String(round.hincDesigner || '').trim()) return null;
  if (String(round.roundLabel || round.round || '').trim() === 'Trial / Mock-up') return null;
  if (String(round.baseId || round.sampleId || '').toUpperCase().startsWith('TSMP-')) return null;
  const age = reviewAgeDays(round.sampleFinishDate);
  if (age === null) return null;
  if (days && age > Number(days)) return null;          // outside the window the donut counted
  const done = Boolean(String(round.commentDate || '').trim()) && Boolean(String(round.outcome || '').trim());
  if (done) return 'done';
  return age < 14 ? 'in_review' : 'waiting';
};

// These three round statuses are computed (not stored), so matching them uses the same predicates the KPI
// cards count with — that keeps card numbers and the "All Rounds" filter perfectly in sync.
const ROUND_STATUS_PREDICATES = {
  'Pending Outcome': isPendingOutcomeRound,
  'RM Waiting': isRmWaitingRound,
  'In Production': isInProductionRound,
  // "In Progress" is exactly what the In Progress Rounds card counts: a round nobody has given an
  // outcome yet. It used to be spelled out as the four statuses RM Waiting / In Production /
  // Pending Outcome / Advance, which matched neither end — Advance IS an outcome so it added 93
  // rows the card never counted, while 11 rounds with no outcome matched none of the three
  // predicates and were dropped. Card and filter now share one definition (Max, 2026-08-13).
  'In Progress': (round) => !round.outcome,
};
/* The Project Tracker's KPI numbers, as pure functions of the SKU list.

   Exported because the Project Owner report shows the same two rows of cards (Max, 2026-08-22: "trong
   section 2 trước cái bảng sẽ là 2 dòng cards này"). Two hand-written copies of "what counts as an
   active project" is how the Ticket Station column order and the four Project Information blocks went
   wrong; one function cannot disagree with itself. */
export const groupStateKeysOf = (projects) => {
  const byGroup = new Map();
  (projects || []).forEach((p) => {
    if (p.approvalStatus && p.approvalStatus !== 'Approved') return;
    const gkey = p.groupId || p.id || p.projectItem;
    if (!byGroup.has(gkey)) byGroup.set(gkey, []);
    byGroup.get(gkey).push(p.status || '');
  });
  /* THREE STATES OF A PROJECT, and they add up to the project count — Max, 2026-10-20: "cần thêm
     trường hợp là các sku của 1 project là dropped nữa nhé". A project whose every SKU is Dropped
     was counted as ACTIVE ("still has an unfinished SKU"), which it is not: nothing on it is being
     worked. Measured that day: 48 all Complete, 6 all Dropped, 0 a mix of the two.
     DROPPED = every SKU Dropped. COMPLETED = nothing left open and at least one SKU Complete — so a
     mix of Complete and Dropped is Completed, the "most active SKU" rule Project Approval's chart
     and the Project Owner report already read a project by (Complete ranks above Dropped).
     ACTIVE = the rest. An empty project has finished nothing and dropped nothing, so it is Active. */
  const done = new Set();
  const dropped = new Set();
  const isDrop = (st) => st === 'Dropped' || st === 'Drop';
  byGroup.forEach((statuses, gkey) => {
    if (!statuses.length) return;
    if (statuses.every(isDrop)) dropped.add(gkey);
    else if (statuses.every((st) => st === 'Complete' || isDrop(st))) done.add(gkey);
  });
  return { done, dropped };
};

export const completedGroupKeysOf = (projects) => groupStateKeysOf(projects).done;

export const projectKpiCounts = (projects, stateKeys) => {
  const { done, dropped } = stateKeys || groupStateKeysOf(projects);
  const counts = {
    groups: 0, skus: 0, complete: 0, dropped: 0, active: 0,
    priority: 0, onTrack: 0, concern: 0, delayed: 0, hold: 0, shipFinished: 0,
    projectComplete: 0, projectDropped: 0, projectActive: 0,
  };
  const seenGroups = new Set();
  (projects || []).forEach((p) => {
    const gkey = p.groupId || p.id || p.projectItem;
    if (!seenGroups.has(gkey)) {
      seenGroups.add(gkey);
      counts.groups += 1;
      if (done.has(gkey)) counts.projectComplete += 1;
      else if (dropped.has(gkey)) counts.projectDropped += 1;
    }
    counts.skus += 1;
    const st = p.status || '';
    if (st === 'Dropped' || st === 'Drop') counts.dropped += 1;
    if (st === 'Complete') counts.complete += 1;
    if (!['Dropped', 'Drop', 'Complete'].includes(st)) counts.active += 1;
    if (st === 'Priority') counts.priority += 1;
    else if (st === 'On Track') counts.onTrack += 1;
    else if (st === 'Concern') counts.concern += 1;
    else if (st === 'Delayed') counts.delayed += 1;
    else if (st === 'On Hold') counts.hold += 1;
    else if (st === 'Ship Finished') counts.shipFinished += 1;
  });
  counts.projectActive = Math.max(0, counts.groups - counts.projectComplete - counts.projectDropped);
  return counts;
};

/* Exported for the role-aware home dashboard (2026-08-22). Its cards name a status and count with
   THIS function, so a card and the tracker screen it links to can never disagree — the alternative was
   a second copy of these rules in SQL. */
export const roundMatchesStatus = (round, want) => {
  const predicate = ROUND_STATUS_PREDICATES[want];
  if (predicate) return predicate(round);
  return round.status === want || round.outcome === want;
};
/* A SAMPLE (Base ID row) under a round-status filter — the three card statuses by the rule their cards count with (sampleInWatch),
   every other status by any of its rounds. One function for this screen and the DAC partner track (51_PartnerSamples.jsx), so a
   card's number and the list it opens cannot disagree on either. */
export const sampleMatchesRoundStatus = (sample, want) => (ROUND_STATUS_PREDICATES[want] && want !== 'In Progress'
  ? sampleInWatch(sample, ROUND_STATUS_PREDICATES[want])
  : (sample.rounds || []).some((round) => roundMatchesStatus(round, want)));

export const emptyForm = () => ({
  sampleRoundId: null,
  sampleId: '',
  baseId: '',
  styleItem: '',
  round: 'P1',
  option: '',
  category: '',
  season: '',
  developer: '',
  location: '',
  hincDesigner: '',
  developmentDue: '',
  targetPriceUsd: '',
  requestDate: '',
  roundDue: '',
  rmReadyDate: '',
  materialReadyExpected: false,
  sampleFinishDate: '',
  receivedDate: '',
  commentDate: '',
  qty: '',
  actualCostUsd: '',
  outcome: '',
  reworkReason: '',
  remarks: '',
  projectId: '',
  trialReviewMode: 'designer',
  /* RM ready per material, keyed by the material's uid. Round data, not SKU data — see the block in
     the Sample Plan section (was "HEVN Production") and roundMaterialSave on the server. */
  materialReady: {},
});

/* GTM is staging-only — the same build switch Header and index read. The SKU's GTM field is drawn
   only where the module exists, so production's form neither shows it nor offers a code. */
const GTM_ON = process.env.REACT_APP_GTM === '1';

/* A REPEAT ORDER — Max, 2026-10-09: "trong project SKU cần thêm 1 nút tick là có phải là repeat
   order ko nếu tick là có thì khi đó proto và counter sẽ tự động chuyển sang complete nhé". A style
   ordered again has no proto or counter sample to make, and those two stages are derived and
   read-only, so nobody could mark them done by hand. The tick is the decision; the two boxes follow
   it here at once and the server writes the same thing (it takes the flag only when
   `repeatOrderSet` says the tick was touched — a form spreads the whole row it was opened with).
   Unticking empties the two boxes back to "Auto — from the linked sample".
   L/D S/OFF and Hand-off Meeting follow too (Amy → Max, 2026-10-28) — but they are typed stages, so the tick
   fills them only while empty / Pending and unticking takes back only its own Complete (the server's
   projectTrackerRepeatStage, same rule). */
const REPEAT_DONE_STAGES = ['ldSoff', 'handOffMeeting'];
const repeatStage = (value, on) => {
  const v = String(value || '').trim();
  if (on && (!v || v === 'Pending')) return 'Complete';
  if (!on && v === 'Complete') return 'Pending';
  return value;
};
function RepeatOrderField({ value, stages, onChange, readOnly }) {
  const { t } = useTranslation();
  return (
    <label className={`pt-repeat${value ? ' on' : ''}`}>
      <input type="checkbox" checked={!!value} disabled={readOnly}
        onChange={(event) => {
          const on = event.target.checked;
          onChange({
            repeatOrder: on,
            repeatOrderSet: true,
            stages: {
              ...(stages || {}), proto: on ? 'Complete' : '', counter: on ? 'Complete' : '',
              ...Object.fromEntries(REPEAT_DONE_STAGES.map((k) => [k, repeatStage((stages || {})[k], on)])),
            },
          });
        }} />
      <b>{t('st.pfRepeatOrder')}</b>
      <span>{t('st.pfRepeatOrderHint')}</span>
    </label>
  );
}

/* THE GTM PROGRAMME A SKU WAS LAUNCHED UNDER — Max, 2026-10-08: "bên Project SKU cũng có thêm 1
   trường là GTM để điền nhé". The same column the GTM sheet's "Linked Project SKUs" writes
   (projects.gtm_code), so the two screens cannot disagree. The choices are every active
   programme, in any state (Max: "cho phép chọn project SKU luôn nhé"); the server checks the code
   EXISTS, and only when the value CHANGES. */
function SkuGtmField({ form, filterOptions, onChange, readOnly }) {
  const { t } = useTranslation();
  if (!GTM_ON) return null;
  const options = filterOptions.gtmOptions || [];
  const code = String(form.gtmCode || '').toUpperCase();
  /* The title comes from the list only. A code that is not on it — typed wrong, or a programme
     deleted since — names nothing here rather than a title that may be stale. */
  const title = options.find((o) => o.code === code)?.title || '';
  return (
    <>
      <div className="pt-sku-subttl">{t('st.pfGtm')}</div>
      <Field label="" hint={`${t('st.pfGtmHint')}${title ? ` — ${title}` : ''}`}>
        <input list="pt-gtm-options" value={form.gtmCode || ''} readOnly={readOnly}
          /* `gtmCodeSet` says the field was TOUCHED and `gtmCodeWas` what it held when the form
             opened: the server writes the column only then, and refuses (409) if it has moved since —
             a form spreads the whole row it was opened with, so without the flag a stale tab saving a
             Remark would write its old GTM back over a link made on the GTM sheet. */
          onChange={(event) => onChange({
            gtmCode: event.target.value.trim().toUpperCase(),
            gtmCodeSet: true,
            gtmCodeWas: form.gtmCodeSet ? form.gtmCodeWas : (form.gtmCode || ''),
          })}
          placeholder={t('st.pfGtmPh')} />
      </Field>
      <datalist id="pt-gtm-options">
        {options.map((o) => <option key={o.code} value={o.code}>{o.title}</option>)}
      </datalist>
    </>
  );
}

const emptyProjectForm = () => ({
  id: '',
  projectItem: '',
  subItem: '',
  groupId: '',
  newGroupName: '',
  category: '',
  productType: '',
  typology: '',      // hidden since 2026-08-14, still round-tripped so saving cannot erase it
  frameColor: '',
  assignedTo: '',
  designerName: '',
  allocation: '',
  status: 'On Track',
  startDate: '',
  exfShipDate: '',
  estimateQty: '',
  targetPrice: '',
  launchDate: '',
  remark: '',
  sampleId: '',
  repeatOrder: false,
  stages: stageColumns.reduce((acc, [key]) => ({ ...acc, [key]: 'Pending' }), {}),
});

/* FOUR SKU FIELDS ARE REQUIRED — Max, 2026-10-16: "ẩn các nút TBA nhé và cho bắt buộc điền Estimate
   QTY và EXF Ship Date nhé", then "thêm cột target price và launching date phải điền". The TBA tick
   is gone: it existed so those boxes could be left empty, which is exactly what is no longer allowed.

   WHO HAS TO FILL THEM: a NEW SKU, always; and an existing SKU may not have one EMPTIED once it holds
   it. An existing SKU saved before the rule with the box empty still saves as it is — measured that
   day, 308 of 549 had no quantity, 169 no EXF, and none a target price or a launching date. A
   developer updating a stage on an old SKU does not know the order quantity or the price, and
   refusing the stage would stop the tracker rather than fill the gap.
   The server applies the same rule (skuRequiredMissing in server.js). `_was` is the row as the form
   was opened — set by every place that opens an existing SKU; without it an existing SKU is held to
   nothing, which is the lenient side to fail on. */
const SKU_REQUIRED_FIELDS = [
  // [form key, SkuCard key, label (an i18n key — skuRequiredMissing translates it), kind]
  ['estimateQty', 'qty', 'stx.skuReq.estimateQty', 'num'],
  ['exfShipDate', 'exfDate', 'stx.skuReq.exfShipDate', 'date'],
  ['targetPrice', 'targetPrice', 'stx.skuReq.targetPrice', 'num'],
  ['launchDate', 'launchDate', 'stx.skuReq.launchDate', 'date'],
];
const skuValueOk = (kind, v) => (kind === 'num'
  ? Number(String(v ?? '').replace(/,/g, '').trim()) > 0
  : /^\d{4}-\d{2}-\d{2}/.test(String(v ?? '').trim()));
// The row's own values, captured when a form opens on an existing SKU.
export const skuWas = (project) => Object.fromEntries(
  SKU_REQUIRED_FIELDS.map(([fk]) => [fk, project ? project[fk] : undefined]));
// Which of the four this form must carry (a Set of form keys).
export const skuRequiredKeys = (isNew, was) => new Set(SKU_REQUIRED_FIELDS
  .filter(([fk, , , kind]) => isNew || skuValueOk(kind, (was || {})[fk])).map(([fk]) => fk));
// The labels still missing, from values keyed like the SKU form (estimateQty, exfShipDate, …).
export const skuRequiredMissing = (values, isNew, was) => {
  const need = skuRequiredKeys(isNew, was);
  return SKU_REQUIRED_FIELDS
    .filter(([fk, , , kind]) => need.has(fk) && !skuValueOk(kind, (values || {})[fk]))
    .map(([, , label]) => i18n.t(label));
};
// The same for a SkuCard draft (qty, exfDate, …), which is new unless it carries an id.
export const skuCardValues = (sku) => Object.fromEntries(
  SKU_REQUIRED_FIELDS.map(([fk, ck]) => [fk, String((sku || {})[ck] ?? '') === 'TBA' ? '' : (sku || {})[ck]]));
export const skuCardMissing = (sku) => skuRequiredMissing(
  skuCardValues(sku), !(sku && (sku.projectId || sku.id)), sku && sku._was);

const validateProjectForm = (form) => {
  const errors = [];
  if (!String(form.projectItem || '').trim()) errors.push(i18n.t('stx.v.projectName'));
  skuRequiredMissing(form, !form.id, form._was).forEach((label) => errors.push(i18n.t('stx.v.required', { label })));
  return errors;
};

// Multi-SKU "New Project" form — one Special Project group + N SKU rows (each becomes a PRJ item).
// Ports Portal.html ptFmItems model (ptFmAddSkuFromFields / ptFmReadSkuCard).
export const emptySku = () => ({
  item: '',
  itemCategory: '',
  typology: '',      // hidden since 2026-08-14, still round-tripped so saving cannot erase it
  frameColor: '',
  assigned: '',
  designer: '',
  allocation: '',
  qty: '',
  targetPrice: '',
  startDate: '',
  exfDate: '',
  launchDate: '',
  status: 'On Track',
  remark: '',
  samples: '',
  repeatOrder: false,
  stages: stageColumns.reduce((acc, [key]) => ({ ...acc, [key]: 'Pending' }), {}),
});

/* A copy of a staged SKU — Max, 2026-09-08: "thêm tính năng duplicate project SKU nhé". A project
   is often five colourways of one thing, and typing the same nine fields five times is the tax.

   Three things are NOT copied, because they belong to the SKU that earned them rather than to the
   shape of it: the id (a copy is a new row), the linked sample, and the stage progress. Copying the
   stages would have the new SKU claiming work nobody has done on it.

   The name gets "Copy of", the way a duplicated ticket and a duplicated round already do — two cards
   reading the same name in one list is the thing that makes a duplicate hard to use. */
export const duplicateSku = (sku) => ({
  ...emptySku(),
  ...sku,
  id: undefined,
  item: String(sku.item || '').trim() ? `Copy of ${String(sku.item).trim()}` : '',
  samples: '',
  sampleId: '',
  // A repeat order's copy is a repeat too (another colourway of the same reorder), and its two
  // derived stages say so; everything else starts Pending.
  stages: stageColumns.reduce((acc, [key]) => ({
    ...acc, [key]: sku.repeatOrder && (key === 'proto' || key === 'counter' || REPEAT_DONE_STAGES.includes(key)) ? 'Complete' : 'Pending',
  }), {}),
});

/* The same copy, made from a SKU that ALREADY EXISTS — Max, 2026-09-08: "model này chưa có". The
   two shapes are not the same: a saved row calls things subItem/productType/assignedTo/estimateQty
   and a staged card calls them item/itemCategory/assigned/qty, so the fields are mapped across
   before duplicateSku applies the one rule about what a copy is and is not.

   The result is a STAGED SKU, not a saved one. It appears in the "new SKUs" list and becomes a real
   PRJ item when the group is saved — which is what makes this need no endpoint of its own, and what
   lets somebody change their mind by pressing Cancel. */
export const duplicateExistingSku = (sku) => duplicateSku({
  ...emptySku(),
  item: sku.subItem || sku.projectItem || '',
  itemCategory: sku.productType || '',
  typology: sku.typology || '',
  frameColor: sku.frameColor || '',
  assigned: sku.assignedTo || '',
  designer: sku.designerName || '',
  allocation: sku.allocation || '',
  qty: String(sku.estimateQty ?? '').trim(),
  targetPrice: String(sku.targetPrice ?? '').trim(),
  startDate: sku.startDate || '',
  exfDate: String(sku.exfShipDate ?? '').trim(),
  launchDate: sku.launchDate || '',
  status: sku.status || 'On Track',
  remark: sku.remark || '',
  repeatOrder: !!sku.repeatOrder,
});

const emptyGroupForm = () => ({
  // Project Name is no longer typed: it is built from Season + Partner/Line + Keyword (Jay item 7).
  teamKey: '',
  ownerUserId: '',
  salesRepId: '',
  season: '',
  seasonYear: '',
  seasonParts: [],
  partnerId: '',
  keyword: '',
  targetLaunchMonth: '',
  approvalNote: '',
  projectPurpose: '',
  purposeRequired: true,
  category: '',
  projectCase: '',
  entity: '',
  // SKUs are optional at creation (item 2) — a project often starts before the SKUs are known.
  skus: [],
});

// Season options: the current and next few seasons plus plain years for projects with no season
// (Jay: "For projects with no season, use the year"). Generated rather than stored — a season is
// mechanical, unlike the Partner list which is a managed master.
/* Season is Year + up to two of Spring / Summer / Fall / Winter (Jay via Max, 2026-08-23). The label the
   project name uses is composed from that pick — one season gives its first TWO letters (Spring -> SP),
   two seasons give one letter each in calendar order (Spring + Summer -> SS). The server composes the same
   label on save; this copy exists so the form can show the project name before saving, the way it already
   previewed the old single-select value. */
export const SEASON_PART_ORDER = ['Spring', 'Summer', 'Fall', 'Winter'];
const SEASON_PART_ABBR = { Spring: 'SP', Summer: 'SU', Fall: 'FA', Winter: 'WI' };
export const seasonPartsOrdered = (parts) => {
  const wanted = new Set((Array.isArray(parts) ? parts : String(parts || '').split(','))
    .map((x) => String(x || '').trim().toLowerCase()).filter(Boolean));
  return SEASON_PART_ORDER.filter((sp) => wanted.has(sp.toLowerCase()));
};
export const seasonLabelOf = (year, parts) => {
  const y = Number(year) || 0;
  if (!y) return '';
  const ordered = seasonPartsOrdered(parts);
  if (!ordered.length) return String(y);
  const yy = String(y % 100).padStart(2, '0');
  return ordered.length === 1
    ? yy + SEASON_PART_ABBR[ordered[0]]
    : yy + ordered.map((sp) => sp[0].toUpperCase()).join('');
};
export const seasonYearOptions = (currentYear) => {
  const years = [];
  for (let i = -1; i <= 4; i += 1) years.push(currentYear + i);
  return years;
};

export const seasonOptions = (currentYear) => {
  const yy = currentYear % 100;
  const seasons = [];
  for (let i = 0; i <= 3; i += 1) {
    seasons.push(`${String(yy + i).padStart(2, '0')}SS`, `${String(yy + i).padStart(2, '0')}FW`);
  }
  const years = [];
  for (let i = 0; i <= 3; i += 1) years.push(String(currentYear + i));
  return [...seasons, ...years];
};

// Mirrors projectGroupDisplayName on the server so the form can show the name before saving.
// For Collaboration " x Helinox" belongs to the partner, so it sits between partner and keyword.
/* A season-shaped token at the START of a string: '26FW', '27SP', '2027'. The two-letter codes are the
   ones seasonLabelOf can emit, so this recognises what the system writes and not any pair of letters.
   Mirrors SEASON_TOKEN_RE in the backend — the name it previews has to be the name that gets saved. */
export const SEASON_TOKEN_RE = /^((?:19|20)\d{2}|\d{2}(?:SS|SF|SW|FW|SP|SU|FA|WI))(?=\s|$)/i;
export const stripLeadingSeason = (text) => String(text || '').trim().replace(SEASON_TOKEN_RE, '').trim();

export const buildProjectName = ({ season, partnerName, keyword, category }) => {
  const partner = String(partnerName || '').trim();
  const isCollab = String(category || '').trim().toLowerCase() === 'collaboration';
  const partnerPart = partner && isCollab && !/x\s*helinox/i.test(partner) ? `${partner} x Helinox` : partner;
  // The season is already the first part; a keyword that repeats it would print it twice.
  return [String(season || '').trim(), partnerPart, stripLeadingSeason(keyword)].filter(Boolean).join(' ');
};

// % of the ten stages other than PO Status marked exactly 'Complete' — mirrors projectTrackerCompletePct.
// PO Status is left out BY KEY: it was "the first 10" while PO Status was last, and moving it up
// (2026-10-15) would have silently counted it and dropped Payment.
const skuStagePct = (stages = {}) => {
  const keys = stageColumns.map(([k]) => k).filter((k) => k !== 'poStatus');
  const done = keys.filter((k) => String(stages[k] || '').toLowerCase() === 'complete').length;
  return Math.round((done / keys.length) * 100);
};

// A row keeps showing the person it is already assigned to even after that name leaves the list.
const withCurrent = (options, value) => (
  value && !options.includes(value) ? [...options, value] : options
);

const SKU_TOUCHED_FIELDS = ['item', 'itemCategory', 'frameColor', 'assigned', 'designer', 'allocation', 'qty', 'targetPrice', 'startDate', 'exfDate', 'launchDate', 'remark', 'sampleId'];
const skuIsTouched = (sku) => SKU_TOUCHED_FIELDS.some((field) => String(sku[field] || '').trim());
/**
 * The fields Edit Project Group compares to decide "was anything changed?". One list, used for the
 * snapshot taken when the form opens AND for the live comparison — see openEditGroupModal.
 * `groupId` is deliberately absent: it never changes, and `mode` is in so switching between the
 * full edit and the tickets view is not read as an edit.
 */
const groupInfoShape = (g) => ({
  mode: g.mode,
  groupName: g.groupName || '',
  category: g.category || '',
  projectCase: g.projectCase || '',
  entity: g.entity || '',
  teamKey: g.teamKey || '',
  ownerUserId: g.ownerUserId ? String(g.ownerUserId) : '',
  season: g.season || '',
  seasonYear: g.seasonYear || '',
  seasonParts: seasonPartsOrdered(g.seasonParts).join(','),
  partnerId: g.partnerId ? String(g.partnerId) : '',
  keyword: g.keyword || '',
  targetLaunchMonth: g.targetLaunchMonth || '',
  salesRepId: g.salesRepId ? String(g.salesRepId) : '',
  approvalNote: g.approvalNote || '',
  projectPurpose: g.projectPurpose || '',
});

const validateGroupForm = (group) => {
  const errors = [];
  if (!String(group.teamKey || '').trim()) errors.push(i18n.t('stx.v.team'));
  if (!String(group.category || '').trim()) errors.push(i18n.t('stx.v.category'));
  // Year is what the person picks; `season` is derived from it, so validating the label would blame the
  // wrong field (Max, 2026-08-23).
  if (!group.seasonYear) errors.push(i18n.t('stx.v.year'));
  if (seasonPartsOrdered(group.seasonParts).length > 2) errors.push(i18n.t('stx.v.maxSeasons'));
  if (!group.ownerUserId) errors.push(i18n.t('stx.v.owner'));
  // Young made this mandatory before he will approve (via Jay, 2026-08-19).
  if (!group.salesRepId) errors.push(i18n.t('stx.v.salesRep'));
  // Only for projects the rule applies to — the ones that already existed are exempt for good, so
  // editing or resubmitting one of them must not be blocked on a field it never had.
  if (group.purposeRequired !== false && !String(group.projectPurpose || '').trim()) {
    errors.push(i18n.t('stx.v.purpose'));
  }
  // Required since 2026-08-13 (Max): the field was already rejected when left blank on save, but
  // nothing on the form said so — no asterisk, and the message surfaced outside the modal.
  if (!String(group.targetLaunchMonth || '').trim()) errors.push(i18n.t('stx.v.targetLaunch'));
  // Before the current month: refused; outside the season's window: ticked as right first (projectLaunchCheck.js).
  errors.push(...launchErrors(launchCheck(group, null), group.launchConfirmed, i18n.t.bind(i18n)));
  // Partner / Line is optional and the keyword is uncapped (Max 2026-08-11).
  // No "at least one SKU" rule any more — an empty project is a valid starting point.
  // A card the user started filling but left without a name used to be dropped without a word.
  (group.skus || []).forEach((sku, idx) => {
    if (!String(sku.item || '').trim() && skuIsTouched(sku)) {
      errors.push(i18n.t('stx.v.skuItemName', { n: idx + 1 }));
    }
    if (String(sku.item || '').trim() || skuIsTouched(sku)) {
      const miss = skuCardMissing(sku);
      if (miss.length) errors.push(i18n.t('stx.v.skuMissing', { n: idx + 1, fields: miss.join(', '), count: miss.length }));
    }
  });
  return errors;
};

export const calcPreview = (form) => ({
  makeDays: daysBetween(form.rmReadyDate, form.sampleFinishDate),
  shipDays: daysBetween(form.sampleFinishDate, form.receivedDate),
  commentDays: daysBetween(form.receivedDate, form.commentDate),
  roundCycleDays: daysBetween(form.sampleFinishDate, form.commentDate),
  dDay: form.developmentDue ? daysBetween(form.developmentDue, todayVN()) : '',
});

export const isInternalTrial = (form) => String(form.trialReviewMode || '').toLowerCase() === 'internal';

// Outcome explanations + accent colours — ports Portal.html setOutcomeUI() descriptions verbatim.
export const OUTCOME_DESCRIPTIONS = {
  Advance: 'Advance - The current sample has been reviewed and approved to proceed to the next development round. A new round must be registered.',
  Rework: 'Rework - The sample has been rejected and requires rework before the next review.',
  Confirmed: 'Confirmed - The sample has been approved, and no further development rounds are required.',
  Dropped: 'Dropped - Development for this style has been discontinued, and no further work will be carried out.',
  'On Hold': 'On Hold - The decision maker has not decided to go or no-go yet. Hold this round outcome until a decision is made.',
};
/* What a description PRINTS, per outcome — the English text above stays the export other screens read. */
const OUTCOME_DESC_KEYS = {
  Advance: 'stx.outcomeDesc.advance',
  Rework: 'stx.outcomeDesc.rework',
  Confirmed: 'stx.outcomeDesc.confirmed',
  Dropped: 'stx.outcomeDesc.dropped',
  'On Hold': 'stx.outcomeDesc.onHold',
};
export const OUTCOME_DESC_STYLE = {
  '': { background: '#F8FAFC', borderColor: '#E2E8F0' },
  Advance: { background: '#EFF6FF', borderColor: '#93C5FD' },
  Rework: { background: '#FFF7ED', borderColor: '#FDBA74' },
  Confirmed: { background: '#F0FDF4', borderColor: '#86EFAC' },
  Dropped: { background: '#F8FAFC', borderColor: '#CBD5E1' },
  'On Hold': { background: '#FAF5FF', borderColor: '#D8B4FE' },
};

const DEV_OR_DESIGNER_REQUIRED = 'A Developer or a Designer is required';
// An error as it is DRAWN: the one message validateForm keeps in English is translated here.
const errorText = (message) => (message === DEV_OR_DESIGNER_REQUIRED ? i18n.t('stx.v.devOrDesigner') : message);
export const validateForm = (form, mode) => {
  const errors = [];
  /* Style / Item cannot be typed since 2026-09-07, so "it is required" would name a field the person
     cannot fill. What they can do is pick the sample this round belongs to, which is what to say. */
  if (!String(form.styleItem || '').trim()) {
    errors.push(mode === 'new' ? i18n.t('stx.v.pickSample') : i18n.t('stx.v.styleItem'));
  }
  if (!String(form.round || '').trim()) errors.push(i18n.t('stx.v.round'));
  if (!form.rmReadyDate) errors.push(i18n.t('stx.v.rmReady'));
  // Marked required on the form but never enforced, so a round could be saved with no developer.
  // One of the two, matching the server — see the note by sampleTrackerValidatePayload. A SKU with a
  // designer and no developer is normal for HINC DT work, and its designer is the person creating the
  // sample.
  if (!String(form.developer || '').trim() && !String(form.hincDesigner || '').trim()) {
    /* LEFT IN ENGLISH ON PURPOSE: 51_PartnerSamples.jsx removes this one message by comparing the exact
       string, so it is translated where it is DRAWN (RoundModal, `errorText`), never here. */
    errors.push(DEV_OR_DESIGNER_REQUIRED);
  }
  if (isInternalTrial(form) && !form.requestDate) errors.push(i18n.t('stx.v.reqDateInternal'));
  // An outcome is only decided at the comment meeting, so any outcome needs its Review (Comment) Date.
  if (!isInternalTrial(form) && String(form.outcome || '').trim()) {
    if (!form.commentDate) errors.push(i18n.t('stx.v.reviewDate', { outcome: form.outcome }));
    if (form.outcome === 'Dropped' && !String(form.remarks || '').trim()) {
      errors.push(i18n.t('stx.v.remarksDropped'));
    }
  }
  if (form.outcome === 'Rework' && !String(form.reworkReason || '').trim()) {
    errors.push(i18n.t('stx.v.reworkReason'));
  }
  return errors;
};

export const SAMPLE_SORT_GETTERS = {
  baseId: (sample) => sample.baseId,
  styleItem: (sample) => sample.styleItem,
  round: (sample) => sample.latest?.round,
  season: (sample) => sample.latest?.season,
  location: (sample) => sample.location,
  developer: (sample) => sample.developer,
  designer: (sample) => sample.designer,
  projectId: (sample) => sample.latest?.projectId,
  requestDate: (sample) => sample.latest?.requestDate,
  developmentDue: (sample) => sample.developmentDue,
  rmReadyDate: (sample) => sample.latest?.rmReadyDate,
  roundDue: (sample) => sample.latest?.roundDue,
  sampleFinishDate: (sample) => sample.latest?.sampleFinishDate,
  receivedDate: (sample) => sample.latest?.receivedDate,
  commentDate: (sample) => sample.latest?.commentDate,
  qty: (sample) => sample.latest?.qty,
  roundCount: (sample) => sample.roundCount,
  status: (sample) => sample.latestStatus || sample.status,
  dDay: (sample) => sample.dDay,
  rework: (sample) => sample.rework,
  productStatus: (sample) => sample.productViewStatus,
};

/* BLANKS GO LAST IN BOTH DIRECTIONS, as Excel puts them (2026-10-13). Descending used to be the
   ascending list reversed, which carried every empty cell to the TOP — on Cash Set up's exchange rates
   that was the twelve weeks still to come, sitting above the highest rate somebody sorted to find. */
export const sortRows = (rows, sort, getters) => {
  const getter = getters[sort.key];
  if (!getter) return rows;
  const dir = sort.dir === 'desc' ? -1 : 1;
  const blank = (v) => v === undefined || v === null || v === '';
  return rows.slice().sort((a, b) => {
    const va = getter(a);
    const vb = getter(b);
    if (va === vb) return 0;
    if (blank(va)) return 1;
    if (blank(vb)) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
    return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir;
  });
};

const sortSamples = (rows, sort) => sortRows(rows, sort, SAMPLE_SORT_GETTERS);

/* A ROUND'S STYLE / ITEM CARRIES ITS OPTION — Max, 2026-10-15: "cú pháp của Style/ item sẽ thêm là
   _Option": "Morning Chair Side Mesh - Charcoal" with the option Black reads
   "Morning Chair Side Mesh - Charcoal_Option: Black" (the wording Max settled the same day, after
   "…_Black" had shipped — the word says what the suffix is).
   DISPLAYED, NEVER STORED. style_item is the BASE's name: the server hands out Base IDs by it
   (sampleTrackerAssignBaseId), every round of a base shares it, and renaming a sample renames them
   all — written with "_Black" in it, a round would read as another style and could be given a Base
   ID of its own. So the column keeps the name and the option is joined on where a ROUND is shown;
   the Base ID row keeps the bare name, since its rounds may carry different options. */
export const roundStyleLabel = (r) => {
  const style = String(r?.styleItem || '').trim();
  const option = String(r?.option || '').trim();
  return option ? `${style}_Option: ${option}` : style;
};

// Column-filter value accessors. The group table reuses the sort getters so a column always filters on the
// value it displays; the flat round table needs its own set because a round has no `latest`.
export const ROUND_FILTER_GETTERS = {
  baseId: (r) => r.sampleId,
  styleItem: roundStyleLabel,
  round: (r) => r.round,
  season: (r) => r.season,
  location: (r) => r.location,
  developer: (r) => r.developer,
  designer: (r) => r.hincDesigner,
  projectId: (r) => r.projectId,
  requestDate: (r) => r.requestDate,
  developmentDue: (r) => r.developmentDue,
  rmReadyDate: (r) => r.rmReadyDate,
  roundDue: (r) => r.roundDue,
  sampleFinishDate: (r) => r.sampleFinishDate,
  receivedDate: (r) => r.receivedDate,
  commentDate: (r) => r.commentDate,
  qty: (r) => r.qty,
  status: (r) => roundStatus(r),
  dDay: (r) => r.dDay,
};

// Excel-style filtering: one set of allowed values per column, ANDed across columns. `(Blanks)` is a real
// choice rather than an empty label, otherwise a blank cell could not be selected at all.
const FILTER_BLANK = '(Blanks)';

// Stable identity: tableFilters() returns this for an unfiltered table, so useMemo deps stay referentially equal.
const NO_COLUMN_FILTERS = {};

// Date columns get Excel's Year > Month > Day tree instead of a flat list of every single date: a column with
// 200 distinct dates is unusable as a flat list, and people filter by month far more often than by one day.
const FILTER_DATE_KEYS = new Set([
  'requestDate', 'developmentDue', 'rmReadyDate', 'roundDue', 'sampleFinishDate', 'receivedDate', 'commentDate',
  // Project Tracker and Ticket Station date columns — same Year > Month > Day tree.
  'startDate', 'exfShipDate', 'dueDate', 'launchDate',
  // Capacity. The toolbar filters delivery by Year > Month; the column funnel goes down to the day.
  'deliveryDate', 'fiDate', 'newExfty',
  // Daily Output's list of sheets: 400 days is a wall of dates, a Year > Month > Day tree is not.
  'outputDate',
  // GTM Programmes and GTM Approval (2026-10-08) — the same tree on their date columns.
  'gtmLaunch', 'gtmCreatedAt', 'gtmUpdatedAt', 'gtmSentOn', 'gtmDecidedOn',
  // GTM Tracker (2026-10-16): the next step's expected date and the day it was approved.
  'gtmNextDue', 'gtmApprovedOn',
  // Sales Planning's orders table (2026-10-20): the order's dates and the day it was created.
  'splOrderDue', 'splExFactory', 'splFcDue', 'splCreatedAt',
  // A partner's sample track (DAC VN, 2026-10-18): the day a Base ID was made.
  'psCreatedAt',
  // Users (2026-10-09) — the Created column.
  'userCreatedAt',
  // Cash Report Set up (2026-10-13) — the week-ending Friday on Exchange rates and Period.
  'week_ending',
  // Cash Report Approval (2026-10-15) — when a report was sent and last moved.
  'cashSentAt', 'cashUpdatedAt',
  // Courier Requests / Courier Approval (2026-10-19) — when a request was raised and decided.
  'crCreatedAt', 'crDecidedAt', 'crSentAt',
  // Sales Data Load (2026-10-23) — when a run started, a held row's sales date.
  'cwStartedAt', 'cwSalesDate', 'ivInbound', 'ivShip', 'ivExpected',
]);
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

/** [{ year, months: [{ month, label, days: [{ value, label }] }] }] — newest first, like Excel. */
const buildDateTree = (values) => {
  const years = new Map();
  const loose = [];
  values.forEach((value) => {
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) { loose.push(value); return; }
    const [, y, m, d] = match;
    if (!years.has(y)) years.set(y, new Map());
    const months = years.get(y);
    if (!months.has(m)) months.set(m, []);
    months.get(m).push({ value, label: d });
  });
  const tree = [...years.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([year, months]) => ({
      year,
      months: [...months.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([month, days]) => ({
        month,
        label: MONTH_NAMES[Number(month) - 1] || month,
        days: days.sort((a, b) => a.label.localeCompare(b.label)),
      })),
    }));
  return { tree, loose };
};
// A getter may return several values for one row — the Project / Item column offers BOTH the project
// group name and the SKU name, so filtering by a group picks up every SKU under it. One value is the
// normal case and still behaves exactly as before.
const filterCellValues = (row, key, getters) => {
  const raw = getters[key] ? getters[key](row) : undefined;
  const list = (Array.isArray(raw) ? raw : [raw])
    .map((v) => (v === null || v === undefined ? '' : String(v).trim()))
    .filter((v, i, arr) => v !== '' && arr.indexOf(v) === i);
  return list.length ? list : [FILTER_BLANK];
};
export const rowPassesColumnFilters = (row, columnFilters, getters, skipKey) => Object.entries(columnFilters)
  .every(([key, allowed]) => (
    key === skipKey || !allowed || !allowed.length
    || filterCellValues(row, key, getters).some((v) => allowed.indexOf(v) !== -1)
  ));
export const distinctColumnValues = (rows, key, columnFilters, getters) => {
  const set = new Set();
  rows.forEach((row) => {
    if (rowPassesColumnFilters(row, columnFilters, getters, key)) {
      filterCellValues(row, key, getters).forEach((v) => set.add(v));
    }
  });
  // Blanks last, everything else naturally sorted so numbers and P-rounds read in order.
  return [...set].sort((a, b) => {
    if (a === FILTER_BLANK) return 1;
    if (b === FILTER_BLANK) return -1;
    return String(a).localeCompare(String(b), undefined, { numeric: true });
  });
};

const parseWeeksLeft = (value) => {
  if (value === 'TBA') return Infinity;
  const num = parseInt(String(value || '').replace('W', ''), 10);
  return Number.isFinite(num) ? num : Infinity;
};

const STAGE_SORT_ORDER = {
  Pending: 10,
  'In Progress': 20,
  Delayed: 30,
  'On Hold': 40,
  Complete: 50,
  'N/A': 60,
};

const stageSortValue = (value) => {
  const val = String(value || 'Pending').trim();
  if (Object.prototype.hasOwnProperty.call(STAGE_SORT_ORDER, val)) return STAGE_SORT_ORDER[val];
  if (val.indexOf('In Progress') !== -1 || val.indexOf('Sewing') !== -1 || val.indexOf('RM Waiting') !== -1 || val.indexOf('Waiting Feedback') !== -1) return 21;
  return 1000 + (val.toLowerCase().charCodeAt(0) || 0);
};

const PROJECT_SORT_GETTERS = {
  id: (project) => project.id,
  teamName: (project) => project.teamName,
  ownerName: (project) => project.ownerName,
  salesRepName: (project) => project.salesRepName,
  projectItem: (project) => project.projectItem,
  category: (project) => project.category,
  productType: (project) => project.productType,
  frameColor: (project) => project.frameColor,
  estimateQty: (project) => project.estimateQty,
  targetPrice: (project) => project.targetPrice,
  launchDate: (project) => project.launchDate,
  assignedTo: (project) => project.assignedTo,
  designerName: (project) => project.designerName,
  allocation: (project) => project.allocation,
  status: (project) => project.status,
  startDate: (project) => project.startDate,
  exfShipDate: (project) => project.exfShipDate,
  leadTime: (project) => parseWeeksLeft(project.leadTime),
  completePct: (project) => project.completePct,
  ldSoff: (project) => stageSortValue(project.ldSoff),
  proto: (project) => stageSortValue(project.proto),
  counter: (project) => stageSortValue(project.counter),
  costing: (project) => stageSortValue(project.costing),
  handOffMeeting: (project) => stageSortValue(project.handOffMeeting),
  ppsPpm: (project) => stageSortValue(project.ppsPpm),
  production: (project) => stageSortValue(project.production),
  finalInspection: (project) => stageSortValue(project.finalInspection),
  exfShipment: (project) => stageSortValue(project.exfShipment),
  payment: (project) => stageSortValue(project.payment),
  poStatus: (project) => stageSortValue(project.poStatus),
  remark: (project) => project.remark,
  sampleId: (project) => project.sampleId,
  groupCase: (project) => project.groupCase,
  groupEntity: (project) => project.groupEntity,
};

/**
 * Column-filter accessors for the Project Tracker, and for the Ticket Station below.
 *
 * These are NOT the sort getters: sorting a stage column has to order Pending < In Progress < Complete, so
 * PROJECT_SORT_GETTERS maps a stage to a rank NUMBER. Filtering must offer the words a person sees, so the
 * stage columns and Weeks Left read the raw value here. Everything else is shared with the sort map.
 */
const PROJECT_FILTER_GETTERS = {
  ...PROJECT_SORT_GETTERS,
  // The ID column DISPLAYS the PRJ-### of the group, but every row underneath is a SKU, so the filter list
  // offered 306 SKU ids and not one of the PRJ numbers written in the column being filtered. Both, now:
  // picking PRJ-140 keeps the whole project, picking SKU-001 keeps just that row — the same rule the
  // Project / Item column already followed. Sorting is deliberately left alone: PROJECT_SORT_GETTERS keeps
  // one value per row, because a row cannot sort by two keys at once.
  id: (project) => [project.groupId, project.id],
  // Project group name first, then the SKU's own name: picking "S27" in the filter keeps the whole
  // group, picking one SKU keeps just that row.
  projectItem: (project) => [project.groupName, project.projectItem],
  leadTime: (project) => project.leadTime,
  groupCase: (project) => project.groupCase,
  groupEntity: (project) => project.groupEntity,
  ...stageColumns.reduce((acc, [key]) => ({ ...acc, [key]: (project) => project[key] || 'Pending' }), {}),
};

const TASK_FILTER_GETTERS = {
  dDay: (r) => r.dDay,
  teamName: (r) => r.teamName,
  ownerName: (r) => r.ownerName,
  taskId: (task) => task.taskId,
  name: (task) => task.name,
  projectId: (task) => task.projectId,
  projectName: (task) => task.projectName,
  department: (task) => task.department,
  assignee: (task) => task.assignee,
  taskType: (task) => task.taskType,
  priority: (task) => task.priority || 'Medium',
  dueDate: (task) => task.dueDate,
  status: (task) => taskNormalizeStatus(task.status),
  /* The four audit columns (Max, 2026-09-08). The two timestamps filter and sort on the DAY, not the
     instant — a funnel offering every distinct second is a list nobody can pick from, and "sort by
     when it was created" and "sort by that day" put the rows in the same order anyway. */
  createdBy: (task) => task.createdBy,
  createdAt: (task) => fmtDateVN(task.createdAt, ''),
  updatedBy: (task) => task.updatedBy,
  updatedAt: (task) => fmtDateVN(task.updatedAt, ''),
};

/* ONE GROUPING AND ONE COUNT, shared with the partner track (51_PartnerSamples.jsx, 2026-10-18 — Max: "bê y
   chang logic"). They lived inside SampleTracker; moved out word for word so a second screen cannot come to
   count a sample differently. `enrichedRounds` is the rounds with `status` = roundStatus(round) on each. */
export const groupSamplesOf = (enrichedRounds) => {
  const map = new Map();
  enrichedRounds.forEach((round) => {
    const key = round.baseId || round.styleItem || round.sampleId;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(round);
  });
  return Array.from(map.entries()).map(([baseId, items]) => {
    const latest = items[items.length - 1] || {};
    const rework = items.filter((round) => round.outcome === 'Rework').length;
    // All Samples table row / Sample Status filter / local KPI cards show the latest round's own
    // outcome directly (Advance/Rework/Confirmed/Dropped, or "In Progress" if blank) - matches
    // Portal.html's renderMy() exactly. This is deliberately different from productViewStatus below.
    const status = latest.outcome || 'In Progress';
    // What the Status pill on the group row shows: the newest round's own status, using the same rule as
    // the round rows below it (Internal Trial / Pending Outcome / In Production / RM Waiting / outcome),
    // so the parent row never disagrees with its last child. `status` above stays outcome-only because the
    // Sample Status filter and the KPI cards bucket on Confirmed/Rework/Dropped/In Progress.
    const latestStatus = latest.status || 'In Progress';
    // Sample-level Summary (Product View) and Dashboard KPIs use a separate "any round" cross-check
    // (Confirmed wins over Dropped wins over In Progress) - matches WebApp.gs getProductViewData()/getKpis().
    const productViewStatus = items.some((round) => round.outcome === 'Confirmed')
      ? 'Confirmed'
      : items.some((round) => round.outcome === 'Dropped') ? 'Dropped' : 'In Progress';
    return {
      baseId,
      latest,
      rounds: items,
      styleItem: latest.styleItem,
      location: latest.location,
      developer: latest.developer,
      designer: latest.hincDesigner,
      developmentDue: latest.developmentDue,
      dDay: latest.dDay,
      dDayDone: latest.dDayDone,
      roundCount: items.length,
      rework,
      status,
      latestStatus,
      productViewStatus,
    };
  }).sort((a, b) => String(a.baseId).localeCompare(String(b.baseId), undefined, { numeric: true }));
};

export const computeSampleStats = (groups, rounds) => {
  // 'On Hold' is an outcome in its own right, so it has to be excluded here too - otherwise the same
  // sample would be counted by both the In Progress card and the On Hold card.
  const inProgressBucket = (status) => !['Confirmed', 'Rework', 'Dropped', 'On Hold'].includes(status);
  return {
    totalSamples: groups.length,
    totalRounds: rounds.length,
    sampleInProgress: groups.filter((sample) => inProgressBucket(sample.status)).length,
    sampleConfirmed: groups.filter((sample) => sample.status === 'Confirmed').length,
    sampleRework: groups.filter((sample) => sample.status === 'Rework').length,
    sampleDropped: groups.filter((sample) => sample.status === 'Dropped').length,
    sampleOnHold: groups.filter((sample) => sample.status === 'On Hold').length,
    roundInProgress: rounds.filter((round) => !round.outcome).length,
    roundConfirmed: rounds.filter((round) => round.outcome === 'Confirmed').length,
    roundRework: rounds.filter((round) => round.outcome === 'Rework').length,
    roundDropped: rounds.filter((round) => round.outcome === 'Dropped').length,
    roundOnHold: rounds.filter((round) => round.outcome === 'On Hold').length,
    pendingOutcome: rounds.filter(isPendingOutcomeRound).length,
    waiting: rounds.filter(isRmWaitingRound).length,
    inProduction: rounds.filter(isInProductionRound).length,
    // The same three states counted by SAMPLE for the Sample / Base ID row: how many Base IDs
    // have at least one such round. The row above them counts samples, so these had to as well —
    // they were showing the round numbers, identical to the Rounds row (Max, 2026-08-13).
    samplePendingOutcome: groups.filter((sample) => sampleInWatch(sample, isPendingOutcomeRound)).length,
    sampleWaiting: groups.filter((sample) => sampleInWatch(sample, isRmWaitingRound)).length,
    sampleInProduction: groups.filter((sample) => sampleInWatch(sample, isInProductionRound)).length,
    avgRounds: avg(groups.map((sample) => sample.roundCount), 1),
    reworkRatio: pct(rounds.filter((round) => round.outcome === 'Rework').length, rounds.length),
    pastDue: groups.filter((sample) => Number.isFinite(sample.dDay) && sample.dDay > 0).length,
  };
};

export const findDuplicateRound = (form, rounds, excludeSampleId) => rounds.find((row) => (
  row.sampleId !== excludeSampleId
  && String(row.styleItem || '').trim().toLowerCase() === String(form.styleItem || '').trim().toLowerCase()
  && String(row.round || '').trim().toLowerCase() === String(form.round || '').trim().toLowerCase()
  && String(row.option || '').trim().toLowerCase() === String(form.option || '').trim().toLowerCase()
));

// The stage boxes are datalist combos: pick a suggestion or type anything (a status or a date).
// A datalist is resolved by id anywhere in the document, but the element has to actually be
// rendered — so every modal that contains stage boxes must render this once.
export function Kpi({ label, value, sub, tone = '', onClick, active = false }) {
  return (
    <button type="button" className={`mv-kpi ${tone} ${onClick ? 'clickable' : ''} ${active ? 'active' : ''}`} onClick={onClick || undefined}>
      <div className="kl">{label}</div>
      <div className="kv">{value}</div>
      {sub && <div className="ks">{sub}</div>}
    </button>
  );
}

export const MULTI_FILTER_NONE = '__NONE__';

// Compact multi-select filter (checkbox list) — mirrors the web app's multi-choice dropdowns.
/* One choice, plain <select> — the dropdown from Project Approval's Pending tab. It is used for the
   Sales OP TEAM picker only (Max, 2026-08-25): a project belongs to one team, so one choice is the
   honest control there, while every other filter stays multi-choice.
   It writes into the SAME array-shaped state the multi-select used ([] = all, ['X'] = that one), so
   the filtering, the KPI cards and the drill-through links downstream need no change at all. */
function SingleFilter({ allLabel, options, value, onChange }) {
  const selected = (Array.isArray(value) ? value : []).filter((v) => v !== MULTI_FILTER_NONE);
  return (
    <select
      className="pt-single"
      value={selected.length === 1 ? selected[0] : ''}
      onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
    >
      <option value="">{allLabel}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

/* `labelOf` renames what an option PRINTS, never what it filters by: the ticket status list
   holds the stored values, so a box ticked as "Open" still narrows the grid by `Pending`. */
export function MultiFilter({ allLabel, options, value, onChange, labelOf }) {
  const { t } = useTranslation();
  const show = (o) => (labelOf ? labelOf(o) : o);
  const ref = useRef(null);
  const selected = Array.isArray(value) ? value : [];
  const isNone = selected.includes(MULTI_FILTER_NONE);
  const isAll = !isNone && selected.length === 0;
  const selectedOptions = isNone ? [] : selected.filter((item) => item !== MULTI_FILTER_NONE);
  const summary = isNone
    ? t('stx.noneSelected')
    : !selectedOptions.length
    ? allLabel
    : selectedOptions.length <= 2 ? selectedOptions.map(show).join(', ') : `${selectedOptions.slice(0, 2).map(show).join(', ')} +${selectedOptions.length - 2}`;
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && ref.current.open && !ref.current.contains(e.target)) ref.current.removeAttribute('open'); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const toggleAll = () => {
    onChange(isAll ? [MULTI_FILTER_NONE] : []);
  };
  const toggle = (o) => {
    if (isAll) {
      onChange(options.filter((option) => option !== o));
      return;
    }
    const next = selectedOptions.includes(o) ? selectedOptions.filter((x) => x !== o) : [...selectedOptions, o];
    onChange(next.length === options.length ? [] : next);
  };
  return (
    <details className="pt-multi" ref={ref}>
      <summary title={summary}>{summary}</summary>
      <div className="pt-multi-panel" onClick={(e) => e.stopPropagation()}>
        <label className="pt-multi-row"><input type="checkbox" checked={isAll} onChange={toggleAll} /> {allLabel}</label>
        <div className="pt-multi-list">
          {options.map((o) => (
            <label className="pt-multi-row" key={o}><input type="checkbox" checked={isAll || selectedOptions.includes(o)} onChange={() => toggle(o)} /> {show(o)}</label>
          ))}
          {!options.length && <div className="pt-multi-empty">{t('stx.noOptions')}</div>}
        </div>
      </div>
    </details>
  );
}


function StageBadge({ value }) {
  const { t } = useTranslation();
  const late = stageIsOverdue(value);
  return (
    <span
      className={`pt-stage-readonly ${stageClass(value)}`}
      title={late ? t('stx.stageLateHint') : undefined}
    >
      {late && <span className="pt-stage-late" aria-hidden="true">!</span>}
      {valueLabel(fmt(value || 'Pending'))}
    </span>
  );
}


/* The Costing cell's source when a Collab Pricing quote is linked to the SKU (cpTrackerLink.js, Amy 30/10): the quote's
   number under the word, opening the quote. Like Proto's word comes from the sample, this one comes from the quote. */
function CostingQuoteLink({ quote }) {
  const { t } = useTranslation();
  return (
    <a className="pt-costing-quote" href={`/sales/pricing-calculator?open=${encodeURIComponent(quote.id)}`}
      title={t('stx.costingFromQuote', { id: quote.id })} onClick={(e) => e.stopPropagation()}>
      {quote.id}
    </a>
  );
}

/* ---- Target Launching: a MONTH, shown as mm/yyyy ------------------------------------
   Stored as 'YYYY-MM' (sorts as text, and it is what <input type="month"> speaks). The display is
   pinned to mm/yyyy rather than left to the browser, which renders month inputs differently per
   locale. */
// Jay, 2026-08-18: a project whose launch month is not decided yet is marked ASAP rather than left
// blank, so the approver can tell "no date yet" apart from "nobody filled this in".
export const TARGET_ASAP = 'ASAP';
export const monthToDisplay = (value) => {
  const v = String(value || '').trim();
  if (v.toUpperCase() === TARGET_ASAP) return TARGET_ASAP;
  const m = v.match(/^(\d{4})-(\d{2})$/);
  return m ? `${m[2]}/${m[1]}` : '';
};

// i18n keys, one per month, in calendar order — MonthField draws them through t().
const MONTH_LABELS = ['stx.monShort.m01', 'stx.monShort.m02', 'stx.monShort.m03', 'stx.monShort.m04',
  'stx.monShort.m05', 'stx.monShort.m06', 'stx.monShort.m07', 'stx.monShort.m08', 'stx.monShort.m09',
  'stx.monShort.m10', 'stx.monShort.m11', 'stx.monShort.m12'];
// The same for the column filter's Year > Month > Day tree, keyed by the two-digit month.
const MONTH_LONG_KEYS = {
  '01': 'stx.monLong.m01', '02': 'stx.monLong.m02', '03': 'stx.monLong.m03', '04': 'stx.monLong.m04',
  '05': 'stx.monLong.m05', '06': 'stx.monLong.m06', '07': 'stx.monLong.m07', '08': 'stx.monLong.m08',
  '09': 'stx.monLong.m09', '10': 'stx.monLong.m10', '11': 'stx.monLong.m11', '12': 'stx.monLong.m12',
};

export function MonthField({ value, onChange, placeholder = 'mm/yyyy', readOnly }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState(null);
  // The year shown in the panel — starts at the picked year, or this year for an empty field.
  const [year, setYear] = useState(() => Number(String(value || '').slice(0, 4)) || new Date().getFullYear());
  const wrapRef = useRef(null);

  // Esc closes the menu only — the form behind it stays open (see useEscapeClose).
  useEscapeClose(() => setOpen(false), open);

  useEffect(() => {
    if (open) setYear(Number(String(value || '').slice(0, 4)) || new Date().getFullYear());
  }, [open, value]);

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const below = window.innerHeight - r.bottom - 12;
      const dropUp = below < 250;
      setBox({
        left: Math.min(r.left, window.innerWidth - 250),
        ...(dropUp ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      });
    };
    place();
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  const isAsap = String(value || '').trim().toUpperCase() === TARGET_ASAP;
  const selectedYear = isAsap ? '' : String(value || '').slice(0, 4);
  const selectedMonth = isAsap ? '' : String(value || '').slice(5, 7);
  const pick = (idx) => {
    onChange(`${year}-${String(idx + 1).padStart(2, '0')}`);
    setOpen(false);
  };

  return (
    <div className="stage-field" ref={wrapRef}>
      <input
        value={monthToDisplay(value)}
        placeholder={placeholder}
        readOnly
        disabled={readOnly}
        onClick={() => { if (!readOnly) setOpen((v) => !v); }}
        style={readOnly ? undefined : { cursor: 'pointer' }}
      />
      {!readOnly && (
        <button type="button" className="stage-cal-btn" tabIndex={-1} onClick={() => setOpen((v) => !v)} aria-label={t('stx.pickMonth')}>
          <CalendarDays size={13} />
        </button>
      )}
      {open && box && (
        /* The month grid is drawn here rather than delegating to <input type="month">: that put a
           second, browser-drawn panel behind a click, and its year could not be changed in one step. */
        <div className="stage-pop month-pop" style={box}>
          <div className="month-year-bar">
            <button type="button" onClick={() => setYear((y) => y - 1)} aria-label={t('stx.prevYear')}>‹</button>
            <input
              type="number"
              className="month-year-input"
              value={year}
              onChange={(e) => setYear(Number(e.target.value) || year)}
              aria-label={t('stx.year')}
            />
            <button type="button" onClick={() => setYear((y) => y + 1)} aria-label={t('stx.nextYear')}>›</button>
          </div>
          <div className="month-grid">
            {MONTH_LABELS.map((label, idx) => {
              const on = selectedYear === String(year) && selectedMonth === String(idx + 1).padStart(2, '0');
              return (
                <button key={label} type="button" className={`month-cell ${on ? 'on' : ''}`} onClick={() => pick(idx)}>
                  {t(label)}
                </button>
              );
            })}
          </div>
          <div className="month-pop-foot">
            <button
              type="button"
              className={`month-asap ${isAsap ? 'on' : ''}`}
              onClick={() => { onChange(TARGET_ASAP); setOpen(false); }}
            >
              ASAP
            </button>
            <button type="button" className="stage-pop-clear month-pop-clear" onClick={() => { onChange(''); setOpen(false); }}>
              {t('st.rmClear')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* A stage field that opens a calendar on click (Jay item 4), while still accepting the workflow
   statuses the data already uses. "Pending" stays the empty state; clicking gives a date picker plus
   one-tap status buttons, so neither way of filling a stage is harder than the other. */
export function StageField({ value, onChange, className = '', placeholder, readOnly }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState(null);
  const wrapRef = useRef(null);
  const dateRef = useRef(null);

  // Esc closes the menu only — the form behind it stays open (see useEscapeClose).
  useEscapeClose(() => setOpen(false), open);

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const below = window.innerHeight - r.bottom - 12;
      const dropUp = below < 210;
      setBox({
        left: Math.min(r.left, window.innerWidth - 268),
        ...(dropUp ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      });
    };
    place();
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  const late = stageIsOverdue(value);
  return (
    <div className="stage-field" ref={wrapRef}>
      <input
        className={`${className} ${stageClass(value)} ${late ? 'is-late' : ''}`}
        value={value ?? ''}
        title={late ? t('stx.stageLateHint') : undefined}
        placeholder={placeholder || 'Pending'}
        disabled={readOnly}
        onFocus={() => { if (!readOnly) setOpen(true); }}
        onClick={() => { if (!readOnly) setOpen(true); }}
        onChange={(e) => onChange(e.target.value)}
      />
      {!readOnly && (
        <button type="button" className="stage-cal-btn" tabIndex={-1} onClick={() => setOpen((v) => !v)} aria-label={t('stx.pickDate')}>
          <CalendarDays size={13} />
        </button>
      )}
      {late && <span className="stage-late-flag" title={t('stx.stageLateHint')}>!</span>}
      {open && box && (
        <div className="stage-pop" style={box}>
          {late && <div className="stage-pop-late">{t('stx.stageLateHint')}</div>}
          <div className="stage-pop-list">
            {STAGE_STATUS_SUGGESTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className={`stage-pop-opt${value === option ? ' on' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onChange(option); setOpen(false); }}
              >
                {option}
              </button>
            ))}
          </div>
          <label className="stage-pop-date">
            <span>{t('stx.targetDate')}</span>
            <input
              ref={dateRef}
              type="date"
              value={stageDateToISO(value)}
              onChange={(e) => { onChange(stageDateFromISO(e.target.value)); setOpen(false); }}
            />
          </label>
          <button type="button" className="stage-pop-clear" onClick={() => { onChange('Pending'); setOpen(false); }}>
            {t('stx.clearToPending')}
          </button>
        </div>
      )}
    </div>
  );
}

export function SortTh({ label, hint, sortKey, sort, onSort, filterValues, filterSelected, filterOpen, filterAnchor, onOpenFilter, onCloseFilter, onApplyFilter, className = '', style }) {
  const { t } = useTranslation();
  const active = sort.key === sortKey;
  const filtered = !!(filterSelected && filterSelected.length);
  return (
    <th
      className={`sortable-th ${active ? 'active' : ''} ${active ? `dir-${sort.dir}` : ''} ${filtered ? 'filtered' : ''} ${className}`}
      style={style}
    >
      <span className="th-label" title={hint || undefined} onClick={() => onSort(sortKey)}>{label}</span>
      {onOpenFilter ? (
        <button
          type="button"
          className={`th-funnel ${filtered ? 'on' : ''}`}
          title={filtered ? t('st.thFiltered', { count: filterSelected.length }) : t('st.thSortFilter')}
          onClick={(event) => {
            event.stopPropagation();
            // The menu is fixed-positioned from this rect: the table sits in a horizontally scrolling
            // wrapper, and an absolutely-positioned menu would be clipped by it.
            const r = event.currentTarget.getBoundingClientRect();
            // Keep the panel inside the viewport: if there is not enough room below the button, open it
            // upwards. Without this the OK / Cancel row lands below the fold and cannot be clicked at all.
            const MIN_PANEL = 240;
            const below = window.innerHeight - r.bottom - 12;
            const above = r.top - 12;
            const openUp = below < MIN_PANEL && above > below;
            onOpenFilter(sortKey, {
              left: Math.round(Math.max(8, Math.min(r.left - 6, window.innerWidth - 250))),
              top: openUp ? undefined : Math.round(r.bottom + 4),
              bottom: openUp ? Math.round(window.innerHeight - r.top + 4) : undefined,
              space: Math.round(Math.max(openUp ? above : below, 180)),
            });
          }}
        >
          {/* Excel signals state by changing the GLYPH, not the colour: a funnel means the column is
              filtered, an arrow means it is sorted, a plain caret means neither. All of them stay white. */}
          {filtered && <Filter size={11} />}
          {active && (sort.dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
          {!filtered && !active && <ChevronDown size={12} />}
        </button>
      ) : <Filter size={11} className="sort-funnel" />}
      {filterOpen && filterAnchor && (
        <ColumnFilterMenu
          label={label}
          isDate={FILTER_DATE_KEYS.has(sortKey)}
          values={filterValues || []}
          selected={filterSelected}
          sortDir={active ? sort.dir : ''}
          anchor={filterAnchor}
          onSort={(dir) => onSort(sortKey, dir)}
          onApply={(next, all) => onApplyFilter(sortKey, next, all)}
          onClose={onCloseFilter}
        />
      )}
    </th>
  );
}

/**
 * The Excel column menu: sort, a search box, and a checkbox list of the values actually present.
 *
 * Two things it does not do the naive way:
 *  - it is positioned FIXED from the button's own rect, because the table lives inside a horizontally
 *    scrolling wrapper and an absolutely-positioned menu gets clipped by it
 *  - date columns render a Year > Month > Day tree rather than one row per date; the selection is still a
 *    flat list of the real values, so nothing downstream needs to know about the grouping
 */
export function ColumnFilterMenu({ label, isDate, values, selected, sortDir, anchor, onSort, onApply, onClose }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState(() => new Set(selected && selected.length ? selected : values));
  const [collapsed, setCollapsed] = useState({});
  const term = search.trim().toLowerCase();
  const shown = term ? values.filter((v) => String(v).toLowerCase().includes(term)) : values;
  const allShownTicked = shown.length > 0 && shown.every((v) => draft.has(v));

  const setMany = (list, on) => setDraft((prev) => {
    const next = new Set(prev);
    list.forEach((v) => { if (on) next.add(v); else next.delete(v); });
    return next;
  });
  const toggle = (value) => setMany([value], !draft.has(value));
  const toggleAllShown = () => setMany(shown, !allShownTicked);

  // Checkbox that also renders the half-ticked state for a partially selected Year or Month.
  const TriCheck = ({ list }) => {
    const on = list.filter((v) => draft.has(v)).length;
    return (
      <input
        type="checkbox"
        checked={on === list.length && list.length > 0}
        ref={(el) => { if (el) el.indeterminate = on > 0 && on < list.length; }}
        onChange={() => setMany(list, on !== list.length)}
      />
    );
  };

  const { tree, loose } = isDate ? buildDateTree(shown) : { tree: [], loose: shown };

  return createPortal((
    <div
      className="th-menu"
      style={{ top: anchor.top, bottom: anchor.bottom, left: anchor.left }}
      onClick={(event) => event.stopPropagation()}
    >
      <button type="button" className={`th-menu-sort ${sortDir === 'asc' ? 'on' : ''}`} onClick={() => onSort('asc')}>
        <ArrowUpAZ size={13} /> {isDate ? t('st.thSortOldNew') : t('st.thSortAZ')}
      </button>
      <button type="button" className={`th-menu-sort ${sortDir === 'desc' ? 'on' : ''}`} onClick={() => onSort('desc')}>
        <ArrowDownZA size={13} /> {isDate ? t('st.thSortNewOld') : t('st.thSortZA')}
      </button>
      <div className="th-menu-sep" />
      <input
        className="th-menu-search"
        autoFocus
        placeholder={t('st.thSearch')}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <label className="th-menu-opt th-menu-all">
        <input type="checkbox" checked={allShownTicked} onChange={toggleAllShown} />
        <span>{t('st.thSelectAll')}</span>
      </label>
      <div className="th-menu-list" style={{ maxHeight: Math.max(90, Math.min(anchor.space - 150, 300)) }}>
        {isDate && tree.map((yearNode) => {
          const yearValues = yearNode.months.flatMap((m) => m.days.map((d) => d.value));
          const yearOpen = !collapsed[yearNode.year];
          return (
            <div key={yearNode.year}>
              <div className="th-menu-opt th-menu-node">
                <button
                  type="button"
                  className="th-menu-twist"
                  onClick={() => setCollapsed((prev) => ({ ...prev, [yearNode.year]: yearOpen }))}
                >
                  {yearOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </button>
                <TriCheck list={yearValues} />
                <span>{yearNode.year}</span>
              </div>
              {yearOpen && yearNode.months.map((monthNode) => {
                const mKey = `${yearNode.year}-${monthNode.month}`;
                const monthValues = monthNode.days.map((d) => d.value);
                const monthOpen = !collapsed[mKey];
                return (
                  <div key={mKey}>
                    <div className="th-menu-opt th-menu-node lvl2">
                      <button
                        type="button"
                        className="th-menu-twist"
                        onClick={() => setCollapsed((prev) => ({ ...prev, [mKey]: monthOpen }))}
                      >
                        {monthOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </button>
                      <TriCheck list={monthValues} />
                      <span>{MONTH_LONG_KEYS[monthNode.month] ? t(MONTH_LONG_KEYS[monthNode.month]) : monthNode.label}</span>
                    </div>
                    {monthOpen && monthNode.days.map((day) => (
                      <label className="th-menu-opt lvl3" key={day.value}>
                        <input type="checkbox" checked={draft.has(day.value)} onChange={() => toggle(day.value)} />
                        <span>{day.label}</span>
                      </label>
                    ))}
                  </div>
                );
              })}
            </div>
          );
        })}
        {loose.map((value) => (
          <label className="th-menu-opt" key={value}>
            <input type="checkbox" checked={draft.has(value)} onChange={() => toggle(value)} />
            <span>{value === FILTER_BLANK ? t('st.thBlanks') : value}</span>
          </label>
        ))}
        {!shown.length && <div className="th-menu-empty">{t('st.thNoMatches')}</div>}
      </div>
      <div className="th-menu-foot">
        <button type="button" className="th-menu-btn primary" onClick={() => onApply([...draft], values)}>{t('st.thOk')}</button>
        <button type="button" className="th-menu-btn" onClick={onClose}>{t('st.thCancel')}</button>
      </div>
    </div>
  ), document.body);
}

export function IconButton({ variant, title, onClick, children, disabled = false }) {
  return (
    <button type="button" className={`icon-btn icon-${variant}`} title={title} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

export function Field({ label, required, children, hint }) {
  return (
    <label className="ff">
      <span>{label}{required && <span className="req">*</span>}</span>
      {children}
      {hint && <span className="hint">{hint}</span>}
    </label>
  );
}

function RoundModal({ mode, advancingFrom, form, lists, rounds, projects = [], saving, errors, onChange, onClose, onSave,
  photos = [], onChangePhotos, onHistory }) {
  // The three lists behind this form come from the database (Master data screen).
  const roundOptions = usePortalList('rounds', ROUND_OPTIONS);
  const outcomeOptions = usePortalList('outcomes', OUTCOME_OPTIONS);
  const reworkReasons = usePortalList('reworkReasons', REWORK_REASON_OPTIONS.map((name) => ({ name, owner: REASON_OWNER_MAP[name] || '' })));
  const { t } = useTranslation();
  /* VIEW — Jay, 2026-10-16: "missing 'VIEW' button … Same for all similar function". The same form
     with every field locked and one Close, as the Project Tracker's View has been since 2026-10-15.
     One disabled fieldset rather than a readOnly prop per field: most of these never honoured one. */
  const viewing = mode === 'view';
  // Every way out — Esc, ×, Cancel — goes through the guard, which asks only if the form was touched.
  // Photos live outside `form`, so they are declared to the guard separately.
  const { requestClose, prompt: discardPrompt } = useDiscardGuard(
    { form, photoCount: photos.length }, onClose, { active: !saving },
  );
  /* Is this save about to bring a Base ID into existence? Base IDs are assigned from the style name,
     so the answer is simply "no round has this style yet" — the same rule the server applies. When it
     is a new Base ID, Young's photo requirement applies (via Jay, 2026-08-19); adding a round to a
     style that already exists never asks, because that Base ID already has its photo. */
  const normStyle = (v) => String(v || '').trim().toLowerCase();
  const existingBase = mode === 'new'
    ? (rounds.find((r) => normStyle(r.styleItem) === normStyle(form.styleItem)) || null)
    : null;
  const creatingBase = mode === 'new' && !!normStyle(form.styleItem) && !existingBase;
  const preview = calcPreview(form);
  const duplicate = findDuplicateRound(form, rounds, form.sampleId);
  const set = (field) => (event) => onChange({ [field]: event.target.value });
  const setChecked = (field) => (event) => onChange({ [field]: event.target.checked });
  const internal = isInternalTrial(form);
  // HEVN internal review only: no designer/HINC review, no outcome. Request Date is kept — it doubles as
  // the internal Review Date (matches the web app's buildPayload, which blanks everything except it).
  const setTrialMode = (nextInternal) => {
    if (nextInternal) {
      onChange({ trialReviewMode: 'internal', hincDesigner: '', roundDue: '', receivedDate: '', commentDate: '', outcome: '', reworkReason: '' });
    } else {
      onChange({ trialReviewMode: 'designer' });
    }
  };
  /* ONLY ITEMS A ROUND MAY ACTUALLY BE RAISED ON — 2026-09-21. The server refuses a round on a SKU
     that is not approved (409 since 2026-09-19), so offering one here was inviting an action that
     answers with a refusal. `linkedSku` below still looks in the FULL list: a round already linked
     to a SKU that was refused afterwards must still say which SKU it is on. */
  const projectOptions = projects.filter((p2) => (p2.approvalStatus || 'Approved') === 'Approved');
  // The SKU this round is linked to, for the Sales team / Project Owner line under the picker.
  const linkedSku = projects.find((x) => x.id === String(form.projectId || '').trim()) || null;

  const developerOptions = lists.developers?.length ? lists.developers : [];
  const designerOptions = lists.designers?.length ? lists.designers : [];
  const categoryOptions = lists.categories?.length ? lists.categories : CATEGORY_FALLBACK;
  const seasonOptions = lists.seasons?.length ? lists.seasons : SEASON_FALLBACK;
  const locationOptions = lists.locations?.length ? lists.locations : LOCATION_FALLBACK;

  const [nrSearch, setNrSearch] = useState('');
  const [nrLocked, setNrLocked] = useState(mode === 'new' && !!form.baseId);
  /* Whether the one required first step has been taken. Edit and Advance already know their sample;
     so does the + Sample button on a project SKU, which opens this with the style already filled. */
  /* A trial sample has no existing sample to point at — that is the whole reason for the button — so
     the gate is answered by the mode itself and the style name is typed instead of inherited. */
  const isTrial = !!form.trial;
  /* OPENED ON A PROJECT SKU THAT HAS NO SAMPLE YET — the Sample Tracker's picker, or the Project
     Tracker's "+ Sample". There is no existing sample to point at: that is the entire reason for
     being here, and the SKU is already named in the Linked Project SKU block above. Max, on a
     screenshot of it, 2026-09-24. Three things read wrongly in that state and all three key off
     this one flag rather than each working it out again. */
  const firstSampleForSku = mode === 'new' && !isTrial && !!form.projectId && !form.baseId;
  const sampleChosen = mode !== 'new' || isTrial || firstSampleForSku || nrLocked
    || !!String(form.styleItem || '').trim();

  /* One entry per Base ID, carrying every Sample ID that base has produced — Max, 2026-09-07:
     "bắt buộc User phải điền sample ID vào và tạo round mới cho sample đó". A Sample ID is what
     people actually have in front of them (it is on the sample), so it has to be searchable; the
     style name and the Base ID stay searchable because someone with only those should still find
     the row rather than being told to go and look one up. */
  const baseSamples = useMemo(() => {
    const map = new Map();
    rounds.forEach((round) => {
      if (!round.baseId || !round.styleItem) return;
      const entry = map.get(round.baseId) || { baseId: round.baseId, styleItem: round.styleItem, sampleIds: [] };
      entry.styleItem = round.styleItem;
      if (round.sampleId && !entry.sampleIds.includes(round.sampleId)) entry.sampleIds.push(round.sampleId);
      map.set(round.baseId, entry);
    });
    return [...map.values()];
  }, [rounds]);

  const nrSearchTrim = nrSearch.trim().toLowerCase();
  const nrResults = mode === 'new' && !nrLocked && nrSearchTrim
    ? baseSamples.filter((sample) => sample.styleItem.toLowerCase().includes(nrSearchTrim)
        || sample.baseId.toLowerCase().includes(nrSearchTrim)
        || sample.sampleIds.some((id) => String(id).toLowerCase().includes(nrSearchTrim))).slice(0, 8)
    : [];

  // Adding a round to an existing sample carries the sample-level fields over from its latest
  // round and clears only the per-round ones - ports Portal.html selectNrProduct(). Without this
  // the user had to retype Developer / Location / Development Due etc., and a forgotten Developer
  // silently drops the round out of the dashboard and report breakdowns.
  const selectExistingSample = (sample) => {
    const sampleRounds = rounds.filter((round) => round.baseId === sample.baseId);
    // Take the most recent NON-EMPTY value per field, so one round saved without a Developer
    // doesn't wipe the inheritance for every round after it.
    const latest = (key) => {
      for (let i = sampleRounds.length - 1; i >= 0; i -= 1) {
        const value = sampleRounds[i][key];
        if (value !== null && value !== undefined && String(value).trim() !== '') return value;
      }
      return '';
    };
    onChange({
      baseId: sample.baseId,
      styleItem: sample.styleItem,
      // inherited from the sample
      category: latest('category'),
      season: latest('season'),
      developer: latest('developer'),
      location: latest('location'),
      hincDesigner: latest('hincDesigner'),
      developmentDue: latest('developmentDue'),
      targetPriceUsd: latest('targetPriceUsd'),
      option: latest('option'),
      projectId: latest('projectId'),
      // reset for the new round
      round: '',
      requestDate: '',
      roundDue: '',
      rmReadyDate: '',
      materialReadyExpected: true,
      sampleFinishDate: '',
      receivedDate: '',
      commentDate: '',
      qty: '',
      actualCostUsd: '',
      outcome: '',
      reworkReason: '',
      remarks: '',
      trialReviewMode: 'designer',
    });
    setNrLocked(true);
    setNrSearch('');
  };

  const clearNrSelected = () => {
    setNrLocked(false);
    onChange({ baseId: '', styleItem: '' });
  };

  const selectedReasons = form.reworkReason ? form.reworkReason.split('; ').filter(Boolean) : [];
  const toggleReworkReason = (reason) => {
    const next = selectedReasons.includes(reason)
      ? selectedReasons.filter((item) => item !== reason)
      : [...selectedReasons, reason];
    onChange({ reworkReason: next.join('; ') });
  };

  return (
    <div className="st-modal-overlay" role="dialog" aria-modal="true">
      {/* Portals to <body>; only present once the form is dirty and the user tries to leave. */}
      {discardPrompt}
      <div className="st-modal">
        <div className="st-modal-head">
          {/* A trial sample is not "New Round" — it creates the sample as well as its first round, and
              a title that says otherwise is the first thing a person reads.
              NEITHER IS THE FIRST SAMPLE ON A SKU. Opened from a project SKU that has none yet —
              the Project Tracker's "+ Sample", or the Sample Tracker's picker — this form creates
              the SAMPLE, and a dialog headed "New Round" after somebody pressed "Create Sample" is
              exactly the kind of seam Jay asked us to close. */}
          <h3>{viewing ? <>{t('st.rmTitleView')} — {form.sampleId} <span className="pt-view-tag">{t('st.ptViewTag')}</span></>
            : mode === 'edit' ? t('st.rmTitleEdit') : mode === 'advance' ? t('st.rmTitleAdvance')
            : isTrial ? t('st.trTitleNew')
              : (form.projectId && !form.baseId) ? t('st.rmTitleNewSample')
                : t('st.rmTitleNew')}</h3>
          {/* who changed this round, when, and through what — screen or Catchy (Jay 29/10 #5) */}
          {onHistory && form.sampleId && (mode === 'edit' || viewing) && (
            <button type="button" className="btn btn-ghost btn-sm st-hist-btn" onClick={() => onHistory(form.sampleId)} data-st-history="round"><HistoryIcon size={14} /> {t('xCatchy.history')}</button>
          )}
          <button type="button" className="st-modal-close" onClick={requestClose} aria-label={t('st.viewClose')}>&times;</button>
        </div>
        <div className="st-modal-body">
          <fieldset className="pt-ro-fieldset" disabled={viewing}>

          {mode === 'new' && isTrial && (
            <div className="nr-bar nr-bar-trial">
              <div className="nr-bar-title">
                {t('st.trTitle')}
                <span className="nr-bar-sub">{t('st.trSub')}</span>
              </div>
            </div>
          )}

          {/* Not drawn for a first sample: "Select an existing sample to add a round to" asks for
              the one thing that cannot exist here. */}
          {mode === 'new' && !isTrial && !firstSampleForSku && (
            <div className={`nr-bar${sampleChosen ? '' : ' nr-bar-waiting'}`}>
              <div className="nr-bar-title">
                {t('st.rmPickSample')}<span className="req">*</span>
                <span className="nr-bar-sub">{t('st.rmPickSampleSub')}</span>
              </div>
              {/* Max, 2026-09-07: "user vừa vào là biết phải nhập sample ID vào". A hint under a box
                  is easy to walk past, so the box takes the caret on open and everything below stays
                  greyed and inert until it is answered — there is exactly one thing to do first. */}
              {!sampleChosen && <div className="nr-bar-first">{t('st.rmPickFirst')}</div>}
              {!nrLocked && (
                <>
                  <div className="nr-search-row">
                    <input autoFocus value={nrSearch} onChange={(event) => setNrSearch(event.target.value)} placeholder={t('st.rmPickPh')} />
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setNrSearch('')}>{t('st.rmClear')}</button>
                  </div>
                  {!!nrResults.length && (
                    <div className="nr-results">
                      {nrResults.map((sample) => (
                        <button type="button" key={sample.baseId} className="nr-result-row" onClick={() => selectExistingSample(sample)}>
                          <strong>{sample.baseId}</strong> — {sample.styleItem}
                          {!!sample.sampleIds.length && (
                            <span className="nr-result-ids">{sample.sampleIds.slice(-4).join(' · ')}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
              {nrLocked && (
                <div className="nr-selected">
                  <div>
                    <span className="nr-selected-label">{t('st.rmAddingTo')}</span>
                    <div className="nr-selected-value">{form.baseId} — {form.styleItem}</div>
                  </div>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={clearNrSelected}>{t('st.rmChange')}</button>
                </div>
              )}
            </div>
          )}

          {mode === 'advance' && (
            <div className="st-advance-note">
              <strong>{advancingFrom?.sampleId || advancingFrom?.baseId}</strong> {t('st.rmAdvanceNote')}
              <div className="st-advance-note-sub">{t('st.rmAdvanceNoteSub')}</div>
            </div>
          )}

          {duplicate && (
            <div className="dup-warn">
              {t('st.rmDupWarn', {
                sampleId: duplicate.sampleId,
                round: `${duplicate.round}${duplicate.option ? `-${duplicate.option}` : ''}`,
                outcome: duplicate.outcome || t('st.rmDupOpen'),
              })}
            </div>
          )}

          <div className="legend-bar">
            <span className="lbl">{t('st.rmOwnership')}</span>
            <span className="chip chip-hevn"><span className="dot" />{t('st.rmHevnFills')}</span>
            <span className="chip chip-hinc"><span className="dot" />{t('st.rmHincFills')}</span>
            <span className="chip chip-both"><span className="dot" />{t('st.rmBothTeams')}</span>
            <span className="chip chip-auto"><span className="dot" />{t('st.rmAutoGen')}</span>
            <span className="legend-req"><span className="req">*</span> {t('st.rmMandatory')}</span>
          </div>

          <div className={`form-layout${sampleChosen ? '' : ' nr-gated'}`}>
            <div>
              {/* First, not buried below the photos — Max, 2026-09-07: "Phần Linked Project SKU sẽ
                  chuyển lên trên và cho hiện thêm thông tin Sales team và Project owner". Which SKU a
                  round belongs to decides who owns it, so it belongs at the top where it is read
                  before the form is filled rather than after. */}
              {/* Hidden for a trial sample (Max, 2026-09-08): there is no SKU, no project, no sales
                  team and no owner to put in it, so four permanently empty boxes were only saying
                  "not applicable" in the most expensive way available. */}
              {!isTrial && (
              <div className="own-section own-project">
                {/* No "Optional" badge: nothing in here is filled by hand any more, so there is
                    nothing to decline. It reads as a fact about the sample. */}
                <div className="own-hd"><span>{t('st.rmLinkedSku')}</span></div>
                {/* Not typed here any more — Max, 2026-09-07: "Phần Linked Project SKU ko chỗ chỉnh
                    sửa và thể hiện team project name". The link belongs to the SAMPLE, so a round
                    inherits it; setting it on the round was a second place to say the same thing.

                    Four read-only fields rather than a line of loose text underneath ("Các cột cần
                    hiển thị như một trường... nhìn bị lạc quẻ"), so this section reads like every
                    other section of the form. All four are read off the project each time and never
                    copied onto the round: a project changes hands, and a copy taken today would go
                    on naming last month's owner. */}
                <div className="own-body">
                  <div className="fg-sku">
                    <Field label={t('st.rmLinkedSku')}>
                      <input value={form.projectId || ''} disabled className="auto-field"
                        placeholder={t('st.rmNoLinkedSku')} />
                    </Field>
                    <Field label={t('st.rmProjectName')}>
                      <input value={linkedSku?.groupName || ''} disabled className="auto-field" />
                    </Field>
                    <Field label={t('st.rmSalesTeam')}>
                      <input value={linkedSku?.teamName || ''} disabled className="auto-field" />
                    </Field>
                    <Field label={t('st.rmProjectOwner')}>
                      <input value={linkedSku?.ownerName || ''} disabled className="auto-field" />
                    </Field>
                  </div>
                </div>
              </div>
              )}

              <div className="own-section own-hevn">
                <div className="own-hd"><span>{t('st.rmSampleIdent')}</span><span className="owner-tag">{t('st.rmHevnFills')}</span></div>
                <div className="own-body">
                  <div className="fg3">
                    {/* Not typed any more — Max, 2026-09-07: "ko chọn điền style name". A round
                        belongs to a sample, and the style is the sample's, so it arrives either from
                        the sample picked above or from the project SKU that opened this form. Typing
                        it was how a second Base ID for an existing style got created by a spelling. */}
                    {/* Everywhere else this is inherited from the sample or the project SKU and so is
                        read-only. A trial sample has neither, so here it is the field being filled —
                        typed in place rather than in a second box above it (Max, 2026-09-08). */}
                    <Field label={t('st.rmStyleItem')} required
                           hint={isTrial ? t('st.trStyleHint')
                             : firstSampleForSku ? t('st.rmStyleFromSku')
                               : t('st.rmStyleFromSample')}>
                      {isTrial
                        ? <input id="tr-style" autoFocus value={form.styleItem} onChange={set('styleItem')}
                                 placeholder={t('st.trStylePh')} />
                        /* The round's own label — the sample's name, plus "_<Option>" once an
                           option is typed below — so the field reads as the Sample ID beside it does. */
                        : <input value={roundStyleLabel(form)} disabled className="auto-field"
                                 placeholder={t('st.rmStylePlaceholder')} />}
                    </Field>
                    <Field label={t('st.rmBaseId')} hint={t('st.rmAutoAssigned')}>
                      <input value={form.baseId || t('stx.auto')} disabled className="auto-field" />
                    </Field>
                    <Field label={t('st.rmSampleId')} hint={t('st.rmSampleIdHint')}>
                      <input value={form.sampleId || t('stx.auto')} disabled className="auto-field" />
                    </Field>
                    <Field label={t('st.rmRound')} required>
                      <select value={form.round} onChange={set('round')}>
                        {(roundOptions.includes(form.round) || !form.round
                          ? roundOptions
                          : [form.round, ...roundOptions]
                        ).map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </Field>
                    <Field label={t('st.rmOption')} hint={t('st.rmOptionHint')}>
                      <input value={form.option} onChange={set('option')} placeholder={t('st.rmOptionPh')} />
                    </Field>
                    <Field label={t('st.rmCategory')}>
                      <select value={form.category} onChange={set('category')}>
                        <option value="">—</option>
                        {categoryOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </Field>
                    <Field label={t('st.rmSeason')}>
                      <select value={form.season} onChange={set('season')}>
                        <option value="">—</option>
                        {seasonOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </Field>
                    <Field label={t('st.rmDevelopmentDue')} required hint={t('st.rmDevDueHint')}>
                      <input type="date" className="yellow-field" value={form.developmentDue} onChange={set('developmentDue')} />
                    </Field>
                    <Field label={t('st.rmTargetPrice')}>
                      <input type="number" min="0" step="0.01" value={form.targetPriceUsd} onChange={set('targetPriceUsd')} />
                    </Field>
                  </div>
                </div>
              </div>

              {/* Photos belong to the Base ID, not to the round — which is what makes "required on the
                  first round only" stop being a special case. Shown while a Base ID is being created,
                  and read-only afterwards from the Base ID's own row in All Samples. */}
              {mode === 'new' && (
                <div className={`own-section ${creatingBase ? 'own-hevn' : 'own-project'}`}>
                  <div className="own-hd">
                    <span>{t('st.rmBasePhotos')}</span>
                    <span className={`owner-tag ${creatingBase ? '' : 'owner-tag-dark'}`}>
                      {creatingBase ? t('st.rmPhotoRequired') : t('st.rmOptional')}
                    </span>
                  </div>
                  <div className="own-body">
                    <div className="pt-help" style={{ marginBottom: 8 }}>
                      {creatingBase
                        ? t('st.rmPhotoNewBase')
                        : existingBase
                          ? t('st.rmPhotoExistingBase', { baseId: existingBase.baseId })
                          : t('st.rmPhotoNoStyle')}
                    </div>
                    {creatingBase && (
                      <ProjectPhotos scope="sample" pending pendingPhotos={photos} onPendingChange={onChangePhotos} />
                    )}
                    {!creatingBase && existingBase && (
                      <ProjectPhotos scope="sample" ownerKey={existingBase.baseId} readOnly />
                    )}
                  </div>
                </div>
              )}

              <div className="own-section own-review">
                <div className="own-hd"><span>{t('st.rmReviewType')}</span><span className="owner-tag">{t('st.rmHevn')}</span></div>
                <div className="own-body">
                  <label className="rt-row">
                    <input type="radio" name="reviewType" checked={!internal} onChange={() => setTrialMode(false)} />
                    <span>{t('st.rmRequiresDesigner')}</span>
                  </label>
                  <label className="rt-row">
                    <input type="radio" name="reviewType" checked={internal} onChange={() => setTrialMode(true)} />
                    <span>{t('st.rmInternalOnly')}</span>
                  </label>
                  <div className="rt-note">
                    {internal
                      ? t('st.rmInternalNote')
                      : t('st.rmDesignerNote')}
                  </div>
                </div>
              </div>

              {internal && (
                <div className="own-section own-review">
                  <div className="own-hd"><span>{t('st.rmInternalReview')}</span><span className="owner-tag">{t('st.rmHevn')}</span></div>
                  <div className="own-body">
                    <Field label={t('st.rmRequestDate')} required hint={t('st.rmInternalReqHint')}>
                      <input type="date" value={form.requestDate} onChange={set('requestDate')} />
                    </Field>
                    <Field label={t('st.rmRemarks')}>
                      <input value={form.remarks} onChange={set('remarks')} placeholder={t('st.rmInternalRemarksPh')} />
                    </Field>
                  </div>
                </div>
              )}

              <div className="own-section own-hevn">
                <div className="own-hd"><span>{t('st.rmSamplePlan')}</span><span className="owner-tag">{t('st.rmHevnFills')}</span></div>
                <div className="own-body">
                  <div className="fg3">
                    {/* Required only while the Designer box is empty, and the other way round: a
                        round needs one of the two names, not this one specifically. A red asterisk on
                        a field a HINC DT designer has nobody to fill is what stopped them creating
                        the sample at all (Max, 2026-09-10). */}
                    <Field label={t('st.rmDeveloper')} required={!String(form.hincDesigner || '').trim()}
                      hint={String(form.hincDesigner || '').trim() ? '' : t('st.rmEitherName')}>
                      <input list="st-developer-options" value={form.developer} onChange={set('developer')} placeholder={t('st.rmDeveloperPh')} />
                    </Field>
                    <Field label={t('st.rmLocation')}>
                      <select value={form.location} onChange={set('location')}>
                        <option value="">—</option>
                        {(locationOptions.includes(form.location) || !form.location
                          ? locationOptions
                          : [form.location, ...locationOptions]
                        ).map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </Field>
                    <Field label={t('st.rmRmReady')} required hint={t('st.rmRmReadyHint')}>
                      <input type="date" value={form.rmReadyDate} onChange={set('rmReadyDate')} />
                      <label className="expected-check">
                        <input type="checkbox" checked={!!form.materialReadyExpected} onChange={setChecked('materialReadyExpected')} />
                        {t('st.rmExpectedDate')}
                      </label>
                    </Field>
                    <Field label={t('st.rmSampleFinish')}>
                      <input type="date" value={form.sampleFinishDate} onChange={set('sampleFinishDate')} />
                    </Field>
                    <Field label={t('st.rmQty')}>
                      <input type="number" min="0" value={form.qty} onChange={set('qty')} />
                    </Field>
                    <Field label={t('st.rmActualCost')}>
                      <input type="number" min="0" step="0.01" value={form.actualCostUsd} onChange={set('actualCostUsd')} />
                    </Field>
                  </div>
                  {/* The SKU's material list, next to RM Ready because that is the date it explains:
                      "khi tạo sample tracker từ Project tracker thì tôi cần liên kết thêm phần
                      material list nữa" (Max, 2026-09-05).

                      READ THROUGH, NOT COPIED. Max asked for the two places to stay in step — "nếu
                      có thêm mới bên project SKU thì bên phiếu sample cũng sẽ tự động update theo" —
                      and the way to guarantee that is to have nothing to synchronise: this reads
                      00_PORTAL_sku_material for the SKU the round is on, the same rows the SKU form
                      writes. A snapshot taken at round creation would have been a second copy going
                      stale from the first save onwards. It is read-only here for the same reason the
                      list has one owner: the SKU. */}
                  <div className="rm-materials">
                    {form.projectId ? (
                      <>
                        <div className="rt-note">{t('st.rmMaterialsHint')}</div>
                        {/* Every column but one is the SKU's and read-only. RM ready date belongs to
                            THIS round — Max chose per-round over one shared date, because a SKU runs
                            P1 then P2 and a shared column would let P2 overwrite what P1 recorded. */}
                        <SkuMaterialList
                          skuId={form.projectId}
                          canEdit={false}
                          readyDates={form.materialReady || {}}
                          onReadyDate={(id, value) => onChange({
                            materialReady: { ...(form.materialReady || {}), [id]: value },
                          })}
                        />
                      </>
                    ) : (
                      /* A round opened from Sample Tracker starts with no SKU linked, so this is
                         what it shows first (Max, 2026-09-05: "modal này chưa có phần bảng material
                         á" — it was there, saying only that there was nothing). It names the field
                         to fill in, which sits above this one, rather than only stating the absence.
                         8 of 328 existing rounds carry no project SKU at all. */
                      <div className="rt-note rm-nosku">{t('st.rmMaterialsNoSku')}</div>
                    )}
                  </div>
                  <div className="calc-summary">
                    <span className="calc-label">{t('st.rmAutoCalc')}</span>
                    &nbsp; {t('st.rmMakeDays')}: <b>{fmtDays(preview.makeDays)}</b>
                    &nbsp;&nbsp; {t('st.rmShipDays')}: <b>{fmtDays(preview.shipDays)}</b>
                    &nbsp;&nbsp; {t('st.rmRoundCycle')}: <b>{fmtDays(preview.roundCycleDays)}</b>
                    &nbsp;&nbsp; {t('st.rmDday')}: <b>{preview.dDay === '' ? '—' : dDayLabel(num(preview.dDay))}</b>
                  </div>
                </div>
              </div>

              {!internal && (
              <div className="own-section own-hinc">
                <div className="own-hd"><span>{t('st.rmHincReview')}</span><span className="owner-tag">{t('st.rmHincFills')}</span></div>
                <div className="own-body">
                  <div className="fg3">
                    <Field label={t('st.rmHincDesigner')}>
                      <input list="st-designer-options" value={form.hincDesigner} onChange={set('hincDesigner')} />
                    </Field>
                    <Field label={t('st.rmRequestDate')} hint={t('st.rmHincReqHint')}>
                      <input type="date" value={form.requestDate} onChange={set('requestDate')} />
                    </Field>
                    <Field label={t('st.rmRoundDue')} hint={t('st.rmRoundDueHint')}>
                      <input type="date" value={form.roundDue} onChange={set('roundDue')} />
                    </Field>
                    <Field label={t('st.rmReceivedDate')} hint={t('st.rmReceivedHint')}>
                      <input type="date" value={form.receivedDate} onChange={set('receivedDate')} />
                    </Field>
                    <Field label={t('st.rmReviewDate')} hint={t('st.rmReviewDateHint')}>
                      <input type="date" value={form.commentDate} onChange={set('commentDate')} />
                    </Field>
                  </div>
                </div>
              </div>
              )}

              {!internal && (
              <div className="own-section own-both">
                <div className="own-hd"><span>{t('st.rmRoundOutcome')}</span><span className="owner-tag">{t('st.rmBothTeams')}</span></div>
                <div className="own-body">
                  <div className="fg3">
                    <Field label={t('st.rmRoundOutcome')} hint={t('st.rmOutcomeHint')}>
                      <select value={form.outcome} onChange={set('outcome')}>
                        <option value="">{t('st.rmNotSetYet')}</option>
                        {outcomeOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </Field>
                  </div>
                  <div className="outcome-desc" style={OUTCOME_DESC_STYLE[form.outcome] || OUTCOME_DESC_STYLE['']}>
                    {OUTCOME_DESC_KEYS[form.outcome] ? t(OUTCOME_DESC_KEYS[form.outcome])
                      : (OUTCOME_DESCRIPTIONS[form.outcome] || t('st.rmOutcomePick'))}
                  </div>
                  {form.outcome === 'Rework' && (
                    <div className="rework-box">
                      <div className="rework-label">{t('st.rmReworkTitle')}</div>
                      <div className="ff" style={{ marginBottom: 10 }}>
                        <span>{t('st.rmReworkReason')}<span className="req">*</span> <span className="hint" style={{ fontWeight: 400 }}>{t('st.rmSelectAll')}</span></span>
                        <div className="reason-cbs">
                          {reworkReasons.map(({ name, owner }) => (
                            <label className="cb-row" key={name}>
                              <span className="cb-row-left">
                                <input type="checkbox" checked={selectedReasons.includes(name)} onChange={() => toggleReworkReason(name)} />
                                <span>{name}</span>
                              </span>
                              <span className={`owner-tag ${reasonOwnerClass(owner)}`}>{owner}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  <Field label={t('st.rmRemarks')} required={form.outcome === 'Dropped'}>
                    <textarea rows={2} value={form.remarks} onChange={set('remarks')} placeholder={t('st.rmRoundRemarksPh')} />
                  </Field>
                </div>
              </div>
              )}
            </div>

          </div>
          </fieldset>
        </div>
        {!!errors.length && (
          <div className="st-alert error st-alert-foot">{errors.map((message) => <div key={message}>{errorText(message)}</div>)}</div>
        )}
        {viewing ? (
          <div className="st-modal-foot st-form-actions">
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{t('st.viewClose')}</button>
          </div>
        ) : (
        <div className="st-modal-foot st-form-actions">
          <button type="button" className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>
            {saving ? t('st.rmSaving') : mode === 'edit' ? t('st.rmSaveChanges')
              : mode === 'advance' ? t('st.rmSaveAdvance')
                : (isTrial || firstSampleForSku) ? t('st.trAddNewSample') : t('st.rmAddNewRound')}
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={requestClose} disabled={saving}>
            {mode === 'advance' ? t('st.rmCancelAdvance') : t('st.rmCancel')}
          </button>
        </div>
        )}
      </div>
      <datalist id="st-developer-options">{developerOptions.map((option) => <option key={option} value={option} />)}</datalist>
      <datalist id="st-designer-options">{designerOptions.map((option) => <option key={option} value={option} />)}</datalist>
      <datalist id="st-project-options">{projectOptions.map((p) => <option key={p.id} value={p.id}>{p.projectItem}</option>)}</datalist>
    </div>
  );
}

/* Developer + Designer on a SKU — ONE block for the three forms that edit a SKU (the standalone SKU
   modal, the group SKU editor, the SkuCard used by New Project and Project Approval).

   Written as a shared component from the start rather than copied three times: the same field appeared
   three times before and every later change had to be made three times, which is exactly how Sales Rep
   and the photo box went missing from two of the forms on 2026-08-19.

   Two lists, not one: Developer offers the Development team, Designer offers HINC DT plus the
   designers who have no portal account — 'External Designer' is the most common designer in the data
   (95 of 290 rounds) and would otherwise be unpickable. Both keep whatever the row already holds, so
   an old value never disappears from a row just because that person left the team. */
export function SkuPeopleFields({ developer, designer, onPick, filterOptions, readOnly }) {
  const { t } = useTranslation();
  const developerOptions = filterOptions.skuDevelopers?.length ? filterOptions.skuDevelopers : (filterOptions.assignees || []);
  const designerOptions = filterOptions.skuDesigners?.length ? filterOptions.skuDesigners : (filterOptions.designers || []);
  return (
    <>
      <Field label={t('st.pfDeveloper')}>
        <select value={developer || ''} disabled={readOnly} onChange={(e) => onPick('developer', e.target.value)}>
          <option value="">—</option>
          {withCurrent(developerOptions, developer).map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      </Field>
      <Field label={t('st.pfDesigner')}>
        <select value={designer || ''} disabled={readOnly} onChange={(e) => onPick('designer', e.target.value)}>
          <option value="">—</option>
          {withCurrent(designerOptions, designer).map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      </Field>
    </>
  );
}

function ProjectModal({ mode, form, filterOptions, rounds, saving, errors, onChange, onClose, onSave }) {
  const { t } = useTranslation();
  const { requestClose, prompt: discardPrompt } = useDiscardGuard(form, onClose, { active: !saving });
  const set = (field) => (event) => onChange({ [field]: event.target.value });
  const need = skuRequiredKeys(!form.id, form._was);

  const categoryOptions = filterOptions.projectCategories;
  const statusOptions = filterOptions.projectStatuses;

  const baseSamples = useMemo(() => {
    const map = new Map();
    rounds.forEach((round) => { if (round.baseId && round.styleItem) map.set(round.baseId, round.styleItem); });
    return [...map.entries()].map(([baseId, styleItem]) => ({ baseId, styleItem }));
  }, [rounds]);
  const linkedSampleStyle = baseSamples.find((s) => s.baseId === form.sampleId)?.styleItem;

  return (
    <div className="st-modal-overlay" role="dialog" aria-modal="true">
      {/* Portals to <body>; only present once the form is dirty and the user tries to leave. */}
      {discardPrompt}
      <div className="st-modal">
        <div className="st-modal-head">
          <h3>{mode === 'edit' ? `${t('st.pfEditPrefix')} — ${form.id}` : t('st.pfNewSku')}</h3>
          <button type="button" className="st-modal-close" onClick={requestClose} aria-label={t('st.viewClose')}>&times;</button>
        </div>
        <div className="st-modal-body">

          {/* Group-level context — edited from "Edit Project Group", read-only here */}
          <div className="own-section own-hevn">
            <div className="own-hd own-hd-sub">
              <span>{t('st.pfProjectInfo')}</span>
              <small>{t('st.pfGroupLevelHint')}</small>
            </div>
            <div className="own-body">
              <div className="fg2">
                <Field label={t('st.pfCategory')} required>
                  <input className="pt-ro-field" value={form.groupCategory || form.category || ''} readOnly />
                </Field>
                <Field label={t('st.pfProjectName')} required>
                  <input className="pt-ro-field" value={form.groupName || form.projectItem || ''} readOnly />
                </Field>
                <Field label={t('st.pfCase')}>
                  <input className="pt-ro-field" value={form.groupCase || '—'} readOnly />
                </Field>
                {form.groupCase === 'B' && (
                  <Field label={t('st.pfEntity')}>
                    <input className="pt-ro-field" value={form.groupEntity || '—'} readOnly />
                  </Field>
                )}
              </div>
            </div>
          </div>

          <div className="own-section own-both">
            <div className="own-hd"><span>{t('st.pfSkuInfo')}</span><span className="owner-tag owner-tag-dark">{form.id || t('st.pfNewTag')}</span></div>
            <div className="own-body">
              <div className="fg2">
                <SkuPeopleFields
                  developer={form.assignedTo}
                  designer={form.designerName}
                  filterOptions={filterOptions}
                  onPick={(role, value) => onChange(role === 'developer' ? { assignedTo: value } : { designerName: value })}
                />
                <Field label={t('st.pfAllocation')}>
                  <select value={form.allocation} onChange={set('allocation')}>
                    <option value="">—</option>
                    {filterOptions.allocations.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                </Field>
                <Field label={t('st.pfProductCategory')}>
                  <select value={form.productType} onChange={set('productType')}>
                    <option value="">—</option>
                    {filterOptions.projectProductTypes.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                </Field>
                <Field label={t('st.pfFrameColor')}>
                  <ListPicker
                    value={form.frameColor}
                    options={filterOptions.frameColors}
                    onChange={(v) => onChange({ frameColor: v })}
                    placeholder={t('st.pfFrameColorPh')}
                  />
                </Field>
                <Field label={t('st.pfItemName')} required>
                  <input value={form.subItem} onChange={set('subItem')} placeholder={t('st.pfItemNamePh')} />
                </Field>
                {/* No TBA tick since 2026-10-16 (Max): the four boxes below are required — SKU_REQUIRED_FIELDS. */}
                <Field label={t('st.pfEstQty')} required={need.has('estimateQty')}>
                  <input type="number" min="0" value={form.estimateQty ?? ''} onChange={set('estimateQty')} />
                </Field>
                <Field label={t('st.pfTargetPrice')} required={need.has('targetPrice')}>
                  <input type="number" min="0" step="0.01" value={form.targetPrice ?? ''} onChange={set('targetPrice')} />
                </Field>
                <Field label={t('st.pfStartDate')}>
                  <input type="date" value={form.startDate} onChange={set('startDate')} />
                </Field>
                <Field label={t('st.pfExfShipDate')} required={need.has('exfShipDate')}>
                  <input type="date" value={form.exfShipDate || ''} onChange={set('exfShipDate')} />
                </Field>
                <Field label={t('st.pfLaunchDate')} required={need.has('launchDate')}>
                  <input type="date" value={form.launchDate || ''} onChange={set('launchDate')} />
                </Field>
                <Field label={t('st.pfProjectStatus')}>
                  <select value={form.status} onChange={set('status')}>
                    {statusOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                </Field>
                <Field label={t('st.pfRemark')}>
                  <input value={form.remark} onChange={set('remark')} placeholder={t('st.pfRemark')} />
                </Field>
              </div>

              <div className="pt-sku-subttl">{t('st.pfInitialStages')} <span style={{ float: 'right', fontWeight: 700 }}>{skuStagePct(form.stages || {})}% {t('st.pfPctComplete')}</span></div>
              <div className="pt-help" style={{ marginBottom: 8 }}>{t('st.pfStageHint')}</div>
              <RepeatOrderField value={form.repeatOrder} stages={form.stages} onChange={onChange} />
              <div className="pt-stage-cards">
                {stageColumns.map(([key, label]) => (
                  <div className="pt-stage-cell" key={key}>
                    <div className="pt-stage-cell-lbl">{t(label)}</div>
                    <StageField value={form.stages[key] ?? ''} readOnly={isAutoStage(key) || (key === 'costing' && !!form.costingQuote)} onChange={(v) => onChange({ stages: { ...form.stages, [key]: v } })} placeholder={isAutoStage(key) ? t('st.pfStageAuto') : t('st.pfStagePh')} />
                  </div>
                ))}
              </div>

              <div className="pt-sku-subttl">{t('st.pfLinkSample')}</div>
              <Field label="" hint={`${t('st.pfLinkSampleHint')}${linkedSampleStyle ? ` — ${linkedSampleStyle}` : ''}`}>
                <input
                  list="pt-linked-sample-options"
                  value={form.sampleId}
                  onChange={(event) => onChange({ sampleId: event.target.value.trim().toUpperCase() })}
                  placeholder={t('st.pfLinkSamplePh')}
                />
              </Field>
              {mode === 'edit' && <SkuGtmField form={form} filterOptions={filterOptions} onChange={onChange} />}
            </div>
          </div>
        </div>
        {!!errors.length && (
          <div className="st-alert error st-alert-foot">{errors.map((message) => <div key={message}>{errorText(message)}</div>)}</div>
        )}
        <div className="st-modal-foot st-form-actions">
          <button type="button" className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>{saving ? t('st.pfSaving') : mode === 'edit' ? t('st.pfSaveChanges') : t('st.pfSaveAddSku')}</button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={requestClose} disabled={saving}>{t('st.pfCancel')}</button>
        </div>
      </div>
      <datalist id="pt-category-options">{categoryOptions.map((option) => <option key={option} value={option} />)}</datalist>
      <datalist id="pt-product-type-options">{filterOptions.projectProductTypes.map((option) => <option key={option} value={option} />)}</datalist>
      <datalist id="pt-assignee-options">{filterOptions.assignees.map((option) => <option key={option} value={option} />)}</datalist>
      <datalist id="pt-allocation-options">{filterOptions.allocations.map((option) => <option key={option} value={option} />)}</datalist>
      <datalist id="pt-linked-sample-options">{baseSamples.map((sample) => <option key={sample.baseId} value={sample.baseId}>{sample.styleItem}</option>)}</datalist>
    </div>
  );
}

/* The editor for a SKU that already exists — the one the group modal opens. SkuCard is the sibling
   used while CREATING one, which is why the material list is here and not there: the list is keyed by
   the SKU's id and a SKU being typed has none yet. */
function ProjectSkuInlineEditor({ form, filterOptions, rounds, onChange, skuId, readOnly, materialDrafts }) {
  const { t } = useTranslation();
  const set = (field) => (event) => onChange({ [field]: event.target.value });
  const need = skuRequiredKeys(!form.id, form._was);
  const statusOptions = filterOptions.projectStatuses;
  const baseSamples = useMemo(() => {
    const map = new Map();
    rounds.forEach((round) => { if (round.baseId && round.styleItem) map.set(round.baseId, round.styleItem); });
    return [...map.entries()].map(([baseId, styleItem]) => ({ baseId, styleItem }));
  }, [rounds]);
  const linkedSampleStyle = baseSamples.find((s) => s.baseId === form.sampleId)?.styleItem;

  return (
    <div className="pt-sku-body pt-group-sku-editor">
      {/* THE PHOTOS BELONG TO THE ITEM — Max, 2026-09-21: "phần upload ảnh sẽ upload theo SKU". A
          group of three chairs had one pile of pictures and nothing saying which chair each was of.
          Only on a SKU that EXISTS: a card still being typed has no id to key a photo to, and the
          group's band above says so rather than offering a box that cannot keep its promise. */}
      {!!skuId && (
        <div className="pt-sku-photos">
          <ProjectPhotos scope="sku" ownerKey={skuId} readOnly={readOnly} />
        </div>
      )}
      {/* VIEW (2026-10-15) — one disabled fieldset rather than a readOnly prop threaded into fifteen
          fields: it locks every input, select and picker inside it, including any added later. */}
      <fieldset className="pt-ro-fieldset" disabled={!!readOnly}>
      <div className="fg2">
        <SkuPeopleFields
          developer={form.assignedTo}
          designer={form.designerName}
          filterOptions={filterOptions}
          onPick={(role, value) => onChange(role === 'developer' ? { assignedTo: value } : { designerName: value })}
        />
        <Field label={t('st.pfAllocation')}>
          <select value={form.allocation} onChange={set('allocation')}>
            <option value="">—</option>
            {filterOptions.allocations.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </Field>
        <Field label={t('st.pfProductCategory')}>
          <select value={form.productType} onChange={set('productType')}>
            <option value="">—</option>
            {filterOptions.projectProductTypes.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </Field>
        <Field label={t('st.pfFrameColor')}>
          <ListPicker
            value={form.frameColor}
            options={filterOptions.frameColors}
            onChange={(v) => onChange({ frameColor: v })}
            placeholder={t('st.pfFrameColorPh')}
          />
        </Field>
        <Field label={t('st.pfItemName')} required>
          <input value={form.subItem} onChange={set('subItem')} placeholder={t('st.pfItemNamePh')} />
        </Field>
        {/* No TBA tick since 2026-10-16 (Max): the four boxes below are required — SKU_REQUIRED_FIELDS. */}
        <Field label={t('st.pfEstQty')} required={need.has('estimateQty')}>
          <input type="number" min="0" value={form.estimateQty ?? ''} onChange={set('estimateQty')} />
        </Field>
        <Field label={t('st.pfTargetPrice')} required={need.has('targetPrice')}>
          <input type="number" min="0" step="0.01" value={form.targetPrice ?? ''} onChange={set('targetPrice')} />
        </Field>
        <Field label={t('st.pfStartDate')}>
          <input type="date" value={form.startDate} onChange={set('startDate')} />
        </Field>
        <Field label={t('st.pfExfShipDate')} required={need.has('exfShipDate')}>
          <input type="date" value={form.exfShipDate || ''} onChange={set('exfShipDate')} />
        </Field>
        <Field label={t('st.pfLaunchDate')} required={need.has('launchDate')}>
          <input type="date" value={form.launchDate || ''} onChange={set('launchDate')} />
        </Field>
        <Field label={t('st.pfProjectStatus')}>
          <select value={form.status} onChange={set('status')}>
            {statusOptions.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </Field>
        <Field label={t('st.pfRemark')}>
          <input value={form.remark} onChange={set('remark')} placeholder={t('st.pfRemark')} />
        </Field>
      </div>
      </fieldset>

      {/* What this SKU is made of — between the SKU's own fields and its process stages, where Max
          put it (2026-09-02). It writes through its own endpoint but goes out with this form's Save
          button (Max, 2026-09-03), which is what the draft store is for. Only for a SKU that exists:
          the list is keyed by its id. */}
      {SKU_MATERIALS_ON && !!skuId && (
        <>
          <div className="pt-sku-subttl">{t('st.rmMaterials')}</div>
          <SkuMaterialList skuId={skuId} canEdit={!readOnly} drafts={materialDrafts} />
        </>
      )}

      <div className="pt-sku-subttl">{t('st.pfInitialStages')} <span style={{ float: 'right', fontWeight: 700 }}>{skuStagePct(form.stages || {})}% {t('st.pfPctComplete')}</span></div>
      <div className="pt-help" style={{ marginBottom: 8 }}>{t('st.pfStageHint')}</div>
      <RepeatOrderField value={form.repeatOrder} stages={form.stages} onChange={onChange} readOnly={readOnly} />
      <fieldset className="pt-ro-fieldset" disabled={!!readOnly}>
      <div className="pt-stage-cards">
        {stageColumns.map(([key, label]) => (
          <div className="pt-stage-cell" key={key}>
            <div className="pt-stage-cell-lbl">{t(label)}</div>
            <StageField value={form.stages[key] ?? ''} readOnly={isAutoStage(key) || (key === 'costing' && !!form.costingQuote)} onChange={(v) => onChange({ stages: { ...form.stages, [key]: v } })} placeholder={isAutoStage(key) ? t('st.pfStageAuto') : t('st.pfStagePh')} />
          </div>
        ))}
      </div>

      <div className="pt-sku-subttl">{t('st.pfLinkSample')}</div>
      <Field label="" hint={`${t('st.pfLinkSampleHint')}${linkedSampleStyle ? ` — ${linkedSampleStyle}` : ''}`}>
        <input
          list="pt-linked-sample-options"
          value={form.sampleId}
          onChange={(event) => onChange({ sampleId: event.target.value.trim().toUpperCase() })}
          placeholder={t('st.pfLinkSamplePh')}
        />
      </Field>
      </fieldset>
      <SkuGtmField form={form} filterOptions={filterOptions} onChange={onChange} readOnly={readOnly} />
    </div>
  );
}

// One collapsible SKU card in the multi-SKU New Project form. Mirrors Portal.html ptFmRenderItems.
export function SkuCard({ index, sku, filterOptions, defaultOpen, onChange, onRemove, onDuplicate, readOnly }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const set = (field) => (event) => onChange({ [field]: event.target.value });
  // 'TBA' is what a card held before 2026-10-16; it reads as empty now (SKU_REQUIRED_FIELDS).
  const tba = (v) => (String(v ?? '') === 'TBA' ? '' : (v ?? ''));
  const need = skuRequiredKeys(!(sku.projectId || sku.id), sku._was);
  const pct = skuStagePct(sku.stages || {});
  return (
    <div className="pt-sku-card">
      <div className="pt-sku-head" onClick={() => setOpen((v) => !v)} role="button" tabIndex={0}>
        <div className="pt-sku-summary">
          <span className="pt-sku-caret">{open ? '▾' : '▸'}</span>
          <span className="pt-sku-name">{sku.item || `SKU ${index + 1}`}</span>
          {/* Jay, 2026-08-22: "SKU Information in Project View: please add Designer's name". */}
          <span className="pt-mini-chip">{sku.assigned || t('st.pfUnassigned')}</span>
          <span className="pt-mini-chip">{sku.designer || t('st.pfNoDesigner')}</span>
          <span className="pt-mini-chip">{sku.itemCategory || t('st.pfNoCategory')}</span>
          <span className="pt-mini-chip">{t('st.pfQty')} {tba(sku.qty) || '—'}</span>
          <span className={`pt-mini-chip ${pct >= 100 ? 'ok' : 'muted'}`}>{pct}% {t('st.pfStageChip')}</span>
        </div>
        <div className="pt-sku-acts">
          {/* Duplicate sits before the delete, and is offered wherever a SKU can be ADDED — the
              approval screen edits existing SKUs and offers neither. */}
          {!readOnly && onDuplicate && (
            <button type="button" className="pt-sku-dup" title={t('st.pfDuplicateSku')}
              onClick={(e) => { e.stopPropagation(); onDuplicate(); }}>
              <Copy size={13} />
            </button>
          )}
          {/* No onRemove means this card cannot be removed here — the approval screen edits existing
              SKUs but leaves deletion to Project Tracker, where the sample rounds are handled too. */}
          {!readOnly && onRemove && <button type="button" className="pt-sku-del" title={t('st.pfRemoveSku')} onClick={(e) => { e.stopPropagation(); onRemove(); }}>&times;</button>}
        </div>
      </div>
      {open && (
        <div className="pt-sku-body">
          {/* THE PHOTOS BELONG TO THE ITEM — Max, 2026-09-21: "2 modal này chưa thay đổi cách upload
              ảnh nè". The New Project form and Project Approval's edit dialog still had one box at
              the foot of the project, so a project of five chairs got one pile of pictures again.
              A SKU that EXISTS takes them straight away; one still being typed holds them in the
              page and the form uploads them to its new id once the save has one. */}
          <div className="pt-sku-photos">
            {sku.projectId
              ? <ProjectPhotos scope="sku" ownerKey={sku.projectId} readOnly={readOnly} />
              : (
                <ProjectPhotos
                  scope="sku"
                  pending
                  readOnly={readOnly}
                  pendingPhotos={sku.photos || []}
                  onPendingChange={(list) => onChange({ photos: list })}
                />
              )}
          </div>
          <div className="fg2">
            <SkuPeopleFields
              developer={sku.assigned}
              designer={sku.designer}
              filterOptions={filterOptions}
              readOnly={readOnly}
              onPick={(role, value) => onChange(role === 'developer' ? { assigned: value } : { designer: value })}
            />
            <Field label={t('st.pfAllocation')}>
              <select value={sku.allocation} disabled={readOnly} onChange={set('allocation')}>
                <option value="">—</option>
                {filterOptions.allocations.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            </Field>
            <Field label={t('st.pfProductCategory')}>
              <select value={sku.itemCategory} disabled={readOnly} onChange={set('itemCategory')}>
                <option value="">—</option>
                {filterOptions.projectProductTypes.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            </Field>
            <Field label={t('st.pfFrameColor')}>
              <ListPicker
                value={sku.frameColor}
                options={filterOptions.frameColors}
                onChange={(v) => onChange({ frameColor: v })}
                placeholder={t('st.pfFrameColorPh')}
                disabled={readOnly}
              />
            </Field>
            <Field label={t('st.pfItemName')} required>
              <input value={sku.item} readOnly={readOnly} onChange={set('item')} placeholder={t('st.pfItemNamePh')} />
            </Field>
            {/* No TBA tick since 2026-10-16 (Max): the four boxes below are required — SKU_REQUIRED_FIELDS. */}
            <Field label={t('st.pfEstQty')} required={need.has('estimateQty')}>
              <input type="number" min="0" value={tba(sku.qty)} disabled={readOnly} onChange={set('qty')} />
            </Field>
            <Field label={t('st.pfTargetPrice')} required={need.has('targetPrice')}>
              <input type="number" min="0" step="0.01" value={sku.targetPrice ?? ''} disabled={readOnly} onChange={set('targetPrice')} />
            </Field>
            <Field label={t('st.pfStartDate')}>
              <input type="date" value={sku.startDate} disabled={readOnly} onChange={set('startDate')} />
            </Field>
            <Field label={t('st.pfExfShipDate')} required={need.has('exfShipDate')}>
              <input type="date" value={tba(sku.exfDate)} disabled={readOnly} onChange={set('exfDate')} />
            </Field>
            <Field label={t('st.pfLaunchDate')} required={need.has('launchDate')}>
              <input type="date" value={sku.launchDate || ''} disabled={readOnly} onChange={set('launchDate')} />
            </Field>
            <Field label={t('st.pfProjectStatus')}>
              <select value={sku.status} disabled={readOnly} onChange={set('status')}>
                {filterOptions.projectStatuses.map((option) => <option key={option} value={option}>{valueLabel(option)}</option>)}
              </select>
            </Field>
            <Field label={t('st.pfRemark')}>
              <input value={sku.remark} readOnly={readOnly} onChange={set('remark')} placeholder={t('st.pfRemark')} />
            </Field>
          </div>
          <div className="pt-sku-subttl">{t('st.pfInitialStages')} <span style={{ float: 'right', fontWeight: 700 }}>{pct}% {t('st.pfPctComplete')}</span></div>
          <div className="pt-help" style={{ marginBottom: 8 }}>{t('st.pfStageHint')}</div>
          <RepeatOrderField value={sku.repeatOrder} stages={sku.stages} onChange={onChange} readOnly={readOnly} />
          <div className="pt-stage-cards">
            {stageColumns.map(([key, label]) => (
              <div className="pt-stage-cell" key={key}>
                <div className="pt-stage-cell-lbl">{t(label)}</div>
                <StageField value={sku.stages[key] ?? ''} readOnly={readOnly || isAutoStage(key) || (key === 'costing' && !!sku.costingQuote)} onChange={(v) => onChange({ stages: { ...sku.stages, [key]: v } })} placeholder={isAutoStage(key) ? t('st.pfStageAuto') : t('st.pfStagePh')} />
              </div>
            ))}
          </div>
          <div className="pt-sku-subttl">{t('st.pfLinkSample')}</div>
          <Field label="" hint={t('st.pfLinkSampleHint')}>
            <input list="pt-linked-sample-options" value={sku.samples} readOnly={readOnly} onChange={(e) => onChange({ samples: e.target.value.trim().toUpperCase() })} placeholder={t('st.pfLinkSamplePh')} />
          </Field>
        </div>
      )}
    </div>
  );
}

// One expandable, editable ticket card in the New Project Tickets section — mirrors Portal.html per-ticket form.
function TicketCard({
  ticket, codeOptions, allTypes, requester, projectName, requestingTeam, projectOwner,
  groupSkus = [], takenSkus = {}, onChange, onRemove, codeLocked = false, readOnly = false, defaultOpen = true,
}) {
  const priorityOptions = usePortalList('taskPriorities', TASK_PRIORITY_OPTIONS);
  const statusOptions = usePortalList('taskStatuses', TASK_STATUS_OPTIONS);
  const { t } = useTranslation();
  /* A ticket being staged opens — it was just added and has to be filled in. One that already exists
     starts closed (Max, 2026-09-04): a project with several of them turned the form into a wall, and
     the header line already says which ticket each one is. */
  const [open, setOpen] = useState(defaultOpen);
  const set = (field) => (e) => onChange({ [field]: e.target.value });
  const changeType = (code) => {
    const preset = makeTicketFromType(code, allTypes);
    onChange({
      taskType: code, name: preset.name, description: preset.description,
      email: preset.email, department: preset.department, fields: {},
    });
  };
  const form = useTicketForm(ticket.taskType);
  const koInfo = useTicketKo(ticket.taskType);
  const skuNames = Object.fromEntries(groupSkus.map((s) => [s.id, s.subItem || s.projectItem || '']));
  const draft = ticketEmailDraft(ticket, requester, projectName, requestingTeam, projectOwner, form, skuNames, koInfo);
  const missing = missingRequired(form, ticket.fields || {});
  // A ticket is always raised as Pending, and afterwards only whoever owns it may move it — the
  // server enforces the same rule, this just stops the field from lying about it.
  const statusLocked = !ticket.taskId || ticket.canEdit === false;
  return (
    <div className="pt-sku-card">
      <div className="pt-sku-head" onClick={() => setOpen((v) => !v)} role="button" tabIndex={0}>
        <div className="pt-sku-summary">
          <span className="pt-sku-caret">{open ? '▾' : '▸'}</span>
          <span className="pt-sku-name">{ticket.name || ticket.taskType || t('st.tkColTicket')}</span>
          <span className="pt-mini-chip">{ticket.taskType}</span>
          {!!ticket.taskId && <span className="pt-mini-chip">{ticket.taskId}</span>}
          <span className="pt-mini-chip">{requester || '—'}</span>
          <span className="pt-mini-chip">{ticket.dueDate || t('st.tkNoDueDate')}</span>
          <span className={`pt-mini-chip ${ticket.status === 'Pending' ? 'muted' : 'ok'}`}>{taskStatusLabel(ticket.status)}</span>
        </div>
        {/* Only a ticket still being staged can be dropped here. One that exists is deleted in
            Ticket Station, where an emailed ticket asks for a reason and sends the cancellation. */}
        {!!onRemove && (
          <button type="button" className="pt-sku-del" title={t('st.tkRemoveTicket')} onClick={(e) => { e.stopPropagation(); onRemove(); }}>&times;</button>
        )}
      </div>
      {open && (
        /* A fieldset, not a div: `readOnly` then disables every control inside in one place, so a
           ticket the server would refuse cannot take typing it will not be able to save. */
        <fieldset className="pt-sku-body pt-ticket-fs" disabled={readOnly}>
          {readOnly && <div className="pt-help pt-tk-locked">{t('st.tkNotYourTeam')}</div>}
          <Field label={t('st.tkTicketCode')} required>
            {/* Locked once the ticket exists: changing the code changes the form under the answers. */}
            <select value={ticket.taskType} disabled={codeLocked} onChange={(e) => changeType(e.target.value)}>
              {codeOptions.map((opt) => <option key={opt.code} value={opt.code}>{opt.code} - {opt.name}</option>)}
            </select>
          </Field>
          <Field label={t('st.tkTicketName')}><input className="pt-ro-field" value={ticket.name} readOnly /></Field>
          <Field label={t('st.tkDescription')}><textarea rows={2} value={ticket.description} onChange={set('description')} /></Field>
          <div className="fg3">
            <Field label={t('st.tkColRequester')}><input className="pt-ro-field" value={requester || ''} readOnly /></Field>
            <Field label={t('st.tkGroupEmail')}><input value={ticket.email} onChange={set('email')} placeholder="team@helinox.com" /></Field>
            <Field label={t('st.tkColTeam')}><input className="pt-ro-field" value={ticket.department} readOnly /></Field>
          </div>
          <div className="fg3">
            <Field label={t('st.tkColPriority')}>
              <select value={ticket.priority} onChange={set('priority')}>{priorityOptions.map((o) => <option key={o} value={o}>{valueLabel(o)}</option>)}</select>
            </Field>
            <Field label={t('st.tkDueDate')}><input type="date" value={ticket.dueDate} onChange={set('dueDate')} /></Field>
            <Field label={t('st.colStatus')}>
              <select value={statusLocked ? 'Pending' : (ticket.status || 'Pending')} onChange={set('status')} disabled={statusLocked}>
                {/* The value is the stored status; only the text is the label. */}
                {statusOptions.map((o) => <option key={o} value={o}>{taskStatusLabel(o)}</option>)}
              </select>
            </Field>
          </div>
          {/* The request block: what this code is actually asking for. Jay's rule — a ticket must
              not be sendable while one of these is empty, because once the mail is out the receiving
              team has to chase the requester by chat, which is the thing being removed. */}
          <TicketFormFields
            form={form}
            values={ticket.fields || {}}
            skus={groupSkus}
            takenSkus={takenSkus}
            onChange={(fields) => onChange({ fields })}
          />
          {!!missing.length && (
            <div className="tkf-missing">
              {t('stx.stillNeeded', { list: missing.join(', ') })}
            </div>
          )}
          {/* What the assignee reported back through the emailed link. Advisory, never the master
              status, and nobody edits it here — it is their answer. A staged ticket has none. */}
          {!!ticket.assigneeStatus && (
            <div className="pt-tk-reply">
              <strong>{ticket.assigneeStatus}</strong>
              {ticket.assigneeUpdatedAt ? ` · ${ticket.assigneeUpdatedAt}` : ''}
              {ticket.assigneeNote ? ` — ${ticket.assigneeNote}` : ''}
            </div>
          )}

          <Field label={t('st.tkAttachment')}><input value={ticket.attachmentUrl} onChange={set('attachmentUrl')} placeholder={t('st.tkAttachmentPh')} /></Field>
          {/* Files, as opposed to the link above — Max, 2026-09-07. They ride out ON the ticket
              email, so what is put here is what the receiving team opens from their inbox. Only on a
              ticket that exists: a file hangs off a task_id and a staged ticket has none. */}
          <Field label={t('st.tkFiles')}>
            <TicketFiles taskId={ticket.taskId || ticket.fileDraftKey} disabled={readOnly} />
          </Field>
          {/* Taller than the old two-row textarea, and formattable — Max, 2026-09-07. The value is
              html now; RichText reads a legacy plain-text note unchanged. */}
          <Field label={t('st.tkColNote')}>
            <RichText value={ticket.note} onChange={(html) => onChange({ note: html })}
              placeholder={t('st.tkOptionalNote')} minHeight={110} />
          </Field>

          {/* The html half of the same mail, where the SKU list is a real table. Carried on the
              element so what will actually land in an inbox can be inspected without sending one. */}
          <div className="pt-email-block" data-mail-html={draft.html || ''}>
            <div className="pt-email-head">
              <label className="pt-ticket-sendemail">
                <input
                  type="checkbox"
                  checked={!!ticket.sendEmail && !missing.length}
                  disabled={!!missing.length}
                  onChange={(e) => onChange({ sendEmail: e.target.checked, sendManual: false })}
                /> {t('st.tkSendAfterSave')}
              </label>
              {/* Max, 2026-09-09: this said "Not sent" on a ticket Ticket Station showed as sent on
                  2026-09-08. The badge only ever read what was QUEUED — sendEmail / sendManual — and
                  never `sentAt`, which the same ticket object has carried all along because both
                  screens read the one `tasks` list. */}
              <span className={`pt-email-status ${ticket.sendEmail || ticket.sendManual ? 'queued' : ticket.sentAt ? 'sent' : ''}`}>
                {ticket.sendEmail ? t('st.tkWillSend')
                  : ticket.sendManual ? t('st.tkQueued')
                  : ticket.sentAt ? `${t('st.tkSent')} ${fmtDateTimeSecVN(ticket.sentAt, '')}`
                  : t('st.tkNotSent')}
              </span>
            </div>
            <div className="pt-email-body">
              <Field label={<>{t('st.tkEmailSubject')} <span className="pt-badge">{t('st.tkSystemGenerated')}</span></>}>
                <input className="pt-ro-field" value={draft.subject} readOnly />
              </Field>
              <Field label={t('st.tkEmailCc')}>
                <input value={ticket.emailCc || ''} onChange={set('emailCc')} placeholder="cc@example.com" />
              </Field>
              <Field label={<>{t('st.tkEmailMessage')} <span className="pt-badge">{t('st.tkReadOnly')}</span></>}>
                {/* The html half, which is what actually lands in an inbox — the SKU list as a real
                    table. The preview used to show the text half, the fallback for a client that
                    refuses html, so a ten-column request read as a wall of "a / b / c" (Max,
                    2026-09-04). Safe to render: every value and label in it is escaped where the
                    html is built, in ticketEmailBody and renderBlockHtml. */}
                <div
                  className="pt-email-preview"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: draft.html || '' }}
                />
              </Field>
              <div className="pt-email-foot">
                <span className="pt-help">{t('st.tkGeneratedNote')}</span>
                {!ticket.sendEmail && (
                  <button
                    type="button"
                    className={`btn btn-sm ${ticket.sendManual ? 'btn-smp' : 'btn-ghost'}`}
                    title={missing.length && !ticket.sendManual ? t('st.tkSendBlocked')
                      : ticket.sendManual ? t('st.tkQueuedCancelTitle') : t('st.tkSendTitle')}
                    /* The checkbox above has always waited for the required answers; this button
                       did not, so it queued a mail the server then withheld on Save. Still pressable
                       while queued, so a queued send can be taken back. */
                    disabled={!String(ticket.email || '').trim() || (!!missing.length && !ticket.sendManual)}
                    onClick={() => onChange({ sendManual: !ticket.sendManual })}
                  >
                    {ticket.sendManual ? `✓ ${t('st.tkQueued')}` : t('st.tkSendEmail')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </fieldset>
      )}
    </div>
  );
}

// Multi-SKU "New Project" modal — one group header + N SKU cards. Mirrors Portal.html ptModal (New Project).
/**
 * Partner / Line picker — a searchable list rather than a plain <select>, because the master list
 * grows over time and scrolling it becomes the slow part of creating a project. Typing filters it;
 * when nothing matches, the same box offers to add what was typed to the master list (managers only,
 * enforced by the API too).
 */
/* One picker for both Partner / Line and Sales Rep (Max, 2026-08-19): same behaviour — search the
   managed list, or add what you typed. Parameterised rather than cloned so a fix lands on both; the
   defaults keep every existing caller unchanged. */
export function PartnerPicker({
  partners, value, onChange, onAddPartner, canCreate, disabled,
  // The two words default to the translated ones; a caller passing its own still wins.
  idKey = 'partnerId', placeholder, addNoun,
}) {
  const { t } = useTranslation();
  const searchPh = placeholder ?? t('stx.searchPartner');
  const listNoun = addNoun ?? t('stx.theList');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  // `typed` separates "the box shows the current choice" from "the user is searching", so clicking
  // the field still shows the whole list instead of filtering it down to the one already chosen.
  const [typed, setTyped] = useState(false);
  // The menu is positioned FIXED, not absolute: its section card is `overflow: hidden`, which cut the
  // bottom off the list so the last entries could not be reached. Fixed escapes that clip, at the
  // cost of having to measure the anchor and keep it in sync while the menu is open.
  const [menuBox, setMenuBox] = useState(null);
  const wrapRef = useRef(null);

  // Esc closes the menu only — the form behind it stays open (see useEscapeClose).
  useEscapeClose(() => setOpen(false), open);

  const selected = partners.find((p) => String(p[idKey]) === String(value)) || null;

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const below = window.innerHeight - r.bottom - 12;
      const above = r.top - 12;
      const dropUp = below < 170 && above > below;   // near the bottom of the screen, open upwards
      setMenuBox({
        left: r.left,
        width: r.width,
        maxHeight: Math.min(260, Math.max(140, dropUp ? above : below)),
        ...(dropUp ? { bottom: window.innerHeight - r.top + 3 } : { top: r.bottom + 3 }),
      });
    };
    place();
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  const q = query.trim().toLowerCase();
  const searching = typed && !!q;
  const matches = searching ? partners.filter((p) => p.name.toLowerCase().includes(q)) : partners;
  const exact = partners.some((p) => p.name.toLowerCase() === q);

  const pick = (partner) => {
    onChange(partner ? String(partner[idKey]) : '');
    setQuery('');
    setTyped(false);
    setOpen(false);
  };

  const addTyped = async () => {
    const name = query.trim();
    if (!name || adding) return;
    setAdding(true);
    try {
      const added = await onAddPartner(name);
      if (added) pick(added);
    } finally {
      setAdding(false);
    }
  };

  if (disabled) {
    return <input type="text" value={selected ? selected.name : ''} disabled />;
  }

  return (
    <div className="pt-partner-picker" ref={wrapRef}>
      <div className="pt-partner-row">
        <input
          type="text"
          value={open ? query : (selected ? selected.name : '')}
          placeholder={searchPh}
          // Seed the box with the current choice and select it, so the name reads as a real value
          // (not grey placeholder text) and typing still replaces it immediately.
          onFocus={(e) => { setOpen(true); setTyped(false); setQuery(selected ? selected.name : ''); e.target.select(); }}
          onChange={(e) => { setQuery(e.target.value); setTyped(true); setOpen(true); }}
        />
        {selected && !open && (
          <button type="button" className="pt-partner-clear" onClick={() => pick(null)} aria-label={t('st.rmClear')}>&times;</button>
        )}
      </div>
      {open && menuBox && (
        <div className="pt-partner-menu" style={menuBox}>
          {matches.map((p) => (
            <button
              key={p[idKey]}
              type="button"
              className={`pt-partner-opt${String(p[idKey]) === String(value) ? ' on' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(p)}
            >
              {p.name}
            </button>
          ))}
          {!matches.length && <div className="pt-partner-empty">{t('stx.noMatch', { q: query.trim() })}</div>}
          {canCreate && searching && !exact && (
            <button type="button" className="pt-partner-add" onMouseDown={(e) => e.preventDefault()} onClick={addTyped} disabled={adding}>
              {adding ? t('stx.adding') : t('stx.addTo', { name: query.trim(), list: listNoun })}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The "Project Information" block — ONE copy, used by every form that edits a project group:
 * New Project, Edit Project Group, the read-only Tickets view, and Project Approval's Edit.
 *
 * It exists because those four forms each carried their own hand-written copy of these fields, so a
 * field added to one never reached the others. Max found the drift on 2026-08-19: Sales Rep and the
 * sample photos were missing from Edit Project Group, and Note could be typed once at creation and
 * then corrected nowhere. Four copies that had to agree by hand did not agree.
 *
 * Callers keep their own state shape and pass `value` + `onChange(patch)` — nothing here knows
 * whether that state lives in a modal, in the page, or in a form object.
 */
export function ProjectInfoFields({
  value, onChange,
  teams = [], partners = [], salesReps = [], categories = [], seasons,
  readOnly = false,
  showNote = true,
  purposeRequired = false,          // false for the projects that existed before the rule
  canAddPartner = false, onAddPartner,
  canAddSalesRep = false, onAddSalesRep,
  names = {},                       // readOnly display fallbacks: { partnerName, salesRepName }
  nameFallback,                     // defaults to t('stx.fillSeasonPartner')
  viewer = null,                    // { userId, isAdmin, isTeamManager, teamKey } — for the handover warning
  launchBefore,                     // the project as stored (null = a new one); undefined = no launch check here
}) {
  const { t } = useTranslation();
  const set = (field) => (event) => onChange({ [field]: event.target.value });

  /* HANDING THE PROJECT OVER IS A ONE-WAY DOOR — Thuan, 2026-10-05, reported by Max. He changed
     PRJ-00185's owner to a colleague, the save succeeded, and his very next save came back 403:
     the group belongs to its owner, its team manager and admins, and he had just stopped being the
     first of those. Only Tina could give it back. Measured in the access log: 200 at 09:36:43,
     403 at 09:37:27, 200 again at 09:37:48.

     The rule is right and is not being changed — what was missing is that the screen let him do it
     without saying what it cost. 34 of the 146 projects are owned by somebody who is not their
     team's manager, so this is the normal case rather than an edge one.

     THE OWNER AS LOADED, kept per project id rather than per mount: the form is seeded from the
     group and `value` is the live draft, so comparing the draft with itself would never fire. The
     id guard is what makes it survive a form that is reused for a second project without
     remounting. */
  const loadedOwner = useRef({ gid: null, owner: null });
  const gid = value.groupId || value.projectId || value.groupName || null;
  if (loadedOwner.current.gid !== gid) {
    loadedOwner.current = { gid, owner: value.ownerUserId ? String(value.ownerUserId) : '' };
  }
  const wasOwner = loadedOwner.current.owner;
  const nowOwner = value.ownerUserId ? String(value.ownerUserId) : '';
  /* Only when the viewer is handing it AWAY FROM THEMSELVES and keeps no other way in. A manager
     or an admin editing somebody else's project loses nothing and is told nothing. */
  const losingAccess = !readOnly && !!viewer && !viewer.isAdmin
    && !!wasOwner && String(wasOwner) === String(viewer.userId)
    && !!nowOwner && nowOwner !== wasOwner
    && !(value.teamKey && (viewer.managerTeams || []).includes(value.teamKey));
  // Categories arrive either as plain strings (the tracker's own list) or as config rows with a name.
  /* A retired category has to stay selectable on the rows that already hold it (Helinox Lah was retired
     2026-08-23 while one project and 19 SKUs still carried it). Without this the select would open blank
     on those rows and the next save would either fail on "Category is required" or quietly change it. */
  const categoryList = withCurrent(
    categories.map((c) => (typeof c === 'string' ? c : c && c.name)).filter(Boolean),
    value.category,
  );
  /* A team manager approves and resubmits; the project is carried by a member, so managers are not
     offered as Project Owner (Max, 2026-08-25). The one exception is a project that ALREADY has a
     manager as its owner — dropping that name would leave the box empty and the next save would
     clear the owner without anyone noticing. */
  const teamMembers = (teams.find((tm) => tm.teamKey === value.teamKey) || {}).members || [];
  /* THE OWNER IS SOMEBODY WHO DOES THE STAFF WORK — Max, 2026-08-25: "a manager approves and
     resubmits; the project is carried by a member", which was read off `is_team_manager` alone.

     That stopped working when Team 2's staff left: Grace was deactivated and the team's only two
     active accounts, Amy and Christine, are both flagged as managers, so the picker offered nobody
     and said "No member on this team yet" on a team that has two. Amy now does both jobs and holds
     the S.OP Staff role beside her manager one (Max, 2026-09-28).

     So it reads the ROLE — what the person is there to do — instead of a flag that says only what
     else they are. Christine, who holds the manager role and not the staff one, is still out, so
     the 2026-08-25 rule still bites. Measured before the change: Team 1 is unaffected (the same
     six names either way) and Team 2 goes from nobody to Amy.

     The already-the-owner exception stays: a project whose owner later loses the role would
     otherwise come up with an empty box and the next save would clear the owner unnoticed. */
  /* AND THE OWNER AS LOADED, not only the one in the draft — 2026-10-05. The exception kept
     whoever the box currently names, so the moment you picked somebody else you dropped out of your
     own list and there was no way to put it back without closing the form and losing every other
     edit. Found by driving the handover warning: the check could hand the project over and not undo
     it. Both are kept, so the list always carries a way back to where it started. */
  const ownerOptions = teamMembers.filter(
    (m) => m.isSopStaff
      || String(m.userId) === String(value.ownerUserId || '')
      || (!!wasOwner && String(m.userId) === String(wasOwner)),
  );
  const partnerName = names.partnerName
    || (partners.find((p) => String(p.partnerId) === String(value.partnerId)) || {}).name || '';
  /* The preview must promise only what the server will actually write, which is the whole convention
     (Season + Partner + Keyword) with one exception: when it would produce nothing but the bare
     season, a project that already has a name keeps it — otherwise "Staple Chair" with a season and
     no keyword would be previewed, and saved, as "26FW". Mirrors `canBuildName` in the group PUT.
     A project being created has no name yet, so there the preview is always the built name. */
  const currentName = String(value.groupName || '').trim();
  // A legacy project keeps its description inside the name, behind the season it was opened under
  // ("25SS Table Storage"). Same read-back the group PUT does, so the box shows the real outcome.
  const legacyKeyword = (!value.keyword && !partnerName && SEASON_TOKEN_RE.test(currentName))
    ? stripLeadingSeason(currentName) : '';
  const builtName = buildProjectName({
    season: value.season, partnerName, keyword: value.keyword || legacyKeyword, category: value.category,
  });
  const addsNothingToSeason = !builtName || builtName === String(value.season || '').trim();
  const keepsCurrentName = !!currentName && (!value.season || addsNothingToSeason);
  const namePreview = keepsCurrentName ? '' : builtName;
  // Team 1 has no Case / Entity (Max, 2026-08-11). Project Approval used to offer them to Team 1 as
  // well, purely because it was a separate copy of this block.
  const showCase = value.teamKey !== 'T1';

  return (
    <div className="fg2">
      <Field label={t('st.pfTeam')} required>
        <select
          value={value.teamKey || ''}
          disabled={readOnly}
          onChange={(e) => onChange(e.target.value === 'T1'
            ? { teamKey: e.target.value, ownerUserId: '', projectCase: '', entity: '' }
            : { teamKey: e.target.value, ownerUserId: '' })}
        >
          <option value="">{t('st.pfSelectTeam')}</option>
          {teams.map((tm) => <option key={tm.teamKey} value={tm.teamKey}>{tm.teamName}</option>)}
        </select>
      </Field>

      <Field label={t('st.pfOwner')} required={!readOnly}>
        <select value={value.ownerUserId || ''} onChange={set('ownerUserId')} disabled={readOnly || !value.teamKey}>
          <option value="">
            {!value.teamKey ? t('st.pfPickTeamFirst')
              : ownerOptions.length ? t('st.pfSelectOwner')
                : t('pa.noMember')}
          </option>
          {ownerOptions.map((m) => (
            <option key={m.userId} value={m.userId}>{m.isTeamManager ? t('stx.nameManager', { name: m.name }) : m.name}</option>
          ))}
        </select>
        {/* Said BEFORE the save, not after it. The server's refusal is correct but arrives once the
            door has already shut, and it names a rule rather than the thing that just happened. */}
        {losingAccess && (
          <p className="pf-handover">
            {t('st.pfHandover', {
              who: (ownerOptions.find((m) => String(m.userId) === nowOwner) || {}).name || '—',
              mgr: (teamMembers.find((m) => m.isTeamManager) || {}).name || t('st.pfHandoverMgr'),
            })}
          </p>
        )}
      </Field>

      {/* Young: "below/next Project Owner … to know who's sales person for this project", picked from
          the HINC sales team, with a PIC from outside Helinox typed in when needed — hence the same
          picker Partner / Line uses. */}
      <Field label={t('stx.salesRepLabel')} required={!readOnly}>
        {readOnly ? (
          <input value={names.salesRepName
            || (salesReps.find((s) => String(s.salesRepId) === String(value.salesRepId)) || {}).name
            || '—'} readOnly disabled />
        ) : (
          <PartnerPicker
            partners={salesReps}
            value={value.salesRepId || ''}
            onChange={(v) => onChange({ salesRepId: v })}
            onAddPartner={onAddSalesRep}
            canCreate={canAddSalesRep}
            idKey="salesRepId"
            placeholder={t('stx.searchSalesRep')}
            addNoun={t('stx.salesRepList')}
          />
        )}
      </Field>

      {/* No team description here on purpose (Max, 2026-08-11): whoever creates a project already
          knows their own team's scope. The definition is shown where it is actually needed — when an
          admin assigns someone to a team in List Users. */}
      <Field label={t('st.pfCategory')} required>
        <select value={value.category || ''} onChange={set('category')} disabled={readOnly}>
          <option value="">{t('st.pfSelectCategory')}</option>
          {categoryList.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      </Field>

      {/* Year is mandatory; Season is optional and capped at two (Max, 2026-08-23). The composed code is
          printed beside the label so nobody has to work out that 2025 + Spring + Summer reads 25SS. */}
      <Field label={t('st.pfYear')} required hint={value.seasonLocked && !readOnly ? t('xDates.seasonLockedHint') : undefined}>
        <select
          value={value.seasonYear || ''}
          disabled={readOnly || value.seasonLocked}
          onChange={(e) => {
            const year = e.target.value ? Number(e.target.value) : '';
            onChange({ seasonYear: year, season: seasonLabelOf(year, value.seasonParts) });
          }}
        >
          <option value="">{t('st.pfYearPick')}</option>
          {seasonYearOptions(new Date().getFullYear()).map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </Field>

      <Field
        label={t('st.pfSeasonParts')}
        hint={value.season ? t('st.pfSeasonCode', { code: value.season }) : t('st.pfSeasonMax2')}
      >
        <div className="pf-season-parts">
          {SEASON_PART_ORDER.map((sp) => {
            const picked = seasonPartsOrdered(value.seasonParts);
            const on = picked.includes(sp);
            // Full at two: the third box is disabled rather than silently dropping one of the others.
            const full = picked.length >= 2 && !on;
            /* an agreed season is locked (Jay 28/10) — it moves only through Dates → a date change request */
            const locked = readOnly || value.seasonLocked;
            return (
              <label key={sp} className={`pf-season-chip${on ? ' on' : ''}${full || locked ? ' off' : ''}`}>
                <input
                  type="checkbox"
                  checked={on}
                  disabled={locked || full}
                  onChange={() => {
                    const next = on ? picked.filter((x) => x !== sp) : [...picked, sp];
                    const ordered = seasonPartsOrdered(next);
                    onChange({ seasonParts: ordered, season: seasonLabelOf(value.seasonYear, ordered) });
                  }}
                />
                <span>{t(`st.season${sp}`)}</span>
              </label>
            );
          })}
        </div>
      </Field>

      <Field label={t('st.pfPartner')}>
        {readOnly ? (
          <input value={partnerName || '—'} readOnly disabled />
        ) : (
          <PartnerPicker
            partners={partners}
            value={value.partnerId || ''}
            canCreate={canAddPartner}
            onChange={(partnerId) => onChange({ partnerId })}
            onAddPartner={onAddPartner}
          />
        )}
      </Field>

      <Field label={t('st.pfKeyword')}>
        <input value={value.keyword || ''} onChange={set('keyword')} disabled={readOnly} placeholder={t('stx.keywordPh')} />
      </Field>

      <Field label={t('st.pfTargetLaunch')} required={!readOnly}>
        <MonthField
          value={value.targetLaunchMonth || ''}
          readOnly={readOnly}
          onChange={(v) => onChange({ targetLaunchMonth: v })}
        />
        {/* Jay, 01/11 (PRJ-00202 saved 27SS with 05/2026): said here, beside the month, as it is picked — not only as
            a refusal at Save. projectLaunchCheck.js holds the rule; the form's save checks the same answer. */}
        {/* READ ONLY (the approver's view): the same two questions about the project as it stands, said and not asked —
            Jay's point 5, "so the approver sees the warning". */}
        {readOnly && launchBefore !== undefined && (() => {
          const lf = launchFlags(value);
          if (!lf.mismatch) return null;
          return (
            <p className="pf-launch" data-launch-warn>
              {t('stx.launchMismatch', {
                month: monthToDisplay(value.targetLaunchMonth),
                season: seasonLabelOf(value.seasonYear, value.seasonParts) || value.season || '',
                from: monthToDisplay(lf.window.from), to: monthToDisplay(lf.window.to),
              })}
            </p>
          );
        })()}
        {!readOnly && launchBefore !== undefined && (() => {
          const lc = launchCheck(value, launchBefore);
          if (lc.past) return <p className="pf-launch err" data-launch-past>{t('stx.launchPast', { month: monthToDisplay(value.targetLaunchMonth) })}</p>;
          if (!lc.mismatch) return null;
          return (
            <div className="pf-launch" data-launch-warn>
              {t('stx.launchMismatch', {
                month: monthToDisplay(value.targetLaunchMonth),
                season: seasonLabelOf(value.seasonYear, value.seasonParts) || value.season || '',
                from: monthToDisplay(lc.window.from), to: monthToDisplay(lc.window.to),
              })}
              <label>
                <input type="checkbox" checked={value.launchConfirmed === lc.signature}
                  onChange={(e) => onChange({ launchConfirmed: e.target.checked ? lc.signature : '' })} />
                {t('stx.launchConfirmBox')}
              </label>
            </div>
          );
        })()}
      </Field>

      {/* Young via Jay, 2026-08-19: "The CEO only decides go / no-go, so he needs at least one line
          to read." Mandatory for new projects; the ones that existed when the rule landed carry
          purpose_required = 0 and are never blocked. The 300-character cap is his, and it is enforced
          on the server too — maxLength alone stops a person, not a direct API call. */}
      <div className="ff full">
        <span>
          {t('st.pfPurpose')}{purposeRequired && !readOnly ? <span className="req">*</span> : null}
          <small className="pf-counter">{(value.projectPurpose || '').length}/300</small>
        </span>
        <textarea
          rows={2}
          maxLength={300}
          value={value.projectPurpose || ''}
          disabled={readOnly}
          onChange={(e) => onChange({ projectPurpose: e.target.value })}
          placeholder={t('st.pfPurposePh')}
        />
        {/* Kept out of the placeholder on purpose: a placeholder disappears the moment someone
            starts typing, and this is the part they need while they are typing. */}
        <small className="pf-hint">{t('st.pfPurposeHint')}</small>
      </div>

      {/* Goes out with the approval request mail, which already prints a Note line (Max, 2026-08-13).
          Editable everywhere now: it used to exist only on the New Project form, so a note could be
          written once and never corrected. Kept alongside Purpose at Max's call (2026-08-20): the
          manager still wants a place for anything else the CEO should weigh. */}
      {showNote && (
        <div className="ff full">
          <span>{t('st.pfNote')}</span>
          <textarea
            rows={2}
            value={value.approvalNote || ''}
            disabled={readOnly}
            onChange={(e) => onChange({ approvalNote: e.target.value })}
            placeholder={t('stx.notePh')}
          />
        </div>
      )}

      <div className="pt-name-preview" style={{ gridColumn: '1 / -1' }}>
        <span>{t('st.pfProjectName')}</span>
        <strong>{namePreview || value.groupName || nameFallback || t('stx.fillSeasonPartner')}</strong>
        <small>{t('st.pfNameHint')}</small>
      </div>

      {showCase && (
        <Field label={t('st.pfCase')}>
          <select
            value={value.projectCase || ''}
            disabled={readOnly}
            onChange={(e) => { const v = e.target.value; onChange({ projectCase: v, entity: v === 'B' ? value.entity : '' }); }}
          >
            <option value="">{t('st.pfSelectCase')}</option>
            {PROJECT_CASE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{CASE_LABEL_KEYS[o.value] ? t(CASE_LABEL_KEYS[o.value]) : o.label}</option>)}
          </select>
        </Field>
      )}

      {showCase && value.projectCase === 'B' && (
        <Field label={t('st.pfEntity')}>
          <select value={value.entity || ''} onChange={set('entity')} disabled={readOnly}>
            <option value="">{t('st.pfSelectEntity')}</option>
            {PROJECT_ENTITY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
      )}
    </div>
  );
}

function NewProjectModal({ group, filterOptions, trackerConfig, canCreate, rounds, saving, errors, onChangeGroup, onChangeSku, onAddSku, onRemoveSku, onDuplicateSku, onClose, onSave, onAddPartner, onAddSalesRep, canAddSalesRep, photos = [], onChangePhotos, onOpenMove, onRemoveMoveIn }) {
  const { t } = useTranslation();
  // Esc = the same as the × button. Blocked mid-save so a slow POST cannot lose the form.
  useEscapeClose(onClose, !saving);
  // Categories come from the config (Team 1 first, then Team 2 — Jay item 6) and fall back to the
  // tracker's own list if the config call has not landed yet.
  const categoryOptions = (trackerConfig?.categories || []).length
    ? trackerConfig.categories.map((c) => c.name)
    : filterOptions.projectCategories;
  const teams = trackerConfig?.teams || [];
  const partners = trackerConfig?.partners || [];
  const salesReps = trackerConfig?.salesReps || [];
  const seasons = useMemo(() => seasonOptions(new Date().getFullYear()), []);
  const baseSamples = useMemo(() => {
    const map = new Map();
    rounds.forEach((round) => { if (round.baseId && round.styleItem) map.set(round.baseId, round.styleItem); });
    return [...map.entries()].map(([baseId, styleItem]) => ({ baseId, styleItem }));
  }, [rounds]);

  return (
    <div className="st-modal-overlay" role="dialog" aria-modal="true">
      <div className="st-modal">
        <div className="st-modal-head">
          <h3>{t('st.pfNewProject')}</h3>
          <button type="button" className="st-modal-close" onClick={onClose} aria-label={t('st.viewClose')}>&times;</button>
        </div>
        <div className="st-modal-body">

          <div className="own-section own-hevn">
            <div className="own-hd own-hd-sub">
              <span>{t('st.pfProjectInfo')}</span>
              <small>{t('st.pfProjectInfoHint')}</small>
            </div>
            <div className="own-body">
              <ProjectInfoFields
                value={group}
                onChange={onChangeGroup}
                launchBefore={null}
                purposeRequired
                teams={teams}
                partners={partners}
                salesReps={salesReps}
                categories={categoryOptions}
                seasons={seasons}
                canAddPartner={canCreate}
                onAddPartner={onAddPartner}
                canAddSalesRep={canAddSalesRep}
                onAddSalesRep={onAddSalesRep}
              />
            </div>
          </div>

          <div className="own-section own-both">
            <div className="own-hd" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{t('st.pfSkuInfo')}</span>
              {/* Beside Add SKU: approved SKUs of another project of the team, moved in on Save. */}
              <span className="own-hd-actions">
                <MoveSkusButton me={trackerConfig?.me} teamKey={group.teamKey} onOpen={onOpenMove} />
                <button type="button" className="btn btn-ghost btn-sm" onClick={onAddSku}>{t('st.pfAddSku')}</button>
              </span>
            </div>
            <div className="own-body">
              <div className="pt-help" style={{ marginBottom: 10 }}>
                {t('st.pfSkuHint')}
              </div>
              {!group.skus.length && !(group.moveIns || []).length && (
                <div className="pt-help pt-ticket-empty">
                  {t('stx.noSkuYet')}
                </div>
              )}
              <MoveInList items={group.moveIns || []} teamKey={group.teamKey} onRemove={onRemoveMoveIn} />
              <div className="pt-sku-list">
                {group.skus.map((sku, idx) => (
                  <SkuCard
                    key={idx}
                    index={idx}
                    sku={sku}
                    filterOptions={filterOptions}
                    defaultOpen={idx === group.skus.length - 1}
                    onChange={(patch) => onChangeSku(idx, patch)}
                    onRemove={() => onRemoveSku(idx)}
                    onDuplicate={() => onDuplicateSku(idx)}
                  />
                ))}
              </div>
              {/* Young wants to see the sample before he approves, so photos are attached while the
                  project is being created. They are held in the page and uploaded the moment the
                  PRJ-ID exists — see uploadPendingPhotos in the save handler. */}
              <div style={{ marginTop: 14, borderTop: '1px solid #eef2f7', paddingTop: 12 }}>
                {/* The group's own box is gone — photos are attached on each SKU above, and a
                    second box down here would ask the same question with no item to answer it. */}
                <div className="pt-help">{t('st.pfPhotosOnSku')}</div>
              </div>
            </div>
          </div>

          {/* The Tickets section was removed here on purpose (Jay item 3): a ticket can only be
              issued on an APPROVED project, so it cannot be part of creating one. Tickets are
              raised from Ticket Station once the CEO has approved the project. */}
          {/* Since the Submit flow (2026-08-21) nothing is emailed on save — the manager decides when
              the approver is asked. The old wording promised an automatic request, so a manager could
              create a project, read "emailed automatically" and walk away leaving it unsent. */}
          <div className="pt-approval-note">
            <strong>{t('st.pfApprovalNoteHead')}</strong> {t('st.pfApprovalNoteBody')}
          </div>
        </div>
        {!!errors.length && (
          <div className="st-alert error st-alert-foot">{errors.map((message) => <div key={message}>{errorText(message)}</div>)}</div>
        )}
        <div className="st-modal-foot st-form-actions">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>{t('st.pfCancel')}</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>{saving ? t('st.pfSaving') : t('st.pfAddProject')}</button>
        </div>
      </div>
      <datalist id="pt-category-options">{categoryOptions.map((option) => <option key={option} value={option} />)}</datalist>
      <datalist id="pt-product-type-options">{filterOptions.projectProductTypes.map((option) => <option key={option} value={option} />)}</datalist>
      <datalist id="pt-assignee-options">{filterOptions.assignees.map((option) => <option key={option} value={option} />)}</datalist>
      <datalist id="pt-allocation-options">{filterOptions.allocations.map((option) => <option key={option} value={option} />)}</datalist>
      <datalist id="pt-linked-sample-options">{baseSamples.map((sample) => <option key={sample.baseId} value={sample.baseId}>{sample.styleItem}</option>)}</datalist>
    </div>
  );
}

function TaskModal({ mode, form, taskTypes, projectGroups = [], projectSkus = [], takenSkus = {}, projectDisplay, saving, errors, onChange, onClose, onSave, onDelete, onSendEmail }) {
  const priorityOptions = usePortalList('taskPriorities', TASK_PRIORITY_OPTIONS);
  const statusOptions = usePortalList('taskStatuses', TASK_STATUS_OPTIONS);
  const { t, i18n } = useTranslation();
  const { requestClose, prompt: discardPrompt } = useDiscardGuard(form, onClose, { active: !saving });
  const set = (field) => (event) => onChange({ [field]: event.target.value });
  // View: the same form locked, one Close (Jay, 2026-10-16 — see RoundModal). Open to anybody who
  // sees the row; Edit keeps its own rights.
  const viewing = mode === 'view';
  const title = viewing ? `${t('st.tkTitleView')} — ${form.taskId}`
    : mode === 'edit' ? `${t('st.tkTitleEdit')} — ${form.taskId}` : mode === 'duplicate' ? t('st.tkTitleDup') : t('st.tkTitleNew');
  const projectInputValue = form.projectId ? projectDisplay(form.projectId, '') : '';
  // Jay's v3 codes belong to a team and, for Team 2, to a case. The list narrows to the project's
  // own team and case; when the project carries neither — 123 of the migrated ones do not — nothing
  // is hidden, because an empty dropdown would simply block the ticket (Max, 2026-08-14).
  const selectedGroup = projectGroups.find((g) => g.id === form.projectId);
  const groupCase = selectedGroup ? selectedGroup.projectCase : '';
  const groupTeam = selectedGroup ? selectedGroup.teamKey : '';
  const groupCategory = selectedGroup ? selectedGroup.category : '';
  // Stored as "Inline;Special Inline" — semicolons, because one of the categories is "OEM/ODM".
  const scopeCovers = (scope, category) => String(scope || '').split(';')
    .map((v) => v.trim().toLowerCase())
    .includes(String(category || '').trim().toLowerCase());
  const teamCaseTypes = taskTypes.filter((t) => (
    (!groupTeam || !t.teamKey || t.teamKey === groupTeam)
    && (!groupCase || !t.caseName || t.caseName === groupCase)
  ));
  const byCategory = groupCategory
    ? teamCaseTypes.filter((t) => !t.categoryScope || scopeCovers(t.categoryScope, groupCategory))
    : teamCaseTypes;
  const caseTypes = byCategory.length ? byCategory : teamCaseTypes;
  /* THE WHOLE MASTER LIST, not the codes this project happens to reach — Max, 2026-09-15:
     "Receiving Team phải đồng nhất với Receiving Team bên ticket code... một bên 6 cái một bên 7 cái".
     Built from `caseTypes` it showed whatever survived the team / case / category narrowing, so the
     same field offered six teams here and seven on Ticket Codes and neither number explained itself.
     The box is a FILTER for the code list below it, so offering a team with no code left simply
     empties that list — which says "nothing here for this team" far more plainly than hiding it. */
  const receivingTeams = Array.from(new Set(taskTypes.map((t) => t.receivingTeam).filter(Boolean))).sort();
  const codeOptions = caseTypes.filter((t) => (!form.department || t.receivingTeam === form.department) || t.code === form.taskType);
  // Grouped by phase, phases in the order Jay numbered them (P1 → P6).
  // Phase and code names in the reader's language where the code carries it (pickL10n; English otherwise).
  const codeLang = i18n.language;
  const codeGroups = Object.values(codeOptions.reduce((acc, opt) => {
    const key = pickL10n(opt, 'phase', codeLang) || '—';
    if (!acc[key]) acc[key] = { phase: key, order: opt.phaseOrder || 999, items: [] };
    acc[key].items.push(opt);
    return acc;
  }, {})).sort((a, b) => a.order - b.order || a.phase.localeCompare(b.phase));
  // Same rule as the in-project ticket card: a ticket is raised as Pending, and only whoever owns
  // it moves it afterwards. 'duplicate' counts as new — it produces a fresh ticket.
  const formStatusLocked = mode !== 'edit' || form.canEdit === false;
  /* Same gate as the project dialog: a new ticket needs the project to have an owner. An existing
     one is left alone, so a ticket already raised on one of the ownerless projects stays editable. */
  const needsOwner = mode !== 'edit' && !!selectedGroup && !selectedGroup.ownerUserId;
  /* The request form, exactly as the card in Project Tracker renders it. Ticket Station used to show
     only the wrapper — code, name, priority, dates — and none of what the ticket actually ASKS for
     (Max, 2026-09-05: "nội dung trong modal edit ticket trong ticket station nó ko hiện giống với
     khi tạo ticket trong project tracker"). The answers were never lost, only invisible: the server
     leaves them alone when a save carries no `fields`. One form, one place it is defined. */
  const requestForm = useTicketForm(form.taskType);
  const missing = missingRequired(requestForm, form.fields || {});
  return (
    <div className="st-modal-overlay" role="dialog" aria-modal="true">
      {/* Portals to <body>; only present once the form is dirty and the user tries to leave. */}
      {discardPrompt}
      {/* One width, the same 1100 the project card gives this form. It was 760, which hid 1098px of
          A-2's 1784px table — 39% of the form on screen. Sizing it only when a code is picked was
          worse than either width: New Ticket opens with no code, so the modal jumped from 760 to
          1100 under the cursor the moment one was chosen (Max, 2026-09-05: "sửa cho cả modal này
          nữa"). Every one of the 41 codes has a request form, so the narrow case was a fraction of
          a second on the way to the wide one. */}
      <div className="st-modal" style={{ width: 1100 }}>
        <div className="st-modal-head">
          <h3>{title}{viewing && <span className="pt-view-tag">{t('st.ptViewTag')}</span>}</h3>
          <button type="button" className="st-modal-close" onClick={viewing ? onClose : requestClose} aria-label={t('st.viewClose')}>&times;</button>
        </div>
        <div className="st-modal-body">
          <fieldset className="pt-ro-fieldset" disabled={viewing}>
          <div className="task-edit-grid">
            <Field label={t('st.tkColTicketId')}>
              <input value={form.taskId || ''} disabled placeholder={t('st.tkAuto')} />
            </Field>
            <Field label={t('st.tkProjectGroup')} required>
              <input
                list="te-project-options"
                defaultValue={projectInputValue}
                placeholder={t('st.tkSearchGroup')}
                onChange={(event) => {
                  const raw = event.target.value.trim();
                  const match = projectGroups.find((g) => projectDisplay(g.id, '') === raw || g.id === raw);
                  const m = raw.match(/^(PRJ-\d+)/i);
                  onChange({ projectId: match ? match.id : (m ? m[1].toUpperCase() : raw) });
                }}
              />
            </Field>
            <Field label={t('st.pfCase')}>
              <input className="pt-ro-field" value={groupCase || ''} readOnly placeholder={t('st.tkAutoFromGroup')} />
            </Field>
            <Field label={t('st.tkColTeam')}>
              <select value={form.department || ''} onChange={(e) => onChange({ department: e.target.value })}>
                <option value="">{t('st.tkSelectTeam')}</option>
                {receivingTeams.map((team) => <option key={team} value={team}>{team}</option>)}
              </select>
            </Field>
            {needsOwner && (
              <div className="ff full">
                <div className="tkf-missing">{t('st.pfNeedOwner')}</div>
              </div>
            )}
            <div className="ff full">
              <span>{t('st.tkTicketCode')}<span className="req">*</span></span>
              <select value={form.taskType || ''} onChange={set('taskType')}>
                <option value="">{t('st.tkSelectCode')}</option>
                {codeGroups.map((g) => (
                  <optgroup key={g.phase} label={g.phase}>
                    {g.items.map((opt) => <option key={opt.code} value={opt.code}>{opt.code} - {pickL10n(opt, 'name', codeLang)}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            <div className="ff full">
              <span>{t('st.tkTicketName')}</span>
              <input className="pt-ro-field" value={form.name || ''} readOnly placeholder={t('st.tkSelectTypeFirst')} />
            </div>
            <div className="ff full">
              <span>{t('st.tkDescription')}</span>
              <textarea rows={3} value={form.description || ''} onChange={set('description')} placeholder={t('st.tkDescriptionPh')} />
            </div>
            <Field label={t('st.tkColRequester')}>
              {/* LOCKED — Max, 2026-09-05: "do trường requester bạn đang cho điều chỉnh xóa á. Bạn
                  khóa lại giúp tui nhé". It was a free text box, and the mail is rebuilt from it on
                  every save, so clearing it rewrote "Requester: Christine" as "Requester: -" and
                  that is the version TSK-0016 was sent with. Who raised a ticket is a fact the
                  portal already recorded, not an opinion to type: it reads created_by when the
                  column itself is empty, which is the case for every ticket raised from a project
                  card before today. The project card's own copy of this field was already read-only. */}
              <input
                className="pt-ro-field"
                value={form.assignee || form.createdBy || ''}
                readOnly
                placeholder={t('st.tkRequesterPh')}
              />
            </Field>
            <Field label={t('st.tkGroupEmail')}>
              {/* prefilled from the ticket code but editable - the recipient sometimes has to be overridden */}
              <input type="email" value={form.email || ''} onChange={set('email')} placeholder={t('st.tkGroupEmailPh')} />
            </Field>
            <Field label={t('st.tkColPriority')}>
              <select value={form.priority || 'Medium'} onChange={set('priority')}>
                {priorityOptions.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label={t('st.tkDueDate')}>
              <input type="date" value={form.dueDate || ''} onChange={set('dueDate')} />
            </Field>
            <Field label={t('st.colStatus')}>
              <select
                value={formStatusLocked ? 'Pending' : (form.status || 'Pending')}
                onChange={set('status')}
                disabled={formStatusLocked}
              >
                {statusOptions.map((s) => <option key={s} value={s}>{taskStatusLabel(s)}</option>)}
              </select>
            </Field>
            <Field label={t('st.tkAttachment')}>
              <input value={form.attachmentUrl || ''} onChange={set('attachmentUrl')} placeholder={t('st.tkAttachmentPh')} />
            </Field>
            {/* Files, not a link — they ride out ON the ticket email (Max, 2026-09-07). Full width:
                a list of file names beside a one-line input reads as a footnote to it. */}
            <div className="ff full">
              <span>{t('st.tkFiles')}</span>
              {/* The draft key while the ticket is being written; the real id once it has one. */}
              <TicketFiles taskId={form.taskId || form.fileDraftKey} disabled={viewing} />
            </div>
            {/* After Attachment Link, not before it. Status and Attachment Link are a pair in this
                grid, and dropping a full-width block between them left Status alone with an empty
                cell beside it. */}
            {!!requestForm && (
              /* NOT `ff full`. `.ff input` carries min-height: 36px, and a minimum beats a height —
                 wrapping the table in it stretched every 26px text box and turned the 13px tick into
                 a 13x36 sliver that Chrome drew a hover ring around (Max, 2026-09-05: "cái hover của
                 tickbox bị kì"). This class only spans the grid. */
              <div className="tkf-slot">
                <TicketFormFields
                  form={requestForm}
                  values={form.fields || {}}
                  skus={projectSkus}
                  takenSkus={takenSkus}
                  onChange={(fields) => onChange({ fields })}
                />
                {!!missing.length && (
                  <div className="tkf-missing">
                    {t('stx.stillNeeded', { list: missing.join(', ') })}
                  </div>
                )}
              </div>
            )}
            <div className="ff full">
              <span>{t('st.tkColNote')}</span>
              <RichText value={form.note || ''} onChange={(html) => onChange({ note: html })}
                placeholder={t('st.tkNotePh')} minHeight={130} />
            </div>
            <div className="ff full">
              <div className="task-email-edit">
                <div className="pt-task-email-head">
                  {/* Jay's rule, the same one the card enforces: an incomplete request must not be
                      sendable, because the mail is what the receiving team works from. */}
                  <label>
                    <input
                      type="checkbox"
                      checked={!!form.sendEmail && !missing.length}
                      disabled={!!missing.length}
                      onChange={(e) => onChange({ sendEmail: e.target.checked })}
                    /> {t('st.tkSendAfterSave')}
                  </label>
                  {/* Down to the second, in Vietnam time — Max, 2026-09-07. "Sent 2026-09-07" on a
                      ticket that can be re-sent says nothing about which send you are looking at. */}
                  <span className={`pt-email-status ${form.sentAt ? 'sent' : ''}`}>{form.sentAt ? `${t('st.tkSent')} ${fmtDateTimeSecVN(form.sentAt, '')}` : t('st.tkNotSent')}</span>
                </div>
                <div className="task-email-fields">
                  <div>
                    <label>{t('st.tkEmailSubject')} <span className="pt-readonly-badge">{t('st.tkSystemGenerated')}</span></label>
                    <input value={form.emailSubject || ''} readOnly placeholder={t('stx.subjectPh')} />
                  </div>
                  <div>
                    <label>{t('st.tkEmailCc')}</label>
                    <input type="email" value={form.emailCc || ''} onChange={set('emailCc')} placeholder="cc@example.com" />
                  </div>
                </div>
                <div className="task-email-message">
                  <label>{t('st.tkEmailMessage')} <span className="pt-readonly-badge">{t('st.tkReadOnly')}</span></label>
                  {/* The html half — what actually lands in an inbox, with the SKU list as a real
                      table. This showed the text half, which is the fallback for a client that
                      refuses html, so a ten-column request read as a wall of "a / b / c"; the card
                      was fixed on 2026-09-04 and this preview was left behind. Safe to render:
                      every value and label is escaped where the html is built — except the note,
                      which is markup on purpose and is filtered by taskNoteSanitize on the way into
                      the database, before it can reach anyone's screen or inbox. */}
                  <div
                    className="pt-email-preview"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: form.emailHtml || '' }}
                  />
                </div>
                <div className="pt-email-actions">
                  <span className="pt-generated-note">{t('st.tkGeneratedNote')}</span>
                  {/* Send only while the ticket has never gone out; after that it is Remind, the
                      same rule the Action column has followed since 2026-09-14 — Max, 2026-09-16.
                      The two disagreeing was the trap: the list said Remind, the dialog still said
                      Send, so the dialog was the one place a second copy of the original request
                      could be posted as though it were new. */}
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={onSendEmail}
                    disabled={saving || mode !== 'edit'}
                    title={form.sentAt ? t('st.tkRemindHint') : t('st.tkSendHint')}
                  >
                    {form.sentAt ? t('st.tkRemind') : t('st.tkSendEmail')}
                  </button>
                </div>
              </div>
            </div>
          </div>
          </fieldset>
          {/* Questions and answers on the ticket (Young, 2026-10-27) — outside the read-only fieldset on purpose: asking
              is not editing, so a View-only reader and a closed ticket can still use it. Only a ticket that exists. */}
          {!!form.taskId && <TicketMessages taskId={form.taskId} />}
        </div>
        {!!errors.length && (
          <div className="st-alert error st-alert-foot">{errors.map((message) => <div key={message}>{errorText(message)}</div>)}</div>
        )}
        {viewing ? (
          <div className="st-modal-foot st-form-actions">
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{t('st.viewClose')}</button>
          </div>
        ) : (
        <div className="st-modal-foot st-form-actions">
          {mode === 'edit' && (
            <button type="button" className="btn btn-ghost btn-sm btn-danger" onClick={() => onDelete(form.taskId)} disabled={saving}>{t('st.tkDeleteTicket')}</button>
          )}
          <button type="button" className="btn btn-ghost btn-sm" onClick={requestClose} disabled={saving}>{t('st.pfCancel')}</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>{saving ? t('st.rmSaving') : t('st.tkSaveTicket')}</button>
        </div>
        )}
      </div>
      <datalist id="te-project-options">{projectGroups.map((g) => <option key={g.id} value={projectDisplay(g.id, '')} />)}</datalist>
    </div>
  );
}

function SampleTracker() {
  // Chrome only — table VALUES are never translated, they are the data people typed.
  const { t, i18n } = useTranslation();
  // The AI's few words on an Active project come in all four languages (PROMPT_V 3, 2026-10-21); a summary written
  // before that carries English and Korean only, and reads English in the other two until it is written again.
  const aiLang = langOf(i18n.language);
  // Column definitions are module-level constants, so their labels are stored as i18n keys and resolved here.
  // Anything that is not a key (the process-stage names, which come from the API) passes through unchanged.
  const colLabel = (label) => (typeof label === 'string' && label.startsWith('st.') ? t(label) : label);
  const location = useLocation();
  const navigate = useNavigate();
  const { name: userName, functions: authFunctions } = useAuth();
  // Max, 2026-08-19: only an Admin gets the Delete action on a project row. Taken from the server's
  // own answer (role_management = edit) rather than the role name in the header, so the button can
  // never appear for someone the endpoint would refuse.
  // Read the grant from useAuth, not from the /config payload: useAuth narrows to the role picked in
  // the header's role switcher, while the API always answers with the union of every role the user
  // holds. Using the API answer here would leave the button visible while previewing as another role.
  // The backend still enforces the real grant — this only decides what is shown.
  const canCreateProject = Array.isArray(authFunctions) && authFunctions.includes('project_tracker_create');
  /* Read from the URL in the INITIAL state, not in an effect afterwards.
     Defaulting to 'dash' mounted the Dashboard for one render on EVERY landing — long enough for it
     to fire /dashboard-kpis, which is ~700 ms of server work to return 1.9 KB — and then unmounted
     it again. Measured on production: the call went out on ?tab=tickets, ?tab=sample and
     ?tab=project alike, so every tab paid for a screen nobody was looking at (Max, 2026-09-09:
     "khi chuyển tab thì sẽ bị lag").
     The effect below still runs, because the tab has to follow a URL that changes later. */
  const [activeTab, setActiveTab] = useState(() => sampleTabFromSearch(location.search));
  const [sampleView, setSampleView] = useState('all');
  const [rounds, setRounds] = useState([]);
  const [projects, setProjects] = useState([]);
  /* An Active project's AI-written status and the projects still being written (projectAiStatus.js), both sent WITH the
     rows by the bootstrap — Max, 2026-10-21: on a refresh the column read "Active" for a second before the words came. */
  const [aiStatus, setAiStatus] = useState({});
  const [aiPending, setAiPending] = useState(() => new Set());
  // Group list is kept in sync for future use; the grid reads group fields off each project row.
  const [groups, setGroups] = useState([]);   // project groups incl. ones with no SKU yet
  /* the approved-baseline side of each project (projectChange.js): delayed / open date change, read by the group row */
  const groupMetaById = useMemo(() => new Map(groups.map((g) => [g.groupId, g])), [groups]);
  const [datesFor, setDatesFor] = useState(null);   // the project whose Dates window is open
  /* Jay 28/10: "a way to see which projects still have no confirmed original baseline, so owners can see their own gaps" */
  const [unconfirmedOnly, setUnconfirmedOnly] = useState(false);
  const [lists, setLists] = useState({});
  const [ownOnly, setOwnOnly] = useState(null);   // { employeeIds } when this person may change only their own rounds (Jay 30/10)
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [expanded, setExpanded] = useState({});
  const [sampleSort, setSampleSort] = useState({ key: '', dir: 'asc' });
  const [sampleFilters, setSampleFilters] = useState(SAMPLE_FILTERS_DEFAULT);
  // 'group' = one row per Base ID (expandable) · 'rounds' = flat list of round IDs only.
  const [sampleRowMode, setSampleRowMode] = useState('group');
  const [projectFilters, setProjectFilters] = useState({ status: [], category: [], assigned: [], designer: [], allocation: [], team: [], owner: [], salesRep: [], search: '', groupState: '', flag: '' });
  // Jay item 5: managers switch between "my team's projects" and "who is carrying what".
  const [projectMemberView, setProjectMemberView] = useState(false);
  // Photos picked while the New Project form is open. They cannot be uploaded yet — the project has
  // no PRJ-ID until it saves — so they wait here and go up right after creation (Max, 2026-08-19).
  const [newProjectPhotos, setNewProjectPhotos] = useState([]);
  // Photos held for a Base ID that does not exist yet. Sent WITH the round, not after it: a new Base
  // ID that ends up with no photo would break Young's rule the moment it was introduced.
  const [newRoundPhotos, setNewRoundPhotos] = useState([]);
  /* Everything the person is about to lose by deleting a project — SKU count, the tickets that get
     cancelled, and the reason that goes into the cancellation email. One dialog, no browser popup. */
  const [deleteGroupPrompt, setDeleteGroupPrompt] = useState(null); // { group, loading, skus, tickets }
  const [deleteReason, setDeleteReason] = useState('');
  // Deleting ONE ticket: same shape of question. The reason is required only when the ticket was
  // actually emailed — a ticket that never left the portal promised nothing to anybody.
  const [deleteTicketOne, setDeleteTicketOne] = useState(null); // { taskId, name, emailed, to }
  const [deleteTicketOneReason, setDeleteTicketOneReason] = useState('');
  const [baseModal, setBaseModal] = useState(null);   // { baseId, styleItem, rounds }
  const [baseStyleDraft, setBaseStyleDraft] = useState('');
  useEffect(() => { setBaseStyleDraft(baseModal?.styleItem || ''); }, [baseModal]);
  const [projectSort, setProjectSort] = useState({ key: '', dir: 'asc' });
  const [projectColumnVisible, setProjectColumnVisible] = useState(loadProjectColumnVisible);
  const [projectColumnMenuOpen, setProjectColumnMenuOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({}); // group key -> expanded?
  const [groupModal, setGroupModal] = useState(null); // { groupId, groupName, category, projectCase, entity }
  const [groupNewSkus, setGroupNewSkus] = useState([]);   // SKUs added inline while editing a group
  // Approved SKUs of another project picked to move into the group being edited, moved on Save.
  const [groupMoveIns, setGroupMoveIns] = useState([]);
  const [movePicker, setMovePicker] = useState(null);    // { into: 'new' | 'group' }
  const [groupSkuDrafts, setGroupSkuDrafts] = useState({}); // project id -> editable SKU draft inside Edit Project Group
  /* Material lists typed inside a form, held until that form's Save. They are not part of the group
     PUT — own table, own endpoint — but Max wants one button, so each form carries its own store and
     writes it alongside the SKUs. One per form: the SKU modal can be opened from the tracker while a
     group modal is closed, and neither should ever flush the other's typing. */
  const groupMaterials = useSkuMaterialDrafts();
  const skuModalMaterials = useSkuMaterialDrafts();
  const [groupSkuOpen, setGroupSkuOpen] = useState({}); // project id -> expanded editor in Edit Project Group
  const [groupNewTickets, setGroupNewTickets] = useState([]); // tickets staged inline, created on Save Group
  /* Edits made to tickets that ALREADY exist, expanded inside the group modal. Seeded from the
     ticket the first time it is touched, exactly like groupSkuDrafts — an untouched ticket is not in
     here at all, so Save Group does not rewrite every ticket on the project. */
  const [groupTicketDrafts, setGroupTicketDrafts] = useState({});
  const [groupTicketTeam, setGroupTicketTeam] = useState('');
  const [groupSaving, setGroupSaving] = useState(false);
  const [groupError, setGroupError] = useState('');
  // Edit Project Group lives here rather than in a modal component, so it snapshots itself by hand
  // when it opens instead of on mount like the other forms.
  const groupPristine = useRef('');
  const [confirmGroupDiscard, setConfirmGroupDiscard] = useState(false);
  const [modal, setModal] = useState(null); // { mode: 'new' | 'edit', form: {...} }
  const [modalErrors, setModalErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [projectModal, setProjectModal] = useState(null); // { mode: 'edit', form: {...} } — single project (edit/duplicate)
  const [projectModalErrors, setProjectModalErrors] = useState([]);
  const [projectSaving, setProjectSaving] = useState(false);
  const [inlineProject, setInlineProject] = useState(null); // the SKU being edited in its own form
  // one record's history drawer (CatchyCard.jsx RecordHistory): { entity: 'sku' | 'project' | 'sample_round', id }
  const [historyOf, setHistoryOf] = useState(null);
  const [inlineProjectView, setInlineProjectView] = useState(false); // that form opened by View: nothing to save
  const [inlineProjectError, setInlineProjectError] = useState('');
  const [newProject, setNewProject] = useState(null); // multi-SKU New Project group form
  const [newProjectErrors, setNewProjectErrors] = useState([]);
  const [newProjectPristine, setNewProjectPristine] = useState('');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [duplicateWarn, setDuplicateWarn] = useState(null); // { matches: [{groupId, groupName}] }
  const [tasks, setTasks] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [tasksLoaded, setTasksLoaded] = useState(false);
  const [taskFilters, setTaskFilters] = useState({ status: [], requester: [], project: [], dept: [], priority: [], team: [], search: '' });
  const [taskKpiFilter, setTaskKpiFilter] = useState('all');
  const [taskSort, setTaskSort] = useState({ key: '', dir: 'asc' });
  const [taskModal, setTaskModal] = useState(null); // { mode, form }
  /* the item open in a dialog, for the floating Catchy (Jay 30/10, pageContext.js): "this sample / this SKU / this project" */
  const TAB_SCREEN = { sample: 'Sample Tracker', samples: 'Sample Tracker', projects: 'Project Tracker', project: 'Project Tracker', tickets: 'Ticket Station', dash: 'Development Dashboard' };
  usePageItem((() => {
    const screen = TAB_SCREEN[activeTab] || 'Sample Tracker';
    if (modal?.form?.sampleId && modal.mode !== 'new') return { kind: 'sample_round', id: modal.form.sampleId, label: modal.form.styleItem || '', screen };
    if (baseModal?.baseId) return { kind: 'sample', id: baseModal.baseId, label: baseModal.styleItem || '', screen };
    if (inlineProject?.id) return { kind: 'sku', id: inlineProject.id, label: inlineProject.subItem || inlineProject.projectItem || '', screen };
    if (projectModal?.form?.id) return { kind: 'sku', id: projectModal.form.id, label: projectModal.form.subItem || '', screen };
    if (groupModal?.groupId) return { kind: 'project', id: groupModal.groupId, label: groupModal.groupName || '', screen };
    if (taskModal?.form?.taskId) return { kind: 'ticket', id: taskModal.form.taskId, label: taskModal.form.name || '', screen };
    return null;
  })());
  const [taskModalErrors, setTaskModalErrors] = useState([]);
  const [taskSaving, setTaskSaving] = useState(false);
  // Ticket Station has a second view: the editable master of ticket codes (Jay item 9).
  const [ticketCodesOpen, setTicketCodesOpen] = useState(false);
  // Teams, partners, ordered categories, approval settings and who the current user is in Sales OP
  // terms. Everything team-scoped on this screen reads from here.
  const [trackerConfig, setTrackerConfig] = useState(null);

  const loadTrackerConfig = async () => {
    try {
      const res = await fetch(`${PROJECT_API_BASE}/config`, { credentials: 'include' });
      if (!res.ok) return null;
      const data = await res.json();
      setTrackerConfig(data);
      return data;
    } catch {
      return null;
    }
  };

  const loadData = async () => {
    setError('');
    try {
      const response = await fetch(`${API_BASE}/bootstrap`, { credentials: 'include' });
      if (!response.ok) throw new Error(portalI18n.t('stx.loadFailed', { status: response.status }));
      const data = await response.json();
      setRounds(data.rounds || []);
      if (data.aiStatus) {
        setAiStatus(data.aiStatus.items || {});
        setAiPending(new Set(data.aiStatus.pendingKeys || []));
      }
      setProjects(data.projects || []);
      setGroups(data.groups || []);
      setLists(data.lists || {});
      setOwnOnly(data.ownOnly || null);
    } catch (err) {
      setError(err.message);
    }
  };

  /* The seventeen request forms. Fetched once, beside the dropdown lists: a ticket card reads its
     code's form from here and so does the email builder. */
  const [ticketForms, setTicketForms] = useState(null);
  const loadTicketForms = async () => {
    if (!TICKET_FORMS_ON) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/tickets/forms`, { credentials: 'include' });
      if (response.ok) setTicketForms(await response.json());
    } catch (err) { /* the ticket card falls back to the old body when there is no form */ }
  };

  useEffect(() => {
    loadData();
    loadTrackerConfig();
    loadTicketForms();
  }, []);

  // Status banners disappear on their own after 20s.
  useEffect(() => {
    if (!message) return undefined;
    const timer = window.setTimeout(() => setMessage(''), 20000);
    return () => window.clearTimeout(timer);
  }, [message]);

  useEffect(() => {
    if (!projectColumnMenuOpen) return undefined;
    const handleClick = (event) => {
      if (!event.target.closest('.pt-column-menu-wrap')) setProjectColumnMenuOpen(false);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [projectColumnMenuOpen]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    /* Drill-through from the role-aware home (2026-08-22): a card links here with the filter it counted
       with, so the list on screen is exactly the number that was clicked. The filter is applied only
       when the URL carries one — arriving with a bare ?tab= must not wipe filters the person set. */
    const roundStatus = params.get('roundStatus');
    const developer = params.get('developer');
    /* getAll, not get: the donut hands over EVERY designer it counted, which on a manager's screen
       is the whole team minus the externals it leaves out. One name still arrives as a list of one,
       so the older single-name links are unaffected. */
    const designers = params.getAll('designer').filter(Boolean);
    if (roundStatus || developer || designers.length) {
      setSampleFilters((prev) => ({
        ...prev,
        roundStatus: roundStatus ? [roundStatus] : prev.roundStatus,
        developer: developer ? [developer] : prev.developer,
        designer: designers.length ? designers : prev.designer,
      }));
      // The counted rows are rounds, so land on the round list rather than the Base ID roll-up: the
      // Base ID view counts a style if ANY of its rounds matches and would show a different number.
      setSampleRowMode('rounds');
    }
    /* A project id in the URL puts that id in the project list's search box — the approval mail
       links each PRJ id here (Max, 2026-09-08), and landing on a screen of 123 projects with the one
       you clicked somewhere in it is not arriving anywhere. */
    /* Padded to the five-digit shape: the approval mails already sent link to PRJ-088, and the
       rows have said PRJ-00088 since 2026-09-15. Without this an old mail lands on an empty list. */
    const project = padProjectId(params.get('project') || '');
    if (project) setProjectFilters((prev) => ({ ...prev, search: project }));

    /* One row clicked on the home page, not a whole card — Max, 2026-09-12: "tôi bấm thì ko nhảy đến
       đúng á". The sample id goes into the round list's search box, the same way ?project= does for
       a project, so the screen opens on the one thing that was clicked. */
    /* A ticket id from the HEVN Assistant (2026-10-14) goes into Ticket Station's search box, the same
       way ?sample= and ?project= do for their lists. */
    const ticket = params.get('ticket');
    if (ticket) setTaskFilters((prev) => ({ ...prev, search: ticket }));

    const sample = params.get('sample');
    if (sample) {
      setSampleFilters((prev) => ({ ...prev, search: sample }));
      setSampleRowMode('rounds');
    }

    /* A Sales Rep arriving from their own welcome page. The filter is visible in the toolbar like any
       other — a list narrowed by something nothing on screen mentions is the same as a broken list. */
    const salesRep = params.get('salesRep');
    if (salesRep) setProjectFilters((prev) => ({ ...prev, salesRep: [salesRep] }));

    /* The Sales OP home cards — Max, 2026-09-15. Each arrives with the rule it counted with, so the
       list is the number that was clicked rather than the whole tracker. `lateDays` travels in the
       URL rather than being read here because the threshold lives in a settings table the browser
       has no reason to know about; the card that counted with it passes it on. */
    const flag = params.get('flag');
    if (flag === 'past_due' || flag === 'no_sample' || STAGE_PENDING_FLAGS[flag]) {
      setProjectFilters((prev) => ({ ...prev, flag }));
    }
    /* The donut's slice, carrying the window it counted with — the same shape ?lateDays= uses, and
       for the same reason: the rule lives on the server and the thing that was clicked passes it on
       rather than the screen guessing. */
    const review = params.get('review');
    if (REVIEW_STATES.includes(String(review || ''))) {
      setSampleFilters((prev) => ({
        ...prev, review, reviewDays: Number(params.get('reviewDays')) || 60,
      }));
      setSampleRowMode('rounds');
    }
    const lateDays = params.get('lateDays');
    if (lateDays && Number(lateDays) > 0) {
      setSampleFilters((prev) => ({ ...prev, lateDays: Number(lateDays) }));
      // The card counted ROUNDS, so land on the round list — the Base ID roll-up counts a style if
      // any round matches and would show a different number.
      setSampleRowMode('rounds');
    }

    if (tab === 'sample') {
      setActiveTab('sample');
      setSampleView('all');
    } else if (tab === 'project') {
      setActiveTab('projects');
    } else if (tab === 'tickets') {
      setActiveTab('tickets');
    } else if (tab === 'dash' || tab === 'dashboard') {
      setActiveTab('dash');
    } else {
      setActiveTab('dash');
    }
  /* the location KEY too (Jay 01/11): the same notification clicked a second time arrives at the same address — after the
     search box was cleared, it must still bring the item back */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.key]);

  const enrichedRounds = useMemo(() => rounds.map((round) => ({
    ...round,
    status: roundStatus(round),
  })), [rounds]);


  // Rounds arrive from the API already ordered by source_row_number (insertion order) - the "latest" round
  // for a Base ID is whichever was inserted last, NOT the alphabetically-last Sample ID (e.g. a "Counter"
  // round entered after "P2" is the true latest even though "Counter" < "P2" alphabetically).
  const groupedSamples = useMemo(() => groupSamplesOf(enrichedRounds), [enrichedRounds]);


  const filterOptions = useMemo(() => ({
    developers: lists.developers?.length ? lists.developers : unique(enrichedRounds, 'developer'),
    designers: lists.designers?.length ? lists.designers : unique(enrichedRounds, 'hincDesigner'),
    locations: lists.locations?.length ? lists.locations : unique(enrichedRounds, 'location'),
    categories: unique(projects, 'category'),
    // Assigned To offers the Development department only.
    assignees: lists.assignees?.length ? lists.assignees : unique(projects, 'assignedTo'),
    // Split lists for the SKU's Developer / Designer pair (2026-08-22). Fall back to the names already
    // in the data if the server is older than this build.
    skuDevelopers: lists.skuDevelopers?.length ? lists.skuDevelopers : unique(projects, 'assignedTo'),
    skuDesigners: lists.skuDesigners?.length ? lists.skuDesigners : unique(projects, 'designerName'),
    designerFilters: [...new Set([...(lists.skuDesigners || []), ...unique(projects, 'designerName')])],
    // The column filter still lists every name present in the data, or an older SKU could not be
    // filtered to at all.
    assigneeFilters: [...new Set([...(lists.assignees || []), ...unique(projects, 'assignedTo')])],
    allocations: lists.allocations?.length ? lists.allocations : unique(projects, 'allocation'),
    projectCategories: lists.projectCategories?.length ? lists.projectCategories : unique(projects, 'category'),
    projectStatuses: lists.projectStatuses?.length ? lists.projectStatuses : ['Priority', 'On Track', 'Concern', 'Delayed', 'On Hold', 'Ship Finished', 'Complete', 'Dropped'],
    projectTypologies: lists.projectTypologies?.length ? lists.projectTypologies : unique(projects, 'typology'),
    frameColors: lists.frameColors?.length ? lists.frameColors : unique(projects, 'frameColor'),
    projectStageStatuses: lists.projectStageStatuses?.length ? lists.projectStageStatuses : ['Pending', 'Complete', 'Delayed', 'On Hold'],
    projectProductTypes: lists.projectProductTypes?.length ? lists.projectProductTypes : unique(projects, 'productType'),
    gtmOptions: lists.gtmOptions || [],
  }), [enrichedRounds, projects, lists]);


  // Excel-style per-column filters, NAMESPACED PER TABLE: { [tableId]: { [columnKey]: allowedValues[] } }.
  // Namespacing matters because several grids share a column key (`status`, `projectId`, `startDate`) — a flat
  // map would silently apply the Project Tracker's Status filter to Ticket Station as well.
  const [columnFilters, setColumnFilters] = useState({});
  const [openColumnFilter, setOpenColumnFilter] = useState(null); // { table, key } | null
  const [columnFilterAnchor, setColumnFilterAnchor] = useState(null);
  const tableFilters = (tableId) => columnFilters[tableId] || NO_COLUMN_FILTERS;

  const applyColumnFilter = (tableId) => (key, values, allValues) => {
    setColumnFilters((prev) => {
      const current = { ...(prev[tableId] || {}) };
      // Ticking every value is not a filter — storing it would leave a funnel on a column that hides nothing.
      const isAll = allValues && allValues.length && values.length >= allValues.length;
      if (!values || !values.length || isAll) delete current[key]; else current[key] = values;
      const next = { ...prev };
      if (Object.keys(current).length) next[tableId] = current; else delete next[tableId];
      return next;
    });
    setOpenColumnFilter(null);
  };
  const clearColumnFilters = (tableId) => {
    setColumnFilters((prev) => {
      if (!tableId) return {};
      if (!prev[tableId]) return prev;
      const next = { ...prev };
      delete next[tableId];
      return next;
    });
    setOpenColumnFilter(null);
  };

  /**
   * Header props for one table's columns. `sourceRows` must be the rows AFTER the toolbar filters but BEFORE
   * this table's column filters, so the menu lists exactly the values still reachable — offering a value that
   * yields nothing is how Excel-style filters become dead ends.
   */
  const columnFilterProps = (tableId, getters, sourceRows) => (key) => {
    const active = tableFilters(tableId);
    const isOpen = !!openColumnFilter && openColumnFilter.table === tableId && openColumnFilter.key === key;
    return {
      filterValues: isOpen ? distinctColumnValues(sourceRows, key, active, getters) : undefined,
      filterSelected: active[key],
      filterOpen: isOpen,
      filterAnchor: columnFilterAnchor,
      onOpenFilter: (k, anchor) => {
        setColumnFilterAnchor(anchor);
        setOpenColumnFilter((prev) => (
          prev && prev.table === tableId && prev.key === k ? null : { table: tableId, key: k }
        ));
      },
      onCloseFilter: () => setOpenColumnFilter(null),
      onApplyFilter: applyColumnFilter(tableId),
    };
  };

  const toolbarFilteredGroups = useMemo(() => {
    const search = sampleFilters.search.trim().toLowerCase();
    const isProductView = sampleView === 'product';
    const filtered = groupedSamples.filter((sample) => {
      const latest = sample.latest || {};
      // Which series the sample belongs to is written in its Base ID and nowhere else.
      const matchesType = !sampleFilters.sampleType.length || sampleFilters.sampleType.includes(
        isTrialBase(sample.baseId) ? SAMPLE_TYPE_TRIAL : SAMPLE_TYPE_PROJECT);
      const matchesSampleStatus = !sampleFilters.sampleStatus.length || sampleFilters.sampleStatus.some((want) => (
        isProductView
          ? sample.productViewStatus === want
          // 'On Hold' belongs to its own card, so the In Progress bucket must drop it here too —
          // the KPI count already did, which is why clicking the card used to widen the result
          // instead of matching it (Max, 2026-08-13).
          : want === 'In Progress'
            ? !['Confirmed', 'Rework', 'Dropped', 'On Hold'].includes(sample.status)
            : sample.status === want
      ));
      /* the three card statuses list a sample by the same rule the card counts it with; the rest, any round */
      const matchesRoundStatus = isProductView || !sampleFilters.roundStatus.length
        || sampleFilters.roundStatus.some((want) => sampleMatchesRoundStatus(sample, want));
      const matchesSearch = !search || [sample.baseId, sample.styleItem, latest.sampleId, latest.remarks, latest.projectId]
        .some((value) => String(value || '').toLowerCase().includes(search));
      /* Badly late, the same rule the server counts with: an OPEN round whose target date is more
         than N days past. Any round of the style qualifying is enough, because the row on screen is
         the style and that is what the card counted. */
      const matchesLate = !sampleFilters.lateDays || sample.rounds.some((round) => {
        /* An outcome means the round is finished, and a finished round that ran late is history
           rather than a queue — the same line the server draws. `dDay` is the screen's own number,
           positive when overdue since 2026-08-12, so this reads the figure the column shows instead
           of recomputing the date and risking the two disagreeing. */
        if (String(round.outcome || '').trim()) return false;
        return Number.isFinite(round.dDay) && round.dDay > Number(sampleFilters.lateDays);
      });
      /* ANY round of the style qualifying is enough, the same rule `lateDays` above follows: the row
         on screen is the style, and that is the grain the donut's own count rolls up to once the
         list lands on the round view. */
      const matchesReview = !sampleFilters.review
        || sample.rounds.some((round) => roundReviewState(round, sampleFilters.reviewDays) === sampleFilters.review);
      return matchesType
        && matchesSampleStatus
        && matchesLate
        && matchesReview
        && matchesRoundStatus
        /* ANY ROUND, not the latest one — 2026-10-04, measured. `sample.developer` and
           `sample.designer` are rolled up from the LATEST round, so a style that changed hands
           dropped out of its earlier owner's list entirely, taking that person's own rounds with
           it. The donut is what made it visible: it counted 67 Done rounds and the list its slice
           opened held 66, the missing one belonging to a style whose last round carries somebody
           else's name. Same "any round qualifies" rule as the late and review filters above, with
           `toolbarFilteredRounds` then cutting the round list down to the rounds that actually
           matched. Product View is left out of the designer test as it always has been. */
        && (!sampleFilters.developer.length
            || sample.rounds.some((round) => sampleFilters.developer.includes(round.developer)))
        && (isProductView || !sampleFilters.designer.length
            || sample.rounds.some((round) => sampleFilters.designer.includes(round.hincDesigner)))
        && (!sampleFilters.location.length || sampleFilters.location.includes(sample.location))
        && matchesSearch;
    });
    return filtered;
  }, [groupedSamples, sampleFilters, sampleView]);

  // Column filters run after the toolbar filters, so a column menu always offers the values still reachable.
  // All Samples and Sample-level Summary are separate grids with different column sets, so they keep separate
  // filter slices — a Developer filter on one must not silently narrow the other.
  const sampleColumnFilters = tableFilters('samples');
  const summaryColumnFilters = tableFilters('summary');

  const filteredSampleGroups = useMemo(() => {
    const columnFiltered = Object.keys(sampleColumnFilters).length
      ? toolbarFilteredGroups.filter((sample) => rowPassesColumnFilters(sample, sampleColumnFilters, SAMPLE_SORT_GETTERS))
      : toolbarFilteredGroups;
    return sortSamples(columnFiltered, sampleSort);
  }, [toolbarFilteredGroups, sampleSort, sampleColumnFilters]);

  const summarySampleGroups = useMemo(() => {
    const columnFiltered = Object.keys(summaryColumnFilters).length
      ? toolbarFilteredGroups.filter((sample) => rowPassesColumnFilters(sample, summaryColumnFilters, SAMPLE_SORT_GETTERS))
      : toolbarFilteredGroups;
    return sortSamples(columnFiltered, sampleSort);
  }, [toolbarFilteredGroups, sampleSort, summaryColumnFilters]);

  // Flat round rows for the "Rounds" display mode — only rounds that themselves match the round-level filter.
  const toolbarFilteredRounds = useMemo(() => {
    const rows = [];
    toolbarFilteredGroups.forEach((sample) => sample.rounds.forEach((round) => rows.push(round)));
    /* The late filter is applied to the ROUNDS as well as to their styles. Matching the style is
       what puts it in the list; without this line the list then showed every round of that style —
       twenty-four rows where the card had counted twelve late ones, which is exactly the "the number
       I clicked is not the number I got" the drill-through exists to avoid. */
    const lateOnly = (round) => !sampleFilters.lateDays
        || (!String(round.outcome || '').trim()
            && Number.isFinite(round.dDay) && round.dDay > Number(sampleFilters.lateDays));
    /* And the review filter, for the same reason: the donut counted ROUNDS, so a style that
       qualifies must not then print every round it has. */
    const reviewOnly = (round) => !sampleFilters.review
        || roundReviewState(round, sampleFilters.reviewDays) === sampleFilters.review;
    /* THE DESIGNER FILTER REACHES THE ROUNDS TOO, 2026-10-04. It has always matched `sample.designer`,
       which is the LATEST round's name, so filtering to one designer and switching to the round view
       printed their colleagues' rounds on any style that had changed hands. The donut made it
       visible — it counts rounds BY designer, and the list it opens has to be those rounds. Same
       argument as the late filter two lines up, and it leaves the Base ID view untouched: there the
       row IS the style and the latest round is what names it. */
    const designerOnly = (round) => !sampleFilters.designer.length
        || sampleFilters.designer.includes(round.hincDesigner);
    const developerOnly = (round) => !sampleFilters.developer.length
        || sampleFilters.developer.includes(round.developer);
    return rows.filter(lateOnly).filter(reviewOnly).filter(designerOnly).filter(developerOnly)
      .filter((round) => !sampleFilters.roundStatus.length
      || sampleFilters.roundStatus.some((want) => roundMatchesStatus(round, want)));
  }, [toolbarFilteredGroups, sampleFilters]);

  const flatFilteredRounds = useMemo(() => {
    const columnFiltered = Object.keys(sampleColumnFilters).length
      ? toolbarFilteredRounds.filter((round) => rowPassesColumnFilters(round, sampleColumnFilters, ROUND_FILTER_GETTERS))
      : toolbarFilteredRounds;
    // The Base-ID list has always been sorted here; the flat round list was returned unsorted, so
    // clicking any header in rounds mode did nothing at all (Max, 2026-08-13). Same sort state, same
    // helper — only the getter map differs, because a round has no `latest`.
    return sortRows(columnFiltered, sampleSort, ROUND_FILTER_GETTERS);
  }, [toolbarFilteredRounds, sampleColumnFilters, sampleSort]);

  // Props every All Samples header cell needs, so the menu lists the right values for the active row mode.
  const thFilter = columnFilterProps(
    'samples',
    sampleRowMode === 'rounds' ? ROUND_FILTER_GETTERS : SAMPLE_SORT_GETTERS,
    sampleRowMode === 'rounds' ? toolbarFilteredRounds : toolbarFilteredGroups,
  );
  const thFilterSummary = columnFilterProps('summary', SAMPLE_SORT_GETTERS, toolbarFilteredGroups);

  // KPI cards show filtered counts (with "- of N total" sub text) once a status/developer/designer/location
  // filter is active, matching the legacy Portal.html isFiltered/totalX-vs-filtX behavior. Free-text search alone
  // does not trigger this (matches legacy too).
  /* Read off the filter object rather than a hand-written list of its keys. The list was the bug:
     `sampleType` was added on 2026-09-09 and never appeared here, so the pills filtered the table
     while the cards above it went on reporting all 214 — "No data to display" sitting under a card
     reading 214 (Max). `search` had been missing the same way for far longer. A key added tomorrow
     is counted without anyone having to remember this line. */
  const isSampleFiltered = Object.values(sampleFilters).some((value) => (
    Array.isArray(value) ? value.length > 0 : String(value ?? '').trim() !== ''
  ));

  const totalSampleStats = useMemo(
    () => computeSampleStats(groupedSamples, enrichedRounds),
    [enrichedRounds, groupedSamples],
  );

  const filteredSampleStats = useMemo(
    () => computeSampleStats(
      filteredSampleGroups,
      // A sample survives the round filter if ANY of its rounds matched, so its other rounds are
      // still in `sample.rounds` — counting those made the Confirmed / Dropped cards show rounds
      // that the table itself was hiding. When the grid lists rounds, count the listed rounds.
      sampleRowMode === 'rounds' ? flatFilteredRounds : filteredSampleGroups.flatMap((sample) => sample.rounds),
    ),
    [filteredSampleGroups, flatFilteredRounds, sampleRowMode],
  );

  const sampleStats = isSampleFiltered ? filteredSampleStats : totalSampleStats;

  /* Counted over EVERY sample, never the filtered set. A card that took its number from the current
     filter would read 0 for the very series you are about to click on — the trap the tab counts had
     to avoid for the same reason. */
  const typeCounts = useMemo(() => {
    const trial = groupedSamples.filter((sample) => isTrialBase(sample.baseId)).length;
    return { all: groupedSamples.length, project: groupedSamples.length - trial, trial };
  }, [groupedSamples]);

  /* null is the Both pill. It is lit whenever the filter is not narrowed to exactly one series —
     which covers "nothing selected" and "ctrl+click selected both", because those two are the same
     state. */
  const typeSelected = (value) => (value === null
    ? sampleFilters.sampleType.length !== 1
    : sampleFilters.sampleType.length === 1 && sampleFilters.sampleType[0] === value);

  /* Sets its own axis and nothing else. The status cards clear each other because they answer the
     same question; a sample's type does not, so a Developer or a search already typed stays put.

     Ctrl (or ⌘) held turns the pill into a toggle, so the two can be combined the way Jay asked.
     Selecting both collapses to the empty filter — the Both pill's own state — rather than a second
     way of saying the same thing. */
  const filterByType = (value, event) => {
    const multi = !!event && (event.ctrlKey || event.metaKey);
    setSampleFilters((prev) => {
      if (value === null) return { ...prev, sampleType: [] };
      if (!multi) return { ...prev, sampleType: [value] };
      const next = prev.sampleType.includes(value)
        ? prev.sampleType.filter((v) => v !== value)
        : [...prev.sampleType, value];
      return { ...prev, sampleType: next.length === SAMPLE_TYPES.length ? [] : next };
    });
    setExpanded({});
  };

  /* Only the rows in view are built. Nothing else moved the number: not turning off the frozen
     columns, not building the rows in slices across frames, not content-visibility, not
     table-layout: fixed, which this table already had. The row COUNT did — 210 rows go up in one
     218-296 ms task, 5 rows in 30 ms with no blocking task at all (Max, 2026-09-09).

     47px, measured, and true of every row only after the cells were made single-line and the Status
     column widened enough for "Pending Outcome" to fit on one.

     Windowed only while every group is COLLAPSED. An expanded group inserts its round rows between
     the group rows, and the spacer arithmetic can only be honest about a list whose rows are all one
     height. Landing on the tab — the case that felt like a stutter — is always the collapsed case;
     expanding a row hands the table back in full until it is closed again. */
  /* Which base row has its remarks opened out — Max, 2026-09-16: "phần remark này trên dòng base ID
     thì sẽ hiện theo round mới nhất. Nếu round mới có nhiều option thì gôm lại và tách theo dòng".

     Opening one makes that row taller, and `useRowWindow`'s arithmetic is only honest while every
     row is the same height — so it joins `expanded` in switching the windowing off, exactly as
     expanding a group already does. Measured before choosing this: of 219 base IDs only THREE have
     a latest round carrying more than one remark, and the most is three lines, so the collapsed
     state is the whole truth for 216 of them and the cost of the taller row is paid by the few. */
  const [openRemarks, setOpenRemarks] = useState({});
  const sampleAnyExpanded = Object.values(expanded).some(Boolean)
    || Object.values(openRemarks).some(Boolean);
  const [sampleBoxRef, sampleWin] = useRowWindow({
    rowHeight: SAMPLE_ROW_H,
    total: sampleRowMode === 'rounds'
      ? flatFilteredRounds.length
      : (sampleAnyExpanded ? 0 : filteredSampleGroups.length),
    resetKey: `${activeTab}|${sampleRowMode}|${sampleSort.key}${sampleSort.dir}|${JSON.stringify(sampleFilters)}`,
  });
  const sampleFreeze = useFreezeFit(sampleBoxRef, SAMPLE_FREEZE_LEVELS, SAMPLE_SCROLL_ROOM, [activeTab, sampleView]);

  const reviewLabel = (key) => (REVIEW_LABEL_KEYS[key] ? t(REVIEW_LABEL_KEYS[key]) : (REVIEW_LABEL[key] || key));
  const withTotalSub = (label, filteredValue, totalValue) => (
    isSampleFiltered && filteredValue !== totalValue ? t('stx.ofTotal', { label, total: totalValue }) : label
  );

  const toggleSampleSort = (key, dir) => {
    if (dir) { setSampleSort({ key, dir }); setOpenColumnFilter(null); return; }
    setSampleSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  };

  const toggleProjectSort = (key, dir) => {
    if (dir) { setProjectSort({ key, dir }); setOpenColumnFilter(null); return; }
    setProjectSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  };

  // Toolbar filters only — this is what the column menus enumerate, so a menu never offers a dead value.
  const toolbarFilteredProjects = useMemo(() => {
    const search = projectFilters.search.trim().toLowerCase();
    return projects.filter((project) => (
      // The tracker is the list of LIVE projects. Pending and Rejected ones belong to Project
      // Approval until the CEO clears them (Jay item 3). A project with no group at all keeps
      // showing — it predates the gate and has no approval state to judge it by.
      (!project.approvalStatus || project.approvalStatus === 'Approved')
      &&
      (!projectFilters.status.length || projectFilters.status.includes(project.status))
      /* Team / Project Owner live on the group; every SKU row carries a copy so one filter covers both.
         A project with no team is NOT let through: picking "Sales OP Team 1" used to still list the
         44 unassigned legacy projects, which made the filter look broken (Max, 2026-09-03). They are
         still all there under All Teams — which is also where they can be spotted and assigned. */
      && (!projectFilters.team.length || projectFilters.team.includes(project.teamName))
      && (!projectFilters.owner.length || projectFilters.owner.includes(project.ownerName))
      /* The Sales Rep is the group's, copied onto every SKU row — the filter a rep's own home page
         sends them here with. */
      && (!projectFilters.salesRep.length || projectFilters.salesRep.includes(project.salesRepName))
      && (!projectFilters.category.length || projectFilters.category.includes(project.category))
      && (!projectFilters.assigned.length || projectFilters.assigned.includes(project.assignedTo))
      && (!projectFilters.designer.length || projectFilters.designer.includes(project.designerName))
      && (!projectFilters.allocation.length || projectFilters.allocation.includes(project.allocation))
      /* The two flags the Sales OP home cards count with — Max, 2026-09-15: "bấm thì nó chuyển đến
         đúng trang nhưng ko lọc được". A card that lands on an unfiltered list of 500 has not taken
         anybody anywhere. The rule is the SAME one the server counts with, and the toolbar shows a
         chip while it is on, because a list narrowed by something nothing on screen mentions is the
         same as a broken list. */
      && (projectFilters.flag !== 'past_due' || (
        project.exfShipDate && project.exfShipDate < todayVN()
        && !['Complete', 'Dropped', 'Ship Finished'].includes(project.status)))
      && (projectFilters.flag !== 'no_sample' || (
        !String(project.sampleId || '').trim()
        && !rounds.some((r) => r.projectId === project.id)
        && !['Complete', 'Dropped', 'Ship Finished'].includes(project.status)
        // A repeat order makes no sample (2026-10-09) — the card's server rule says the same.
        && !project.repeatOrder))
      && (!STAGE_PENDING_FLAGS[projectFilters.flag] || (
        (project[STAGE_PENDING_FLAGS[projectFilters.flag].key] || 'Pending') === 'Pending'
        && !['Complete', 'Dropped', 'Ship Finished'].includes(project.status)))
      // groupId / groupName are in here because people search by the PRJ-### they see everywhere
      // else (emails, the approval queue) — without them "PRJ-138" matched nothing at all.
      && (!search || [project.id, project.groupId, project.groupName, project.projectItem, project.subItem,
        project.category, project.productType, project.remark, project.sampleId]
        .some((value) => String(value || '').toLowerCase().includes(search)))
    ));
    // `rounds` joins the dependencies because the no_sample flag reads them — without it the list
    // would keep answering from the round set it was built with.
  }, [projectFilters, projects, rounds]);

  // Projects with no SKU yet contribute no row, so the per-column filters could not see them (their
  // Team / Owner were missing from the value list, and a filter would silently drop the project).
  // One synthetic row per empty group fixes both.
  const emptyGroupRows = useMemo(() => {
    const grouped = new Set(projects.map((p) => p.groupId).filter(Boolean));
    return (groups || [])
      .filter((g) => g.groupId && !grouped.has(g.groupId))
      // Same rule for a project that has no SKU yet: it appears here only once approved.
      .filter((g) => (g.approvalStatus || 'Approved') === 'Approved')
      .map((g) => ({
        id: g.groupId,
        groupId: g.groupId,
        groupName: g.groupName || '',
        projectItem: g.groupName || '',
        category: g.category || '',
        teamName: g.teamName || '',
        ownerName: g.ownerName || '',
        salesRepName: g.salesRepName || '',
        groupCase: g.projectCase || '',
        groupEntity: g.entity || '',
        status: '',
        stages: {},
      }));
  }, [groups, projects]);

  // A project is Complete when every one of its SKUs is; a project with no SKU yet is not.
  // Built from the unfiltered list on purpose — see the Completed / Active Projects cards.
  const groupStateKeys = useMemo(() => groupStateKeysOf(projects), [projects]);
  /* A project's state from ALL its approved SKUs (groupStateKeys is built from the unfiltered list): a
     filter changes what is on the screen, not whether the project is finished. */
  const projectStateOf = useCallback((gkey) => (groupStateKeys.done.has(gkey) ? 'complete'
    : (groupStateKeys.dropped.has(gkey) ? 'dropped' : 'active')), [groupStateKeys]);
  const rowProjectState = useCallback(
    (p) => projectStateOf(p.groupId || p.id || p.projectItem), [projectStateOf],
  );
  /* WHERE AN ACTIVE PROJECT IS, IN A FEW WORDS (Jay, 2026-10-21: "please use AI to summarize in 1 short sentence.
     Such as "sampling now!" or "shipment now!" or "waiting payment""). The server works out each open SKU's position
     from its stages and the AI words it (projectAiStatus.js); the answer is kept until something on the project
     changes. A project not answered yet keeps "Active" and the list is asked again while some are pending; a server
     without the feature answers 404 and the column stays as it was. Completed and Dropped are facts, never summaries. */
  // Only while some are still being written: ask again until they are, then stop.
  const aiWaiting = aiPending.size > 0;
  useEffect(() => {
    if (activeTab !== 'projects' || !aiWaiting) return undefined;
    let stop = false;
    let timer = null;
    let tries = 0;
    const ask = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/development/project-ai-status`, { credentials: 'include' });
        if (!res.ok || stop) return;
        const j = await res.json();
        if (stop) return;
        setAiStatus(j.items || {});
        setAiPending(new Set(j.pendingKeys || []));
        tries += 1;
        if (j.pending > 0 && tries < 40) timer = setTimeout(ask, 5000);
      } catch (e) { /* the column keeps what it has */ }
    };
    timer = setTimeout(ask, 3000);
    return () => { stop = true; clearTimeout(timer); };
  }, [activeTab, aiWaiting]);
  // The words the column, its filter, its sort and the Excel file print for a project — one rule for all four.
  const projectStateText = useCallback((gkey) => {
    const state = projectStateOf(gkey);
    const ai = state === 'active' ? aiStatus[gkey] : null;
    return ai ? (ai[aiLang] || ai.en) : projectStateLabel(state, t);
  }, [projectStateOf, aiStatus, aiLang, t]);
  const projectSortGetters = useMemo(() => ({
    ...PROJECT_SORT_GETTERS,
    groupStage: (p) => `${PROJECT_STATE_RANK[rowProjectState(p)]}${projectStateText(p.groupId || p.id || p.projectItem)}`,
  }), [rowProjectState, projectStateText]);
  const projectFilterGetters = useMemo(() => ({
    ...PROJECT_FILTER_GETTERS, groupStage: (p) => projectStateText(p.groupId || p.id || p.projectItem),
  }), [projectStateText]);

  const projectColumnFilters = tableFilters('projects');
  const filteredProjects = useMemo(() => {
    const columnFiltered = Object.keys(projectColumnFilters).length
      ? toolbarFilteredProjects.filter((project) => rowPassesColumnFilters(project, projectColumnFilters, projectFilterGetters))
      : toolbarFilteredProjects;
    const stateFiltered = projectFilters.groupState
      ? columnFiltered.filter((project) => {
        const gkey = project.groupId || project.id || project.projectItem;
        const state = groupStateKeys.done.has(gkey) ? 'complete' : (groupStateKeys.dropped.has(gkey) ? 'dropped' : 'active');
        return state === projectFilters.groupState;
      })
      : columnFiltered;
    return sortRows(stateFiltered, projectSort, projectSortGetters);
  }, [toolbarFilteredProjects, projectSort, projectColumnFilters, projectFilters.groupState, groupStateKeys, projectSortGetters, projectFilterGetters]);
  const thFilterProject = columnFilterProps('projects', projectFilterGetters,
    useMemo(() => [...toolbarFilteredProjects, ...emptyGroupRows], [toolbarFilteredProjects, emptyGroupRows]));

  // Project Tracker KPI cards reflect the CURRENTLY FILTERED projects (so clicking a card / applying a filter
  // updates every card): "Project Groups" = distinct groups; status cards count per SKU/item.
  const projectStats = useMemo(
    () => projectKpiCounts(filteredProjects, groupStateKeys),
    [filteredProjects, groupStateKeys],
  );

  // Group the filtered projects into Special-Project cards (ports Portal.html _ptGroupProjects).
  // projects shown by the other filters whose original target is still owed — the count on the filter button
  const unconfirmedCount = useMemo(() => new Set(filteredProjects.map((p) => p.groupId)
    .filter((id) => id && groupMetaById.get(id)?.needsConfirm)).size, [filteredProjects, groupMetaById]);
  const projectGroups = useMemo(() => {
    const map = new Map();
    const order = [];
    filteredProjects.forEach((p) => {
      const key = p.groupId || p.id || p.projectItem || 'ungrouped';
      if (!map.has(key)) {
        map.set(key, {
          key,
          id: p.groupId || p.id || '',
          name: p.groupName || p.projectItem || t('stx.projectFallback'),
          category: p.groupCategory || p.category || '',
          projectCase: p.groupCase || '',
          entity: p.groupEntity || '',
          teamName: p.teamName || '',
          ownerName: p.ownerName || '',
          salesRepName: p.salesRepName || '',
          // The group row's Edit button reads this. Without it the flag stopped at the SKU rows and
          // the group button stayed enabled for every team (Max, 2026-08-13).
          canEditGroup: p.canEditGroup,
          items: [],
        });
        order.push(key);
      }
      map.get(key).items.push(p);
    });

    // A project can be created with zero SKUs (Jay item 2). The grid is built from SKU rows, so such a
    // group has nothing to hang off and would silently disappear from the tracker — exactly what
    // happened to the first approved project. Add them explicitly.
    // SKU-level filters (status / assignee / allocation) are skipped for them: an empty group has no
    // value to match, and showing it under "Status = Delayed" would be a lie.
    const skuFilterActive = !!(projectFilters.status.length || projectFilters.assigned.length
      || projectFilters.designer.length
      || projectFilters.allocation.length || ['complete', 'dropped'].includes(projectFilters.groupState));
    if (!skuFilterActive) {
      const search = projectFilters.search.trim().toLowerCase();
      emptyGroupRows.forEach((row) => {
        const g = { groupId: row.groupId, groupName: row.groupName, category: row.category,
          teamName: row.teamName, ownerName: row.ownerName, projectCase: row.groupCase, entity: row.groupEntity };
        if (map.has(g.groupId)) return;
        if (projectFilters.category.length && !projectFilters.category.includes(g.category)) return;
        // Same rule as the SKU rows above: no team means it belongs to no team's filter.
        if (projectFilters.team.length && !projectFilters.team.includes(g.teamName)) return;
        if (projectFilters.owner.length && !projectFilters.owner.includes(g.ownerName)) return;
        if (projectFilters.salesRep.length && !projectFilters.salesRep.includes(row.salesRepName)) return;
        // The Excel-style column filters apply to the synthetic row, so an empty project can be
        // filtered by Team / Owner / Category like any other.
        if (Object.keys(projectColumnFilters).length
          && !rowPassesColumnFilters(row, projectColumnFilters, projectFilterGetters)) return;
        if (search && ![g.groupId, g.groupName, g.category].some((v) => String(v || '').toLowerCase().includes(search))) return;
        map.set(g.groupId, {
          key: g.groupId,
          id: g.groupId,
          name: g.groupName || t('stx.projectFallback'),
          category: g.category || '',
          projectCase: g.projectCase || '',
          entity: g.entity || '',
          teamName: g.teamName || '',
          ownerName: g.ownerName || '',
          salesRepName: row.salesRepName || '',
          items: [],
        });
        order.unshift(g.groupId);   // newest / empty projects first, so they are not lost at the bottom
      });
    }
    const rows = order.map((k) => map.get(k)).filter((g) => !unconfirmedOnly || groupMetaById.get(g.id)?.needsConfirm);
    if (projectSort.key) return rows;
    return rows.sort((a, b) => String(b.id || '').localeCompare(String(a.id || '')));
  }, [filteredProjects, emptyGroupRows, projectFilters, projectColumnFilters, projectSort.key, projectFilterGetters, t, unconfirmedOnly, groupMetaById]);

  // Every Sales OP owner that exists on a project, plus the members of the configured teams — so a
  // manager can filter to someone who has not been given a project yet and see the empty result.
  const projectOwnerOptions = useMemo(() => {
    const fromProjects = projects.map((p) => p.ownerName).filter(Boolean);
    const fromTeams = (trackerConfig?.teams || []).flatMap((tm) => tm.members.map((m) => m.name));
    return [...new Set([...fromProjects, ...fromTeams])].sort();
  }, [projects, trackerConfig]);

  // Jay item 5: "managers use this to see who is overloaded". One row per Project Owner, counting the
  // groups they own and the SKUs underneath, so a single project with 16 SKUs does not read as light work.
  const projectMemberRollup = useMemo(() => {
    const byOwner = new Map();
    const bump = (owner, team) => {
      const key = owner || UNASSIGNED_OWNER;
      if (!byOwner.has(key)) {
        byOwner.set(key, { owner: key, team: team || '', projects: 0, skus: 0, active: 0, concern: 0, delayed: 0, overdue: 0 });
      }
      return byOwner.get(key);
    };
    projectGroups.forEach((g) => {
      const row = bump(g.ownerName, g.teamName);
      row.projects += 1;
      g.items.forEach((p) => {
        row.skus += 1;
        const st = p.status || '';
        if (!['Dropped', 'Drop', 'Complete'].includes(st)) row.active += 1;
        if (st === 'Concern') row.concern += 1;
        if (st === 'Delayed') row.delayed += 1;
        if (isProjectOverdue(p)) row.overdue += 1;
      });
    });
    return [...byOwner.values()].sort((a, b) => b.skus - a.skus || a.owner.localeCompare(b.owner));
  }, [projectGroups]);

  const toggleGroup = (key) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  const expandAllGroups = () => setOpenGroups(Object.fromEntries(projectGroups.map((g) => [g.key, true])));
  // Groups default to collapsed, so clearing the map collapses everything.
  const collapseAllGroups = () => setOpenGroups({});
  /* Deleting a project asks once, in ONE portal dialog (Max, 2026-08-21: "cần chỉnh ở đây").
     It used to be a browser window.confirm for the SKUs and then a portal dialog for the tickets —
     two different-looking questions for one decision, and the native box can neither list the tickets
     nor take the reason. The dialog asks the server what is attached first (`preflight`, which deletes
     nothing), so everything about to be lost is on screen before anything is pressed. */
  const deleteProjectGroup = async (group) => {
    setDeleteReason('');
    setDeleteGroupPrompt({ group, loading: true, skus: group.items.length, tickets: [] });
    try {
      const res = await fetch(`${PROJECT_API_BASE}/groups/${encodeURIComponent(group.id)}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preflight: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setDeleteGroupPrompt(null);
        setMessage(data.error || t('stx.cannotDelete', { status: res.status }));
        return;
      }
      setDeleteGroupPrompt({
        group,
        loading: false,
        skus: data.skus ?? group.items.length,
        tickets: data.tickets || [],
      });
    } catch (err) {
      setDeleteGroupPrompt(null);
      setMessage(err.message || t('stx.deleteFailedPlain'));
    }
  };

  // What the dialog's Delete button does. Sample rounds survive — the server detaches them.
  const confirmDeleteGroup = async () => {
    const spec = deleteGroupPrompt;
    if (!spec || spec.loading) return;
    const reason = deleteReason.trim();
    if (spec.tickets.length && !reason) return;   // the button is disabled too; this is the backstop
    setDeleteGroupPrompt(null);
    setMessage(t('stx.msg.deletingProject'));
    try {
      const res = await fetch(`${PROJECT_API_BASE}/groups/${encodeURIComponent(spec.group.id)}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ acknowledgeTickets: true, reason }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('stx.deleteFailed', { status: res.status }));
      if (data.projects) setProjects(data.projects);
      if (data.rounds) setRounds(data.rounds);
      const ticketNote = !data.ticketsDeleted ? ''
        : data.ticketsNotified
          ? t('stx.msg.ticketsCancelledNotified', { n: data.ticketsDeleted, m: data.ticketsNotified })
          : t('stx.msg.ticketsCancelled', { n: data.ticketsDeleted });
      setMessage(`${t('stx.msg.groupDeleted', { id: spec.group.id, skus: data.skusDeleted, rounds: data.samplesDetached })}${ticketNote}`);
      await loadTasks(true);
    } catch (err) {
      setMessage(err.message || t('stx.deleteFailedPlain'));
    }
  };

  const avgPct = (items) => {
    const vals = items.map((p) => Number(p.completePct)).filter((n) => Number.isFinite(n));
    return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
  };

  // Clicking a Project ID in Sample Tracker jumps to that SKU in Project Tracker (Max, 2026-08-13).
  // Searching for the SKU id is what makes it land: the tracker's search already matches project.id,
  // and its groups start collapsed, so the group is opened explicitly or the row would stay hidden
  // behind a caret. Every other project filter is cleared, otherwise a filter left over from an
  // earlier visit could hide the very row we are navigating to.
  const goToProjectSku = (projectId) => {
    const sku = String(projectId || '').trim();
    if (!sku) return;
    const target = (projects || []).find((p) => p.id === sku);
    setProjectFilters({ status: [], category: [], assigned: [], designer: [], allocation: [], team: [], owner: [], salesRep: [], search: sku, groupState: '' });
    clearColumnFilters('projects');
    if (target) setOpenGroups((prev) => ({ ...prev, [target.groupId || target.id]: true }));
    navigate('/development/sample-tracker?tab=project');
  };

  const quickFilter = (type) => {
    setSampleView('all');
    // Every KPI card maps to a concrete filter so clicking any of them narrows the table.
    // Base-ID cards show collapsed Base ID rows; Rounds cards switch to a flat round-ID list.
    const ROUND_CARDS = ['roundInProgress', 'roundConfirmed', 'roundRework', 'roundDropped', 'roundOnHold', 'pending', 'waiting', 'inprod', 'totalRounds'];
    setSampleRowMode(ROUND_CARDS.includes(type) ? 'rounds' : 'group');
    setExpanded({});
    const base = { sampleStatus: [], roundStatus: [] };
    if (type === 'all' || type === 'totalRounds') setSampleFilters(SAMPLE_FILTERS_DEFAULT);
    if (type === 'sampleInProgress') setSampleFilters((prev) => ({ ...prev, ...base, sampleStatus: ['In Progress'] }));
    if (type === 'sampleConfirmed') setSampleFilters((prev) => ({ ...prev, ...base, sampleStatus: ['Confirmed'] }));
    if (type === 'sampleRework') setSampleFilters((prev) => ({ ...prev, ...base, sampleStatus: ['Rework'] }));
    if (type === 'sampleDropped') setSampleFilters((prev) => ({ ...prev, ...base, sampleStatus: ['Dropped'] }));
    if (type === 'sampleOnHold') setSampleFilters((prev) => ({ ...prev, ...base, sampleStatus: ['On Hold'] }));
    if (type === 'roundInProgress') setSampleFilters((prev) => ({ ...prev, ...base, roundStatus: ['In Progress'] }));
    if (type === 'roundConfirmed') setSampleFilters((prev) => ({ ...prev, ...base, roundStatus: ['Confirmed'] }));
    if (type === 'roundRework') setSampleFilters((prev) => ({ ...prev, ...base, roundStatus: ['Rework'] }));
    if (type === 'roundDropped') setSampleFilters((prev) => ({ ...prev, ...base, roundStatus: ['Dropped'] }));
    if (type === 'roundOnHold') setSampleFilters((prev) => ({ ...prev, ...base, roundStatus: ['On Hold'] }));
    if (type === 'pending' || type === 'samplePending') setSampleFilters((prev) => ({ ...prev, ...base, roundStatus: ['Pending Outcome'] }));
    if (type === 'waiting' || type === 'sampleWaiting') setSampleFilters((prev) => ({ ...prev, ...base, roundStatus: ['RM Waiting'] }));
    if (type === 'inprod' || type === 'sampleInprod') setSampleFilters((prev) => ({ ...prev, ...base, roundStatus: ['In Production'] }));
    setActiveTab('sample');
  };

  /* What a new round inherits from the sample it is being added to. The in-modal sample picker had
     this list and the Add-round button did not — it seeded a style name and a Base ID and nothing
     else, so a round added from the grid lost the project SKU the rest of the sample carries (Max,
     2026-09-10). One helper now, because two copies of this list is how they drifted apart. */
  const inheritFromSample = (baseId) => {
    const mine = rounds.filter((r) => r.baseId === baseId);
    const latest = (key) => {
      for (let i = mine.length - 1; i >= 0; i -= 1) {
        const value = mine[i][key];
        if (value !== null && value !== undefined && String(value).trim() !== '') return value;
      }
      return '';
    };
    return {
      category: latest('category'),
      season: latest('season'),
      developer: latest('developer'),
      location: latest('location'),
      hincDesigner: latest('hincDesigner'),
      developmentDue: latest('developmentDue'),
      targetPriceUsd: latest('targetPriceUsd'),
      projectId: latest('projectId'),
    };
  };

  /* JAY'S ENTRY POINT — 2026-09-16: "Designers and Developers live entirely in the Sample Tracker
     page. Asking them to jump over to Project Tracker just to create the first sample isn't
     intuitive for someone who never otherwise touches that screen."

     The Project Tracker route is untouched and still the same code: this picker ends by calling
     openNewRoundModal with EXACTLY the prefill that tab's green "+ Sample" button passes. One flow
     with two doors, rather than two flows that drift.

     WHAT IT OFFERS is the only set that can legally be acted on. A sample maps 1:1 to a project SKU
     — measured, and true without exception: all 222 linked SKUs carry exactly one Base ID — so a
     SKU that already has one cannot get another, and the Project Tracker row has always hidden its
     "+ Sample" button on that rule. Dropped and Complete SKUs are out for the same reason the
     tracker closes their buttons, and un-approved SKUs never appear on this screen at all. 510 SKUs
     become 121, in 43 projects. */
  const [samplePicker, setSamplePicker] = useState(null);   // null | { q: string }

  /* THE TOUR POINTS AT CONTROLS, NOT AT PICTURES — Jay, 2026-09-16, wanted a screen that explains
     itself rather than a manual; Max asked for a guide on 2026-09-24. Every step names a selector
     on this page, and TourGuide SKIPS a step whose control is not there, so the trial-sample step
     disappears for everybody who is not allowed to press it and nothing has to know why.
     Ordered the way the work happens: make a sample, add rounds to it, then read the table. */
  const TOUR_STEPS = useMemo(() => [
    { selector: '.toolbar-actions .btn-create-sample', title: 'tour.stCreateT', body: 'tour.stCreateB' },
    { selector: '.toolbar-actions .btn-new-round', title: 'tour.stRoundT', body: 'tour.stRoundB' },
    { selector: '.toolbar-actions .btn-trial-sample', title: 'tour.stTrialT', body: 'tour.stTrialB' },
    { selector: '.mv-kpi-section', title: 'tour.stCardsT', body: 'tour.stCardsB' },
    { selector: '.st-filter-bar', title: 'tour.stFiltersT', body: 'tour.stFiltersB' },
    { selector: 'tbody tr', title: 'tour.stRowT', body: 'tour.stRowB' },
    { selector: '.btn-export-excel', title: 'tour.stExportT', body: 'tour.stExportB' },
    MANUAL_STEP,
  ], []);
  const [tourOpen, setTourOpen] = useState(false);
  /* Once, unasked, and never again — the value is written when it ENDS, by Done or by Skip alike,
     because a person who skipped it has answered the question just as clearly as one who finished. */
  const TOUR_SEEN = 'portalSampleTourSeen';
  useEffect(() => {
    if (activeTab !== 'sample') return;
    let seen = true;
    try { seen = !!localStorage.getItem(TOUR_SEEN); } catch { seen = true; }
    /* WAIT FOR THE SCREEN, not for a guess at how long it takes. A fixed delay opened the tour
       while the tracker config was still in flight, and the step for the button that config
       controls was quietly dropped. */
    if (!seen && trackerConfig && rounds.length) {
      const id = window.setTimeout(() => setTourOpen(true), 400);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [activeTab, trackerConfig, rounds.length]);
  const closeTour = () => {
    setTourOpen(false);
    try { localStorage.setItem(TOUR_SEEN, '1'); } catch { /* a private window still gets the tour */ }
  };

  const openNewRoundModal = (prefill = {}) => {
    setNewRoundPhotos([]);
    setModal({ mode: 'new', form: { ...emptyForm(), ...prefill } });
    setModalErrors([]);
  };

  const canRaiseTrialSample = !!(trackerConfig?.me?.isAdmin
    || (trackerConfig?.me?.managerTeams || []).includes('DEV'));

  /* The exception route (Max, 2026-09-08). Every other way into this form starts from something that
     already exists — a project SKU, or a sample being given another round. This one starts from
     nothing, so the style name is typed and the server gives it a TSMP Base ID instead of an SMP one.
     `trial` rides on the form, which is posted whole, so nothing else has to know about it. */
  const openTrialSampleModal = () => {
    setNewRoundPhotos([]);
    setModal({ mode: 'new', form: { ...emptyForm(), trial: true } });
    setModalErrors([]);
  };

  /* The SKUs a first sample can be raised on today. `projects` is already loaded for the Project
     Tracker tab, so this costs no request — the same rows, read by the same rules. */
  const sampleCandidates = useMemo(() => {
    /* ON TRACK ONLY — Max, 2026-09-24: "chỉ lấy on track". It was every approved SKU that was
       neither Dropped nor Complete, which also swept in Concern, On Hold and Ship Finished: 121
       rows against 108. The three it drops are SKUs somebody has already flagged as not running
       normally, and a picker whose job is to make the ordinary path obvious should offer the
       ordinary case. The Project Tracker's own "+ Sample" is unchanged and still reaches them. */
    const rows = (projects || []).filter((p) => !p.sampleId
      && p.approvalStatus === 'Approved'
      && p.status === 'On Track');
    const q = String(samplePicker?.q || '').trim().toLowerCase();
    const hit = !q ? rows : rows.filter((p) => [p.id, p.subItem, p.projectItem, p.groupId,
      p.groupName, p.styleCode, p.assignedTo, p.designerName]
      .some((v) => String(v || '').toLowerCase().includes(q)));
    /* GROUPED BY PROJECT, because that is the order Jay describes answering it in — "the first
       thing they answer is which project, then they pick the SKU inside that project" — and
       because 121 SKUs in one flat list is a scroll, while 43 projects is a glance. */
    const byProject = new Map();
    hit.forEach((p) => {
      const key = p.groupId || p.projectItem || '—';
      if (!byProject.has(key)) {
        byProject.set(key, { key, groupId: p.groupId, name: p.projectItem || p.groupName || '—', skus: [] });
      }
      byProject.get(key).skus.push(p);
    });
    const groups = [...byProject.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
    return { groups, skus: hit.length, projects: groups.length, total: rows.length };
  }, [projects, samplePicker]);

  const chooseSampleSku = (project) => {
    setSamplePicker(null);
    /* The same prefill the Project Tracker's "+ Sample" passes, field for field. Two callers
       building it two ways is how one of them quietly stops filling the developer in. */
    openNewRoundModal({
      styleItem: project.subItem || project.projectItem,
      projectId: project.id,
      location: project.allocation || '',
      developer: project.assignedTo || '',
      hincDesigner: project.designerName || '',
    });
  };

  /* OWN ITEMS ONLY (Jay, 30/10): a designer / developer changes only the rounds in their own name — the server refuses
     the rest (trackerOwnerRule); here the Edit and Delete of another person's round are locked and say why. */
  const mayChangeRound = (round) => !ownOnly || (ownOnly.employeeIds || []).some((id) => id === round.developerId || id === round.designerId);
  const notMineTitle = (round) => t('stx.notMine', { name: [round.developer, round.hincDesigner].filter(Boolean).join(' / ') || '—' });
  const openEditRoundModal = (round, view = false) => {
    setModal({ mode: view ? 'view' : 'edit', form: { ...emptyForm(), ...round } });
    setModalErrors([]);
  };

  const duplicateRound = (round) => {
    setModal({
      mode: 'new',
      form: { ...emptyForm(), ...round, sampleRoundId: null, sampleId: '', outcome: '', reworkReason: '' },
    });
    setModalErrors([]);
  };

  const closeModal = () => {
    // Nothing was written yet in advance mode, so cancelling really does drop the Advance outcome too -
    // say so, otherwise the user walks away believing the round was saved.
    if (modal?.mode === 'advance') {
      setMessage(t('stx.msg.advanceCancelled', { id: modal.current?.sampleId || t('stx.msg.theRound') }));
    }
    setModal(null);
    setModalErrors([]);
  };

  const updateModalForm = (patch) => setModal((prev) => (prev ? { ...prev, form: { ...prev.form, ...patch } } : prev));

  // Outcome "Advance" is a hand-off, not an end state: the sample only keeps moving if the next round
  // exists. Rather than saving and hoping the user remembers to duplicate the round (they often did not),
  // the follow-up form opens right here and the two rows are committed together by /rounds/advance-chain.
  const openAdvanceChainModal = (current) => {
    const sameSample = rounds.filter((round) => round.baseId && round.baseId === current.baseId
      && round.sampleId !== current.sampleId);
    setModal({
      mode: 'advance',
      current,
      form: {
        ...emptyForm(),
        ...current,
        sampleRoundId: null,
        sampleId: '',
        round: nextRoundLabel(current.round, sameSample),
        option: '',
        // every per-round value has to be entered again for the new round
        requestDate: '',
        roundDue: '',
        rmReadyDate: '',
        // Per-material RM ready too: a fresh round waits on its own materials, on its own dates.
        materialReady: {},
        materialReadyExpected: true,
        sampleFinishDate: '',
        receivedDate: '',
        commentDate: '',
        actualCostUsd: '',
        outcome: '',
        reworkReason: '',
        remarks: '',
        trialReviewMode: 'designer',
      },
    });
    setModalErrors([]);
  };

  const handleSaveRound = async () => {
    if (!modal) return;
    const errors = validateForm(modal.form, modal.mode);
    // A brand-new Base ID needs a photo (Young via Jay, 2026-08-19). Checked here so the person reads
    // it next to the button, and again on the server, which is the one that actually decides.
    if (modal.mode === 'new' && !newRoundPhotos.length) {
      const style = String(modal.form.styleItem || '').trim().toLowerCase();
      const known = rounds.some((r) => String(r.styleItem || '').trim().toLowerCase() === style);
      if (style && !known) errors.push(t('stx.v.newBasePhoto'));
    }
    if (errors.length) {
      setModalErrors(errors);
      return;
    }
    /* ALREADY ADVANCED IS NOT ADVANCING — Max, 2026-10-22: "có những round cũ cần chỉnh lại thông tin
       á mà do nó đã advanced nên cứ bắt là thêm round mới … NHƯNG VẪN GIỮ rule là nếu đổi trạng thái
       thành advanced thì phải tạo newround". The next round of a round whose STORED outcome is
       Advance was made when it advanced; correcting a date or a remark on it later is an edit.
       Only a change TO Advance opens the follow-up form. Read from the loaded rounds, not the form,
       because the form is what is being changed. */
    const wasAdvance = modal.mode === 'edit' && String(rounds.find((r) => r.sampleRoundId
      && r.sampleRoundId === modal.form.sampleRoundId)?.outcome || '').trim() === 'Advance';
    if (modal.mode !== 'advance' && !wasAdvance && String(modal.form.outcome || '').trim() === 'Advance') {
      openAdvanceChainModal(modal.form);
      return;
    }
    setSaving(true);
    setModalErrors([]);
    try {
      const isAdvance = modal.mode === 'advance';
      const isEdit = modal.mode === 'edit';
      const url = isAdvance
        ? `${API_BASE}/rounds/advance-chain`
        : isEdit ? `${API_BASE}/rounds/${encodeURIComponent(modal.form.sampleId)}` : `${API_BASE}/rounds`;
      const response = await fetch(url, {
        method: isAdvance || !isEdit ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        // The photos ride along with the round on a plain create: the server stores them first and
        // undoes them if the round fails, so a Base ID never exists without the photo it needs.
        body: JSON.stringify(isAdvance
          ? { current: modal.current, next: modal.form }
          : (!isEdit && newRoundPhotos.length
            ? { ...modal.form, photos: newRoundPhotos.map((ph) => ({ mimeType: ph.mimeType, dataBase64: ph.dataBase64, fileName: ph.fileName })) }
            : modal.form)),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('stx.saveFailed', { status: response.status }));
      setRounds(data.rounds || []);
      if (data.projects) setProjects(data.projects);
      const photoWord = !isEdit && !isAdvance && newRoundPhotos.length
        ? t('stx.msg.photosAttached', { count: newRoundPhotos.length, base: data.round?.baseId || t('stx.msg.theBaseId') })
        : '';
      setMessage(isAdvance
        ? t('stx.msg.advanceSaved', { from: data.advancedSampleId, to: data.newSampleId })
        : isEdit ? t('stx.msg.roundUpdated') : `${t('stx.msg.roundAdded')}${photoWord}`);
      setNewRoundPhotos([]);
      closeModal();
    } catch (err) {
      setModalErrors([err.message]);
    } finally {
      setSaving(false);
    }
  };

  // Renaming is a sample-level action, not a round-level one: the Style / Item box stays locked inside the
  // round modal because Base ID is assigned from the style name, so one round may never disagree with its
  // siblings. This renames every round of the Base ID in one write.
  const handleRenameSample = async (sample, nextName) => {
    const current = sample.styleItem || '';
    const styleItem = String(nextName ?? '').trim();
    if (!styleItem || styleItem === current) return;
    setSaving(true);
    setError('');
    try {
      // This called /samples/:baseId/style, a route that was never built — renaming a Base ID has
      // been answering 404 the whole time. The endpoint exists now, under the name of the thing it
      // edits (2026-08-20).
      const response = await fetch(`${API_BASE}/bases/${encodeURIComponent(sample.baseId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ styleItem }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('stx.renameFailed', { status: response.status }));
      setRounds(data.rounds || []);
      if (data.projects) setProjects(data.projects);
      setMessage(t('stx.msg.renamed', { id: sample.baseId, name: styleItem, count: Number(data.renamed) || 0 }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRound = async (sampleId) => {
    if (!sampleId) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('stx.msg.confirmDeleteRound', { id: sampleId }))) return;
    setSaving(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/rounds/${encodeURIComponent(sampleId)}`, { method: 'DELETE', credentials: 'include' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('stx.deleteFailed', { status: response.status }));
      setRounds(data.rounds || []);
      setMessage(t('stx.msg.roundDeleted'));
      closeModal();
    } catch (err) {
      setModalErrors([err.message]);
    } finally {
      setSaving(false);
    }
  };

  /* Jay item 8: each screen opens on the viewer's OWN team; the Team filter stays on screen so they
     can widen it to another team or to everything whenever they want. Applied once, after the config
     arrives — re-applying would fight the user every time they cleared the filter.

     Only for the Sales OP teams, though (Max, 2026-08-20). The Team column on a project holds a Sales
     OP team, so opening the page as someone from Development pre-filtered the grid to "Development"
     and matched nothing — Amy saw an empty Project Tracker. `trackerConfig.teams` IS the Sales OP
     list, so the test is a lookup rather than a hardcoded T1/T2. */
  const teamScopeApplied = useRef(false);
  useEffect(() => {
    const mine = trackerConfig?.me?.teamName;
    if (teamScopeApplied.current || !mine) return;
    teamScopeApplied.current = true;
    const isSalesOpTeam = (trackerConfig?.teams || []).some((tm) => tm.teamKey === trackerConfig?.me?.teamKey);
    if (!isSalesOpTeam) return;      // everyone else opens on All Teams
    setProjectFilters((prev) => ({ ...prev, team: [mine] }));
    setTaskFilters((prev) => ({ ...prev, team: [mine] }));
  }, [trackerConfig]);

  const openNewProjectModal = () => {
    const form = emptyGroupForm();
    // Whoever opens this creates for their own team, so start there — including admins, who can
    // still switch. The team is taken from the account (List Users → Sales OP team).
    if (trackerConfig?.me?.teamKey) form.teamKey = trackerConfig.me.teamKey;
    setNewProject(form);
    // Snapshot of the untouched form. Closing is only worth a confirmation when something has
    // actually been typed — the preset Team must not count as "the user entered data"
    // (Max, 2026-08-13).
    setNewProjectPristine(JSON.stringify(form));
    setNewProjectErrors([]);
  };

  // Cancel / X go through here: ask before throwing away work, close straight away otherwise.
  const requestCloseNewProject = () => {
    // Photos are held outside `newProject`, so a form whose fields are untouched can still have
    // images attached — count them as unsaved work too, and clear them on a real discard.
    if (newProject && (JSON.stringify(newProject) !== newProjectPristine || newProjectPhotos.length)) setConfirmDiscard(true);
    else { setNewProject(null); setNewProjectPhotos([]); }
  };
  const updateNewProject = (patch) => setNewProject((prev) => (prev ? { ...prev, ...patch } : prev));
  const updateSku = (idx, patch) => setNewProject((prev) => {
    if (!prev) return prev;
    const skus = prev.skus.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    return { ...prev, skus };
  });
  const addSku = () => setNewProject((prev) => (prev ? { ...prev, skus: [...prev.skus, emptySku()] } : prev));
  /* The copy lands directly BELOW the card it came from, not at the end of the list. Five colourways
     added one after another read as a group; a copy that appears eight cards away has to be found. */
  const duplicateSkuAt = (index) => setNewProject((prev) => {
    if (!prev) return prev;
    const next = [...prev.skus];
    next.splice(index + 1, 0, duplicateSku(prev.skus[index] || {}));
    return { ...prev, skus: next };
  });
  const removeSku = (idx) => setNewProject((prev) => {
    if (!prev) return prev;
    // Removing the last card leaves the project with zero SKUs, which is now allowed.
    return { ...prev, skus: prev.skus.filter((_, i) => i !== idx) };
  });

  // Add a Partner / Line to the managed master list without leaving the form (managers only).
  // Returns the created row so the picker can select it straight away.
  const handleAddPartner = async (rawName) => {
    const name = String(rawName || '').trim();
    if (!name) return null;
    try {
      const res = await fetch(`${PROJECT_API_BASE}/partners`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('stx.msg.addPartnerFailed'));
      const cfg = await loadTrackerConfig();
      setMessage(t('stx.msg.partnerAdded', { name }));
      return (cfg?.partners || []).find((p) => p.partnerId === data.partnerId) || null;
    } catch (err) {
      setNewProjectErrors([err.message]);
      return null;
    }
  };

  /* Add a Sales Rep to the managed list without leaving the form. Mirrors handleAddPartner; the
     server decides who may (a manager, or anyone who owns a project) — this only reports failure. */
  const handleAddSalesRep = async (rawName) => {
    const name = String(rawName || '').trim();
    if (!name) return null;
    try {
      const res = await fetch(`${PROJECT_API_BASE}/sales-reps`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('stx.msg.addSalesRepFailed'));
      const cfg = await loadTrackerConfig();
      setMessage(t('stx.msg.salesRepAdded', { name }));
      return (cfg?.salesReps || []).find((r) => r.salesRepId === data.salesRepId) || null;
    } catch (err) {
      setNewProjectErrors([err.message]);
      return null;
    }
  };

  const handleSaveNewProject = async (confirmDuplicate = false) => {
    if (!newProject) return;
    const errors = validateGroupForm(newProject);
    // The server moves SKUs within one team only; a team changed after picking would fail half way.
    if ((newProject.moveIns || []).some((x) => x.teamKey !== newProject.teamKey)) errors.push(t('st.mvTeamChanged'));
    if (errors.length) { setNewProjectErrors(errors); return; }
    setProjectSaving(true);
    setNewProjectErrors([]);
    try {
      const skus = newProject.skus
        .filter((s) => String(s.item || '').trim() || String(s.itemCategory || '').trim() || String(s.assigned || '').trim())
        .map((s) => ({ ...s, samples: s.samples ? [String(s.samples).trim()] : [] }));
      const response = await fetch(`${PROJECT_API_BASE}/projects/batch`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          teamKey: newProject.teamKey,
          ownerUserId: newProject.ownerUserId || null,
          salesRepId: newProject.salesRepId || null,
          season: newProject.season,
          seasonYear: newProject.seasonYear || null,
          seasonParts: seasonPartsOrdered(newProject.seasonParts),
          partnerId: newProject.partnerId || null,
          keyword: newProject.keyword,
          targetLaunchMonth: newProject.targetLaunchMonth,
          approvalNote: newProject.approvalNote,
          projectPurpose: newProject.projectPurpose,
          category: newProject.category,
          projectCase: newProject.projectCase,
          entity: newProject.entity,
          skus,
          confirmDuplicate,
        }),
      });
      const data = await response.json();
      // Same Season + Partner already exists: warn in the portal's own dialog, then let the manager
      // decide (item 7). A native window.confirm would also freeze the page for browser automation.
      if (response.status === 409 && data.duplicate) {
        setDuplicateWarn({ matches: data.matches || [] });
        setProjectSaving(false);
        return;
      }
      if (!response.ok) throw new Error(data.error || t('stx.saveFailed', { status: response.status }));
      setProjects(data.projects || []);
      if (data.rounds) setRounds(data.rounds);
      const partnerName = (trackerConfig?.partners || []).find((p) => String(p.partnerId) === String(newProject.partnerId))?.name || '';
      const builtName = buildProjectName({ season: newProject.season, partnerName, keyword: newProject.keyword, category: newProject.category });
      if (data.groupId) {
        setGroups((prev) => [...prev, {
          groupId: data.groupId, groupName: builtName, category: newProject.category,
          projectCase: newProject.projectCase, entity: newProject.entity,
          teamKey: newProject.teamKey, approvalStatus: 'Pending', projectIds: data.projectIds || [],
          // Carried so reopening the project straight after creating it shows what was just typed
          // instead of reading back blank — the row in `groups` is what seeds the Edit form.
          ownerUserId: newProject.ownerUserId || null, season: newProject.season || '',
          seasonYear: newProject.seasonYear || null, seasonParts: seasonPartsOrdered(newProject.seasonParts),
          partnerId: newProject.partnerId || null, keyword: newProject.keyword || '',
          targetLaunchMonth: newProject.targetLaunchMonth || '',
          salesRepId: newProject.salesRepId || null,
          approvalNote: newProject.approvalNote || '',
          projectPurpose: newProject.projectPurpose || '',
          purposeRequired: true,
        }]);
      }
      /* Creating a project no longer emails the approver (Max's boss, 2026-08-21): it lands in Pending
         and the manager submits it, alone or with others. So say what still has to happen — a manager
         who reads "created" and walks away would leave it sitting unseen. */
      const mailNote = data.mail?.ok ? t('stx.msg.mailSent', { to: data.mail.to })
        : data.mail?.error ? t('stx.msg.mailFailed', { err: data.mail.error })
          : t('stx.msg.mailPending');
      // The project exists now, so the photos held in the form can go up. A photo that fails does
      // not undo the project — it is reported and can be added again from the Edit form.
      /* EACH SKU'S OWN PHOTOS, to the id it was just given. The server inserts the SKUs in the
         order they were sent and returns their ids in that order, so index i belongs to card i —
         and if the two lists ever disagree the upload is SKIPPED and said so, because a photo on
         the wrong item is worse than a photo nobody uploaded. */
      let photoNote = '';
      const wanted = skus.reduce((n, sk) => n + ((sk.photos || []).length), 0);
      const ids = Array.isArray(data.projectIds) ? data.projectIds : [];
      if (wanted && ids.length !== skus.length) {
        photoNote = t('stx.msg.photosNotUploaded', { count: wanted });
      } else if (wanted) {
        let done = 0;
        for (let i = 0; i < skus.length; i += 1) {
          const mine = skus[i].photos || [];
          // eslint-disable-next-line no-await-in-loop
          if (mine.length) done += await uploadPendingPhotos(ids[i], mine, 'sku');
        }
        photoNote = done === wanted
          ? t('stx.msg.photosUploaded', { count: done })
          : t('stx.msg.photosPartial', { done, wanted });
      }
      /* APPROVED SKUs MOVED IN, now that the project exists. They carry their approval, so the
         project turns Approved on the server as they land — said in the line rather than "Pending". */
      const moveIns = newProject.moveIns || [];
      let moveNote = '';
      if (moveIns.length && data.groupId) {
        const mv = await moveSkusInto(trackerApi, data.groupId, moveIns);
        moveNote = mv.moved ? t('stx.msg.movedInApproved', { count: mv.moved }) : '';
        if (mv.failed.length) moveNote += t('stx.msg.notMovedRetry', { list: mv.failed.join('; ') });
        await loadData();
      }
      const newCount = skus.length || !moveNote ? t('stx.msg.newSkus', { count: skus.length }) : '';
      setMessage(`${moveNote ? t('stx.msg.created', { id: data.groupId }) : t('stx.msg.createdPending', { id: data.groupId })}${newCount}`
        + `${moveNote}${skus.length || !moveNote ? mailNote : ''}${photoNote}`);
      setNewProjectPhotos([]);
      setNewProject(null);
    } catch (err) {
      setNewProjectErrors([err.message]);
    } finally {
      setProjectSaving(false);
    }
  };

  // --- Edit Project Group helpers -------------------------------------------------
  // Member SKUs / tickets of the group currently open in the group modal.
  const groupSkus = groupModal ? projects.filter((p) => p.groupId === groupModal.groupId) : [];
  const groupTickets = groupModal ? tasks.filter((t) => t.projectId === groupModal.groupId) : [];
  // Task types for this group's Case, the receiving-team list, and what's still addable.
  const groupCaseTicketTypes = (() => {
    if (!groupModal) return [];
    const byTeamCase = taskTypes.filter((t) => (
      (!groupModal.teamKey || !t.teamKey || t.teamKey === groupModal.teamKey)
      && (!groupModal.projectCase || !t.caseName || t.caseName === groupModal.projectCase)
    ));
    const cat = groupModal.category || '';
    const byCat = cat
      ? byTeamCase.filter((t) => !t.categoryScope || String(t.categoryScope).split(';')
        .map((v) => v.trim().toLowerCase()).includes(cat.trim().toLowerCase()))
      : byTeamCase;
    return byCat.length ? byCat : byTeamCase;
  })();
  const groupReceivingTeams = Array.from(new Set(groupCaseTicketTypes.map((t) => t.receivingTeam).filter(Boolean))).sort();

  // Add / edit / drop SKUs inline (created with POST /projects on Save Group).
  const addGroupSku = () => setGroupNewSkus((prev) => [...prev, emptySku()]);
  const updateGroupSku = (idx, patch) => setGroupNewSkus((prev) => prev.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  const removeGroupSku = (idx) => setGroupNewSkus((prev) => prev.filter((_, i) => i !== idx));
  /* From a saved SKU. It lands at the END of the staged list, where the eye goes after pressing a
     button that says "duplicate" on a row above — and where Save Group will pick it up. */
  const duplicateSavedSku = (sku) => setGroupNewSkus((prev) => [...prev, duplicateExistingSku(sku)]);
  const duplicateGroupSku = (idx) => setGroupNewSkus((prev) => {
    const next = [...prev];
    next.splice(idx + 1, 0, duplicateSku(prev[idx] || {}));
    return next;
  });

  // Tickets staged inline (created with POST /tasks on Save Group).
  const groupStagedCodes = groupNewTickets.map((t) => t.taskType);
  /* A code this project already has a ticket for is OFF the list (Max, 2026-09-04). It used to stay,
     greyed out and marked "already added", because hiding codes once made the dropdown look short of
     them (Max, 2026-08-14) — but the tickets themselves are listed right below it now, so the greyed
     rows only said twice what the list already said. */
  /* A CODE CAN BE RAISED AGAIN WHILE IT HAS ITEMS LEFT TO COVER — Max, 2026-09-21: "lần đầu
     ticket apply cho 3 SKU sau đó user tạo thêm SKU thì ticket mới sẽ được tạo cùng request nhưng
     khác SKU". A code used once disappeared from the picker for ever, so a SKU added a month later
     could never be asked for under it — and a project is not finished when its first three items
     are. It leaves the list only when there is nothing left for it to be raised ABOUT: every SKU in
     the project already carried by a ticket of that code, staged or saved. */
  const skusCoveredByCode = (() => {
    const map = new Map();
    const add = (code, ids) => {
      if (!code) return;
      if (!map.has(code)) map.set(code, new Set());
      (ids || []).forEach((id) => map.get(code).add(id));
    };
    groupTickets.forEach((tk) => add(tk.taskType, (tk.fields || {}).skus));
    groupNewTickets.forEach((tk) => add(tk.taskType, (tk.fields || {}).skus));
    return map;
  })();
  const groupSkuIds = groupSkus.map((p) => p.id);
  const groupTakenCodes = new Set([...skusCoveredByCode.entries()]
    .filter(([, covered]) => groupSkuIds.length > 0 && groupSkuIds.every((id) => covered.has(id)))
    .map(([code]) => code));
  /* Which items are spoken for, for ONE ticket — every other ticket of the same code on this
     project. This ticket is left out, or editing it would find every row it owns locked against
     itself. A staged ticket has no id yet and says so. */
  const takenSkusFor = (code, taskId) => {
    const out = {};
    groupTickets.forEach((tk) => {
      if (tk.taskType !== code || tk.taskId === taskId) return;
      ((tk.fields || {}).skus || []).forEach((id) => { out[id] = tk.taskId; });
    });
    groupNewTickets.forEach((tk, i) => {
      if (tk.taskType !== code || tk === taskId) return;
      ((tk.fields || {}).skus || []).forEach((id) => { out[id] = out[id] || t('stx.msg.newTicketN', { n: i + 1 }); });
    });
    return out;
  };
  /* A project with no owner cannot raise a ticket — Max, 2026-09-06. The mail prints "Project Owner:"
     from the project, and one going out with a dash there is what Jay asked about this morning. The
     server refuses it either way; this stops the person filling a card in first and being told
     afterwards. Only NEW tickets: the 64 ownerless projects already carry some, and locking their
     edits would strand work in flight. */
  const groupNeedsOwner = !!groupModal && !groupModal.ownerUserId;
  const groupAvailableTicketTypes = groupCaseTicketTypes
    .filter((t) => !groupTicketTeam || t.receivingTeam === groupTicketTeam)
    .filter((t) => !groupTakenCodes.has(t.code));
  const addGroupTicket = (code) => {
    if (!code || groupStagedCodes.includes(code)) return;
    setGroupNewTickets((prev) => [...prev, makeTicketFromType(code, taskTypes)]);
  };
  const updateGroupTicket = (idx, patch) => setGroupNewTickets((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  const removeGroupTicket = (idx) => setGroupNewTickets((prev) => prev.filter((_, i) => i !== idx));

  // Detach a SKU from the group (keeps the project, just clears its group membership).
  const unlinkSkuFromGroup = async (projectId) => {
    if (!groupModal) return;
    setGroupSaving(true); setGroupError('');
    try {
      const remaining = groupSkus.map((p) => p.id).filter((id) => id !== projectId);
      const res = await fetch(`${PROJECT_API_BASE}/groups/${encodeURIComponent(groupModal.groupId)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ ...groupModal, projectIds: remaining }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('stx.msg.removeSkuFailed'));
      setProjects(data.projects || []);
      setMessage(t('stx.msg.skuRemovedFrom', { id: projectId, group: groupModal.groupId }));
    } catch (err) { setGroupError(err.message); }
    finally { setGroupSaving(false); }
  };

  // Two entry points into the same modal: the full group edit, and a tickets-only view where the
  // project information is shown for context but locked (Max 2026-08-11).
  const openGroupTicketsModal = (group) => openEditGroupModal(group, 'tickets');

  const openEditGroupModal = (group, mode = 'full') => {
    // Seed the structured fields from the stored group, so editing does not blank Team / Owner /
    // Season / Partner / Keyword just because this form did not know about them.
    const stored = groups.find((g) => g.groupId === group.id) || {};
    // Built once and used for BOTH the form state and the pristine snapshot. Written out twice, the
    // two lists drifted the moment a field was added — Sales Rep and Note reached the form on
    // 2026-08-19 and the snapshot would have kept comparing without them, so editing either one
    // would have closed silently instead of asking.
    const seeded = {
      mode,
      /* Development, HINC DT and Production Team may open this form to work on the SKUs and nothing
         else (Max, 2026-08-21). Read off the server's own two flags rather than re-listing the teams
         here — the group PUT refuses them regardless, so the lock is real and not just visual. */
      skuOnly: group.canEditGroup === false && group.canOpenGroup !== false,
      // the season is agreed once the project has a baseline season (projectChange.js) — the group PUT refuses a change
      seasonLocked: !!stored.baselineSeason,
      groupId: group.id,
      groupName: group.name || '',
      category: group.category || '',
      projectCase: group.projectCase || '',
      entity: group.entity || '',
      teamKey: stored.teamKey || '',
      ownerUserId: stored.ownerUserId ? String(stored.ownerUserId) : '',
      season: stored.season || '',
      seasonYear: stored.seasonYear || '',
      seasonParts: seasonPartsOrdered(stored.seasonParts),
      partnerId: stored.partnerId ? String(stored.partnerId) : '',
      keyword: stored.keyword || '',
      targetLaunchMonth: stored.targetLaunchMonth || '',
      salesRepId: stored.salesRepId ? String(stored.salesRepId) : '',
      approvalNote: stored.approvalNote || '',
      projectPurpose: stored.projectPurpose || '',
      purposeRequired: stored.purposeRequired !== false,
    };
    groupMaterials.reset();
    setGroupTicketDrafts({});
    setGroupModal(seeded);
    setGroupError('');
    setGroupNewSkus([]);
    setGroupMoveIns([]);
    const memberSkus = projects.filter((p) => p.groupId === group.id);
    const seededDrafts = Object.fromEntries(memberSkus.map((sku) => [sku.id, projectToSkuDraft(sku)]));
    setGroupSkuDrafts(seededDrafts);
    setGroupSkuOpen({});
    setGroupNewTickets([]);
    groupPristine.current = fingerprint({ group: groupInfoShape(seeded), skus: seededDrafts });
    setConfirmGroupDiscard(false);
    setGroupTicketTeam('');
    loadTasks(); // tickets + task types for the in-modal Tickets section
  };
  const updateGroupTicketDraft = (ticket, patch) => setGroupTicketDrafts((prev) => ({
    ...prev, [ticket.taskId]: { ...(prev[ticket.taskId] || ticket), ...patch },
  }));

  /* Exactly the draft the ticket card shows, so what is stored and what was previewed are the same
     text — same helper, same form, same answers. */
  const ticketMailFor = (tk) => ticketEmailDraft(
    tk,
    tk.createdBy || tk.assignee || userName,
    [groupModal.groupId, groupModal.groupName].filter(Boolean).join('_'),
    (trackerConfig?.teams || []).find((tm) => tm.teamKey === groupModal.teamKey)?.teamName || '',
    ((trackerConfig?.teams || []).find((tm) => tm.teamKey === groupModal.teamKey)?.members || [])
      .find((m) => String(m.userId) === String(groupModal.ownerUserId))?.name || '',
    ticketFormOf(ticketForms, tk.taskType),
    Object.fromEntries(groupSkus.map((sk) => [sk.id, sk.subItem || sk.projectItem || ''])),
    ticketKoOf(ticketForms, tk.taskType),
  );

  /* What happened to each ticket's mail on this save. The server SAVES a ticket whose mail it
     withholds — a half-filled request is a legitimate draft to keep — and says why in `email.error`.
     These paths read only `ok`, so a withheld mail looked exactly like a sent one: Amy ticked "Send
     email after saving" on TSK-0091 (2026-10-15), saved twice, and nothing on the screen said the
     mail had stayed behind. Ticket Station's own save has always printed it. */
  const ticketMailOutcome = (data, tally) => {
    if (!data || !data.email) return;
    if (data.email.error) tally.failed.push(`${data.taskId || t('stx.msg.ticketWord')}: ${data.email.error}`);
    else if (data.email.ok) tally.sent += 1;
  };
  const ticketMailNote = (tally) => `${tally.sent ? t('stx.msg.ticketsEmailed', { n: tally.sent }) : ''}${
    tally.failed.length ? t('stx.msg.emailNotSent', { list: tally.failed.join('; ') }) : ''}`;

  const saveGroup = async () => {
    if (!groupModal) return;
    if (!String(groupModal.groupName || '').trim()) { setGroupError(t('stx.v.groupName')); return; }
    if (groupMoveIns.some((x) => x.teamKey !== groupModal.teamKey)) { setGroupError(t('st.mvTeamChanged')); return; }
    /* Target Launching against today and the season — checked HERE, before the first SKU is written: the group is saved
       last (below), so a refusal from it would leave a half-saved form. Only what this save changed is judged. */
    if (!groupModal.skuOnly && groupModal.mode !== 'tickets' && groupModal.mode !== 'view') {
      const stored = groups.find((g) => g.groupId === groupModal.groupId) || null;
      const launchErr = launchErrors(launchCheck(groupModal, stored), groupModal.launchConfirmed, t);
      if (launchErr.length) { setGroupError(launchErr.join(' ')); return; }
    }
    setGroupSaving(true); setGroupError('');
    /* SKU-only mode skips the group write entirely. The server would refuse it anyway (403 from
       mayEditGroup), and that refusal would abort the whole save before a single SKU was written —
       so the SKUs would silently fail to save for exactly the teams meant to edit them. */
    const skuOnly = !!groupModal.skuOnly;
    // Tickets-only mode never writes the project or its SKUs — the form showed them locked, so
    // sending them back would be a silent no-op at best and an accidental overwrite at worst.
    if (groupModal.mode === 'tickets') {
      try {
        let made = 0;
        const mail = { sent: 0, failed: [] };
        for (const ticket of groupNewTickets) {
          // Same helper the card previews with — see the note on the other create loop. With the
          // request forms off it falls through to the shape the mail has always had, so this is the
          // one path on production too.
          const draft = ticketMailFor(ticket);
          // eslint-disable-next-line no-await-in-loop
          const tr = await fetch(`${PROJECT_API_BASE}/tasks`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
            body: JSON.stringify({
              projectId: groupModal.groupId, taskType: ticket.taskType, name: ticket.name, description: ticket.description,
              priority: ticket.priority, dueDate: ticket.dueDate, status: ticket.status, note: ticket.note,
              attachmentUrl: ticket.attachmentUrl, email: ticket.email, department: ticket.department,
              /* Who raised it. The card prints this in the mail but never sent it, so the Requester
                 column stayed empty and Ticket Station had nothing to show or to rebuild from. */
              assignee: ticket.createdBy || userName,
              fields: ticket.fields || {},
              // Anything attached before this ticket had an id. The server re-keys it to the new
              // task id before it composes the mail, so a ticket sent on creation carries its files.
              fileDraftKey: ticket.fileDraftKey || '',
              emailDraft: {
                sendEmail: !!(ticket.sendEmail || ticket.sendManual), subject: draft.subject,
                body: draft.body, html: draft.html || '', cc: ticket.emailCc || '',
              },
            }),
          });
          // The approval gate answers 403 here when the project is not approved yet — surface it
          // rather than reporting "0 tickets added" with no reason.
          // eslint-disable-next-line no-await-in-loop
          const td = await tr.json().catch(() => ({}));
          if (tr.ok) { made += 1; ticketMailOutcome(td, mail); }
          else throw new Error(td.error || t('stx.msg.createTicketFailed', { code: ticket.taskType }));
        }
        if (made) await loadTasks(true);
        setMessage(made ? `${t('stx.msg.ticketsAddedTo', { count: made, id: groupModal.groupId })}${ticketMailNote(mail)}` : t('stx.msg.noNewTicket'));
        setGroupModal(null);
      } catch (err) { setGroupError(err.message); }
      finally { setGroupSaving(false); }
      return;
    }
    try {
      let projectsAfter = [];
      /* THE GROUP IS SAVED LAST — Thuan, 2026-10-05, through Max. It used to go first, and changing
         the project owner in the same save is what exposed why that is wrong: the group PUT wrote
         the new owner, and every per-SKU PUT after it was judged against that new owner, so the
         person who had just pressed Save was refused on his own SKUs.
         What he saw was the worst version of it — "SKU-00652 is assigned to Minwoo Nam", and then
         the new owner sitting there anyway after a refresh. Max: "nhìn như hệ thống bị lỗi á". Half
         the save had gone through and the screen reported the half that had not.
         The group update is the ONLY step that can take the caller's rights away, so it is the only
         one whose position matters. Everything else is then judged against the person who opened
         the form, which is who they still were when they pressed Save. */
      const saveGroupFields = async () => {
        if (skuOnly) return;
        const res = await fetch(`${PROJECT_API_BASE}/groups/${encodeURIComponent(groupModal.groupId)}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify(groupModal),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || t('stx.saveFailedPlain'));
        projectsAfter = data.projects || projectsAfter;
      };
      const savedPartnerName = (trackerConfig?.partners || [])
        .find((pp) => String(pp.partnerId) === String(groupModal.partnerId))?.name || '';
      const savedName = buildProjectName({
        season: groupModal.season, partnerName: savedPartnerName,
        keyword: groupModal.keyword, category: groupModal.category,
      }) || groupModal.groupName;
      const patchGroupRow = () => setGroups((prev) => prev.map((g) => (g.groupId === groupModal.groupId
        ? {
          ...g,
          groupName: savedName,
          category: groupModal.category,
          projectCase: groupModal.projectCase,
          entity: groupModal.entity,
          teamKey: groupModal.teamKey || '',
          teamName: (trackerConfig?.teams || []).find((tm) => tm.teamKey === groupModal.teamKey)?.teamName || '',
          ownerUserId: groupModal.ownerUserId || null,
          ownerName: ((trackerConfig?.teams || []).find((tm) => tm.teamKey === groupModal.teamKey)?.members || [])
            .find((m) => String(m.userId) === String(groupModal.ownerUserId))?.name || '',
          season: groupModal.season || '',
          // the launch check reads the stored Year + parts as "before" on the next edit (projectLaunchCheck.js)
          seasonYear: groupModal.seasonYear || null,
          seasonParts: seasonPartsOrdered(groupModal.seasonParts),
          partnerId: groupModal.partnerId || null,
          partnerName: savedPartnerName,
          keyword: groupModal.keyword || '',
          targetLaunchMonth: groupModal.targetLaunchMonth || '',
          // These two were missing here while the form already sent them, so reopening the form read
          // back a blank Note and a blank Sales Rep even though the save had worked — the row in
          // `groups` is what seeds the form. Third hand-written copy of the same field list.
          salesRepId: groupModal.salesRepId || null,
          salesRepName: (trackerConfig?.salesReps || [])
            .find((sr) => String(sr.salesRepId) === String(groupModal.salesRepId))?.name || '',
          approvalNote: groupModal.approvalNote || '',
          projectPurpose: groupModal.projectPurpose || '',
        }
        : g)));

      /* EVERY SKU IS BUILT AND CHECKED BEFORE ANY OF THEM IS SENT — Max, 2026-10-05. This loop
         used to validate each one as it reached it, so a form with five SKUs and a problem on the
         third wrote the first two and then threw; the screen reported a failure about a save that
         had half happened.
         TWO THINGS COULD REFUSE MID-WAY and both are knowable first: the form's own rules, which
         are checked here, and whether this person may edit each SKU at all, which only the server
         knows — hence the precheck below. */
      const editedSkus = Object.values(groupSkuDrafts).filter((sku) => sku && sku.id);
      const skuForms = editedSkus.map((sku) => ({
        ...sku,
        projectItem: String(groupModal.groupName || sku.projectItem || '').trim(),
        category: groupModal.category,
        groupId: groupModal.groupId,
      }));
      for (const skuForm of skuForms) {
        const errors = validateProjectForm(skuForm);
        if (errors.length) throw new Error(`${skuForm.id}: ${errors.join(', ')}`);
      }
      // The SKUs being ADDED are checked here too, before anything is sent — the four required fields.
      groupNewSkus
        .filter((sku) => String(sku.item || '').trim() || String(sku.itemCategory || '').trim() || String(sku.assigned || '').trim())
        .forEach((sku, i) => {
          const miss = skuCardMissing(sku);
          if (miss.length) throw new Error(t('stx.v.namedMissing', { name: sku.item || t('stx.newSkuN', { n: i + 1 }), fields: miss.join(', '), count: miss.length }));
        });
      /* THE SAME RIGHTS THE WRITES WILL BE JUDGED BY, asked once for all of them. A person can be
         allowed to edit some SKUs of a project and not others — which is exactly what Thuan hit —
         so asking row by row is what made a half-save possible. */
      {
        const pre = await fetch(
          `${PROJECT_API_BASE}/groups/${encodeURIComponent(groupModal.groupId)}/precheck`,
          { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
            body: JSON.stringify({ projectIds: skuForms.map((x) => x.id), group: !skuOnly }) });
        const preData = await pre.json().catch(() => ({}));
        if (!pre.ok) throw new Error(preData.error || t('stx.msg.refusedBefore'));
      }
      for (const skuForm of skuForms) {
        // eslint-disable-next-line no-await-in-loop
        const skuRes = await fetch(`${PROJECT_API_BASE}/projects/${encodeURIComponent(skuForm.id)}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify(skuForm),
        });
        // eslint-disable-next-line no-await-in-loop
        const skuData = await skuRes.json();
        if (!skuRes.ok) throw new Error(skuData.error || t('stx.msg.updateFailed', { id: skuForm.id }));
        projectsAfter = skuData.projects || projectsAfter;
        if (skuData.rounds) setRounds(skuData.rounds);
      }

      // The material lists typed on the SKU cards. After the SKU writes, because a SKU that failed
      // validation should stop the save before its materials are written to a row that did not move.
      await groupMaterials.save();

      /* Tickets that already existed and were edited in their card. The same POST that created
         them — it updates when a taskId is present — and the same payload, because it is the same
         form. The mail is rebuilt from the edited answers: it is what a resend would use, and the
         server only actually sends a ticket that has never gone out. */
      const editedTickets = Object.values(groupTicketDrafts).filter((tk) => tk && tk.taskId);
      const ticketMail = { sent: 0, failed: [] };
      for (const tk of editedTickets) {
        const draft = ticketMailFor(tk);
        // eslint-disable-next-line no-await-in-loop
        const tr = await fetch(`${PROJECT_API_BASE}/tasks`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({
            taskId: tk.taskId, projectId: groupModal.groupId, taskType: tk.taskType, name: tk.name,
            description: tk.description, priority: tk.priority, dueDate: tk.dueDate, status: tk.status,
            note: tk.note, attachmentUrl: tk.attachmentUrl, email: tk.email, department: tk.department,
            fields: tk.fields || {},
            emailDraft: {
              sendEmail: !!(tk.sendEmail || tk.sendManual), subject: draft.subject, body: draft.body,
              html: draft.html || '', cc: tk.emailCc || '',
            },
          }),
        });
        // eslint-disable-next-line no-await-in-loop
        const td = await tr.json().catch(() => ({}));
        if (!tr.ok) throw new Error(td.error || t('stx.msg.updateTicketFailed', { id: tk.taskId }));
        ticketMailOutcome(td, ticketMail);
      }

      // Create the SKUs added inline (one PRJ item each, all under this group).
      const newSkus = groupNewSkus.filter((sku) => String(sku.item || '').trim() || String(sku.itemCategory || '').trim() || String(sku.assigned || '').trim());
      for (const sku of newSkus) {
        // eslint-disable-next-line no-await-in-loop
        const r = await fetch(`${PROJECT_API_BASE}/projects`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({
            projectItem: String(sku.item || '').trim() || groupModal.groupName,
            subItem: String(sku.item || '').trim(),
            category: groupModal.category,
            productType: sku.itemCategory || '',
            typology: sku.typology || '',
            frameColor: sku.frameColor || '',
            assignedTo: sku.assigned || '',
            designerName: sku.designer || '',
            allocation: sku.allocation || '',
            status: sku.status || 'On Track',
            startDate: sku.startDate || '',
            exfShipDate: String(sku.exfDate || '') === 'TBA' ? '' : (sku.exfDate || ''),
            estimateQty: String(sku.qty || '') === 'TBA' ? '' : (sku.qty || ''),
            targetPrice: sku.targetPrice || '',
            launchDate: sku.launchDate || '',
            remark: sku.remark || '',
            sampleId: sku.samples || '',
            stages: sku.stages || {},
            repeatOrder: !!sku.repeatOrder,
            groupId: groupModal.groupId,
          }),
        });
        // eslint-disable-next-line no-await-in-loop
        const rd = await r.json();
        if (!r.ok) throw new Error(rd.error || t('stx.msg.addSkuFailed'));
        projectsAfter = rd.projects || projectsAfter;
      }
      /* AND NOW THE GROUP, after everything that needed the caller to still be its owner. The row
         on the grid is patched from the same values, after the save that wrote them. */
      await saveGroupFields();
      if (!skuOnly) patchGroupRow();
      setProjects(projectsAfter);

      // Create the tickets staged inline.
      let ticketsMade = 0;
      for (const t of groupNewTickets) {
        /* The card's own draft builder, not buildTaskEmailDraft: that one knows nothing about the
           request form, so a ticket raised here was stored — and emailed — with the block the card
           had just previewed missing from its body. */
        const draft = ticketMailFor(t);
        // eslint-disable-next-line no-await-in-loop
        const tr = await fetch(`${PROJECT_API_BASE}/tasks`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({
            projectId: groupModal.groupId, taskType: t.taskType, name: t.name, description: t.description,
            priority: t.priority, dueDate: t.dueDate, status: t.status, note: t.note,
            attachmentUrl: t.attachmentUrl, email: t.email, department: t.department,
            assignee: t.createdBy || userName,
            fields: t.fields || {},
            /* The files attached while the ticket was being written. This is the THIRD place in this
               file that posts a new ticket — tickets-only mode, the full group save, and Ticket
               Station — and adding the key to two of them shipped a feature that worked from one
               screen and silently dropped the files from another (Max, 2026-09-08: "mở lại ticket
               vừa tạo thì ko thấy file"). A payload built three times is one that has to be changed
               three times. */
            fileDraftKey: t.fileDraftKey || '',
            emailDraft: {
              sendEmail: !!(t.sendEmail || t.sendManual), subject: draft.subject, body: draft.body,
              html: draft.html || '', cc: t.emailCc || '',
            },
          }),
        });
        // eslint-disable-next-line no-await-in-loop
        const td = await tr.json().catch(() => ({}));
        if (tr.ok) { ticketsMade += 1; ticketMailOutcome(td, ticketMail); }
      }
      /* Editing a ticket that already exists counts too. It did not, so a save wrote the answers to
         the database and left the screen holding the copy it had loaded before — reopening the group
         showed the ticket empty again and looked like the save had been thrown away (Max,
         2026-09-03: "lưu xong mở lên mất tiu"). Nothing was ever lost; the list was stale. */
      const ticketsTouched = ticketsMade || editedTickets.length;
      if (ticketsTouched) loadTasks(true);

      // Approved SKUs picked from another project of the team, moved in now the group is saved.
      let moveNote = '';
      if (groupMoveIns.length) {
        const mv = await moveSkusInto(trackerApi, groupModal.groupId, groupMoveIns);
        moveNote = mv.moved ? t('stx.msg.skuMovedIn', { n: mv.moved }) : '';
        if (mv.failed.length) moveNote += t('stx.msg.notMoved', { list: mv.failed.join('; ') });
        setGroupMoveIns([]);
      }

      await loadData();
      if (ticketsTouched) await loadTasks(true);
      setMessage(`${t('stx.msg.groupUpdated')}${editedSkus.length ? t('stx.msg.skusUpdated', { n: editedSkus.length }) : ''}${editedTickets.length ? t('stx.msg.ticketsUpdated', { n: editedTickets.length }) : ''}${newSkus.length ? t('stx.msg.skusAdded', { n: newSkus.length }) : ''}${ticketsMade ? t('stx.msg.ticketsAdded', { n: ticketsMade }) : ''}${ticketMailNote(ticketMail)}${moveNote}`);
      setGroupModal(null);
    } catch (err) { setGroupError(err.message); }
    finally { setGroupSaving(false); }
  };
  // Mirror of goToProjectSku. Switching the tab through the URL, not local state: the address bar
  // and the sidebar highlight both follow the route, so setting the tab directly moved the content
  // while the sidebar still read "Project Tracker" (Max, 2026-08-13).
  // Complete = tell Production AND move the status, as one action. The dialog previews the exact
  // mail; cancelling leaves the SKU untouched, so the status can never claim Production was told
  // when no mail went out (Max, 2026-08-13).
  const [completeFor, setCompleteFor] = useState(null);   // { project, to, cc, subject, body }
  const [completeBusy, setCompleteBusy] = useState(false);

  // Esc for the dialogs written inline in this component (the ones built as components take it
  // themselves). Each is registered only while it is on screen, so the shared stack closes the
  // top-most one: Esc on the discard prompt answers the prompt, it does not also drop the form.
  // Edit Project Group: dirty = any field, any SKU draft, or anything staged inline (new SKUs /
  // tickets, which exist only in this form until Save).
  const groupDirty = () => {
    if (!groupModal) return false;
    if (groupNewSkus.length || groupNewTickets.length || groupMoveIns.length) return true;
    if (groupMaterials.dirty() || Object.keys(groupTicketDrafts).length) return true;
    const live = fingerprint({ group: groupInfoShape(groupModal), skus: groupSkuDrafts });
    return live !== groupPristine.current;
  };
  const requestCloseGroupModal = () => {
    if (groupDirty()) setConfirmGroupDiscard(true);
    else setGroupModal(null);
  };

  useEscapeClose(() => setDeleteTicketOne(null), !!deleteTicketOne);
  useEscapeClose(() => setDeleteGroupPrompt(null), !!deleteGroupPrompt);
  useEscapeClose(() => setBaseModal(null), !!baseModal && !saving);
  useEscapeClose(requestCloseGroupModal, !!groupModal && !groupSaving);
  useEscapeClose(() => setConfirmGroupDiscard(false), confirmGroupDiscard);
  useEscapeClose(() => setCompleteFor(null), !!completeFor && !completeBusy);
  useEscapeClose(() => setDuplicateWarn(null), !!duplicateWarn);
  useEscapeClose(() => setConfirmDiscard(false), !!confirmDiscard);
  const [completeError, setCompleteError] = useState('');

  const openCompleteModal = async (project) => {
    setCompleteError('');
    setCompleteFor({ project, to: '', cc: '', subject: '', body: '', loading: true });
    try {
      const res = await fetch(`${PROJECT_API_BASE}/projects/${encodeURIComponent(project.id)}/complete-preview`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('stx.msg.previewFailed'));
      setCompleteFor({ project, to: data.to || '', cc: data.cc || '', subject: data.subject, body: data.body, loading: false });
    } catch (err) {
      setCompleteFor(null);
      setMessage(t('stx.msg.completeOpenFailed', { err: err.message }));
    }
  };

  const submitComplete = async () => {
    if (!completeFor || !String(completeFor.to || '').trim()) { setCompleteError(t('stx.v.recipient')); return; }
    setCompleteBusy(true);
    setCompleteError('');
    try {
      const res = await fetch(`${PROJECT_API_BASE}/projects/${encodeURIComponent(completeFor.project.id)}/complete`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ to: completeFor.to, cc: completeFor.cc, subject: completeFor.subject, body: completeFor.body }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('stx.msg.completeFailed'));
      setProjects(data.projects || []);
      setMessage(t('stx.msg.completed', { id: completeFor.project.id, to: data.to }));
      setCompleteFor(null);
    } catch (err) {
      setCompleteError(err.message);
    } finally {
      setCompleteBusy(false);
    }
  };

  const viewProjectSamples = (project) => {
    setSampleView('all');
    setSampleFilters({ ...SAMPLE_FILTERS_DEFAULT, search: project.id });
    clearColumnFilters('samples');
    navigate('/development/sample-tracker?tab=sample');
  };

  const isProjectColVisible = (col) => col.locked || projectColumnVisible[col.key] !== false;
  const visibleProjectColumns = useMemo(
    () => PROJECT_COLUMNS.filter((col) => col.locked || projectColumnVisible[col.key] !== false),
    [projectColumnVisible],
  );
  const projectGridWidth = useMemo(() => visibleProjectColumns.reduce((sum, c) => sum + c.width, 0), [visibleProjectColumns]);

  /* Export the Project Tracker grid to .xlsx (Max, 2026-08-17). Reuses the sample-tracker
     export endpoint, which is generic — it takes columns, rows and widths and does the styling.
     Two deliberate differences from the All Samples export:
       - the Columns picker drives the export, so what you hid on screen stays out of the file;
       - every SKU of every filtered group is written, not just the expanded ones. A collapsed
         grid would otherwise export as a list of group headers with no data in it. */
  const projectExportValue = (col, ctx) => {
    const { group, project, index } = ctx;
    const isGroup = !project;
    if (col.key.startsWith('stage:')) return isGroup ? '' : (project[col.key.slice(6)] || '');
    switch (col.key) {
      case 'no': return isGroup ? index + 1 : '';
      case 'id': return isGroup ? (group.id || 'PG') : project.id;
      case 'name': return isGroup ? group.name : (project.subItem || project.projectItem || '');
      case 'team': return isGroup ? group.teamName : '';
      case 'owner': return isGroup ? group.ownerName : '';
      case 'salesRep': return isGroup ? group.salesRepName : '';
      case 'projectStage': return isGroup ? projectStateText(group.key) : project.status;
      case 'category': return isGroup ? group.category : project.category;
      case 'projectCase': return isGroup ? group.projectCase : '';
      case 'entity': return isGroup ? group.entity : '';
      case 'itemCategory': return isGroup ? '' : project.productType;
      case 'frameColor': return isGroup ? '' : project.frameColor;
      case 'qty': return isGroup ? '' : project.estimateQty;
      case 'assigned': return isGroup ? '' : project.assignedTo;
      case 'designer': return isGroup ? '' : project.designerName;
      case 'allocation': return isGroup ? '' : project.allocation;
      case 'startDate': return isGroup ? '' : project.startDate;
      case 'exfDate': return isGroup ? '' : project.exfShipDate;
      case 'launchDate': return isGroup ? '' : project.launchDate;
      case 'targetPrice': return isGroup ? '' : (project.targetPrice ?? '');
      case 'weeksLeft': return isGroup ? '' : project.leadTime;
      case 'pct': return isGroup
        ? `${avgPct(group.items)}%`
        : (Number.isFinite(project.completePct) ? `${project.completePct}%` : '');
      case 'remark': return isGroup ? '' : project.remark;
      case 'sampleId': return isGroup ? '' : project.sampleId;
      default: return '';
    }
  };

  const handleExportProjectsXlsx = async () => {
    const cols = visibleProjectColumns.filter((c) => c.key !== 'action');
    if (!projectGroups.length) { setMessage(t('st.ptNoProjects')); return; }

    const rows = [];
    projectGroups.forEach((group, index) => {
      rows.push({ type: 'base', values: cols.map((c) => projectExportValue(c, { group, index })) });
      group.items.forEach((project) => {
        rows.push({ type: 'round', values: cols.map((c) => projectExportValue(c, { group, project })) });
      });
    });

    const listLabel = (list, allLabel) => (list && list.length ? list.join(', ') : allLabel);
    const filters = [
      t('stx.xls.status', { v: listLabel(projectFilters.status, t('st.allStatus')) }),
      t('stx.xls.team', { v: listLabel(projectFilters.team, t('st.ptAllTeams')) }),
      t('stx.xls.owner', { v: listLabel(projectFilters.owner, t('st.ptAllOwners')) }),
      t('stx.xls.developer', { v: listLabel(projectFilters.assigned, t('st.ptAllAssignees')) }),
      t('stx.xls.designer', { v: listLabel(projectFilters.designer, t('st.ptAllDesigners')) }),
      t('stx.xls.salesRep', { v: listLabel(projectFilters.salesRep, t('st.ptAllSalesReps')) }),
    ];
    if (projectFilters.search.trim()) filters.push(t('stx.xls.search', { v: projectFilters.search.trim() }));

    const stamp = fmtDateTimeVN(new Date(), '');
    const fileStamp = stamp.replace(/[: ]/g, '-');
    // The grid's widths are pixels; Excel counts characters, and ~7px per character matches.
    const widths = cols.map((c) => Math.max(8, Math.round(c.width / 7)));
    // The SKU's status lives in Project Status since 2026-10-20 (the old Status column came off).
    const statusColumn = cols.findIndex((c) => c.key === 'projectStage') + 1; // 1-based, 0 = no tinting

    try {
      setMessage(t('stx.preparingExport'));
      const response = await fetch(`${BACKEND_URL}/api/development/sample-tracker/export-xlsx`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          title: t('stx.xls.titleProjects'), generated: stamp, filters,
          columns: cols.map((c) => colLabel(c.label)), rows, widths,
          sheetName: 'Project Tracker', statusColumn, freezeColumns: 3,
          filename: `HEVN_Project_Tracker_${fileStamp}.xlsx`,
        }),
      });
      if (!response.ok) {
        let msg = t('stx.exportFailed', { status: response.status });
        try { const j = await response.json(); if (j.error) msg = j.error; } catch (e) { /* non-JSON */ }
        throw new Error(msg);
      }
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = match ? match[1] : `HEVN_Project_Tracker_${fileStamp}.xlsx`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
      setMessage(t('stx.msg.exportedProjects', { groups: projectGroups.length, skus: filteredProjects.length }));
    } catch (err) {
      setMessage(err.message || t('stx.exportFailedPlain'));
    }
  };
  /* EXCEL OUT OF TICKET STATION — Jay, 2026-09-16: "It will be better to have EXCEL EXPORT
     function as open tickets are getting increasing."

     It exports `ticketRows`, which is the grid AFTER every filter, the KPI cards and the column
     funnels — what is on screen is what lands in the file. The filter line at the top of the sheet
     says which rules produced it, **including the KPI card**: that card narrows the grid as much as
     any dropdown and is not one of the filter boxes, so a file quietly holding only the overdue
     rows with nothing to say so is the same trap as a screen filtered by something it does not
     mention.

     Two columns of the grid hold two things each and are split here, because a spreadsheet is
     sorted and filtered per column: Ticket carries the name and the description, Assignee reply
     the status and their note. And the Actions column becomes **Email sent** — the buttons cannot
     travel, but what they mean (Send on a ticket never emailed, Remind after) is a date, and with
     tickets piling up that is the column somebody will sort by.

     The backend endpoint is the Project Tracker's, unchanged: it takes columns, rows and widths and
     knows nothing about which screen sent them. */
  const handleExportTicketsXlsx = async () => {
    if (!ticketRows.length) { setMessage(t('st.tkExportEmpty')); return; }
    const cols = [
      ['st.tkColProject', 14, (r) => r.row.projectId || ''],
      ['st.tkColProjectName', 30, (r) => r.group.projectTitle || ''],
      ['st.tkColTicketId', 14, (r) => r.row.taskId || ''],
      ['st.tkColTicket', 34, (r) => r.row.name || ''],
      ['st.tkColDescription', 40, (r) => r.row.description || ''],
      ['st.tkManagerStatus', 16, (r) => taskStatusLabel(taskNormalizeStatus(r.row.status))],
      ['st.tkColAssigneeReply', 16, (r) => r.row.assigneeStatus || 'Not started'],
      ['st.tkColAssigneeNote', 32, (r) => r.row.assigneeNote || ''],
      ['st.ptTeam', 18, (r) => r.row.teamName || ''],
      ['st.ptOwner', 18, (r) => r.row.ownerName || ''],
      ['st.tkColTeam', 20, (r) => r.row.department || ''],
      ['st.tkColRequester', 16, (r) => r.row.assignee || ''],
      ['st.tkColType', 14, (r) => r.row.taskType || ''],
      ['st.tkColPriority', 12, (r) => r.row.priority || 'Medium'],
      ['st.tkColDue', 13, (r) => fmtDateVN(r.row.dueDate, '')],
      ['st.colDday', 9, (r) => (r.row.dDay == null ? '' : r.row.dDay)],
      ['st.tkColNote', 34, (r) => r.row.note || ''],
      ['st.tkColEmailSent', 17, (r) => fmtDateTimeVN(r.row.sentAt, '')],
      ['st.tkColCreatedBy', 14, (r) => r.row.createdBy || ''],
      ['st.tkColCreatedAt', 13, (r) => fmtDateVN(r.row.createdAt, '')],
      ['st.tkColUpdatedBy', 14, (r) => r.row.updatedBy || ''],
      ['st.tkColUpdatedAt', 13, (r) => fmtDateVN(r.row.updatedAt, '')],
    ];
    const rows = ticketRows.map((entry) => ({
      type: 'round', values: cols.map(([, , get]) => get(entry)),
    }));

    const listLabel = (list, allLabel) => (list && list.length ? list.join(', ') : allLabel);
    const kpiLabel = {
      all: t('st.tkAll'), done: t('st.tkDone'), cancelled: t('st.tkCancelled'),
      pending: t('st.tkPending'), overdue: t('st.tkOverdue'),
    };
    const filters = [
      t('stx.xls.card', { v: kpiLabel[taskKpiFilter] || kpiLabel.all }),
      t('stx.xls.status', { v: listLabel(taskFilters.status, t('st.allStatus')) }),
      t('stx.xls.requester', { v: listLabel(taskFilters.requester, t('st.tkAllRequesters')) }),
      /* `team` starts life as [] and becomes a STRING once somebody picks one — it is a
         SingleFilter over state initialised like the multi ones. An empty array is truthy, so
         `taskFilters.team || fallback` printed nothing at all where "All Sales OP Teams" belonged. */
      t('stx.xls.sopTeam', { v: (Array.isArray(taskFilters.team)
        ? (taskFilters.team.length ? taskFilters.team.join(', ') : '')
        : String(taskFilters.team || '').trim()) || t('st.tkAllSopTeams') }),
      t('stx.xls.receivingTeam', { v: listLabel(taskFilters.dept, t('st.tkAllTeams')) }),
      t('stx.xls.priority', { v: listLabel(taskFilters.priority, t('st.tkAllPriority')) }),
    ];
    if (taskFilters.search.trim()) filters.push(t('stx.xls.search', { v: taskFilters.search.trim() }));

    const stamp = fmtDateTimeVN(new Date(), '');
    const fileStamp = stamp.replace(/[: ]/g, '-');
    try {
      setMessage(t('stx.preparingExport'));
      const response = await fetch(`${BACKEND_URL}/api/development/sample-tracker/export-xlsx`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          title: t('stx.xls.titleTickets'), generated: stamp, filters,
          columns: cols.map(([key]) => t(key)), rows,
          widths: cols.map(([, w]) => w),
          sheetName: 'Ticket Station',
          // 1-based, and it is the Manager status column — the one the cards count.
          statusColumn: 6, freezeColumns: 3,
          /* Excel's own filter on the header row — Max, 2026-09-16. Safe here and not on the
             Project Tracker's sheet, which is grouped: filtering there could hide a base row and
             leave its rounds under the wrong one. */
          autoFilter: true,
          // D-Day, 1-based. As text it sorts 10 before 9, which only becomes visible once the sheet
          // can be sorted — so it travels with the filter rather than after it.
          numericColumns: [16],
          filename: `HEVN_Ticket_Station_${fileStamp}.xlsx`,
        }),
      });
      if (!response.ok) {
        let msg = t('stx.exportFailed', { status: response.status });
        try { const j = await response.json(); if (j.error) msg = j.error; } catch (e) { /* non-JSON */ }
        throw new Error(msg);
      }
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = match ? match[1] : `HEVN_Ticket_Station_${fileStamp}.xlsx`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
      setMessage(t('stx.msg.exportedTickets', { n: ticketRows.length }));
    } catch (err) {
      setMessage(err.message || t('stx.exportFailedPlain'));
    }
  };

  const toggleProjectColumn = (key, visible) => {
    const next = { ...projectColumnVisible, [key]: visible };
    setProjectColumnVisible(next);
    try { localStorage.setItem(PT_PROJECT_COL_STORE, JSON.stringify(next)); } catch (e) { /* ignore */ }
  };
  const showAllProjectColumns = () => {
    const next = {};
    PROJECT_COLUMNS.forEach((c) => { next[c.key] = true; });
    setProjectColumnVisible(next);
    try { localStorage.setItem(PT_PROJECT_COL_STORE, JSON.stringify(next)); } catch (e) { /* ignore */ }
  };

  const openEditProjectModal = (project) => {
    setProjectModal({
      mode: 'edit',
      form: {
        ...emptyProjectForm(),
        ...project,
        _was: skuWas(project),
        stages: stageColumns.reduce((acc, [key]) => ({ ...acc, [key]: project[key] || 'Pending' }), {}),
      },
    });
    setProjectModalErrors([]);
  };

  /* Opens the SKU's own form. It was `openInlineProject` and turned the row into twenty inputs;
     Max asked for a form instead (2026-09-02), and the state and the save path are the same. */
  const openSkuModal = (project, view = false) => {
    skuModalMaterials.reset();
    setInlineProjectView(!!view);
    setInlineProject({
      ...emptyProjectForm(),
      ...project,
      _was: skuWas(project),
      stages: stageColumns.reduce((acc, [key]) => ({ ...acc, [key]: project[key] || 'Pending' }), {}),
    });
    setInlineProjectError('');
  };

  const updateInlineProject = (patch) => setInlineProject((prev) => (prev ? { ...prev, ...patch } : prev));

  const saveInlineProject = async () => {
    if (!inlineProject || projectSaving) return;
    const errors = validateProjectForm(inlineProject);
    // All of them at once: four required fields can be missing together, and naming one per Save is a guessing game.
    if (errors.length) { setInlineProjectError(errors.join(' · ')); return; }
    setProjectSaving(true);
    setInlineProjectError('');
    try {
      const response = await fetch(`${PROJECT_API_BASE}/projects/${encodeURIComponent(inlineProject.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(inlineProject),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('stx.saveFailed', { status: response.status }));
      await skuModalMaterials.save();   // same button, same save — see useSkuMaterialDrafts
      setProjects(data.projects || []);
      if (data.rounds) setRounds(data.rounds);
      setInlineProject(null);
      setMessage(t('stx.msg.projectUpdated'));
    } catch (err) {
      setInlineProjectError(err.message);
    } finally {
      setProjectSaving(false);
    }
  };

  const closeProjectModal = () => {
    setProjectModal(null);
    setProjectModalErrors([]);
  };

  const projectToSkuDraft = (project) => ({
    ...emptyProjectForm(),
    ...project,
    _was: skuWas(project),
    projectItem: project.projectItem || project.groupName || '',
    stages: stageColumns.reduce((acc, [key]) => ({ ...acc, [key]: project[key] || 'Pending' }), {}),
  });

  const toggleGroupSkuEditor = (sku) => {
    setGroupSkuDrafts((prev) => (prev[sku.id] ? prev : { ...prev, [sku.id]: projectToSkuDraft(sku) }));
    setGroupSkuOpen((prev) => ({ ...prev, [sku.id]: !prev[sku.id] }));
  };

  const updateGroupSkuDraft = (projectId, patch) => {
    setGroupSkuDrafts((prev) => ({
      ...prev,
      [projectId]: { ...(prev[projectId] || {}), ...patch },
    }));
  };

  const updateProjectModalForm = (patch) => setProjectModal((prev) => (prev ? { ...prev, form: { ...prev.form, ...patch } } : prev));

  const handleSaveProject = async () => {
    if (!projectModal) return;
    const errors = validateProjectForm(projectModal.form);
    if (errors.length) {
      setProjectModalErrors(errors);
      return;
    }
    setProjectSaving(true);
    setProjectModalErrors([]);
    try {
      const isEdit = projectModal.mode === 'edit';
      const form = { ...projectModal.form };
      // Create a new project group on the fly if requested.
      if (form.groupId === '__new__') {
        const gname = String(form.newGroupName || '').trim();
        if (gname) {
          const gr = await fetch(`${PROJECT_API_BASE}/groups`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
            body: JSON.stringify({ groupName: gname }),
          });
          const gd = await gr.json();
          if (!gr.ok) throw new Error(gd.error || t('stx.msg.createGroupFailed'));
          form.groupId = gd.groupId;
          setGroups((prev) => [...prev, { groupId: gd.groupId, groupName: gname, projectIds: [] }]);
        } else {
          form.groupId = '';
        }
      }
      delete form.newGroupName;
      const url = isEdit ? `${PROJECT_API_BASE}/projects/${encodeURIComponent(form.id)}` : `${PROJECT_API_BASE}/projects`;
      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('stx.saveFailed', { status: response.status }));
      setProjects(data.projects || []);
      if (data.rounds) setRounds(data.rounds);
      setMessage(isEdit ? t('stx.msg.projectUpdated') : t('stx.msg.projectAdded'));
      closeProjectModal();
    } catch (err) {
      setProjectModalErrors([err.message]);
    } finally {
      setProjectSaving(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!projectId) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('stx.msg.confirmDeleteProject', { id: projectId }))) return;
    setProjectSaving(true);
    setError('');
    try {
      const response = await fetch(`${PROJECT_API_BASE}/projects/${encodeURIComponent(projectId)}`, { method: 'DELETE', credentials: 'include' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('stx.deleteFailed', { status: response.status }));
      setProjects(data.projects || []);
      if (data.rounds) setRounds(data.rounds);
      setMessage(t('stx.msg.projectDeleted'));
    } catch (err) {
      setError(err.message);
    } finally {
      setProjectSaving(false);
    }
  };

  // ---- Ticket Station ----

  const loadTasks = async (force) => {
    if (tasksLoaded && !force) return;
    try {
      const response = await fetch(`${PROJECT_API_BASE}/tasks`, { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('stx.loadTicketsFailed', { status: response.status }));
      setTasks((data.tasks || []).map((t) => ({ ...t, status: taskNormalizeStatus(t.status) })));
      setTaskTypes(data.taskTypes || []);
      setTasksLoaded(true);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    if (activeTab === 'tickets' && !tasksLoaded) loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, tasksLoaded]);

  // A ticket belongs to a Special Project GROUP (PRJ-###), so the picker lists groups, not SKU items.
  const taskProjectGroups = useMemo(() => {
    const map = new Map();
    projects.forEach((p) => {
      if (!p.groupId || map.has(p.groupId)) return;
      map.set(p.groupId, { id: p.groupId, name: p.groupName || p.projectItem || '', projectCase: p.groupCase || '', teamKey: p.teamKey || '', category: p.groupCategory || p.category || '', ownerUserId: p.ownerUserId || null });
    });
    // A project with no SKU yet contributes no row to `projects`, so its name was missing everywhere a
    // ticket names its project — the subject and body read "(PRJ-138)" with nothing in front of it
    // (Max, 2026-08-13). The group list carries the name whether or not SKUs exist.
    (groups || []).forEach((g) => {
      if (!g.groupId || map.has(g.groupId)) return;
      // Tickets can only be raised on an approved project, so an unapproved one stays out of the picker.
      if ((g.approvalStatus || 'Approved') !== 'Approved') return;
      map.set(g.groupId, { id: g.groupId, name: g.groupName || '', projectCase: g.projectCase || '', teamKey: g.teamKey || '', category: g.category || '', ownerUserId: g.ownerUserId || null });
    });
    return [...map.values()].sort((a, b) => String(a.id).localeCompare(String(b.id)));
  }, [projects, groups]);
  /* Which Sales OP team owns a project group. Resolved from the group's own teamKey through the config,
     so a renamed team follows automatically and there is no second list of team names to maintain. */
  const groupMailInfo = (groupId) => {
    const g = groups.find((x) => x.groupId === groupId)
      || taskProjectGroups.find((x) => x.id === groupId);
    const key = g?.teamKey || '';
    const teamName = key
      ? ((trackerConfig?.teams || []).find((tm) => tm.teamKey === key)?.teamName || g?.teamName || key)
      : '';
    return { teamName, ownerName: g?.ownerName || '' };
  };

  const taskProjectDisplay = (projectId, projectName) => {
    const g = taskProjectGroups.find((x) => x.id === projectId);
    const name = (g && g.name) || projectName || '';
    return [projectId, name].filter(Boolean).join('_');
  };

  // Ports buildTaskEditorEmailDraft: builds the full plain-text email from the form, live.
  const buildTaskEmailDraft = (form) => {
    const name = String(form.name || '').trim() || 'New ticket';
    const projectLabel = taskProjectDisplay(form.projectId, '') || form.projectId || '[project id]';
    // Same stale default as the requesting team was: with no requester filled in, the mail used to
    // announce "Requester: Special Project". A dash says "not filled in", which is the truth.
    /* `createdBy` as the fallback, the same order ticketMailFor uses. The Requester column is filled
       in when a ticket is raised from Ticket Station but NOT when it is raised from a project card,
       so an existing ticket regularly has none — and printing "Requested by: -" over a name the
       creation mail had shown is a regression, not a blank. A typed value still wins: the field is
       editable here. */
    const assignee = String(form.assignee || form.createdBy || '').trim() || '-';
    const type = String(form.taskType || '').trim() || '-';
    const priority = form.priority || 'Medium';
    const due = form.dueDate || '-';
    const note = String(form.note || '').trim();
    const att = String(form.attachmentUrl || '').trim();
    const receivingTeam = String(form.department || '').trim() || '-';
    const existing = tasks.find((t) => t.taskId === form.taskId);
    const updateUrl = existing && existing.updateUrl ? existing.updateUrl : '[update link will be generated when sent]';
    /* The request block belongs in here too. Without it Ticket Station regenerated the mail in the
       shape it had before the forms existed — so editing a ticket ANYWHERE silently stripped the SKU
       table out of the body it would be sent with. The answers ride on the form; the SKU names come
       from the project the ticket is on. */
    const ticketFormCfg = ticketFormOf(ticketForms, type);
    const skuNames = Object.fromEntries(projects
      .filter((p) => p.groupId === form.projectId)
      .map((p) => [p.id, p.subItem || p.projectItem || '']));
    return ticketEmailBody({
      projectLabel,
      code: type,
      name,
      // The team that OWNS the project is the team asking for the work, and its owner is the person
      // the receiving team will go back to. Both come from the group in one lookup.
      requestingTeam: groupMailInfo(form.projectId).teamName,
      projectOwner: groupMailInfo(form.projectId).ownerName,
      receivingTeam,
      requester: titleCaseWords(assignee),
      priority,
      dueDate: due,
      attachmentUrl: att,
      note,
      updateUrl,
      form: ticketFormCfg,
      fieldValues: form.fields || (existing ? existing.fields : null),
      skuNames,
      koInfo: ticketKoOf(ticketForms, type),
    });
  };

  /* Seeding the preview on OPEN, not only on the first edit. The draft is rebuilt from the answers
     the ticket carries, so the modal shows the mail as it stands rather than as it was stored — the
     two differ whenever the form changed since. */
  const seedTaskDraft = (form) => {
    const draft = buildTaskEmailDraft(form);
    return { ...form, emailSubject: draft.subject, emailBody: draft.body, emailHtml: draft.html || '' };
  };
  const openNewTaskModal = () => {
    const form = { ...emptyTaskForm(), assignee: (userName || '') };
    setTaskModal({ mode: 'new', form: seedTaskDraft(form) });
    setTaskModalErrors([]);
  };
  const openEditTaskModal = (task, view = false) => {
    setTaskModal({ mode: view ? 'view' : 'edit', form: seedTaskDraft({ ...emptyTaskForm(), ...task, sendEmail: false }) });
    setTaskModalErrors([]);
  };
  const duplicateTask = (task) => {
    const form = { ...emptyTaskForm(), ...task, taskId: '', name: `Copy of ${task.name || ''}`, status: 'Pending', sentAt: '', sendEmail: false };
    setTaskModal({ mode: 'duplicate', form: seedTaskDraft(form) });
    setTaskModalErrors([]);
  };
  const closeTaskModal = () => { setTaskModal(null); setTaskModalErrors([]); };

  // Applying a ticket type auto-fills name / receiving team / group email / description, then
  // regenerates the email draft — ports applyTaskTypeSelection + refreshTaskEditorEmail.
  const updateTaskModalForm = (patch) => setTaskModal((prev) => {
    if (!prev) return prev;
    let form = { ...prev.form, ...patch };
    if (Object.prototype.hasOwnProperty.call(patch, 'taskType')) {
      const tt = taskTypes.find((t) => t.code === patch.taskType);
      form = {
        ...form,
        name: tt ? (tt.name || '') : '',
        department: tt ? (tt.receivingTeam || '') : '',
        email: tt ? (tt.groupEmail || '') : '',
        description: tt ? [tt.phase, tt.scope].filter(Boolean).join(' | ') : form.description,
      };
    }
    // Email subject/body are always system-generated (read-only in UI), so regenerate on any change.
    /* The html half travels with them. It was dropped here and dropped again from the save payload,
       so every edit made from Ticket Station wrote email_html = NULL — the request table the ticket
       was created with was quietly replaced by the plain-text fallback. */
    const draft = buildTaskEmailDraft(form);
    form.emailSubject = draft.subject;
    form.emailBody = draft.body;
    form.emailHtml = draft.html || '';
    return { ...prev, form };
  });

  const handleSaveTask = async () => {
    if (!taskModal) return;
    const form = taskModal.form;
    const errs = [];
    if (!form.projectId) errs.push(t('stx.v.projectId'));
    if (!form.taskType) errs.push(t('stx.v.ticketType'));
    /* Checked here as well as on the server so the message lands next to the button rather than as a
       500 after a filled-in form. Creation only — see the gate in server.js. */
    if (taskModal.mode !== 'edit') {
      const g = taskProjectGroups.find((x) => x.id === form.projectId);
      if (g && !g.ownerUserId) {
        errs.push(t('stx.v.noOwner', { id: form.projectId }));
      }
    }
    if (form.sendEmail && !String(form.email || '').trim()) errs.push(t('stx.v.groupEmailSend'));
    if (errs.length) { setTaskModalErrors(errs); return; }
    setTaskSaving(true);
    setTaskModalErrors([]);
    try {
      const payload = {
        taskId: taskModal.mode === 'edit' ? form.taskId : '',
        projectId: form.projectId, name: form.name, description: form.description,
        /* The same fallback the box shows, so saving an older ticket FILLS IN the empty column
           rather than writing the blank back. Eleven tickets carry no requester because the card
           that raised them never sent one; each repairs itself the next time it is saved. */
        assignee: form.assignee || form.createdBy || '',
        email: form.email, department: form.department,
        taskType: form.taskType, priority: form.priority, dueDate: form.dueDate,
        status: form.status, attachmentUrl: form.attachmentUrl, note: form.note,
        /* The request form's answers. Without them the server left the stored answers alone — which
           saved this from being a data-loss bug — but a change made in the modal had nowhere to go. */
        fields: form.fields || {},
        fileDraftKey: form.fileDraftKey || '',
        emailDraft: {
          sendEmail: !!form.sendEmail, subject: form.emailSubject, body: form.emailBody,
          html: form.emailHtml || '', cc: form.emailCc,
        },
      };
      const response = await fetch(`${PROJECT_API_BASE}/tasks`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('stx.saveFailed', { status: response.status }));
      setTasks((data.tasks || []).map((tk) => ({ ...tk, status: taskNormalizeStatus(tk.status) })));
      if (data.email && data.email.error) setMessage(t('stx.msg.ticketSavedMailFailed', { err: data.email.error }));
      else setMessage(data.email ? t('stx.msg.ticketSavedSent') : t('stx.msg.ticketSaved'));
      closeTaskModal();
    } catch (err) {
      setTaskModalErrors([err.message]);
    } finally {
      setTaskSaving(false);
    }
  };

  /* Deleting a ticket used to be a browser confirm and nothing else — no reason, no notice to the
     team holding the request. Max asked for the notice on 2026-08-21; I built it into the
     delete-a-whole-project path and missed this one, which is the path people actually use. */
  const handleDeleteTask = (taskId) => {
    if (!taskId) return;
    const tk = tasks.find((t) => t.taskId === taskId);
    setDeleteTicketOneReason('');
    setDeleteTicketOne({
      taskId,
      name: tk?.name || '',
      // The field is `sentAt`, not `emailSentAt` — I wrote the wrong name first and the dialog would
      // have told every ticket "never emailed" and skipped the notice entirely.
      emailed: !!tk?.sentAt,
      to: tk?.email || '',
    });
  };

  const confirmDeleteTask = async () => {
    const spec = deleteTicketOne;
    if (!spec) return;
    const reason = deleteTicketOneReason.trim();
    if (spec.emailed && !reason) return;          // the button is disabled too; this is the backstop
    setDeleteTicketOne(null);
    setError('');
    try {
      const response = await fetch(`${PROJECT_API_BASE}/tasks/${encodeURIComponent(spec.taskId)}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('stx.deleteFailed', { status: response.status }));
      setTasks((data.tasks || []).map((tk) => ({ ...tk, status: taskNormalizeStatus(tk.status) })));
      setMessage(data.notified
        ? t('stx.msg.ticketDeletedNotified', { id: spec.taskId, to: data.notified })
        : (data.wasEmailed
          ? t('stx.msg.ticketDeletedMailFailed', { id: spec.taskId })
          : t('stx.msg.ticketDeletedNeverEmailed', { id: spec.taskId })));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSendTaskEmail = async () => {
    if (!taskModal || !taskModal.form.taskId) {
      setTaskModalErrors([t('stx.v.saveBeforeSend')]);
      return;
    }
    if (!String(taskModal.form.email || '').trim()) { setTaskModalErrors([t('stx.v.groupEmailBefore')]); return; }
    setTaskSaving(true);
    try {
      const form = taskModal.form;
      /* An already-sent ticket reminds; only a never-sent one sends. The endpoint follows the label
         rather than the label being decoration over the old behaviour.

         `html` is in the payload now, and it was the bug that made this worth doing: the draft sent
         subject, body and cc only, so the server fell back to the STORED html — and since mail
         clients show the html half, a resend after an edit delivered the OLD request under a new
         plain-text body. The two halves of one mail said different things. */
      const path = form.sentAt ? 'remind' : 'send-email';
      const response = await fetch(`${PROJECT_API_BASE}/tasks/${encodeURIComponent(form.taskId)}/${path}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(form.sentAt ? {} : {
          emailDraft: {
            subject: form.emailSubject, body: form.emailBody, cc: form.emailCc, html: form.emailHtml,
          },
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('stx.sendFailed', { status: response.status }));
      if (data.tasks) setTasks(data.tasks.map((tk) => ({ ...tk, status: taskNormalizeStatus(tk.status) })));
      if (data.sentAt) updateTaskModalForm({ sentAt: data.sentAt });
      setMessage(form.sentAt ? t('stx.msg.reminderSentTo', { to: data.to || form.email }) : t('stx.msg.emailSent'));
    } catch (err) {
      setTaskModalErrors([err.message]);
    } finally {
      setTaskSaving(false);
    }
  };

  const toggleTaskSort = (key, dir) => {
    if (dir) { setTaskSort({ key, dir }); setOpenColumnFilter(null); return; }
    setTaskSort((prev) => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
  };

  /* ONE SLOT IN THE ACTION COLUMN, TWO JOBS — Max, 2026-09-14: "Nếu ticket chưa gửi thì thay nút
     remind thành nút send. Khi nào ticket đã được gửi rồi thì nút send sẽ sửa thành nút remind."

     What prompted it: Tina raised TSK-0047/48/49 on 08/09 without sending them, then pressed Remind
     on 14/09. The receiving team was reminded about a request they had never been told about, and
     the reminder mail — eight summary lines by design — was the only thing they ever got, so
     Sanghyun read it as the ticket mail having lost its style names. Both buttons existed; nothing
     said which one this ticket needed. Now the row says it. */
  const [remindingId, setRemindingId] = useState('');
  const remindTask = async (row) => {
    setRemindingId(row.taskId);
    try {
      const res = await fetch(`${PROJECT_API_BASE}/tasks/${encodeURIComponent(row.taskId)}/remind`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('stx.msg.remindFailedPlain'));
      setMessage(t('stx.msg.reminderFor', { id: row.taskId, to: data.to }));
    } catch (err) {
      setMessage(t('stx.msg.reminderFailed', { err: err.message }));
    } finally {
      setRemindingId('');
    }
  };

  /* The first send, from the list. Nothing is composed here: the server falls back to the subject,
     body and html the ticket already stores, which is the same mail the form would have sent on the
     day it was raised — style names, attachments and all. */
  const sendTaskNow = async (row) => {
    setRemindingId(row.taskId);
    try {
      const res = await fetch(`${PROJECT_API_BASE}/tasks/${encodeURIComponent(row.taskId)}/send-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('stx.msg.sendTicketFailed'));
      // The refreshed list is what flips this row's button from Send to Remind.
      if (data.tasks) setTasks(data.tasks.map((t) => ({ ...t, status: taskNormalizeStatus(t.status) })));
      setMessage(t('st.tkSendDone', { id: row.taskId, to: row.email || '' }));
    } catch (err) {
      setMessage(t('st.tkSendFailed', { err: err.message }));
    } finally {
      setRemindingId('');
    }
  };

  const taskFilterOptions = useMemo(() => ({
    requesters: unique(tasks, 'assignee'),
    projects: [...new Set(tasks.map((t) => t.projectId).filter(Boolean))],
    depts: unique(tasks, 'department'),
  }), [tasks]);

  const toolbarFilteredTasks = useMemo(() => {
    const q = taskFilters.search.trim().toLowerCase();
    return tasks.filter((t) => {
      const status = taskNormalizeStatus(t.status);
      if (taskFilters.status.length && !taskFilters.status.includes(status)) return false;
      if (taskFilters.requester.length && !taskFilters.requester.includes(t.assignee || '')) return false;
      if (taskFilters.project.length && !taskFilters.project.includes(t.projectId || '')) return false;
      if (taskFilters.dept.length && !taskFilters.dept.includes(t.department || '')) return false;
      // Sales OP team of the ticket's project. A ticket whose project has no team yet stays visible
      // for everyone — hiding it would lose it until Jay finishes assigning teams (Jay item 8).
      if (taskFilters.team.length && t.teamName && !taskFilters.team.includes(t.teamName)) return false;
      if (taskFilters.priority.length && !taskFilters.priority.includes(t.priority || '')) return false;
      if (taskKpiFilter === 'overdue' && !taskIsOverdue(t)) return false;
      if (q) {
        const hay = [t.taskId, t.name, t.description, t.projectId, t.projectName, t.assignee, t.email, t.department, richToText(t.note)].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tasks, taskFilters, taskKpiFilter]);

  const taskColumnFilters = tableFilters('tasks');
  const filteredTasks = useMemo(() => {
    let arr = Object.keys(taskColumnFilters).length
      ? toolbarFilteredTasks.filter((task) => rowPassesColumnFilters(task, taskColumnFilters, TASK_FILTER_GETTERS))
      : toolbarFilteredTasks;
    if (taskSort.key) {
      const dir = taskSort.dir === 'asc' ? 1 : -1;
      arr = arr.slice().sort((a, b) => {
        if (taskSort.key === 'priority') {
          const rank = { High: 1, Medium: 2, Low: 3 };
          return ((rank[a.priority] || 9) - (rank[b.priority] || 9)) * dir;
        }
        return String(a[taskSort.key] || '').localeCompare(String(b[taskSort.key] || '')) * dir;
      });
    }
    return arr;
  }, [toolbarFilteredTasks, taskColumnFilters, taskSort]);
  const thFilterTask = columnFilterProps('tasks', TASK_FILTER_GETTERS, toolbarFilteredTasks);

  // Ticket Station is grouped by project, parent/child, exactly like the Project Tracker grid
  // (Max, 2026-08-12): one row per project, its tickets underneath. Built from filteredTasks so the
  // toolbar filters, the column filters and the sort all keep working — a project only appears if
  // one of its tickets survived the filters, and the ticket order inside a group follows the sort.
  const ticketGroups = useMemo(() => {
    const byProject = new Map();
    filteredTasks.forEach((task) => {
      const key = String(task.projectId || '').trim() || '__none__';
      if (!byProject.has(key)) {
        byProject.set(key, {
          key: `tkg:${key}`,
          id: task.projectId || '',
          name: [task.projectId, (taskProjectGroups.find((g) => g.id === task.projectId) || {}).name || task.projectName || '']
            .filter(Boolean).join('_') || t('st.tkNoProject'),
          // The name on its own, for the Project Name column — the joined "PRJ-126_..." form is the
          // old group header and repeats the id the Project column already shows.
          projectTitle: (taskProjectGroups.find((g) => g.id === task.projectId) || {}).name
            || task.projectName || t('st.tkNoProject'),
          teamName: task.teamName || '',
          ownerName: task.ownerName || '',
          items: [],
        });
      }
      byProject.get(key).items.push(task);
    });
    return [...byProject.values()];
  }, [filteredTasks, taskProjectGroups, t]);
  /* One flat row per ticket (Jay, 2026-08-25: "I don't prefer collapsed / expanded function. I don't
     want it to look like so many missing fields"). The grouping is kept only as an ORDER — tickets of
     the same project stay together and the first of each project draws a divider — so nothing is
     hidden behind a toggle and every row carries its own project, team and owner. */
  const ticketRows = useMemo(() => ticketGroups.flatMap((group) => group.items.map((row, idx) => ({
    row, group, firstOfProject: idx === 0,
  }))), [ticketGroups]);

  const taskStats = useMemo(() => ({
    all: filteredTasks.length,
    pending: filteredTasks.filter((t) => taskNormalizeStatus(t.status) === 'Pending').length,
    done: filteredTasks.filter((t) => taskNormalizeStatus(t.status) === 'Done').length,
    cancelled: filteredTasks.filter((t) => taskNormalizeStatus(t.status) === 'Cancelled').length,
    overdue: filteredTasks.filter(taskIsOverdue).length,
  }), [filteredTasks]);

  const setTaskKpi = (kind) => {
    setTaskKpiFilter(kind);
    setTaskFilters((prev) => ({
      ...prev,
      status: kind === 'pending' ? ['Pending'] : kind === 'done' ? ['Done'] : kind === 'cancelled' ? ['Cancelled'] : [],
    }));
  };
  const clearTaskFilters = () => {
    setTaskKpiFilter('all');
    setTaskFilters({ status: [], requester: [], project: [], dept: [], priority: [], team: [], search: '' });
    setTaskSort({ key: '', dir: 'asc' });
    /* The per-column (Excel-style) filters go too — Max, 2026-09-15: "nút clear ko work cho filter
       và sort ở bảng dưới". Clearing only the toolbar left a funnel still narrowing the grid with
       nothing on screen to say so, which reads as Clear being broken rather than as a filter being
       missed. The Project Tracker's Clear has done this since it was written; this one did not. */
    setColumnFilters((prev) => ({ ...prev, tasks: {} }));
    setOpenColumnFilter(null);
  };

  // "Days Waiting" only has a well-defined meaning relative to a specific quick-filter context
  // (Pending Outcome / RM Waiting / In Production) - matches legacy Portal.html's _myRoundDaysValue,
  // which returns null (rendered as "—") unless exactly one of those filters is active.
  const daysSince = (dateStr) => {
    if (!dateStr) return 0;
    const value = daysBetween(dateStr, todayVN());
    return value === '' ? 0 : value;
  };

  const onlySampleStatus = (s) => sampleFilters.sampleStatus.length === 1 && sampleFilters.sampleStatus[0] === s;
  const onlyRoundStatus = (s) => sampleFilters.roundStatus.length === 1 && sampleFilters.roundStatus[0] === s;
  // the one card the list is filtered by, if it is one of the three computed ones (the group row then names the matching round)
  const cardFilter = ['Pending Outcome', 'RM Waiting', 'In Production'].find((s) => onlyRoundStatus(s)) || null;

  const activeDaysWaitingContext = (
    onlyRoundStatus('Pending Outcome') ? 'pending'
      : onlyRoundStatus('RM Waiting') ? 'waiting'
        : onlyRoundStatus('In Production') ? 'inprod'
          : ''
  );

  const daysWaitingFor = (round, context) => {
    if (context === 'pending') return round.sampleFinishDate ? daysSince(round.sampleFinishDate) : '';
    if (context === 'waiting') return daysSince(round.requestDate || round.rmReadyDate);
    if (context === 'inprod') return round.rmReadyDate ? daysSince(round.rmReadyDate) : '';
    return '';
  };

  // Build the All Samples export payload (22 columns) from the currently filtered view — mirrors
  // Portal.html exportAllSamplesExcel (base group row + one row per round), then download the styled .xlsx.
  const EXPORT_COLUMNS = [
    /* 'Project SKU ID', not 'Project ID' — Jay, 2026-09-14: the same two words named a SKU here
       and a PRJ on Project Approval, and people were talking past each other. */
    /* 'R&D Location', matching the column on screen — Jay, 2026-09-16. These headers are written
       out rather than read from the table, so a rename on screen has to be made here too or the
       file and the screen start calling one column two things. */
    /* i18n keys since 2026-10-21 — the file is export only, so it is written in the reader's language
       (the English is word for word what it always was). '#' is not a key and passes through. */
    '#', 'st.colBaseId', 'st.colStyleItem', 'st.colRound', 'st.colSeason', 'st.colLocation', 'st.colDeveloper',
    'st.colDesigner', 'st.colProjectId', 'st.colRequestDate', 'st.colDevelopmentDue', 'st.colRmReady', 'st.colRoundDue',
    'stx.xls.sampleFinish', 'st.colReceived', 'st.colComment',
    'stx.xls.qty', 'st.colRounds', 'st.colStatus', 'st.colDday', 'st.colDaysWaiting', 'st.colRemarks',
  ];
  const handleExportSamplesXlsx = async () => {
    const ctx = activeDaysWaitingContext;
    const dash = (v) => (v === '' || v === null || v === undefined ? '-' : String(v));
    // Export exactly what the table currently shows: in Rounds mode a flat round list; otherwise
    // filtered base rows plus round rows only for groups the user has expanded.
    const exportRows = [];
    if (sampleRowMode === 'rounds') {
      flatFilteredRounds.forEach((round, i) => {
        exportRows.push({
          type: 'round',
          values: [
            i + 1, round.sampleId, roundStyleLabel(round), round.round, round.season, round.location, round.developer,
            round.hincDesigner, round.projectId, fmt(round.requestDate), fmt(round.developmentDue), fmt(round.rmReadyDate),
            fmt(round.roundDue), fmt(round.sampleFinishDate), fmt(round.receivedDate), fmt(round.commentDate),
            fmt(round.qty), '', round.status || round.outcome || 'Pending', dDayLabel(round.dDay),
            fmtDays(daysWaitingFor(round, ctx)), fmt(round.remarks),
          ].map(dash),
        });
      });
    }
    if (sampleRowMode === 'group') filteredSampleGroups.forEach((sample, i) => {
      const latest = sample.latest || {};
      exportRows.push({
        type: 'base',
        values: [
          i + 1, sample.baseId, sample.styleItem, latest.round, latest.season, sample.location, sample.developer,
          sample.designer, latest.projectId, fmt(latest.requestDate), fmt(sample.developmentDue), fmt(latest.rmReadyDate),
          fmt(latest.roundDue), fmt(latest.sampleFinishDate), fmt(latest.receivedDate), fmt(latest.commentDate),
          fmt(latest.qty), sample.roundCount, sample.latestStatus, dDayLabel(sample.dDay),
          fmtDays(daysWaitingFor(latest, ctx)), fmt(latest.remarks),
        ].map(dash),
      });
      if (!expanded[sample.baseId]) return;
      sample.rounds.forEach((round) => {
        exportRows.push({
          type: 'round',
          values: [
            '', round.sampleId, roundStyleLabel(round), round.round, round.season, round.location, round.developer,
            round.hincDesigner, round.projectId, fmt(round.requestDate), fmt(round.developmentDue), fmt(round.rmReadyDate),
            fmt(round.roundDue), fmt(round.sampleFinishDate), fmt(round.receivedDate), fmt(round.commentDate),
            fmt(round.qty), '', round.status || round.outcome || 'Pending', dDayLabel(round.dDay),
            fmtDays(daysWaitingFor(round, ctx)), fmt(round.remarks),
          ].map(dash),
        });
      });
    });
    if (!exportRows.length) { setMessage(t('stx.msg.noRowsExport')); return; }

    const labelOf = (list, allLabel) => (
      list.includes(MULTI_FILTER_NONE)
        ? t('stx.noneSelected')
        : list.length ? list.join(', ') : allLabel
    );
    const filters = [
      t('stx.xls.sample', { v: labelOf(sampleFilters.sampleStatus, t('st.tabAll')) }),
      t('stx.xls.round', { v: labelOf(sampleFilters.roundStatus, t('st.allRounds')) }),
      t('stx.xls.developer', { v: labelOf(sampleFilters.developer, t('st.allDevelopers')) }),
      t('stx.xls.designer', { v: labelOf(sampleFilters.designer, t('st.allDesigners')) }),
      t('stx.xls.location', { v: labelOf(sampleFilters.location, t('stx.xls.allLocations')) }),
    ];
    if (sampleFilters.search.trim()) filters.push(t('stx.xls.search', { v: sampleFilters.search.trim() }));

    const stamp = fmtDateTimeVN(new Date(), '');
    const fileStamp = stamp.replace(/[: ]/g, '-');

    try {
      setMessage(t('stx.preparingExport'));
      const response = await fetch(`${BACKEND_URL}/api/development/sample-tracker/export-xlsx`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          title: t('stx.xls.titleSamples'), generated: stamp, filters,
          columns: EXPORT_COLUMNS.map((c) => (c.includes('.') ? t(c) : c)), rows: exportRows, filename: `HEVN_All_Samples_${fileStamp}.xlsx`,
        }),
      });
      if (!response.ok) {
        let msg = t('stx.exportFailed', { status: response.status });
        try { const j = await response.json(); if (j.error) msg = j.error; } catch (e) { /* non-JSON */ }
        throw new Error(msg);
      }
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `HEVN_All_Samples_${fileStamp}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
      setMessage(t('stx.msg.exported'));
    } catch (err) {
      setMessage(err.message || t('stx.exportFailedPlain'));
    }
  };

  /* The rows above and below the slice, as one spacer each, so the scrollbar stays the true length
     of the list rather than the length of what happens to be built. */
  const sampleSpacer = (h, where) => (h > 0
    ? <tr className="st-spacer" key={`sp-${where}`} style={{ height: h }}><td colSpan="24" /></tr>
    : null);

  const renderSampleTable = (rows) => (
    <div className="twrap data-xscroll sample-data-wrap" ref={sampleBoxRef}>
      <table className={`sample-scroll-table fz-${sampleFreeze}`}>
        <colgroup>
          {SAMPLE_COL_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}
        </colgroup>
        <thead>
          <tr>
            <th />
            <th>#</th>
            <SortTh label={t('st.colBaseId')} sortKey="baseId" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('baseId')} />
            <SortTh label={t('st.colStyleItem')} sortKey="styleItem" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('styleItem')} />
            <SortTh label={t('st.colRound')} sortKey="round" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('round')} />
            <SortTh label={t('st.colSeason')} sortKey="season" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('season')} />
            <SortTh label={t('st.colLocation')} sortKey="location" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('location')} />
            <SortTh label={t('st.colDeveloper')} sortKey="developer" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('developer')} />
            <SortTh label={t('st.colDesigner')} sortKey="designer" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('designer')} />
            <SortTh label={t('st.colProjectId')} sortKey="projectId" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('projectId')} />
            <SortTh label={t('st.colRequestDate')} sortKey="requestDate" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('requestDate')} />
            <SortTh label={t('st.colDevelopmentDue')} sortKey="developmentDue" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('developmentDue')} />
            <SortTh label={t('st.colRmReady')} sortKey="rmReadyDate" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('rmReadyDate')} />
            <SortTh label={t('st.colRoundDue')} sortKey="roundDue" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('roundDue')} />
            <SortTh label={t('st.colSampleFinish')} sortKey="sampleFinishDate" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('sampleFinishDate')} />
            <SortTh label={t('st.colReceived')} sortKey="receivedDate" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('receivedDate')} />
            <SortTh label={t('st.colComment')} sortKey="commentDate" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('commentDate')} />
            <SortTh label={t('st.colQty')} sortKey="qty" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('qty')} />
            <SortTh label={t('st.colRounds')} sortKey="roundCount" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('roundCount')} />
            <SortTh label={t('st.colStatus')} sortKey="status" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('status')} />
            <SortTh label={t('st.colDday')} hint={t('st.colDdayHint')} sortKey="dDay" sort={sampleSort} onSort={toggleSampleSort} {...thFilter('dDay')} />
            <th>{t('st.colRemarks')}</th>
            <th>{t('st.colActions')}</th>
          </tr>
        </thead>
        <tbody>
          {sampleRowMode === 'rounds' && sampleSpacer(sampleWin.padTop, 'top')}
          {sampleRowMode === 'rounds' && (sampleWin.windowed
            ? flatFilteredRounds.slice(sampleWin.first, sampleWin.first + sampleWin.count)
            : flatFilteredRounds).map((round, i) => {
            // The # column must keep counting from the top of the list, not from the top of the slice.
            const ri = (sampleWin.windowed ? sampleWin.first : 0) + i;
            return (
            <tr className="my-round-row" key={round.sampleId}>
              <td />
              <td className="pt-col-no">{ri + 1}</td>
              <td className="idcell">
                <strong>{round.sampleId}</strong>
                {isTrialBase(round.baseId) && <span className="st-trial-tag">{t('st.tagTrial')}</span>}
              </td>
              <td>{roundStyleLabel(round)}</td>
              <td>{round.round}</td>
              <td>{round.season || '—'}</td>
              <td>{round.location || '—'}</td>
              <td>{round.developer || '—'}</td>
              <td>{round.hincDesigner || '—'}</td>
              <td>{round.projectId ? <button type="button" className="st-link" title={t('st.goToProject')} onClick={() => goToProjectSku(round.projectId)}>{round.projectId}</button> : '—'}</td>
              <td>{fmt(round.requestDate)}</td>
              <td>{fmt(round.developmentDue)}</td>
              <td>{fmt(round.rmReadyDate)}</td>
              <td>{fmt(round.roundDue)}</td>
              <td>{fmt(round.sampleFinishDate)}</td>
              <td>{fmt(round.receivedDate)}</td>
              <td>{fmt(round.commentDate)}</td>
              <td>{fmt(round.qty)}</td>
              <td>-</td>
              <td><span className={`pill ${outcomePillClass(roundStatus(round))}`}>{valueLabel(roundStatus(round))}</span></td>
              <td className={dDayClass(round.dDay, round.dDayDone)}>{dDayLabel(round.dDay)}</td>
              <td className="st-remarks" title={round.remarks || ''}>{fmt(round.remarks)}</td>
              <td>
                <div className="action-icons">
                  <IconButton variant="view" title={t('st.viewRound')} onClick={() => openEditRoundModal(round, true)}><Eye size={14} /></IconButton>
                  <IconButton variant="edit" title={mayChangeRound(round) ? t('stx.editRound') : notMineTitle(round)} disabled={!mayChangeRound(round)} onClick={() => openEditRoundModal(round)}><Pencil size={14} /></IconButton>
                  <IconButton variant="duplicate" title={t('stx.duplicateRound')} onClick={() => duplicateRound(round)}><Copy size={14} /></IconButton>
                  <IconButton variant="delete" title={mayChangeRound(round) ? t('stx.deleteRound') : notMineTitle(round)} disabled={!mayChangeRound(round)} onClick={() => handleDeleteRound(round.sampleId)}><Trash2 size={14} /></IconButton>
                </div>
              </td>
            </tr>
            );
          })}
          {sampleRowMode === 'rounds' && sampleSpacer(sampleWin.padBottom, 'bot')}
          {sampleRowMode === 'rounds' && !flatFilteredRounds.length && <tr><td colSpan="24" className="st-empty">{t('stx.noRounds')}</td></tr>}
          {sampleRowMode === 'group' && sampleSpacer(sampleWin.padTop, 'top')}
          {sampleRowMode === 'group' && (sampleWin.windowed
            ? rows.slice(sampleWin.first, sampleWin.first + sampleWin.count)
            : rows).map((sample, i) => {
            const si = (sampleWin.windowed ? sampleWin.first : 0) + i;
            const isOpen = expanded[sample.baseId];
            const latest = sample.latest || {};
            const latestRemarks = [...new Set((sample.rounds || [])
              .filter((r) => String(r.round || '') === String(latest.round || ''))
              .map((r) => String(r.remarks || '').trim())
              .filter(Boolean))];
            return (
              <React.Fragment key={sample.baseId}>
                <tr className="my-group-hd" onClick={() => setExpanded((prev) => ({ ...prev, [sample.baseId]: !prev[sample.baseId] }))}>
                  <td>{isOpen ? '▾' : '▸'}</td>
                  <td className="pt-col-no">{si + 1}</td>
                  <td className="idcell">
                    <strong>{sample.baseId}</strong>
                    {isTrialBase(sample.baseId) && <span className="st-trial-tag">{t('st.tagTrial')}</span>}
                  </td>
                  {/* Renaming used to happen here, behind a pencil that appeared on hover, with an
                      inline input. Max removed it on 2026-08-20: it now lives in the Base ID form on
                      the Action column, so there is one way in instead of two. The inline editor went
                      with the pencil — nothing else opened it. */}
                  <td className="st-name-cell" title={sample.styleItem || ''}><strong>{sample.styleItem}</strong></td>
                  <td>{latest.round || '—'}</td>
                  <td>{latest.season || '—'}</td>
                  <td>{sample.location || '—'}</td>
                  <td>{sample.developer || '—'}</td>
                  <td>{sample.designer || '—'}</td>
                  <td>{latest.projectId ? <button type="button" className="st-link" title={t('st.goToProject')} onClick={() => goToProjectSku(latest.projectId)}>{latest.projectId}</button> : '—'}</td>
                  <td>{fmt(latest.requestDate)}</td>
                  <td>{fmt(sample.developmentDue)}</td>
                  <td>{fmt(latest.rmReadyDate)}</td>
                  <td>{fmt(latest.roundDue)}</td>
                  <td>{fmt(latest.sampleFinishDate)}</td>
                  <td>{fmt(latest.receivedDate)}</td>
                  <td>{fmt(latest.commentDate)}</td>
                  {/* QTY and Remarks are ROUND facts, and this row is the base. SMP-0234 has two P1
                      rounds of 4 with different notes; printing the newest one here made the base
                      look like it had one quantity and one comment (Max, 2026-09-10). The Rounds
                      count next to it says how many there are, and expanding shows them. */}
                  <td>—</td>
                  <td>{sample.roundCount}</td>
                  {/* FILTERED BY ONE OF THE THREE CARDS, the row says which round put it there (Jay 29/10: the lists "show irrelevant"
                      items" — a base listed under Pending Outcome showed its newest round's status, e.g. Photo / RM Waiting). The
                      newest matching round's status, and its name when it is not the newest round. */}
                  {(() => {
                    const hit = cardFilter ? [...(sample.rounds || [])].reverse().find(ROUND_STATUS_PREDICATES[cardFilter]) : null;
                    const st = hit ? cardFilter : sample.latestStatus;
                    const older = hit && hit !== sample.latest;
                    return (
                      <td>
                        <span className={`pill ${outcomePillClass(st)}`}>{valueLabel(st)}</span>
                        {older && <span className="st-pill-round" data-st-matched={hit.sampleId}>{String(hit.sampleId || '').replace(`${sample.baseId}-`, '')}</span>}
                      </td>
                    );
                  })()}
                  <td className={dDayClass(sample.dDay, sample.dDayDone)}>{dDayLabel(sample.dDay)}</td>
                  {/* The LATEST ROUND'S remarks. This cell was blanked on 2026-09-10 because
                      printing the newest round's single note made a base with two P1 options look
                      as though it had one comment; gathering every remark of that round answers
                      that objection instead of reopening it — nothing is picked and nothing is
                      dropped. Matched on the round LABEL, which is what options share.
                      Identical notes collapse to one line: two options of a round often carry the
                      same note, and printing it twice reads as two different ones. */}
                  <td
                    className="st-remarks"
                    title={latestRemarks.join('\n') || ''}
                    onClick={(event) => {
                      if (latestRemarks.length < 2) return;
                      event.stopPropagation();
                      setOpenRemarks((prev) => ({ ...prev, [sample.baseId]: !prev[sample.baseId] }));
                    }}
                  >
                    {latestRemarks.length ? (
                      <div className={`st-remark-stack${openRemarks[sample.baseId] ? ' on' : ''}`}>
                        {latestRemarks.map((line, n) => <div key={n}>{line}</div>)}
                        {latestRemarks.length > 1 && !openRemarks[sample.baseId] && (
                          <span className="st-remark-more">+{latestRemarks.length - 1}</span>
                        )}
                      </div>
                    ) : '—'}
                  </td>
                  <td onClick={(event) => event.stopPropagation()}>
                    <IconButton variant="add" title={t('stx.addRound')} onClick={() => openNewRoundModal({
                      styleItem: sample.styleItem, baseId: sample.baseId, ...inheritFromSample(sample.baseId),
                    })}><Plus size={14} /></IconButton>
                    {/* The Base ID had no screen of its own until now: everything on this row is read
                        off the newest round. Only what genuinely belongs to the base is in there —
                        its photos and its style name (Max, 2026-08-20). */}
                    <IconButton variant="view" title={t('st.viewBase')} onClick={() => setBaseModal({ baseId: sample.baseId, styleItem: sample.styleItem || '', rounds: sample.roundCount, view: true })}><Eye size={14} /></IconButton>
                    {/* renaming touches every round of the sample — locked unless they are all this person's (Jay 30/10) */}
                    {(() => { const other = sample.rounds.find((r) => !mayChangeRound(r)); return (
                      <IconButton variant="edit" title={other ? notMineTitle(other) : t('stx.editBase')} disabled={!!other} onClick={() => setBaseModal({ baseId: sample.baseId, styleItem: sample.styleItem || '', rounds: sample.roundCount })}><Pencil size={14} /></IconButton>
                    ); })()}
                  </td>
                </tr>
                {isOpen && sample.rounds.map((round) => (
                  <tr className="my-round-row" key={round.sampleId}>
                    <td />
                    <td className="pt-col-no" />
                    <td>{round.sampleId}</td>
                    <td>{roundStyleLabel(round)}</td>
                    <td>{round.round}</td>
                    <td>{round.season || '—'}</td>
                    <td>{round.location || '—'}</td>
                    <td>{round.developer || '—'}</td>
                    <td>{round.hincDesigner || '—'}</td>
                    <td>{round.projectId ? <button type="button" className="st-link" title={t('st.goToProject')} onClick={() => goToProjectSku(round.projectId)}>{round.projectId}</button> : '—'}</td>
                    <td>{fmt(round.requestDate)}</td>
                    <td>{fmt(round.developmentDue)}</td>
                    <td>{fmt(round.rmReadyDate)}</td>
                    <td>{fmt(round.roundDue)}</td>
                    <td>{fmt(round.sampleFinishDate)}</td>
                    <td>{fmt(round.receivedDate)}</td>
                    <td>{fmt(round.commentDate)}</td>
                    <td>{fmt(round.qty)}</td>
                    <td>-</td>
                    <td><span className={`pill ${outcomePillClass(roundStatus(round))}`}>{valueLabel(roundStatus(round))}</span></td>
                    <td className={dDayClass(round.dDay, round.dDayDone)}>{dDayLabel(round.dDay)}</td>
                          <td className="st-remarks" title={round.remarks || ''}>{fmt(round.remarks)}</td>
                    <td>
                      <div className="action-icons">
                        <IconButton variant="view" title={t('st.viewRound')} onClick={() => openEditRoundModal(round, true)}><Eye size={14} /></IconButton>
                        <IconButton variant="edit" title={mayChangeRound(round) ? t('stx.editRound') : notMineTitle(round)} disabled={!mayChangeRound(round)} onClick={() => openEditRoundModal(round)}><Pencil size={14} /></IconButton>
                        <IconButton variant="duplicate" title={t('stx.duplicateRound')} onClick={() => duplicateRound(round)}><Copy size={14} /></IconButton>
                        <IconButton variant="delete" title={mayChangeRound(round) ? t('stx.deleteRound') : notMineTitle(round)} disabled={!mayChangeRound(round)} onClick={() => handleDeleteRound(round.sampleId)}><Trash2 size={14} /></IconButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {sampleRowMode === 'group' && sampleSpacer(sampleWin.padBottom, 'bot')}
          {sampleRowMode === 'group' && !rows.length && <tr><td colSpan="24" className="st-empty">{t('stx.noData')}</td></tr>}
        </tbody>
      </table>
    </div>
  );

  const renderProductView = (rows) => (
    <div className="twrap data-xscroll sample-data-wrap">
      <table className="product-view-table">
        <thead>
          <tr>
            <SortTh label={t('st.colBaseId')} sortKey="baseId" sort={sampleSort} onSort={toggleSampleSort}  {...thFilterSummary('baseId')} />
            <SortTh label={t('st.colStyleItem')} sortKey="styleItem" sort={sampleSort} onSort={toggleSampleSort}  {...thFilterSummary('styleItem')} />
            <SortTh label={t('st.colLocation')} sortKey="location" sort={sampleSort} onSort={toggleSampleSort}  {...thFilterSummary('location')} />
            <SortTh label={t('st.colDeveloper')} sortKey="developer" sort={sampleSort} onSort={toggleSampleSort}  {...thFilterSummary('developer')} />
            <SortTh label={t('st.colDevelopmentDue')} sortKey="developmentDue" sort={sampleSort} onSort={toggleSampleSort}  {...thFilterSummary('developmentDue')} />
            <SortTh label={t('st.colDday')} hint={t('st.colDdayHint')} sortKey="dDay" sort={sampleSort} onSort={toggleSampleSort}  {...thFilterSummary('dDay')} />
            <SortTh label={t('st.colRounds')} sortKey="roundCount" sort={sampleSort} onSort={toggleSampleSort}  {...thFilterSummary('roundCount')} />
            <SortTh label={t('st.colRework')} sortKey="rework" sort={sampleSort} onSort={toggleSampleSort}  {...thFilterSummary('rework')} />
            <SortTh label={t('st.colStatus')} sortKey="productStatus" sort={sampleSort} onSort={toggleSampleSort}  {...thFilterSummary('productStatus')} />
            {[1, 2, 3, 4, 5].map((n) => <th key={n}>{`R${n}`}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((sample) => (
            <tr key={sample.baseId} className={sample.productViewStatus !== 'In Progress' ? 'past' : ''}>
              <td className="idcell">
                <strong>{sample.baseId}</strong>
                {isTrialBase(sample.baseId) && <span className="st-trial-tag">{t('st.tagTrial')}</span>}
              </td>
              <td>{sample.styleItem}</td>
              <td>{sample.location || '—'}</td>
              <td>{sample.developer || '—'}</td>
              <td>{fmt(sample.developmentDue)}</td>
              <td className={dDayClass(sample.dDay, sample.dDayDone)}>{dDayLabel(sample.dDay)}</td>
              <td className={sample.roundCount >= 4 ? 'red' : ''}>{sample.roundCount}</td>
              <td>{sample.rework > 0 ? <span className="red">{sample.rework}</span> : sample.rework}</td>
              <td><span className={`pill ${productStatusPillClass(sample.productViewStatus)}`}>{valueLabel(sample.productViewStatus)}</span></td>
              {[0, 1, 2, 3, 4].map((idx) => {
                const round = sample.rounds[idx];
                const short = round ? SHORT_OUTCOME[round.outcome] : '';
                return (
                  <td key={idx}>
                    {short ? <span className={`pill ${outcomePillClass(round.outcome)}`}>{short}</span> : null}
                  </td>
                );
              })}
            </tr>
          ))}
          {!rows.length && <tr><td colSpan="14" className="st-empty">{t('stx.noData')}</td></tr>}
        </tbody>
      </table>
    </div>
  );

  const renderSample = () => {
    const title = sampleView === 'product' ? t('st.titleSummary') : t('st.titleTracker');
    // Narrowed to trials, the hint says what that pill holds; otherwise the two views explain themselves.
    const hint = typeSelected(SAMPLE_TYPE_TRIAL)
      ? t('st.hintTrial')
      : (sampleView === 'product' ? t('st.hintSummary') : t('st.hintTracker'));
    return (
      <section className="screen active">
        {/* The pills sit on the Sample-level Summary too — it lists the same samples, and a summary
            quietly holding a different set from the table is the same disagreement in another place. */}
        <div className="st-kind-tabs" role="tablist">
          {SAMPLE_PILLS.map((pill) => (
            <button
              type="button"
              role="tab"
              key={pill.label}
              aria-selected={typeSelected(pill.id)}
              className={typeSelected(pill.id) ? 'active' : ''}
              title={pill.id ? t('st.pillCtrlHint') : undefined}
              onClick={(event) => filterByType(pill.id, event)}
            >
              {t(pill.label)}
              <span className="st-kind-count">{typeCounts[pill.count]}</span>
            </button>
          ))}
        </div>
        <div className="screen-title"><h2>{title}</h2><p>{hint}</p></div>
        {sampleView === 'all' && (
          <div className="mv-kpi-section pt-kpi">
            <div className="mv-kpi-title">{t('st.groupSample')}</div>
            <div className="mv-kpi-row">
              <Kpi label={t('st.totalSamples')} value={sampleStats.totalSamples} active={!isSampleFiltered && sampleRowMode === 'group'} sub={withTotalSub(t('st.totalSamplesSub'), sampleStats.totalSamples, totalSampleStats.totalSamples)} onClick={() => quickFilter('all')} />
              <Kpi label={t('st.inProgressSamples')} value={sampleStats.sampleInProgress} tone="kv-pend" active={onlySampleStatus('In Progress')} onClick={() => quickFilter('sampleInProgress')} />
              <Kpi label={t('st.confirmedSamples')} value={sampleStats.sampleConfirmed} tone="kv-conf" active={onlySampleStatus('Confirmed')} onClick={() => quickFilter('sampleConfirmed')} />
              <Kpi label={t('st.reworkSamples')} value={sampleStats.sampleRework} tone="kv-rew" active={onlySampleStatus('Rework')} onClick={() => quickFilter('sampleRework')} />
              <Kpi label={t('st.droppedSamples')} value={sampleStats.sampleDropped} tone="kv-drop" active={onlySampleStatus('Dropped')} onClick={() => quickFilter('sampleDropped')} />
              <Kpi label={t('st.onHoldSamples')} value={sampleStats.sampleOnHold} tone="kv-hold" active={onlySampleStatus('On Hold')} onClick={() => quickFilter('sampleOnHold')} />
              <Kpi label={t('st.pendingOutcome')} value={sampleStats.samplePendingOutcome} tone="kv-pend split-start" active={onlyRoundStatus('Pending Outcome') && sampleRowMode === 'group'} sub={withTotalSub(t('st.pendingOutcomeSub'), sampleStats.samplePendingOutcome, totalSampleStats.samplePendingOutcome)} onClick={() => quickFilter('samplePending')} />
              <Kpi label={t('st.rmWaiting')} value={sampleStats.sampleWaiting} tone="kv-wait" active={onlyRoundStatus('RM Waiting') && sampleRowMode === 'group'} sub={withTotalSub(t('st.rmWaitingSub'), sampleStats.sampleWaiting, totalSampleStats.sampleWaiting)} onClick={() => quickFilter('sampleWaiting')} />
              <Kpi label={t('st.inProduction')} value={sampleStats.sampleInProduction} tone="kv-conf" active={onlyRoundStatus('In Production') && sampleRowMode === 'group'} sub={withTotalSub(t('st.inProductionSub'), sampleStats.sampleInProduction, totalSampleStats.sampleInProduction)} onClick={() => quickFilter('sampleInprod')} />
            </div>
          </div>
        )}
        {sampleView === 'all' && (
          <div className="mv-kpi-section pt-kpi">
            <div className="mv-kpi-title">{t('st.groupRounds')}</div>
            <div className="mv-kpi-row">
              <Kpi label={t('st.totalRounds')} value={sampleStats.totalRounds} tone="kv-rnd" active={!isSampleFiltered && sampleRowMode === 'rounds'} sub={withTotalSub(t('st.totalRoundsSub'), sampleStats.totalRounds, totalSampleStats.totalRounds)} onClick={() => quickFilter('totalRounds')} />
              <Kpi label={t('st.inProgressRounds')} value={sampleStats.roundInProgress} tone="kv-pend" active={onlyRoundStatus('In Progress')} onClick={() => quickFilter('roundInProgress')} />
              <Kpi label={t('st.confirmedRounds')} value={sampleStats.roundConfirmed} tone="kv-conf" active={onlyRoundStatus('Confirmed')} onClick={() => quickFilter('roundConfirmed')} />
              <Kpi label={t('st.reworkRounds')} value={sampleStats.roundRework} tone="kv-rew" active={onlyRoundStatus('Rework')} onClick={() => quickFilter('roundRework')} />
              <Kpi label={t('st.droppedRounds')} value={sampleStats.roundDropped} tone="kv-drop" active={onlyRoundStatus('Dropped')} onClick={() => quickFilter('roundDropped')} />
              <Kpi label={t('st.onHoldRounds')} value={sampleStats.roundOnHold} tone="kv-hold" active={onlyRoundStatus('On Hold')} onClick={() => quickFilter('roundOnHold')} />
              <Kpi label={t('st.pendingOutcome')} value={sampleStats.pendingOutcome} tone="kv-pend split-start" active={onlyRoundStatus('Pending Outcome') && sampleRowMode === 'rounds'} sub={withTotalSub(t('st.pendingOutcomeSub'), sampleStats.pendingOutcome, totalSampleStats.pendingOutcome)} onClick={() => quickFilter('pending')} />
              <Kpi label={t('st.rmWaiting')} value={sampleStats.waiting} tone="kv-wait" active={onlyRoundStatus('RM Waiting') && sampleRowMode === 'rounds'} sub={withTotalSub(t('st.rmWaitingSub'), sampleStats.waiting, totalSampleStats.waiting)} onClick={() => quickFilter('waiting')} />
              <Kpi label={t('st.inProduction')} value={sampleStats.inProduction} tone="kv-conf" active={onlyRoundStatus('In Production') && sampleRowMode === 'rounds'} sub={withTotalSub(t('st.inProductionSub'), sampleStats.inProduction, totalSampleStats.inProduction)} onClick={() => quickFilter('inprod')} />
            </div>
          </div>
        )}
        {sampleView === 'all' && (
          <div className="toolbar st-filter-bar">
            <MultiFilter value={sampleFilters.sampleStatus} onChange={(value) => setSampleFilters({ ...sampleFilters, sampleStatus: value })} allLabel={t('st.tabAll')} options={['In Progress', 'Confirmed', 'Rework', 'Dropped', 'On Hold']} labelOf={valueLabel} />
            <MultiFilter value={sampleFilters.roundStatus} onChange={(value) => setSampleFilters({ ...sampleFilters, roundStatus: value })} allLabel={t('st.allRounds')} options={['In Progress', 'RM Waiting', 'In Production', 'Pending Outcome', 'Advance', 'Rework', 'Confirmed', 'On Hold', 'Dropped']} labelOf={valueLabel} />
            <MultiFilter value={sampleFilters.developer} onChange={(value) => setSampleFilters({ ...sampleFilters, developer: value })} allLabel={t('st.allDevelopers')} options={filterOptions.developers} />
            <MultiFilter value={sampleFilters.designer} onChange={(value) => setSampleFilters({ ...sampleFilters, designer: value })} allLabel={t('st.allDesigners')} options={filterOptions.designers} />
            <MultiFilter value={sampleFilters.location} onChange={(value) => setSampleFilters({ ...sampleFilters, location: value })} allLabel={t('st.allLocations')} options={filterOptions.locations} />
            <input value={sampleFilters.search} onChange={(event) => setSampleFilters({ ...sampleFilters, search: event.target.value })} placeholder={t('st.searchPlaceholder')} />
            {!!sampleFilters.lateDays && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                title={t('st.clear')}
                onClick={() => setSampleFilters((prev) => ({ ...prev, lateDays: '' }))}
              >
                {t('stx.lateChip', { n: sampleFilters.lateDays })} &times;
              </button>
            )}
            {/* THE DONUT'S SLICE SAYS SO, and is removable. The rule this list was narrowed by is
                not one of the dropdowns above, so without a chip the screen would be filtered by
                something nothing on it mentions — which this codebase already treats as the same
                thing as a broken list. */}
            {!!sampleFilters.review && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                title={t('st.clear')}
                onClick={() => setSampleFilters((prev) => ({ ...prev, review: '', reviewDays: 0 }))}
              >
                {sampleFilters.reviewDays
                  ? t('stx.reviewChipDays', { label: reviewLabel(sampleFilters.review), n: sampleFilters.reviewDays })
                  : reviewLabel(sampleFilters.review)} &times;
              </button>
            )}
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setSampleFilters(SAMPLE_FILTERS_DEFAULT); setSampleSort({ key: '', dir: 'asc' }); setSampleRowMode('group'); setExpanded({}); clearColumnFilters(); }}>{t('st.clear')}</button>
            {/* The two create buttons wrap as ONE block. Left loose, the row broke between them and
                left the trial button alone on a second line, hard against the left edge. */}
            <div className="toolbar-actions">
              {/* TWO PEERS AND AN EXCEPTION, and the styling says exactly that. Create Sample and
                  New Round are the same act at two moments — the sample does not exist yet, or it
                  does — so they wear the same green and the same weight; giving one of them more
                  would claim an order of importance that is not there, and the labels are what
                  tell them apart. Create Sample is FIRST because a sample exists before its rounds.
                  The trial button keeps its dashed warm outline: it is rare, it is permissioned,
                  and it should not look like one of the two things people do every day.
                  (It briefly wore `btn-smp-new`, which is the small green button on a Project
                  Tracker ROW — a second green at a row's size, in a toolbar.) */}
              <button type="button" className="btn btn-primary btn-sm btn-green btn-create-sample" onClick={() => setSamplePicker({ q: '' })}>{t('st.csButton')}</button>
              <button type="button" className="btn btn-primary btn-sm btn-green btn-new-round" onClick={() => openNewRoundModal()}>{t('st.newRound')}</button>
              {/* Max, 2026-09-08: "chỉ có Development manager và admin là thấy được nút này". The
                  Development manager is the manager flag on the DEV team — a real attribute one person
                  carries today — rather than a role that would have to be invented for two people.
                  The server enforces the same rule on POST, so hiding the button is the courtesy and
                  not the lock. */}
              {canRaiseTrialSample && (
                <button type="button" className="btn btn-sm btn-trial-sample" title={t('st.trSub')} onClick={openTrialSampleModal}>{t('st.trButton')}</button>
              )}
              {/* Last, and quiet: it is how somebody asks for the tour again, not one of the three
                  things they came here to do. */}
              <button type="button" className="btn btn-sm btn-tour" title={t('st.tourReopen')} onClick={() => setTourOpen(true)}>
                {t('st.tourButton')}
              </button>
              <ManualButton manual="sample" />
            </div>
          </div>
        )}
        {sampleView === 'product' && (
          <div className="toolbar">
            <MultiFilter value={sampleFilters.sampleStatus} onChange={(value) => setSampleFilters({ ...sampleFilters, sampleStatus: value })} allLabel={t('st.allStatus')} options={['In Progress', 'Confirmed', 'Dropped', 'On Hold']} labelOf={valueLabel} />
            <MultiFilter value={sampleFilters.location} onChange={(value) => setSampleFilters({ ...sampleFilters, location: value })} allLabel={t('st.allLocations')} options={filterOptions.locations} />
            <MultiFilter value={sampleFilters.developer} onChange={(value) => setSampleFilters({ ...sampleFilters, developer: value })} allLabel={t('st.allDevelopers')} options={filterOptions.developers} />
            <input value={sampleFilters.search} onChange={(event) => setSampleFilters({ ...sampleFilters, search: event.target.value })} placeholder={t('stx.searchSample')} />
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSampleFilters(SAMPLE_FILTERS_DEFAULT)}>{t('st.clear')}</button>
          </div>
        )}
        {sampleView === 'all' && (
          <div className="pg-bar">
            {sampleRowMode === 'group' ? (
              <>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setExpanded(Object.fromEntries(filteredSampleGroups.map((row) => [row.baseId, true])))}>{t('st.expandAll')}</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setExpanded({})}>{t('st.collapseAll')}</button>
              </>
            ) : (
              <>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setSampleRowMode('group'); setExpanded({}); }}>{t('st.backToBase')}</button>
              </>
            )}
            <button type="button" className="btn btn-ghost btn-sm btn-export-excel" onClick={handleExportSamplesXlsx}><Download size={14} /> {t('st.exportExcel')}</button>
          </div>
        )}
        {sampleView === 'all' && renderSampleTable(filteredSampleGroups)}
        {sampleView === 'product' && renderProductView(summarySampleGroups)}
      </section>
    );
  };

  const onlyStatus = (s) => projectFilters.status.length === 1 && projectFilters.status[0] === s;
  const renderProjects = () => (
    <section className="screen active">
      <div className="mh-titlerow">
        <div className="screen-title"><h2>{t('st.ptTitle')}</h2><p>{t('st.ptHint')}</p></div>
        <ModuleHelp manual="project" steps={PROJECT_TOUR} tourKey="projectTracker" ready={projectGroups.length > 0} />
      </div>
      <div className="mv-kpi-section pt-kpi">
        <div className="mv-kpi-row">
          {/* Counted off the grouped rows, not off the SKU rows: a project with zero SKUs is still a project. */}
          {/* Project-level: these two count PROJECTS. Everything after them counts SKU rows. */}
          <Kpi label={t('st.ptGroups')} value={projectGroups.length} active={!projectFilters.status.length && !projectFilters.groupState} onClick={() => setProjectFilters({ ...projectFilters, status: [], groupState: '' })} />
          <Kpi label={t('st.ptProjectComplete')} value={projectStats.projectComplete} tone="kv-conf" sub={t('st.ptProjectCompleteSub')} active={projectFilters.groupState === 'complete'} onClick={() => setProjectFilters({ ...projectFilters, status: [], groupState: projectFilters.groupState === 'complete' ? '' : 'complete' })} />
          <Kpi label={t('st.ptProjectDropped')} value={projectStats.projectDropped} tone="kv-drop" sub={t('st.ptProjectDroppedSub')} active={projectFilters.groupState === 'dropped'} onClick={() => setProjectFilters({ ...projectFilters, status: [], groupState: projectFilters.groupState === 'dropped' ? '' : 'dropped' })} />
          <Kpi label={t('st.ptProjectActive')} value={Math.max(0, projectGroups.length - projectStats.projectComplete - projectStats.projectDropped)} tone="kv-rnd" sub={t('st.ptProjectActiveSub')} active={projectFilters.groupState === 'active'} onClick={() => setProjectFilters({ ...projectFilters, status: [], groupState: projectFilters.groupState === 'active' ? '' : 'active' })} />
          <Kpi label={t('st.ptSkus')} value={projectStats.skus} onClick={() => setProjectFilters({ ...projectFilters, status: [], groupState: '' })} />
          <Kpi label={t('st.ptComplete')} value={projectStats.complete} tone="kv-conf" active={onlyStatus('Complete')} onClick={() => setProjectFilters({ ...projectFilters, groupState: '', status: ['Complete'] })} />
          <Kpi label={t('st.ptDrop')} value={projectStats.dropped} tone="kv-drop" active={onlyStatus('Dropped')} onClick={() => setProjectFilters({ ...projectFilters, groupState: '', status: ['Dropped'] })} />
          <Kpi label={t('st.ptActive')} value={projectStats.active} tone="kv-rnd" onClick={() => setProjectFilters({ ...projectFilters, groupState: '', status: PROJECT_ACTIVE_STATUSES })} />
        </div>
      </div>
      <div className="mv-kpi-section pt-kpi">
        <div className="mv-kpi-row">
          {/* SKU PRIORITY first — Max, 2026-10-15: "Thêm 1 cái card cho sku priority nhé". Its own violet,
              the one hue not already carrying a status on this row. */}
          <Kpi label={t('st.ptPriority')} value={projectStats.priority} tone="kv-pri" active={onlyStatus('Priority')} onClick={() => setProjectFilters({ ...projectFilters, groupState: '', status: ['Priority'] })} />
          <Kpi label={t('st.ptOnTrack')} value={projectStats.onTrack} tone="kv-conf" active={onlyStatus('On Track')} onClick={() => setProjectFilters({ ...projectFilters, groupState: '', status: ['On Track'] })} />
          <Kpi label={t('st.ptConcern')} value={projectStats.concern} tone="kv-pend" active={onlyStatus('Concern')} onClick={() => setProjectFilters({ ...projectFilters, groupState: '', status: ['Concern'] })} />
          <Kpi label={t('st.ptDelayed')} value={projectStats.delayed} tone="kv-rew" active={onlyStatus('Delayed')} onClick={() => setProjectFilters({ ...projectFilters, groupState: '', status: ['Delayed'] })} />
          <Kpi label={t('st.ptOnHold')} value={projectStats.hold} tone="kv-drop" active={onlyStatus('On Hold')} onClick={() => setProjectFilters({ ...projectFilters, groupState: '', status: ['On Hold'] })} />
          <Kpi label={t('st.ptShipFinished')} value={projectStats.shipFinished} tone="kv-rnd" active={onlyStatus('Ship Finished')} onClick={() => setProjectFilters({ ...projectFilters, groupState: '', status: ['Ship Finished'] })} />
        </div>
      </div>
      <div className="pg-bar">
        <MultiFilter value={projectFilters.status} onChange={(value) => setProjectFilters({ ...projectFilters, status: value })} allLabel={t('st.allStatus')} options={filterOptions.projectStatuses} labelOf={valueLabel} />
        <SingleFilter value={projectFilters.team} onChange={(value) => setProjectFilters({ ...projectFilters, team: value })} allLabel={t('st.ptAllTeams')} options={(trackerConfig?.teams || []).map((tm) => tm.teamName)} />
        <MultiFilter value={projectFilters.owner} onChange={(value) => setProjectFilters({ ...projectFilters, owner: value })} allLabel={t('st.ptAllOwners')} options={projectOwnerOptions} />
        <MultiFilter value={projectFilters.assigned} onChange={(value) => setProjectFilters({ ...projectFilters, assigned: value })} allLabel={t('st.ptAllAssignees')} options={filterOptions.assigneeFilters} />
        <MultiFilter value={projectFilters.designer} onChange={(value) => setProjectFilters({ ...projectFilters, designer: value })} allLabel={t('st.ptAllDesigners')} options={filterOptions.designerFilters} />
        <MultiFilter value={projectFilters.salesRep} onChange={(value) => setProjectFilters({ ...projectFilters, salesRep: value })} allLabel={t('st.ptAllSalesReps')} options={(trackerConfig?.salesReps || []).map((r) => r.name)} />
        <input value={projectFilters.search} onChange={(event) => setProjectFilters({ ...projectFilters, search: event.target.value })} placeholder={t('st.ptSearch')} />
        {/* ORIGINAL TARGET OWED (Jay 28/10) — on when there is something to find, and while it is on */}
        {(unconfirmedCount > 0 || unconfirmedOnly) && (
          <button type="button" className={`btn btn-sm ${unconfirmedOnly ? 'btn-primary' : 'btn-ghost'} pd-confirm-filter`}
            title={t('xDates.confirmFilterHint')} onClick={() => setUnconfirmedOnly((v) => !v)} data-pd-confirm-filter="1">
            {t('xDates.confirmFilter', { n: unconfirmedCount })}
          </button>
        )}
        {/* The flag says itself, and clicking it clears it — a filter that arrives from a link has to
            be visible and removable in the same place as the ones people set by hand. */}
        {!!projectFilters.flag && (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            title={t('st.clear')}
            onClick={() => setProjectFilters({ ...projectFilters, flag: '' })}
          >
            {STAGE_PENDING_FLAGS[projectFilters.flag]
              ? t(STAGE_PENDING_FLAGS[projectFilters.flag].label)
              : (projectFilters.flag === 'past_due' ? t('stx.flag.pastDue') : t('stx.flag.noSample'))} &times;
          </button>
        )}
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => {
            setProjectFilters({ status: [], category: [], assigned: [], designer: [], allocation: [], team: [], owner: [], salesRep: [], search: '', groupState: '', flag: '' });
            setProjectSort({ key: '', dir: 'asc' });
            // Clear means clear: the per-column (Excel-style) filters go too, otherwise a hidden
            // column filter keeps the grid narrowed after the toolbar looks empty.
            setColumnFilters((prev) => ({ ...prev, projects: {} }));
            setOpenColumnFilter(null);
          }}
        >
          {t('st.clear')}
        </button>
        <button
          type="button"
          className={`btn btn-sm ${projectMemberView ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setProjectMemberView((v) => !v)}
        >
          {t('st.ptByMember')}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={expandAllGroups}>{t('st.ptExpandAll')}</button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={collapseAllGroups}>{t('st.ptCollapseAll')}</button>
        <div className="pt-column-menu-wrap pt-toolbar-right">
          <button type="button" className="btn btn-ghost btn-sm" onClick={(event) => { event.stopPropagation(); setProjectColumnMenuOpen((open) => !open); }}>{t('st.ptColumns')}</button>
          {projectColumnMenuOpen && (
            <div className="pt-column-menu">
              <div className="pt-column-menu-h">
                {t('stx.showColumns')}
                <button type="button" onClick={showAllProjectColumns}>{t('stx.showAll')}</button>
              </div>
              {PROJECT_COLUMNS.map((col) => (
                <label key={col.key} className={col.locked ? 'locked' : ''}>
                  <input
                    type="checkbox"
                    checked={isProjectColVisible(col)}
                    disabled={col.locked}
                    onChange={(event) => toggleProjectColumn(col.key, event.target.checked)}
                  />
                  <span>{colLabel(col.label)}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={handleExportProjectsXlsx}><Download size={14} /> {t('st.exportExcel')}</button>
        <button type="button" className="btn btn-ghost btn-sm" data-tour="pt-report" onClick={() => window.open('/development/project-tracker/report', '_blank')}>{t('st.ptReport')}</button>
        {/* Creating a project is a team-manager action (Jay item 1). Everyone else keeps view + edit. */}
        {canCreateProject && (
          <button type="button" className="btn btn-primary btn-sm" data-tour="pt-new" onClick={() => openNewProjectModal()}>{t('st.ptNewProject')}</button>
        )}
      </div>
      {projectMemberView && (
        <div className="pt-member-panel">
          <div className="pt-member-head">
            <strong>{t('st.ptByMemberTitle')}</strong>
            <small>{t('st.ptByMemberHint')}</small>
          </div>
          <div className="pt-member-scroll">
            <table className="pt-member-table">
              <thead>
                <tr>
                  <th>{t('st.ptOwner')}</th>
                  <th>{t('st.ptTeam')}</th>
                  <th className="num">{t('st.ptGroups')}</th>
                  <th className="num">{t('st.ptSkus')}</th>
                  <th className="num">{t('st.ptActive')}</th>
                  <th className="num">{t('st.ptConcern')}</th>
                  <th className="num">{t('st.ptDelayed')}</th>
                  <th className="num">{t('st.ptOverdueExf')}</th>
                </tr>
              </thead>
              <tbody>
                {projectMemberRollup.map((row) => (
                  <tr
                    key={row.owner}
                    className={projectFilters.owner.includes(row.owner) ? 'on' : ''}
                    onClick={() => setProjectFilters({
                      ...projectFilters,
                      owner: projectFilters.owner.includes(row.owner) ? [] : [row.owner],
                    })}
                  >
                    <td className="pt-member-name">{row.owner === UNASSIGNED_OWNER ? t('stx.unassigned') : row.owner}</td>
                    <td>{row.team || '—'}</td>
                    <td className="num">{row.projects}</td>
                    <td className="num">{row.skus}</td>
                    <td className="num">{row.active}</td>
                    <td className={`num ${row.concern ? 'warn' : ''}`}>{row.concern}</td>
                    <td className={`num ${row.delayed ? 'bad' : ''}`}>{row.delayed}</td>
                    <td className={`num ${row.overdue ? 'bad' : ''}`}>{row.overdue}</td>
                  </tr>
                ))}
                {!projectMemberRollup.length && (
                  <tr><td colSpan={8} className="st-empty">{t('st.ptNoProjects')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="pt-scroll-grid">
        <table className="pt-table-exact" style={{ width: projectGridWidth, minWidth: projectGridWidth }}>
          <colgroup>
            {visibleProjectColumns.map((col) => <col key={col.key} style={{ width: col.width }} />)}
          </colgroup>
          <thead>
            <tr>
              <th>{t('st.ptNo')}</th>
              <SortTh label={t('st.ptId')} sortKey="id" sort={projectSort} onSort={toggleProjectSort}  {...thFilterProject('id')} />
              <SortTh label={t('st.ptProjectItem')} sortKey="projectItem" sort={projectSort} onSort={toggleProjectSort}  {...thFilterProject('projectItem')} />
              {projectColumnVisible.team !== false && <SortTh label={t('st.ptTeam')} sortKey="teamName" sort={projectSort} onSort={toggleProjectSort} {...thFilterProject('teamName')} />}
              {projectColumnVisible.owner !== false && <SortTh label={t('st.ptOwner')} sortKey="ownerName" sort={projectSort} onSort={toggleProjectSort} {...thFilterProject('ownerName')} />}
              {projectColumnVisible.salesRep !== false && <SortTh label={t('st.ptSalesRep')} sortKey="salesRepName" sort={projectSort} onSort={toggleProjectSort} {...thFilterProject('salesRepName')} />}
              {projectColumnVisible.projectStage !== false && <SortTh label={t('st.ptWhere')} sortKey="groupStage" sort={projectSort} onSort={toggleProjectSort} {...thFilterProject('groupStage')} />}
              {projectColumnVisible.category !== false && <SortTh label={t('st.ptCategory')} sortKey="category" sort={projectSort} onSort={toggleProjectSort}  {...thFilterProject('category')} />}
              {projectColumnVisible.projectCase !== false && <SortTh label={t('st.ptCase')} sortKey="groupCase" sort={projectSort} onSort={toggleProjectSort}  {...thFilterProject('groupCase')} />}
              {projectColumnVisible.entity !== false && <SortTh label={t('st.ptEntity')} sortKey="groupEntity" sort={projectSort} onSort={toggleProjectSort}  {...thFilterProject('groupEntity')} />}
              {projectColumnVisible.itemCategory !== false && <SortTh label={t('st.ptItemCategory')} sortKey="productType" sort={projectSort} onSort={toggleProjectSort}  {...thFilterProject('productType')} />}
              {projectColumnVisible.frameColor !== false && <SortTh label={t('st.ptFrameColor')} sortKey="frameColor" sort={projectSort} onSort={toggleProjectSort}  {...thFilterProject('frameColor')} />}
              {projectColumnVisible.qty !== false && <SortTh label={t('st.ptQty')} sortKey="estimateQty" sort={projectSort} onSort={toggleProjectSort}  {...thFilterProject('estimateQty')} />}
              {projectColumnVisible.targetPrice !== false && <SortTh label={t('st.ptTargetPrice')} sortKey="targetPrice" sort={projectSort} onSort={toggleProjectSort}  {...thFilterProject('targetPrice')} />}
              {projectColumnVisible.assigned !== false && <SortTh label={t('st.ptAssigned')} sortKey="assignedTo" sort={projectSort} onSort={toggleProjectSort}  {...thFilterProject('assignedTo')} />}
              {projectColumnVisible.designer !== false && <SortTh label={t('st.ptDesigner')} sortKey="designerName" sort={projectSort} onSort={toggleProjectSort}  {...thFilterProject('designerName')} />}
              {projectColumnVisible.allocation !== false && <SortTh label={t('st.ptAllocation')} sortKey="allocation" sort={projectSort} onSort={toggleProjectSort}  {...thFilterProject('allocation')} />}
              {projectColumnVisible.startDate !== false && <SortTh label={t('st.ptStart')} sortKey="startDate" sort={projectSort} onSort={toggleProjectSort}  {...thFilterProject('startDate')} />}
              {projectColumnVisible.exfDate !== false && <SortTh label={t('st.ptExfDate')} sortKey="exfShipDate" sort={projectSort} onSort={toggleProjectSort}  {...thFilterProject('exfShipDate')} />}
              {projectColumnVisible.launchDate !== false && <SortTh label={t('st.ptLaunchDate')} sortKey="launchDate" sort={projectSort} onSort={toggleProjectSort}  {...thFilterProject('launchDate')} />}
              {projectColumnVisible.weeksLeft !== false && <SortTh label={t('st.ptWeeksLeft')} sortKey="leadTime" sort={projectSort} onSort={toggleProjectSort}  {...thFilterProject('leadTime')} />}
              {projectColumnVisible.pct !== false && <SortTh label={t('st.ptProgress')} sortKey="completePct" sort={projectSort} onSort={toggleProjectSort}  {...thFilterProject('completePct')} />}
              {/* stageColumns holds i18n KEYS, so the label must go through t() — passing it straight through
                  printed the key itself ("st.stgLdSoff") in the header. */}
              {stageColumns.filter(([key]) => projectColumnVisible[`stage:${key}`] !== false).map(([key, label]) => <SortTh key={key} label={t(label)} sortKey={key} sort={projectSort} onSort={toggleProjectSort} {...thFilterProject(key)} />)}
              {projectColumnVisible.remark !== false && <SortTh label={t('st.ptRemark')} sortKey="remark" sort={projectSort} onSort={toggleProjectSort}  {...thFilterProject('remark')} />}
              {projectColumnVisible.sampleId !== false && <SortTh label={t('st.ptSampleId')} sortKey="sampleId" sort={projectSort} onSort={toggleProjectSort}  {...thFilterProject('sampleId')} />}
              <th>{t('st.ptAction')}</th>
            </tr>
          </thead>
          <tbody>
            {projectGroups.map((group, gi) => {
              // Groups start collapsed on every load — the user expands what they need.
              const open = openGroups[group.key] === true;
              const count = group.items.length;
              return (
                <React.Fragment key={group.key}>
                  <tr className="pt-group-row" onClick={() => toggleGroup(group.key)}>
                    <td className="pt-col-no">{gi + 1}</td>
                    <td className="pt-col-id"><span className="pt-id">{group.id || 'PG'}</span></td>
                    <td className="pt-col-name">
                      <span className="pt-group-title"><span className="pt-group-toggle">{open ? '▾' : '▸'}</span>{group.name}</span>
                      <small><span className="pt-mini-chip">{t('stx.prjItems', { count })}</span>
                        <DelayBadge delayed={groupMetaById.get(group.id)?.delayed} openChangeId={groupMetaById.get(group.id)?.openChangeId}
                          needsConfirm={groupMetaById.get(group.id)?.needsConfirm} /></small>
                    </td>
                    {projectColumnVisible.team !== false && <td>{group.teamName || '—'}</td>}
                    {projectColumnVisible.owner !== false && <td>{group.ownerName || '—'}</td>}
                    {projectColumnVisible.salesRep !== false && <td>{group.salesRepName || '—'}</td>}
                    {projectColumnVisible.projectStage !== false && (() => {
                      const state = projectStateOf(group.key);
                      const ai = state === 'active' ? aiStatus[group.key] : null;
                      if (!ai && state === 'active' && aiPending.has(group.key)) {
                        return (
                          <td>
                            <AiStatusPill testid="pt-ai-status" tagTestid="pt-ai-tag" className="pt-state-ai-wait" seed={group.key}
                              text={t('st.ptAiWriting')} head={t('st.ptAiHead')} />
                          </td>
                        );
                      }
                      if (ai) {
                        return (
                          <td>
                            <AiStatusPill testid="pt-ai-status" tagTestid="pt-ai-tag" seed={group.key} text={ai[aiLang] || ai.en} why={ai.why}
                              head={t('st.ptAiHead')} />
                          </td>
                        );
                      }
                      return <td><span className={`pill ${projectStatePill(state)}`}>{projectStateLabel(state, t)}</span></td>;
                    })()}
                    {projectColumnVisible.category !== false && <td>{group.category || '—'}</td>}
                    {projectColumnVisible.projectCase !== false && <td>{group.projectCase || '—'}</td>}
                    {projectColumnVisible.entity !== false && <td>{group.entity || '—'}</td>}
                    {projectColumnVisible.itemCategory !== false && <td />}
                    {projectColumnVisible.frameColor !== false && <td />}
                    {projectColumnVisible.qty !== false && <td />}
                    {projectColumnVisible.targetPrice !== false && <td />}
                    {projectColumnVisible.assigned !== false && <td />}
                    {projectColumnVisible.designer !== false && <td />}
                    {projectColumnVisible.allocation !== false && <td />}
                    {projectColumnVisible.startDate !== false && <td />}
                    {projectColumnVisible.exfDate !== false && <td />}
                    {projectColumnVisible.launchDate !== false && <td />}
                    {projectColumnVisible.weeksLeft !== false && <td />}
                    {projectColumnVisible.pct !== false && <td className="pt-progress">{avgPct(group.items)}%</td>}
                    {stageColumns.filter(([key]) => projectColumnVisible[`stage:${key}`] !== false).map(([key]) => <td key={key} />)}
                    {projectColumnVisible.remark !== false && <td />}
                    {projectColumnVisible.sampleId !== false && <td />}
                    <td className="pt-col-action">
                      <div className="pt-action-wrap">
                        <button type="button" className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); toggleGroup(group.key); }}>{open ? t('st.ptCollapse') : t('st.ptExpand')}</button>
                        {/* VIEW — Max, 2026-10-15: "thêm action view cho cột action". The same form, every
                            field locked and no Save: open to anybody who sees the row, including the
                            people Edit is greyed out for. */}
                        {group.id && <IconButton variant="view" title={t('st.ptViewProject')} onClick={(e) => { e.stopPropagation(); openEditGroupModal(group, 'view'); }}><Eye size={14} /></IconButton>}
                        {/* DATES — approved baseline vs forecast, and the date change request to the CEO (Young 27/10) */}
                        {/* only a real project group has dates to approve — an old SKU with no group is listed under its own id */}
                        {groupMetaById.has(group.id) && <IconButton variant="dates" title={t('xDates.openTitle')} onClick={(e) => { e.stopPropagation(); setDatesFor(group); }}><CalendarDays size={14} /></IconButton>}
                        {group.id && <IconButton variant="edit" title={group.canOpenGroup === false ? t('st.ptNotYours') : (group.canEditGroup === false ? t('stx.editSkusOfProject') : t('stx.editGroup'))} disabled={group.canOpenGroup === false} onClick={(e) => { e.stopPropagation(); openEditGroupModal(group); }}><Pencil size={14} /></IconButton>}
                        {/* Tickets follow the project, not the SKU (Max, 2026-08-13): only the
                            project owner, that team's manager or an admin may raise them. */}
                        {group.id && <IconButton variant="ticket" title={group.canEditGroup === false ? t('st.ptTicketNotYours') : t('stx.ticketsForProject')} disabled={group.canEditGroup === false} onClick={(e) => { e.stopPropagation(); openGroupTicketsModal(group); }}><TicketIcon size={14} /></IconButton>}
                        {group.id && trackerConfig?.me?.isAdmin && (
                          <IconButton variant="delete" title={t('stx.deleteProjectAdmin')} onClick={(e) => { e.stopPropagation(); deleteProjectGroup(group); }}><Trash2 size={14} /></IconButton>
                        )}
                      </div>
                    </td>
                  </tr>
                  {/* A SKU row is read-only: the pencil opens the SKU's own form instead of turning
                      twenty cells into inputs (Max, 2026-09-02). One form — with the material list in
                      it — beats changing a product across a horizontal scroll.

                      A SKU's OWN name is `subItem`; its `projectItem` is the group's name, written
                      there when the SKU is saved. Every cell and button on this row reads
                      `subItem || projectItem` for that reason — "+ Sample" did not, so a new round
                      opened with the project group's name in Style / Item instead of the SKU's
                      (Max, 2026-09-07). */}
                  {open && group.items.map((project) => (
                    <tr key={project.id} className="pt-child-row">
                      <td className="pt-col-no" />
                      <td className="pt-col-id"><span className="pt-id">{project.id}</span></td>
                      <td className="pt-col-name pt-child-name">
                        {<strong>{project.subItem || project.projectItem}</strong>}
                      </td>
                      {projectColumnVisible.team !== false && <td />}
                      {projectColumnVisible.owner !== false && <td />}
                      {projectColumnVisible.salesRep !== false && <td />}
                      {projectColumnVisible.projectStage !== false && <td><span className={`pill ${projectStatusClass(project.status)}`}>{valueLabel(project.status) || '—'}</span></td>}
                      {projectColumnVisible.category !== false && <td>{project.category || '—'}</td>}
                      {projectColumnVisible.projectCase !== false && <td />}
                      {projectColumnVisible.entity !== false && <td />}
                      {projectColumnVisible.itemCategory !== false && <td>{(project.productType || '—')}</td>}
                      {projectColumnVisible.frameColor !== false && <td>{(project.frameColor || '—')}</td>}
                      {projectColumnVisible.qty !== false && <td>{(project.estimateQty || '—')}</td>}
                      {projectColumnVisible.targetPrice !== false && <td>{project.targetPrice ? Number(project.targetPrice).toFixed(2) : '—'}</td>}
                      {projectColumnVisible.assigned !== false && <td>{(project.assignedTo || '—')}</td>}
                      {projectColumnVisible.designer !== false && <td>{(project.designerName || '—')}</td>}
                      {projectColumnVisible.allocation !== false && <td>{(project.allocation || '—')}</td>}
                      {projectColumnVisible.startDate !== false && <td>{fmt(project.startDate)}</td>}
                      {projectColumnVisible.exfDate !== false && (
                        <td className={isProjectOverdue(project) ? 'red' : ''}>{fmt(project.exfShipDate)}
                          {/* the approved EXF under the forecast, whenever they differ (projectChange.js) */}
                          {project.baselineExf && project.baselineExf !== project.exfShipDate && (
                            <span className={`pd-cell-sub${project.exfLate ? ' pd-late-text' : ''}`} title={project.exfLate ? t('xDates.delayedTitle') : undefined}>
                              {t('xDates.colBaseline')}: {fmtDay(project.baselineExf)}</span>
                          )}
                        </td>
                      )}
                      {projectColumnVisible.launchDate !== false && <td>{fmt(project.launchDate)}</td>}
                      {projectColumnVisible.weeksLeft !== false && <td className={project.leadTime && parseWeeksLeft(project.leadTime) < 0 ? 'red' : ''}>{project.leadTime || '—'}</td>}
                      {projectColumnVisible.pct !== false && <td className="pt-progress">{Number.isFinite(project.completePct) ? `${project.completePct}%` : '—'}</td>}
                      {stageColumns.filter(([key]) => projectColumnVisible[`stage:${key}`] !== false).map(([key]) => <td key={key} className="pt-col-stage">{<StageBadge value={project[key]} />}{key === 'costing' && project.costingQuote && <CostingQuoteLink quote={project.costingQuote} />}</td>)}
                      {projectColumnVisible.remark !== false && <td className="pt-col-remark">{(project.remark || '—')}</td>}
                      {projectColumnVisible.sampleId !== false && <td>{(project.sampleId || '—')}</td>}
                      <td className="pt-col-action">
                        <div className="pt-action-wrap">
                          {project.sampleId ? (
                            <button type="button" className="btn btn-smp btn-sm" title={t('st.pfViewSample')} onClick={() => viewProjectSamples(project)}>{t('st.pfSampleBtn')}</button>
                          ) : (
                            <button type="button" className="btn btn-smp-new btn-sm" title={project.canEdit === false ? t('st.ptNotYourSku') : t('stx.createSampleFor')} disabled={project.canEdit === false} onClick={() => openNewRoundModal({ styleItem: project.subItem || project.projectItem, projectId: project.id, location: project.allocation || '', developer: project.assignedTo || '', hincDesigner: project.designerName || '' })}>{t('stx.plusSample')}</button>
                          )}
                          {project.status !== 'Complete' && project.canEdit !== false && (
                            <button type="button" className="btn btn-done btn-sm" title={t('stx.markComplete')} onClick={() => openCompleteModal(project)}>{t('stx.complete')}</button>
                          )}
                          {/* A completed SKU is closed: Production has been told, so editing or
                              deleting it behind their back is exactly what must not happen. */}
                          <IconButton variant="view" title={t('st.ptViewSku')} onClick={() => openSkuModal(project, true)}><Eye size={14} /></IconButton>
                          <IconButton variant="edit" title={project.status === 'Complete' ? t('st.ptCompletedLocked') : (project.canEdit === false ? t('st.ptNotYourSku') : t('stx.editSku'))} disabled={project.canEdit === false || project.status === 'Complete'} onClick={() => openSkuModal(project)}><Pencil size={14} /></IconButton>
                          {/* Deleting is stricter than editing (Max, 2026-08-13): the developer owns
                              their SKU line but removing it is the project side's call. */}
                          <IconButton variant="delete" title={project.status === 'Complete' ? t('st.ptCompletedLocked') : (project.canEditGroup === false ? t('st.ptDeleteNotYours') : t('stx.deleteProject'))} disabled={project.canEditGroup === false || project.status === 'Complete'} onClick={() => handleDeleteProject(project.id)}><Trash2 size={14} /></IconButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            {!filteredProjects.length && <tr><td colSpan={visibleProjectColumns.length} className="st-empty">{t('st.ptNoProjects')}</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );

  const renderTasks = () => (ticketCodesOpen ? (
    <section className="screen active">
      <TicketCodes />
    </section>
  ) : (
    <section className="screen active">
      <div className="mh-titlerow">
        <div className="screen-title"><h2>{t('st.tkTitle')}</h2><p>{t('st.tkHint')}</p></div>
        <ModuleHelp manual="project" steps={TICKET_TOUR} tourKey="ticketStation" ready={tasksLoaded} />
      </div>
      <div className="task-kpis">
        {[
          /* Open first, then closed, then cancelled — the order the report's cards and its chart
             have always read in, and the order the work actually goes. The two screens are read by
             the same people about the same tickets; one of them counting backwards is the kind of
             small disagreement that makes a reader check whether the numbers mean the same thing. */
          { kind: 'all', label: t('st.tkAll'), value: taskStats.all, cls: 'all' },
          { kind: 'pending', label: t('st.tkPending'), value: taskStats.pending, cls: 'pending' },
          { kind: 'done', label: t('st.tkDone'), value: taskStats.done, cls: 'done' },
          { kind: 'cancelled', label: t('st.tkCancelled'), value: taskStats.cancelled, cls: 'cancel' },
          { kind: 'overdue', label: t('st.tkOverdue'), value: taskStats.overdue, cls: 'overdue' },
        ].map((k) => (
          <button
            type="button"
            key={k.kind}
            className={`task-kpi ${k.cls} clickable ${taskKpiFilter === k.kind ? 'active' : ''}`}
            onClick={() => setTaskKpi(k.kind)}
          >
            <div className="v">{k.value}</div>
            <div className="l">{k.label}</div>
          </button>
        ))}
      </div>
      <div className="pg-bar">
        <MultiFilter value={taskFilters.status} onChange={(value) => { setTaskFilters({ ...taskFilters, status: value }); setTaskKpiFilter('all'); }} allLabel={t('st.allStatus')} options={lists.taskStatuses?.length ? lists.taskStatuses : TASK_STATUS_OPTIONS} labelOf={taskStatusLabel} />
        <MultiFilter value={taskFilters.requester} onChange={(value) => setTaskFilters({ ...taskFilters, requester: value })} allLabel={t('st.tkAllRequesters')} options={taskFilterOptions.requesters} />
        <SingleFilter value={taskFilters.team} onChange={(value) => setTaskFilters({ ...taskFilters, team: value })} allLabel={t('st.tkAllSopTeams')} options={(trackerConfig?.teams || []).map((tm) => tm.teamName)} />
        <MultiFilter value={taskFilters.dept} onChange={(value) => setTaskFilters({ ...taskFilters, dept: value })} allLabel={t('st.tkAllTeams')} options={taskFilterOptions.depts} />
        <MultiFilter value={taskFilters.priority} onChange={(value) => setTaskFilters({ ...taskFilters, priority: value })} allLabel={t('st.tkAllPriority')} options={lists.taskPriorities?.length ? lists.taskPriorities : TASK_PRIORITY_OPTIONS} />
        <input value={taskFilters.search} onChange={(event) => setTaskFilters({ ...taskFilters, search: event.target.value })} placeholder={t('st.tkSearch')} />
        <button type="button" className="btn btn-ghost btn-sm" onClick={clearTaskFilters}>{t('st.clear')}</button>
        {/* Beside Clear, where the Project Tracker keeps its own — the two grids should not hide the
            same action in two different places. */}
        <button type="button" className="btn btn-ghost btn-sm" onClick={handleExportTicketsXlsx}><Download size={14} /> {t('st.exportExcel')}</button>
        <button type="button" className="btn btn-primary btn-sm pt-toolbar-right" data-tour="tk-new" onClick={openNewTaskModal}>{t('st.tkNewTicket')}</button>
      </div>
      <div className="twrap task-scroll">
        <table className="task-table">
          <colgroup>
            {TASK_COL_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}
          </colgroup>
          <thead>
            <tr>
              <th>#</th>
              <SortTh label={t('st.tkColProject')} sortKey="projectId" sort={taskSort} onSort={toggleTaskSort}  {...thFilterTask('projectId')} />
              <SortTh label={t('st.tkColProjectName')} sortKey="projectName" sort={taskSort} onSort={toggleTaskSort}  {...thFilterTask('projectName')} />
              <SortTh label={t('st.tkColTicketId')} sortKey="taskId" sort={taskSort} onSort={toggleTaskSort}  {...thFilterTask('taskId')} />
              <SortTh label={t('st.tkColTicket')} sortKey="name" sort={taskSort} onSort={toggleTaskSort}  {...thFilterTask('name')} />
              <SortTh label={t('st.tkManagerStatus')} sortKey="status" sort={taskSort} onSort={toggleTaskSort}  {...thFilterTask('status')} />
              <th>{t('st.tkColAssigneeReply')}</th>
              {/* Team and Project Owner come from the ticket's project group — they are what decides
                  who may adjust the ticket. */}
              <SortTh label={t('st.ptTeam')} sortKey="teamName" sort={taskSort} onSort={toggleTaskSort}  {...thFilterTask('teamName')} />
              <SortTh label={t('st.ptOwner')} sortKey="ownerName" sort={taskSort} onSort={toggleTaskSort}  {...thFilterTask('ownerName')} />
              <SortTh label={t('st.tkColTeam')} sortKey="department" sort={taskSort} onSort={toggleTaskSort}  {...thFilterTask('department')} />
              <SortTh label={t('st.tkColRequester')} sortKey="assignee" sort={taskSort} onSort={toggleTaskSort}  {...thFilterTask('assignee')} />
              <SortTh label={t('st.tkColType')} sortKey="taskType" sort={taskSort} onSort={toggleTaskSort}  {...thFilterTask('taskType')} />
              <SortTh label={t('st.tkColPriority')} sortKey="priority" sort={taskSort} onSort={toggleTaskSort}  {...thFilterTask('priority')} />
              <SortTh label={t('st.tkColDue')} sortKey="dueDate" sort={taskSort} onSort={toggleTaskSort}  {...thFilterTask('dueDate')} />
              {/* Same rule as the Sample Tracker's D-day: today − Due, positive is overdue, and a
                  closed ticket freezes at the day it closed instead of drifting (Max, 2026-08-13). */}
              <SortTh label={t('st.colDday')} hint={t('st.tkDdayHint')} sortKey="dDay" sort={taskSort} onSort={toggleTaskSort}  {...thFilterTask('dDay')} />
              <th>{t('st.tkColNote')}</th>
              {/* Who raised it and who last touched it — Max, 2026-09-08. Sortable and filterable
                  like the rest: "everything Tina changed" is the question these columns exist for. */}
              <SortTh label={t('st.tkColCreatedBy')} sortKey="createdBy" sort={taskSort} onSort={toggleTaskSort}  {...thFilterTask('createdBy')} />
              <SortTh label={t('st.tkColCreatedAt')} sortKey="createdAt" sort={taskSort} onSort={toggleTaskSort}  {...thFilterTask('createdAt')} />
              <SortTh label={t('st.tkColUpdatedBy')} sortKey="updatedBy" sort={taskSort} onSort={toggleTaskSort}  {...thFilterTask('updatedBy')} />
              <SortTh label={t('st.tkColUpdatedAt')} sortKey="updatedAt" sort={taskSort} onSort={toggleTaskSort}  {...thFilterTask('updatedAt')} />
              <th>{t('st.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {ticketRows.map(({ row, group, firstOfProject }, i) => (
              /* The divider is the only thing left of the grouping: same project = adjacent rows,
                 new project = a line above it. */
              <tr key={row.taskId} className={firstOfProject && i ? 'tk-row tk-newproj' : 'tk-row'}>
                <td className="pt-col-no">{i + 1}</td>
                <td><strong>{row.projectId || '—'}</strong></td>
                <td className="tk-projname" title={group.projectTitle}>{group.projectTitle}</td>
                <td className="task-id">{row.taskId}</td>
                <td>
                  <div className="task-title">{row.name}</div>
                  {row.description && <div className="task-desc" title={row.description}>{row.description}</div>}
                </td>
                <td><span className={`task-pill ${taskStatusClass(row.status)}`}>{taskStatusLabel(taskNormalizeStatus(row.status))}</span></td>
                <td>
                  {row.assigneeStatus || row.assigneeNote ? (
                    <div className="task-reply">
                      {row.assigneeStatus && (
                        <span className={`task-pill ${taskStatusClass(row.assigneeStatus)}`}>{row.assigneeStatus}</span>
                      )}
                      {row.assigneeUpdatedAt && <span className="task-reply-when">{row.assigneeUpdatedAt}</span>}
                      {row.assigneeNote && (
                        <div className="task-ro-note" title={row.assigneeNote}>{row.assigneeNote}</div>
                      )}
                    </div>
                  ) : (
                    /* NOT STARTED, not a dash — Max, 2026-09-17: "để Not started luôn nha chứ đừng
                       để trống". A dash reads as "no information"; what the empty cell actually
                       means is that nobody on the receiving team has answered, which IS the first of
                       their four states and is exactly how the report counts it.

                       Derived, never stored: the column stays NULL until that team presses a button,
                       and writing the word in would be the portal answering on their behalf. It
                       wears the muted pill and says so on hover. */
                    <span className="task-pill muted" title={row.sentAt
                      ? t('stx.noReplyEmailed')
                      : t('stx.noReplyNeverEmailed')}>
                      {/* Not translated, unlike the cards above it: this cell sits in a column of
                          the receiving team's own answers — Done, In Progress, Cancelled — which are
                          stored values and print in English in both languages. A Korean word among
                          them would read as a different kind of thing. */}
                      Not started
                    </span>
                  )}
                </td>
                {/* Team and Owner used to live on the group header only, which is what made the ticket
                    rows look full of empty cells. */}
                <td>{row.teamName || group.teamName || '—'}</td>
                <td>{firstNameOf(row.ownerName || group.ownerName)}</td>
                <td>{row.department || '—'}</td>
                <td>{firstNameOf(row.assignee)}</td>
                <td>{row.taskType || '—'}</td>
                <td><span className={`task-pri-${String(row.priority || 'Medium').toLowerCase()}`}>{valueLabel(row.priority || 'Medium')}</span></td>
                <td className={taskIsOverdue(row) ? 'red' : ''}>{row.dueDate || '—'}</td>
                <td className={dDayClass(row.dDay, row.dDayDone)}>{dDayLabel(row.dDay)}</td>
                {/* One clamped line in a dense table, so the note is flattened here rather than
                    rendered — its bold and its colours belong in the card and in the mail. */}
                <td><div className="task-ro-note" title={richToText(row.note)}>{richToText(row.note) || '—'}</div></td>
                {/* Vietnam time, to the minute. The seconds are on the Sent badge where a re-send has
                    to be told apart; a row in a list only needs to say which day and roughly when. */}
                <td>{row.createdBy || '—'}</td>
                <td className="task-ts">{fmtDateTimeVN(row.createdAt, '—')}</td>
                <td>{row.updatedBy || '—'}</td>
                <td className="task-ts">{fmtDateTimeVN(row.updatedAt, '—')}</td>
                <td className="pt-col-action">
                  <div className="pt-action-wrap">
                    <IconButton variant="view" title={t('st.tkView')} onClick={() => openEditTaskModal(row, true)}><Eye size={14} /></IconButton>
                    <IconButton variant="edit" title={row.canEdit === false ? t('st.tkNotYourTeam') : t('st.tkEdit')} disabled={row.canEdit === false} onClick={() => openEditTaskModal(row)}><Pencil size={14} /></IconButton>
                    <IconButton variant="duplicate" title={row.canEdit === false ? t('st.tkNotYourTeam') : t('st.tkDuplicate')} disabled={row.canEdit === false} onClick={() => duplicateTask(row)}><Copy size={14} /></IconButton>
                    <IconButton variant="delete" title={row.canEdit === false ? t('st.tkNotYourTeam') : t('st.tkDelete')} disabled={row.canEdit === false} onClick={() => handleDeleteTask(row.taskId)}><Trash2 size={14} /></IconButton>
                    {/* Send until the mail has gone out, Remind afterwards. `sentAt` is the same
                        value the ticket card's Sent badge reads, so the two can never disagree. */}
                    {row.canEdit !== false && !['Done', 'Cancelled'].includes(taskNormalizeStatus(row.status)) && (
                      <button
                        type="button"
                        className={`btn btn-sm ${row.sentAt ? 'btn-ghost' : 'btn-smp'}`}
                        title={row.sentAt ? t('st.tkRemindHint') : t('st.tkSendHint')}
                        disabled={remindingId === row.taskId}
                        onClick={() => (row.sentAt ? remindTask(row) : sendTaskNow(row))}
                      >
                        {remindingId === row.taskId ? '…' : (row.sentAt ? t('st.tkRemind') : t('st.tkSend'))}
                      </button>
                    )}
                    {/* NOT UNTIL THE MAIL HAS GONE — Max, 2026-09-17: "khi nào gửi mail ticket lần
                        đầu thì nút link này mới available còn nếu chưa gửi thì ko mở dc".

                        This button hands over the reply token, and the reply page asks for nothing
                        else. On a ticket nobody has been told about, that is a way to record an
                        answer from a team that does not know the request exists — which is how
                        seventeen never-emailed tickets came to say the receiving team had finished
                        them (2026-09-14). `sentAt` is the same value Send/Remind reads, so the two
                        buttons cannot disagree about whether the mail went.

                        Shown and DISABLED rather than hidden: a button that vanishes leaves people
                        looking for it, and the tooltip is where the reason goes. */}
                    {row.updateUrl && (
                      <button
                        type="button"
                        className="btn btn-smp btn-sm"
                        disabled={!row.sentAt}
                        title={row.sentAt ? t('st.tkOpenLink') : t('st.tkLinkNotSent')}
                        onClick={() => window.open(row.updateUrl, '_blank')}
                      >
                        {t('st.tkLink')}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!ticketRows.length && <tr><td colSpan={TASK_COL_WIDTHS.length} className="st-empty">{t('st.tkNoTickets')}</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  ));

  return (
    <PortalListsContext.Provider value={lists}>
    <TicketFormsContext.Provider value={ticketForms}>
    <div className="st-page">
      <Header />
      <main className="st-shell sample-portal-shell">
        <div className="sample-app">
          {/* The quote of the day lives in the header now (2026-10-15), before the bell. */}
          <div className={`sample-subtabs view-tabs ${activeTab === 'sample' ? 'show' : ''}`} role="tablist">
            {sampleTabs.map((tab) => (
              <button type="button" role="tab" key={tab.id} aria-selected={sampleView === tab.id} className={sampleView === tab.id ? 'active' : ''} onClick={() => setSampleView(tab.id)}>
                <TabIcon k={`st-${tab.id}`} />{t(tab.label)}
                {/* Max, 2026-09-09: "Nút all Sample cho tôi số lượng tổng sample của 2 loại nha".
                    Both series added together. It is the one count on this screen that does NOT move
                    when the Project/Trial tab below it changes, which is what makes it readable as
                    the full R&D load rather than as one more filtered number. */}
                {tab.id === 'all' && (
                  <span className="st-tab-count">{totalSampleStats.totalSamples}</span>
                )}
              </button>
            ))}
          </div>
          {/* Only whoever may edit the master ever sees the second pill. */}
          <div className={`sample-subtabs view-tabs ${activeTab === 'tickets' && authFunctions?.includes('ticket_station_config') ? 'show' : ''}`} role="tablist">
            {ticketTabs.map((tab) => (
              <button
                type="button"
                role="tab"
                key={tab.id}
                aria-selected={(ticketCodesOpen ? 'codes' : 'tickets') === tab.id}
                className={(ticketCodesOpen ? 'codes' : 'tickets') === tab.id ? 'active' : ''}
                onClick={() => { const next = tab.id === 'codes'; if (!next && ticketCodesOpen) loadTasks(true); setTicketCodesOpen(next); }}
              >
                <TabIcon k={tab.id} />{t(tab.label)}
              </button>
            ))}
          </div>
          {(message || error) && <div className={`st-alert ${error ? 'error' : 'success'}`}>{error || message}</div>}
          {activeTab === 'dash' && <DevDashboard embedded />}
          {activeTab === 'sample' && renderSample()}
          {activeTab === 'projects' && renderProjects()}
          {activeTab === 'tickets' && renderTasks()}
        </div>
      </main>
      {/* One SKU, one form — the same editor the group modal uses for an existing SKU, so the
          material list and the process stages come with it rather than being a second layout to
          keep in step. */}
      {!!inlineProject && (
        <div className="st-modal-overlay" role="dialog" aria-modal="true">
          <div className="st-modal pt-sku-modal">
            <div className="st-modal-head">
              <h3>
                {inlineProject.id}
                {' — '}
                {inlineProject.subItem || inlineProject.projectItem || 'SKU'}
                {inlineProjectView && <span className="pt-view-tag">{t('st.ptViewTag')}</span>}
              </h3>
              <button type="button" className="btn btn-ghost btn-sm st-hist-btn" onClick={() => setHistoryOf({ entity: 'sku', id: inlineProject.id })} data-st-history="sku">
                <HistoryIcon size={14} /> {t('xCatchy.history')}
              </button>
              <button
                type="button"
                className="st-modal-close"
                aria-label={t('st.viewClose')}
                onClick={() => { skuModalMaterials.reset(); setInlineProject(null); setInlineProjectError(''); }}
              >
                &times;
              </button>
            </div>
            <div className="st-modal-body">
              {!!inlineProjectError && <div className="st-alert error">{inlineProjectError}</div>}
              <ProjectSkuInlineEditor
                form={inlineProject}
                filterOptions={filterOptions}
                rounds={rounds}
                skuId={inlineProject.id}
                materialDrafts={skuModalMaterials}
                readOnly={inlineProjectView}
                onChange={updateInlineProject}
              />
            </div>
            <div className="st-modal-foot st-form-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => { skuModalMaterials.reset(); setInlineProject(null); setInlineProjectError(''); }}
              >
                {inlineProjectView ? t('st.ptViewClose') : t('st.pfCancel')}
              </button>
              {!inlineProjectView && (
                <button type="button" className="btn btn-primary" disabled={projectSaving} onClick={saveInlineProject}>
                  {projectSaving ? t('st.pfSaving') : t('st.pfSaveChanges')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {historyOf && <RecordHistory entity={historyOf.entity} id={historyOf.id} onClose={() => setHistoryOf(null)} />}
      <TourGuide steps={TOUR_STEPS} open={tourOpen} onClose={closeTour} onFinish={closeTour} />

      {/* THE PICKER AND THE FORM ARE NEVER BOTH UP: choosing a SKU closes this and opens that, in
          one handler. Two overlays from one file sharing a z-index is how this codebase has lost a
          click five times, and the cheapest fix is for the second one not to exist yet. */}
      {samplePicker && (
        <div className="st-modal-overlay st-cs-overlay" role="dialog" aria-modal="true" aria-label={t('st.csTitle')}>
          <div className="st-modal st-cs-modal">
            <div className="st-modal-head">
              <h3>{t('st.csTitle')}</h3>
              <button type="button" className="st-modal-close" onClick={() => setSamplePicker(null)} aria-label={t('st.viewClose')}>&times;</button>
            </div>
            <div className="st-modal-body">
              <p className="st-cs-intro">{t('st.csIntro')}</p>
              <input
                className="st-cs-search"
                autoFocus
                value={samplePicker.q}
                placeholder={t('st.csSearch')}
                onChange={(e) => setSamplePicker({ q: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Escape') setSamplePicker(null); }}
              />
              <div className="st-cs-count">
                {t('st.csCount', { skus: sampleCandidates.skus, projects: sampleCandidates.projects })}
              </div>
              {!sampleCandidates.total ? <p className="st-cs-empty">{t('st.csNone')}</p>
                : !sampleCandidates.skus ? <p className="st-cs-empty">{t('st.csNoMatch')}</p> : (
                  <div className="st-cs-list">
                    {sampleCandidates.groups.map((g) => (
                      <section className="st-cs-group" key={g.key}>
                        <h4>{g.name}{!!g.groupId && <span className="st-cs-prj">{g.groupId}</span>}</h4>
                        {g.skus.map((p) => (
                          <button
                            type="button"
                            key={p.id}
                            className="st-cs-row"
                            disabled={p.canEdit === false}
                            title={p.canEdit === false ? t('st.ptNotYourSku') : undefined}
                            onClick={() => chooseSampleSku(p)}
                          >
                            <span className="st-cs-sku">{p.id}</span>
                            <span className="st-cs-name">{p.subItem || p.projectItem || '—'}</span>
                            <span className="st-cs-who">{[p.assignedTo, p.designerName].filter(Boolean).join(' · ') || '—'}</span>
                            <span className="st-cs-status">{valueLabel(p.status) || '—'}</span>
                          </button>
                        ))}
                      </section>
                    ))}
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {modal && (
        <RoundModal
          mode={modal.mode}
          advancingFrom={modal.current}
          form={modal.form}
          lists={lists}
          rounds={rounds}
          projects={projects}
          saving={saving}
          errors={modalErrors}
          onChange={updateModalForm}
          onClose={closeModal}
          onSave={handleSaveRound}
          photos={newRoundPhotos}
          onChangePhotos={setNewRoundPhotos}
          onHistory={(id) => setHistoryOf({ entity: 'sample_round', id })}
        />
      )}
      {projectModal && (
        <ProjectModal
          mode={projectModal.mode}
          form={projectModal.form}
          filterOptions={filterOptions}
          rounds={rounds}
          saving={projectSaving}
          errors={projectModalErrors}
          onChange={updateProjectModalForm}
          onClose={closeProjectModal}
          onSave={handleSaveProject}
        />
      )}
      {newProject && (
        <NewProjectModal
          group={newProject}
          filterOptions={filterOptions}
          trackerConfig={trackerConfig}
          canCreate={canCreateProject}
          rounds={rounds}
          saving={projectSaving}
          errors={newProjectErrors}
          onChangeGroup={updateNewProject}
          onChangeSku={updateSku}
          onAddSku={addSku}
          onDuplicateSku={duplicateSkuAt}
          onRemoveSku={removeSku}
          onAddPartner={handleAddPartner}
          onAddSalesRep={handleAddSalesRep}
          canAddSalesRep={canCreateProject || !!trackerConfig?.me?.isAdmin
            || projectGroups.some((g) => Number(g.ownerUserId) === Number(trackerConfig?.me?.userId))}
          onClose={requestCloseNewProject}
          onSave={() => handleSaveNewProject(false)}
          photos={newProjectPhotos}
          onChangePhotos={setNewProjectPhotos}
          onOpenMove={() => setMovePicker({ into: 'new' })}
          onRemoveMoveIn={(id) => setNewProject((prev) => {
            const next = (prev.moveIns || []).filter((x) => x.projectId !== id);
            // Dropped rather than left as [] once empty, so a form put back as it was reads as unchanged.
            return { ...prev, moveIns: next.length ? next : undefined };
          })}
        />
      )}
      {movePicker && (
        <MoveSkusPicker
          api={trackerApi}
          teamKey={movePicker.into === 'new' ? newProject?.teamKey : groupModal?.teamKey}
          excludeGroupId={movePicker.into === 'new' ? '' : groupModal?.groupId}
          taken={movePicker.into === 'new' ? (newProject?.moveIns || []) : groupMoveIns}
          onClose={() => setMovePicker(null)}
          onAdd={(items) => {
            if (movePicker.into === 'new') setNewProject((prev) => (prev ? { ...prev, moveIns: [...(prev.moveIns || []), ...items] } : prev));
            else setGroupMoveIns((prev) => [...prev, ...items]);
            setMovePicker(null);
          }}
        />
      )}
      {completeFor && (
        <div className="st-modal-overlay st-overlay-top" role="dialog" aria-modal="true">
          <div className="st-modal" style={{ width: 720 }}>
            <div className="st-modal-head">
              <h3>{t('stx.completeTitle', { id: completeFor.project.id })}</h3>
              <button type="button" className="st-modal-close" onClick={() => setCompleteFor(null)} aria-label={t('st.viewClose')}>&times;</button>
            </div>
            <div className="st-modal-body">
              {completeFor.loading ? <div className="st-empty">{t('stx.preparingEmail')}</div> : (
                <div className="pt-form-grid pt-complete-form">
                  <div className="ff full">
                    <span>{t('stx.to')}<span className="req">*</span></span>
                    <input value={completeFor.to} onChange={(e) => setCompleteFor((p2) => ({ ...p2, to: e.target.value }))} placeholder="production@helinox.com" />
                  </div>
                  <div className="ff full">
                    <span>CC</span>
                    <input value={completeFor.cc} onChange={(e) => setCompleteFor((p2) => ({ ...p2, cc: e.target.value }))} placeholder="cc@helinox.com" />
                  </div>
                  <div className="ff full">
                    <span>{t('stx.subject')} <span className="pt-readonly-badge">{t('st.tkSystemGenerated')}</span></span>
                    <input className="pt-ro-field" value={completeFor.subject} readOnly />
                  </div>
                  <div className="ff full">
                    <span>{t('stx.message')} <span className="pt-readonly-badge">{t('st.tkSystemGenerated')}</span></span>
                    <textarea className="pt-ro-field" rows={12} value={completeFor.body} readOnly />
                  </div>
                  <div className="pt-approval-note pt-complete-note" style={{ gridColumn: '1 / -1' }}>
                    <Trans t={t} i18nKey="stx.completeNote" components={{ b: <strong /> }} />
                  </div>
                </div>
              )}
            </div>
            {!!completeError && <div className="st-alert error st-alert-foot">{completeError}</div>}
            <div className="st-modal-foot st-form-actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCompleteFor(null)} disabled={completeBusy}>{t('st.pfCancel')}</button>
              <button type="button" className="btn btn-primary btn-sm" onClick={submitComplete} disabled={completeBusy || completeFor.loading}>
                {completeBusy ? t('stx.sending') : t('stx.sendMarkComplete')}
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteTicketOne && (
        <div className="st-modal-overlay st-overlay-top" role="dialog" aria-modal="true">
          <div className="st-modal st-modal-sm">
            <div className="st-modal-head">
              <h3>{t('st.dt1Title', { id: deleteTicketOne.taskId })}</h3>
              <button type="button" className="st-modal-close" onClick={() => setDeleteTicketOne(null)} aria-label={t('st.viewClose')}>&times;</button>
            </div>
            <div className="st-modal-body">
              <p className="dup-lead">{deleteTicketOne.name}</p>
              <p className="dup-hint">
                {deleteTicketOne.emailed
                  ? t('st.dt1Emailed', { to: deleteTicketOne.to || '—' })
                  : t('st.dt1NotEmailed')}
              </p>
              {deleteTicketOne.emailed && (
                <div className="ff full" style={{ marginTop: 12 }}>
                  <span>{t('st.dtReason')}<span className="req">*</span></span>
                  <textarea
                    rows={2}
                    autoFocus
                    maxLength={500}
                    value={deleteTicketOneReason}
                    onChange={(e) => setDeleteTicketOneReason(e.target.value)}
                    placeholder={t('st.dtReasonPh')}
                  />
                  <small className="pf-hint">{t('st.dt1ReasonHint')}</small>
                </div>
              )}
            </div>
            <div className="st-modal-foot st-form-actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDeleteTicketOne(null)}>{t('st.pfCancel')}</button>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                disabled={deleteTicketOne.emailed && !deleteTicketOneReason.trim()}
                onClick={confirmDeleteTask}
              >
                {deleteTicketOne.emailed ? t('st.dt1ConfirmMail') : t('st.dt1Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteGroupPrompt && (
        <div className="st-modal-overlay st-overlay-top" role="dialog" aria-modal="true">
          <div className="st-modal st-modal-sm">
            <div className="st-modal-head">
              <h3>{t('st.dgpTitle', { id: deleteGroupPrompt.group.id })}</h3>
              <button type="button" className="st-modal-close" onClick={() => setDeleteGroupPrompt(null)} aria-label={t('st.viewClose')}>&times;</button>
            </div>
            <div className="st-modal-body">
              <p className="dup-lead"><strong>{deleteGroupPrompt.group.name}</strong></p>
              {deleteGroupPrompt.loading ? (
                <div className="st-empty">{t('st.dgpChecking')}</div>
              ) : (
                <>
                  <ul className="dup-list">
                    <li>{t('st.dgpSkus', { n: deleteGroupPrompt.skus })}</li>
                    <li>{t('st.dgpRounds')}</li>
                    {!!deleteGroupPrompt.tickets.length && <li>{t('st.dgpTickets', { n: deleteGroupPrompt.tickets.length })}</li>}
                  </ul>
                  {!!deleteGroupPrompt.tickets.length && (
                    <ul className="dup-list" style={{ marginTop: 8 }}>
                      {deleteGroupPrompt.tickets.map((tk) => (
                        <li key={tk.taskId}>
                          <span className="dup-id">{tk.taskId}</span> {tk.name}
                          {tk.receivingTeam ? ` — ${tk.receivingTeam}` : ''}
                          {tk.notified ? '' : ` · ${t('st.dtNeverEmailed')}`}
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="dup-hint">{t('st.dgpUndo')}</p>
                  {/* Only when tickets are being cancelled — the reason is what the receiving team reads. */}
                  {!!deleteGroupPrompt.tickets.length && (
                    <div className="ff full" style={{ marginTop: 12 }}>
                      <span>{t('st.dtReason')}<span className="req">*</span></span>
                      <textarea
                        rows={2}
                        autoFocus
                        maxLength={500}
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        placeholder={t('st.dtReasonPh')}
                      />
                      <small className="pf-hint">{t('st.dtReasonHint')}</small>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="st-modal-foot st-form-actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDeleteGroupPrompt(null)}>{t('st.pfCancel')}</button>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                disabled={deleteGroupPrompt.loading || (!!deleteGroupPrompt.tickets.length && !deleteReason.trim())}
                onClick={confirmDeleteGroup}
              >
                {deleteGroupPrompt.tickets.length ? t('st.dtConfirm') : t('st.dgpConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
      {baseModal && (
        <div className="st-modal-overlay" role="dialog" aria-modal="true">
          <div className="st-modal st-modal-sm">
            <div className="st-modal-head">
              <h3>{baseModal.baseId}{baseModal.view && <span className="pt-view-tag">{t('st.ptViewTag')}</span>}</h3>
              <button type="button" className="st-modal-close" onClick={() => setBaseModal(null)} aria-label={t('st.viewClose')}>&times;</button>
            </div>
            <div className="st-modal-body">
              <div className="pt-help" style={{ marginBottom: 10 }}>
                {t('st.bmHint', { rounds: baseModal.rounds })}
              </div>
              <Field label={t('st.rmStyleItem')} required hint={t('st.bmStyleHint')}>
                <input
                  value={baseModal.view ? baseModal.styleItem : baseStyleDraft}
                  onChange={(e) => setBaseStyleDraft(e.target.value)}
                  placeholder={baseModal.styleItem}
                  readOnly={!!baseModal.view}
                  className={baseModal.view ? 'pt-ro-field' : undefined}
                />
              </Field>
              <div style={{ marginTop: 14, borderTop: '1px solid #eef2f7', paddingTop: 12 }}>
                <ProjectPhotos scope="sample" ownerKey={baseModal.baseId} readOnly={!!baseModal.view} />
              </div>
            </div>
            {baseModal.view ? (
              <div className="st-modal-foot st-form-actions">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setBaseModal(null)}>{t('st.viewClose')}</button>
              </div>
            ) : (
            <div className="st-modal-foot st-form-actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setBaseModal(null)}>{t('st.pfCancel')}</button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={saving || !baseStyleDraft.trim() || baseStyleDraft.trim() === baseModal.styleItem}
                onClick={async () => {
                  await handleRenameSample({ baseId: baseModal.baseId, styleItem: baseModal.styleItem }, baseStyleDraft.trim());
                  setBaseModal(null);
                }}
              >
                {t('st.bmRename')}
              </button>
            </div>
            )}
          </div>
        </div>
      )}
      {confirmGroupDiscard && (
        <DiscardPrompt
          onKeep={() => setConfirmGroupDiscard(false)}
          onDiscard={() => { setConfirmGroupDiscard(false); groupMaterials.reset(); setGroupTicketDrafts({}); setGroupMoveIns([]); setGroupModal(null); }}
        />
      )}
      {confirmDiscard && (
        <div className="st-modal-overlay st-overlay-top" role="dialog" aria-modal="true">
          <div className="st-modal st-modal-sm">
            <div className="st-modal-head">
              <h3>{t('st.pfDiscardTitle')}</h3>
            </div>
            <div className="st-modal-body">
              <p>{t('st.pfDiscardBody')}</p>
            </div>
            <div className="st-modal-foot st-form-actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirmDiscard(false)}>{t('st.pfDiscardKeep')}</button>
              <button type="button" className="btn btn-danger btn-sm" onClick={() => { setConfirmDiscard(false); setNewProject(null); setNewProjectPhotos([]); }}>{t('st.pfDiscardConfirm')}</button>
            </div>
          </div>
        </div>
      )}
      {duplicateWarn && (
        <div className="st-modal-overlay st-overlay-top" role="dialog" aria-modal="true">
          <div className="st-modal st-modal-sm">
            <div className="st-modal-head">
              <h3>{t('stx.dupTitle')}</h3>
              <button type="button" className="st-modal-close" onClick={() => setDuplicateWarn(null)} aria-label={t('st.viewClose')}>&times;</button>
            </div>
            <div className="st-modal-body">
              <p className="dup-lead"><Trans t={t} i18nKey="stx.dupLead" components={{ b: <strong /> }} /></p>
              <ul className="dup-list">
                {duplicateWarn.matches.map((m) => (
                  <li key={m.groupId}><span className="dup-id">{m.groupId}</span> {m.groupName}</li>
                ))}
              </ul>
              <p className="dup-hint">
                {t('stx.dupHint')}
              </p>
            </div>
            <div className="st-modal-foot st-form-actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDuplicateWarn(null)}>{t('st.pfCancel')}</button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => { setDuplicateWarn(null); handleSaveNewProject(true); }}
              >
                {t('stx.createAnyway')}
              </button>
            </div>
          </div>
        </div>
      )}
      {datesFor && (
        <ProjectDatesModal groupId={datesFor.id} canRequest={datesFor.canEditGroup === true}
          onClose={() => setDatesFor(null)} onChanged={() => loadData()} />
      )}
      {groupModal && (
        <div className="st-modal-overlay" role="dialog" aria-modal="true">
          <div className="st-modal">
            <div className="st-modal-head">
              <h3>{groupModal.mode === 'tickets' ? t('st.pfTickets') : t('st.gmTitle')} — {groupModal.groupId}
                {groupModal.mode === 'view' && <span className="pt-view-tag">{t('st.ptViewTag')}</span>}</h3>
              {groupModal.groupId && (
                <button type="button" className="btn btn-ghost btn-sm st-hist-btn" onClick={() => setHistoryOf({ entity: 'project', id: groupModal.groupId })} data-st-history="project">
                  <HistoryIcon size={14} /> {t('xCatchy.history')}
                </button>
              )}
              <button type="button" className="st-modal-close" onClick={requestCloseGroupModal} aria-label={t('st.viewClose')}>&times;</button>
            </div>
            <div className="st-modal-body">

              <div className="own-section own-hevn">
                <div className="own-hd own-hd-sub">
                  <span>{t('st.pfProjectInfo')}</span>
                  <small>{groupModal.mode === 'tickets' ? t('st.pfReadOnlyHere')
                    : groupModal.mode === 'view' ? t('st.ptViewHint')
                    : groupModal.skuOnly ? t('st.gmSkuOnlyHint') : t('st.pgSharedHint')}</small>
                </div>
                <div className="own-body">
                  <ProjectInfoFields
                    value={groupModal}
                    onChange={(patch) => setGroupModal((prev) => ({ ...prev, ...patch }))}
                    launchBefore={groups.find((g) => g.groupId === groupModal.groupId) || null}
                    readOnly={groupModal.mode === 'tickets' || groupModal.mode === 'view' || groupModal.skuOnly}
                    purposeRequired={groupModal.purposeRequired !== false}
                    teams={trackerConfig?.teams || []}
                    partners={trackerConfig?.partners || []}
                    salesReps={trackerConfig?.salesReps || []}
                    categories={(trackerConfig?.categories || []).length
                      ? trackerConfig.categories : filterOptions.projectCategories}
                    canAddPartner={canCreateProject}
                    onAddPartner={handleAddPartner}
                    viewer={trackerConfig?.me || null}
                    canAddSalesRep={canCreateProject || !!trackerConfig?.me?.isAdmin}
                    onAddSalesRep={handleAddSalesRep}
                    nameFallback={t('stx.fillSeason')}
                  />
                  {/* Photos were reachable only from New Project and from Young's approval screen, so
                      the save handler's own advice — "add the rest from Edit" — pointed at a form that
                      had no photo box (Max, 2026-08-19). Read-only in the Tickets view: that screen
                      exists to issue tickets, not to change the project. */}
                  <div style={{ marginTop: 14, borderTop: '1px solid #eef2f7', paddingTop: 12 }}>
                    {/* A ROLL-UP, not a place to upload — Max, 2026-09-21. The photos are the
                        SKUs' own now; this is where somebody sees all of them without opening
                        three cards. `key` so it re-reads after a save moved a SKU into or out of
                        the submitted set. */}
                    <ProjectPhotos key={`roll-${groupModal.groupId}`} rollupGroupId={groupModal.groupId} readOnly={groupModal.mode === 'tickets' || groupModal.mode === 'view'} />
                  </div>
                </div>
              </div>

              {groupModal.mode !== 'tickets' && (
              <div className="own-section own-both">
                <div className="own-hd" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{t('st.pfSkuInfo')} <span className="pt-mini-chip">{groupSkus.length} {t('st.pgPrjItems')}</span></span>
                  {groupModal.mode !== 'view' && <span className="own-hd-actions">
                    {!groupModal.skuOnly && (
                      <MoveSkusButton me={trackerConfig?.me} teamKey={groupModal.teamKey} onOpen={() => setMovePicker({ into: 'group' })} />
                    )}
                    <button type="button" className="btn btn-ghost btn-sm" onClick={addGroupSku}>{t('st.pfAddSku')}</button>
                  </span>}
                </div>
                <div className="own-body">
                  {groupModal.mode !== 'view' && (
                    <div className="pt-help" style={{ marginBottom: 10 }}>
                      {t('st.pgSkuHint')}
                    </div>
                  )}
                  {!groupSkus.length ? (
                    <div className="pt-help" style={{ color: '#9CA3AF' }}>{t('st.pgNoSku')}</div>
                  ) : (
                    <div className="pt-sku-list">
                      {groupSkus.map((sku) => (
                        <div className="pt-sku-card" key={sku.id}>
                          <div className="pt-sku-head" onClick={() => toggleGroupSkuEditor(sku)} role="button" tabIndex={0}>
                            <div className="pt-sku-summary">
                              <span className="pt-sku-caret">{groupSkuOpen[sku.id] ? '▾' : '▸'}</span>
                              <span className="pt-mini-chip">{sku.id}</span>
                              <span className="pt-sku-name">{sku.subItem || sku.projectItem}</span>
                              <span className="pt-mini-chip">{sku.assignedTo || t('st.pfUnassigned')}</span>
                              <span className="pt-mini-chip">{sku.designerName || t('st.pfNoDesigner')}</span>
                              <span className="pt-mini-chip">{sku.productType || t('st.pfNoCategory')}</span>
                              <span className="pt-mini-chip">{t('st.pfQty')} {sku.estimateQty || 'TBA'}</span>
                              <span className={`pt-mini-chip ${Number(sku.completePct) >= 100 ? 'ok' : 'muted'}`}>{Number.isFinite(sku.completePct) ? sku.completePct : 0}% {t('st.pfStageChip')}</span>
                            </div>
                            {/* Taking a SKU out of the group is a change to the PROJECT, not to the SKU:
                                it goes through PUT /groups, which refuses the SKU-only teams anyway.
                                Hidden rather than left to fail, so nobody clicks it and gets a 403. */}
                            {groupModal.mode !== 'view' && <div className="pt-sku-acts">
                              {/* Copying a saved SKU stages a NEW one rather than writing anything —
                                  it joins the list below and is created when the group is saved. */}
                              <button
                                type="button"
                                className="pt-sku-dup"
                                title={t('st.pfDuplicateSku')}
                                onClick={(e) => { e.stopPropagation(); duplicateSavedSku(sku); }}
                              >
                                <Copy size={13} />
                              </button>
                              {!groupModal.skuOnly && (
                                <button
                                  type="button"
                                  className="pt-sku-del"
                                  title={t('st.pgRemoveSku')}
                                  onClick={(e) => { e.stopPropagation(); unlinkSkuFromGroup(sku.id); }}
                                >&times;</button>
                              )}
                            </div>}
                          </div>
                          {groupSkuOpen[sku.id] && (
                            <ProjectSkuInlineEditor
                              form={groupSkuDrafts[sku.id] || projectToSkuDraft(sku)}
                              filterOptions={filterOptions}
                              rounds={rounds}
                              skuId={sku.id}
                              materialDrafts={groupMaterials}
                              /* a SKU this person may not change (another developer's, Jay 30/10) opens locked here too */
                              readOnly={groupModal.mode === 'view' || sku.canEdit === false}
                              onChange={(patch) => updateGroupSkuDraft(sku.id, patch)}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <MoveInList
                    items={groupMoveIns}
                    teamKey={groupModal.teamKey}
                    onRemove={(id) => setGroupMoveIns((prev) => prev.filter((x) => x.projectId !== id))}
                  />
                  {!!groupNewSkus.length && (
                    <div className="pt-sku-list" style={{ marginTop: 10 }}>
                      {groupNewSkus.map((sku, idx) => (
                        <SkuCard
                          key={idx}
                          index={groupSkus.length + idx}
                          sku={sku}
                          filterOptions={filterOptions}
                          defaultOpen
                          onChange={(patch) => updateGroupSku(idx, patch)}
                          onRemove={() => removeGroupSku(idx)}
                          onDuplicate={() => duplicateGroupSku(idx)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
              )}

              <div className="own-section own-project">
                <div className="own-hd" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <div className="own-hd-stack">
                    <span>{t('st.pfTickets')}</span>
                    <small>{(groupTickets.length + groupNewTickets.length)
                      ? t('st.pfTicketsAdded', { count: groupTickets.length + groupNewTickets.length })
                      : t('st.pfNoTicketYet')}</small>
                  </div>
                  {groupModal.mode !== 'view' && <div className="pt-ticket-controls">
                    <select className="pt-ticket-select" value={groupTicketTeam} onChange={(e) => setGroupTicketTeam(e.target.value)}>
                      <option value="">{t('st.pfAllReceivingTeams')}</option>
                      {groupReceivingTeams.map((team) => <option key={team} value={team}>{team}</option>)}
                    </select>
                    <select
                      className="pt-ticket-select pt-ticket-add"
                      value=""
                      onChange={(e) => { addGroupTicket(e.target.value); e.target.value = ''; }}
                      disabled={!groupAvailableTicketTypes.length || groupNeedsOwner}
                      title={groupNeedsOwner ? t('st.pfNeedOwnerShort') : ''}
                    >
                      <option value="">{t('st.pfAddTicket')}</option>
                      {groupAvailableTicketTypes.map((t) => (
                        <option key={t.code} value={t.code}>{t.code} — {t.name}</option>
                      ))}
                    </select>
                  </div>}
                </div>
                <div className="own-body">
                  {groupModal.mode !== 'view' && <div className="pt-ticket-note">
                    {/* Says what to do, not what the rule is: the person reading it is one dialog
                        away from being able to fix it. */}
                    <div className={groupNeedsOwner ? 'tkf-missing' : 'pt-help'}>
                      {groupNeedsOwner ? t('st.pfNeedOwner') : t('st.pfTicketHint')}
                    </div>
                    {!!groupNewTickets.length && (
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setGroupNewTickets([])}>{t('st.pfClearTickets')}</button>
                    )}
                  </div>}

                  {/* A ticket that already exists gets the SAME card as one being staged — Max,
                      2026-09-03: "nó phải giống như form lúc tạo chứ còn đằng này nó khác quá". It
                      was a second, narrower layout of the same thing; now there is one form. */}
                  {!!groupTickets.length && (
                    <div className="pt-sku-list" style={{ marginBottom: 10 }}>
                      {groupTickets.map((tk) => {
                        const draft = groupTicketDrafts[tk.taskId] || tk;
                        return (
                          <TicketCard
                            key={tk.taskId}
                            ticket={draft}
                            codeOptions={taskTypes.filter((x) => x.code === draft.taskType)}
                            codeLocked
                            defaultOpen={false}
                            readOnly={tk.canEdit === false || groupModal.mode === 'view'}
                            allTypes={taskTypes}
                            requester={draft.createdBy || draft.assignee || userName}
                            projectName={[groupModal.groupId, groupModal.groupName].filter(Boolean).join('_')}
                            requestingTeam={(trackerConfig?.teams || [])
                              .find((tm) => tm.teamKey === groupModal.teamKey)?.teamName || ''}
                            projectOwner={((trackerConfig?.teams || [])
                              .find((tm) => tm.teamKey === groupModal.teamKey)?.members || [])
                              .find((m) => String(m.userId) === String(groupModal.ownerUserId))?.name || ''}
                            groupSkus={groupSkus}
                            takenSkus={takenSkusFor(draft.taskType, tk.taskId)}
                            onChange={(patch) => updateGroupTicketDraft(tk, patch)}
                          />
                        );
                      })}
                    </div>
                  )}

                  {!!groupNewTickets.length && (
                    <div className="pt-sku-list">
                      {groupNewTickets.map((ticket, idx) => (
                        <TicketCard
                          key={idx}
                          ticket={ticket}
                          codeOptions={taskTypes.filter((t) => t.code === ticket.taskType || (groupCaseTicketTypes.includes(t) && !groupStagedCodes.includes(t.code)))}
                          allTypes={taskTypes}
                          requester={userName}
                          /* The mail prints "Project: {{project_id}} - {{project_name}}", and the
                             modal carries the two separately — passing the name alone left the id
                             as a dash. */
                          projectName={[groupModal.groupId, groupModal.groupName]
                            .filter(Boolean).join('_')}
                          requestingTeam={(trackerConfig?.teams || [])
                            .find((tm) => tm.teamKey === groupModal.teamKey)?.teamName || ''}
                          projectOwner={((trackerConfig?.teams || [])
                            .find((tm) => tm.teamKey === groupModal.teamKey)?.members || [])
                            .find((m) => String(m.userId) === String(groupModal.ownerUserId))?.name || ''}
                          /* The group's own SKUs: what the ticket ticks, and what its columns
                             prefill from. */
                          groupSkus={groupSkus}
                          takenSkus={takenSkusFor(ticket.taskType, ticket)}
                          onChange={(patch) => updateGroupTicket(idx, patch)}
                          onRemove={() => removeGroupTicket(idx)}
                        />
                      ))}
                    </div>
                  )}

                  {!groupTickets.length && !groupNewTickets.length && (
                    <div className="pt-help pt-ticket-empty">{t('st.pfTicketEmpty')}</div>
                  )}
                </div>
              </div>
            </div>
            {groupError && <div className="st-alert error st-alert-foot">{groupError}</div>}
            <div className="st-modal-foot st-form-actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={requestCloseGroupModal}>{groupModal.mode === 'view' ? t('st.ptViewClose') : t('st.pfCancel')}</button>
              {groupModal.mode !== 'view' && <button type="button" className="btn btn-primary btn-sm" disabled={groupSaving} onClick={saveGroup}>{groupSaving ? t('stx.saving') : (groupModal.mode === 'tickets' ? t('st.gmSaveTickets') : t('st.gmSaveGroup'))}</button>}
            </div>
          </div>
        </div>
      )}
      {taskModal && (
        <TaskModal
          mode={taskModal.mode}
          form={taskModal.form}
          taskTypes={taskTypes}
          projectGroups={taskProjectGroups}
          /* The SKUs of the project this ticket is on — what the request table ticks and prefills
             from. Recomputed as the project changes, because the code dropdown lets it change. */
          /* A NEW ticket may only name an APPROVED item — 2026-09-21. The project gate reads the
             GROUP, which says Approved as soon as one of its SKUs is, so the picker was offering
             items nobody had approved (PRJ-00169 is approved with two still Pending). Editing an
             EXISTING ticket keeps the full list: its rows were picked under the old rule and a
             SKU refused afterwards is a conversation, not a row to take away underneath somebody.
             The server refuses the same thing on create — a rule that lives in a picker is not a
             rule, and the ticket endpoint takes a plain POST. */
          /* Which items another ticket of this same code already holds — Max, 2026-09-21. The
             code can be raised again for the items left over; what stops the second ticket
             repeating the first is that those rows cannot be ticked. This ticket is left out of
             its own map, or editing it would lock every row it owns against itself. */
          takenSkus={tasks.reduce((acc, tk) => {
            if (tk.projectId !== taskModal.form.projectId
              || tk.taskType !== taskModal.form.taskType
              || tk.taskId === taskModal.form.taskId) return acc;
            ((tk.fields || {}).skus || []).forEach((id) => { acc[id] = tk.taskId; });
            return acc;
          }, {})}
          projectSkus={projects.filter((p) => p.groupId === taskModal.form.projectId
            && (taskModal.mode === 'edit' || taskModal.mode === 'view' || (p.approvalStatus || 'Approved') === 'Approved'))}
          projectDisplay={taskProjectDisplay}
          saving={taskSaving}
          errors={taskModalErrors}
          onChange={updateTaskModalForm}
          onClose={closeTaskModal}
          onSave={handleSaveTask}
          onDelete={handleDeleteTask}
          onSendEmail={handleSendTaskEmail}
        />
      )}
    </div>
    </TicketFormsContext.Provider>
    </PortalListsContext.Provider>
  );
}

export default SampleTracker;
